// @ts-nocheck
// M4 transition: the artifact was originally written as a single file with
// loose JS-style typing and direct window.storage references. TypeScript checks
// are disabled here until the dataStore is swapped for the Supabase-backed
// implementation in src/lib/dataStore.ts (Phase 3 of M4). The file is otherwise
// unchanged from the M2-final artifact.
import React, { useState, useEffect, useMemo, useCallback, useRef, Fragment } from 'react';
import { Wallet, TrendingUp, Receipt, Plus, Upload, MessageSquare, Trash2, Edit2, X, Calendar, ArrowUpRight, ArrowDownRight, Target, Home, Search, AlertCircle, CheckCircle2, Clock, ChevronLeft, ChevronRight, ChevronDown, Repeat, Pause, Play, Download, CreditCard, Landmark, LogOut, Zap, Bell, FileText, Flag, SlidersHorizontal, RotateCcw, MoreVertical, Minus } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useAuth } from './lib/AuthContext';
import { extractPages } from './lib/statements/pdfExtract.js';
import { dispatchParser } from './lib/statements/dispatch.js';
import { reconcile } from './lib/statements/reconcile.js';

// ============ CONSTANTS ============
// Set to true to enable verbose console logging for materializer, cleanup, and state updates.
const DEBUG = false;
const dlog = (...args) => { if (DEBUG) console.log(...args); };

const EXCHANGE_RATES = {
  AED: 1, USD: 3.6725, EUR: 4.00, GBP: 4.65, JOD: 5.18,
  MAD: 0.37, SAR: 0.98, INR: 0.044, EGP: 0.075,
};
// Seed list (the 9 the app shipped with) is only the pre-load fallback. The
// load effect replaces this with the full currencies table (~166) so manual
// currency dropdowns can pick anything fx-refresh covers. Mutable module-level
// so all the modal dropdowns read the live list once it's loaded.
let CURRENCIES = Object.keys(EXCHANGE_RATES);
const MS_PER_DAY = 86400000;            // milliseconds per day
const MS_PER_YEAR = MS_PER_DAY * 365.25; // avg ms per year (365.25 = leap-adjusted)

const DEFAULT_CATEGORIES = {
  expense: [
    { name: 'Food & Dining', color: '#ef4444', icon: '🍽️' },
    { name: 'Groceries', color: '#f97316', icon: '🛒' },
    { name: 'Transport', color: '#eab308', icon: '🚗' },
    { name: 'Shopping', color: '#ec4899', icon: '🛍️' },
    { name: 'Entertainment', color: '#a855f7', icon: '🎬' },
    { name: 'Utilities', color: '#3b82f6', icon: '💡' },
    { name: 'Rent', color: '#6366f1', icon: '🏠' },
    { name: 'Healthcare', color: '#14b8a6', icon: '⚕️' },
    { name: 'Travel', color: '#06b6d4', icon: '✈️' },
    { name: 'Subscriptions', color: '#8b5cf6', icon: '📱' },
    { name: 'Education', color: '#10b981', icon: '📚' },
    { name: 'Personal Care', color: '#f43f5e', icon: '💅' },
    { name: 'Gifts', color: '#d946ef', icon: '🎁' },
    { name: 'Loan Payment', color: '#0ea5e9', icon: '🏦' },
    { name: 'Other', color: '#64748b', icon: '📦' },
  ],
  income: [
    { name: 'Salary', color: '#22c55e', icon: '💼' },
    { name: 'Freelance', color: '#10b981', icon: '💻' },
    { name: 'Investment Returns', color: '#06b6d4', icon: '📈' },
    { name: 'Cashback & Rewards', color: '#f59e0b', icon: '💳' },
    { name: 'Gifts Received', color: '#a855f7', icon: '🎁' },
    { name: 'Other Income', color: '#64748b', icon: '💰' },
  ],
};
const ALL_CATEGORIES = [...DEFAULT_CATEGORIES.expense, ...DEFAULT_CATEGORIES.income, { name: 'Transfer', color: '#06b6d4', icon: '🔄' }];

// 'credit' and 'loan' types removed — cards and loans have their own tables
// since M3 and the DB's accounts_type_check rejects those values. The artifact
// originally lumped them into accounts.
const ACCOUNT_TYPES = [
  { value: 'cash', label: 'Cash', icon: '💵' },
  { value: 'checking', label: 'Checking', icon: '🏦' },
  { value: 'savings', label: 'Savings', icon: '🔐' },
  { value: 'investment', label: 'Investment', icon: '📈' },
  { value: 'crypto', label: 'Crypto', icon: '₿' },
];

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WORKING_WEEKEND = [0, 6]; // Sun, Sat (Mon-Fri working week)

// ============ UTILITIES ============
// M1 fix: FX rates are now live from the exchange_rates table, not the
// EXCHANGE_RATES constant. The load effect calls setLiveFxRates() after
// fetching them; until then we fall back to the hardcoded constants so the
// page renders with sensible defaults on first paint.
let liveRates = null;
const getRate = (currency) => {
  if (liveRates && liveRates[currency] != null) return liveRates[currency];
  return EXCHANGE_RATES[currency] || 1;
};
const setLiveFxRates = (rows) => {
  const byCode = {};
  const dateByCode = {};
  for (const r of rows || []) {
    const code = r.currencyCode || r.currency_code;
    const date = r.asOfDate || r.as_of_date || '';
    const rate = Number(r.rateToBase ?? r.rate_to_base) || 1;
    if (!code) continue;
    if (!dateByCode[code] || date >= dateByCode[code]) {
      byCode[code] = rate;
      dateByCode[code] = date;
    }
  }
  liveRates = byCode;
};
const toBase = (amount, currency) => (amount || 0) * getRate(currency);
const fromBase = (amount, currency) => (amount || 0) / getRate(currency);
const convert = (amount, from, to) => from === to ? amount : fromBase(toBase(amount, from), to);

// Date utilities — use local date strings (YYYY-MM-DD) consistently to avoid timezone drift
const todayStr = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const dateToStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const strToDate = (s) => {
  // Parse YYYY-MM-DD as local date, not UTC
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const cmpDateStr = (a, b) => a.localeCompare(b);

const fmtMoney = (n, currency = 'AED', round = true) => {
  const val = round ? Math.round(n) : Math.round(n * 100) / 100;
  return `${currency} ${val.toLocaleString('en-US', { maximumFractionDigits: round ? 0 : 2 })}`;
};

// Tiny concurrency pool — caps in-flight promises to `n`. Used by loadDemoData
// where Promise.all over 300+ inserts would 429 against Supabase. Returns
// undefined results; callers don't need them (writes are persistence-only).
const chunkedAll = async (items, concurrency, fn) => {
  const n = Math.max(1, concurrency);
  let i = 0;
  const workers = Array.from({ length: Math.min(n, items.length) }, async () => {
    while (i < items.length) {
      const idx = i++;
      try { await fn(items[idx], idx); } catch (e) { console.error('chunkedAll item failed:', e); }
    }
  });
  await Promise.all(workers);
};

// M4: IDs must be real UUIDs so they fit the Postgres uuid columns. The prefix
// argument is ignored (still passed by callers for readability — left in place
// so call sites don't need touching). Returns crypto.randomUUID() in browsers
// and a polyfill fallback for environments without it.
const uid = (_prefix?: string) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC4122 v4 fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// ============ RECURRENCE ENGINE ============
const lastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const isWorkingDay = (d) => !WORKING_WEEKEND.includes(d.getDay());

const getWeekdayInMonth = (year, month, weekday, occurrence) => {
  if (occurrence === -1) {
    const last = lastDayOfMonth(year, month);
    for (let d = last; d >= 1; d--) {
      const date = new Date(year, month, d);
      if (date.getDay() === weekday) return date;
    }
    return null;
  }
  let count = 0;
  const last = lastDayOfMonth(year, month);
  for (let d = 1; d <= last; d++) {
    const date = new Date(year, month, d);
    if (date.getDay() === weekday) {
      count++;
      if (count === occurrence) return date;
    }
  }
  return null;
};

const getLastWorkingDay = (year, month) => {
  const last = lastDayOfMonth(year, month);
  for (let d = last; d >= 1; d--) {
    const date = new Date(year, month, d);
    if (isWorkingDay(date)) return date;
  }
  return null;
};

// Nth-from-last working day: n=1 is last, n=2 is second-to-last, etc.
const getNthLastWorkingDay = (year, month, n) => {
  const last = lastDayOfMonth(year, month);
  let count = 0;
  for (let d = last; d >= 1; d--) {
    const date = new Date(year, month, d);
    if (isWorkingDay(date)) {
      count++;
      if (count === n) return date;
    }
  }
  return null;
};

const computeOccurrence = (rule, year, month) => {
  switch (rule.pattern) {
    case 'day_of_month': {
      const day = Math.min(Number(rule.dayOfMonth) || 1, lastDayOfMonth(year, month));
      return new Date(year, month, day);
    }
    case 'last_day':
      return new Date(year, month, lastDayOfMonth(year, month));
    case 'last_working_day':
      return getLastWorkingDay(year, month);
    case 'nth_last_working_day':
      return getNthLastWorkingDay(year, month, Number(rule.nthLastWorkingDay) || 1);
    case 'weekday_of_month':
      return getWeekdayInMonth(year, month, Number(rule.weekday) || 0, Number(rule.occurrence) || 1);
    default:
      return null;
  }
};

const generateOccurrences = (rule, fromDateStr, toDateStr) => {
  const dates = [];
  if (!rule.startDate) return dates;

  const ruleStart = strToDate(rule.startDate);
  const ruleEnd = rule.endDate ? strToDate(rule.endDate) : null;
  const from = strToDate(fromDateStr);
  const to = strToDate(toDateStr);
  const cap = ruleEnd && ruleEnd < to ? ruleEnd : to;
  const interval = Math.max(1, Number(rule.interval) || 1);
  const unit = rule.unit || 'month';

  if (unit === 'month' || unit === 'year') {
    const monthsStep = unit === 'year' ? 12 * interval : interval;
    // Yearly: anchor on the user-picked month_of_year if set, else fall back
    // to the start_date's month (back-compat with rules created before the
    // explicit month picker existed).
    const anchorMonth = unit === 'year' && rule.monthOfYear != null
      ? Number(rule.monthOfYear)
      : ruleStart.getMonth();
    let cursor = new Date(ruleStart.getFullYear(), anchorMonth, 1);
    // If the first anchor month falls before ruleStart, jump forward one
    // step so we don't generate a pre-start occurrence.
    if (cursor < new Date(ruleStart.getFullYear(), ruleStart.getMonth(), 1)) {
      cursor.setMonth(cursor.getMonth() + monthsStep);
    }
    let safety = 0;
    while (cursor <= cap && safety < 1200) {
      const occ = computeOccurrence(rule, cursor.getFullYear(), cursor.getMonth());
      if (occ && occ >= ruleStart && occ <= cap && occ >= from) {
        dates.push(new Date(occ));
      }
      cursor.setMonth(cursor.getMonth() + monthsStep);
      safety++;
    }
  } else if (unit === 'week') {
    // Weekly: anchor on the user-picked weekday if set, else use start_date's
    // weekday. Advance to the first matching weekday on or after start_date.
    const targetWeekday = rule.weekday != null && rule.weekday !== ''
      ? Number(rule.weekday)
      : ruleStart.getDay();
    let cursor = new Date(ruleStart);
    const diff = (targetWeekday - cursor.getDay() + 7) % 7;
    cursor.setDate(cursor.getDate() + diff);
    let safety = 0;
    while (cursor <= cap && safety < 1200) {
      if (cursor >= from) dates.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 7 * interval);
      safety++;
    }
  }

  return dates;
};

const describeRule = (rule) => {
  if (rule?.isInterestRule) {
    const rate = Number(rule.interestAnnualRatePct) || 0;
    const cap = rule.maxWithdrawalsPerMonth != null && rule.maxWithdrawalsPerMonth !== ''
      ? ` · cap ${rule.maxWithdrawalsPerMonth} withdrawals` : '';
    return `Interest ${rate.toFixed(2)}% p.a. on day ${rule.dayOfMonth}${cap}`;
  }
  const interval = Math.max(1, Number(rule.interval) || 1);
  const unit = rule.unit || 'month';
  const intervalText = interval === 1
    ? (unit === 'month' ? 'Monthly' : unit === 'week' ? 'Weekly' : 'Yearly')
    : `Every ${interval} ${unit}s`;

  if (unit === 'week') {
    const dayIdx = rule.weekday != null && rule.weekday !== ''
      ? Number(rule.weekday)
      : strToDate(rule.startDate).getDay();
    return `${intervalText} on ${WEEKDAYS[dayIdx]}`;
  }

  // For yearly rules, resolve the month (explicit monthOfYear, else the start
  // date's month) so the schedule reads e.g. "Yearly on Jan 11" not "on day 11".
  const yearMonth = unit === 'year'
    ? ((rule.monthOfYear != null && rule.monthOfYear !== '') ? Number(rule.monthOfYear)
       : (rule.startDate ? strToDate(rule.startDate).getMonth() : null))
    : null;
  const shortMonth = yearMonth != null ? MONTHS[yearMonth].slice(0, 3) : null;

  let when = '';
  switch (rule.pattern) {
    case 'day_of_month': when = shortMonth ? `on ${shortMonth} ${rule.dayOfMonth}` : `on day ${rule.dayOfMonth}`; break;
    case 'last_day': when = 'on the last day'; break;
    case 'last_working_day': when = 'on the last working day'; break;
    case 'nth_last_working_day': {
      const n = Number(rule.nthLastWorkingDay) || 1;
      const ord = n === 1 ? 'last' : n === 2 ? '2nd-to-last' : n === 3 ? '3rd-to-last' : `${n}th-to-last`;
      when = `on the ${ord} working day`;
      break;
    }
    case 'weekday_of_month': {
      const occ = rule.occurrence === -1 ? 'last' : ['first', 'second', 'third', 'fourth'][rule.occurrence - 1];
      when = `on the ${occ} ${WEEKDAYS[rule.weekday]}`;
      break;
    }
  }
  // Other yearly patterns (last day, nth working day, weekday-of-month) read
  // naturally with a trailing " of <Month>"; day_of_month already has it inline.
  if (unit === 'year' && yearMonth != null && rule.pattern !== 'day_of_month') {
    when += ` of ${MONTHS[yearMonth]}`;
  }
  return `${intervalText} ${when}`.trim();
};

// ============ CARD HELPERS ============
// Both halves of the billing cycle are days-of-month, matching how banks quote
// them ("statement closes on the 8th, payment due on the 2nd"):
//   statementDay   = day of month the statement closes (e.g., 25)
//   paymentDueDay  = day of month the payment is due (e.g., 15 of next month)
const nextCardStatementDate = (card) => {
  if (!card?.statementDay) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = Math.min(card.statementDay, lastDayOfMonth(today.getFullYear(), today.getMonth()));
  let next = new Date(today.getFullYear(), today.getMonth(), day);
  if (next <= today) {
    const nm = today.getMonth() + 1;
    const nmY = nm > 11 ? today.getFullYear() + 1 : today.getFullYear();
    const nmM = nm > 11 ? 0 : nm;
    next = new Date(nmY, nmM, Math.min(card.statementDay, lastDayOfMonth(nmY, nmM)));
  }
  return next;
};

const nextCardPaymentDate = (card) => {
  if (!card?.paymentDueDay) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = card.paymentDueDay;
  const thisMonth = Math.min(day, lastDayOfMonth(today.getFullYear(), today.getMonth()));
  let due = new Date(today.getFullYear(), today.getMonth(), thisMonth);
  if (due < today) {
    const nm = today.getMonth() + 1;
    const nmY = nm > 11 ? today.getFullYear() + 1 : today.getFullYear();
    const nmM = nm > 11 ? 0 : nm;
    due = new Date(nmY, nmM, Math.min(day, lastDayOfMonth(nmY, nmM)));
  }
  return due;
};

// ============ LOAN HELPERS ============
// Standard amortization: monthly payment for a loan
const calculateMonthlyPayment = (principal, annualRate, termMonths) => {
  if (!principal || !termMonths) return 0;
  const r = (annualRate || 0) / 100 / 12;
  if (r === 0) return principal / termMonths;
  return (principal * r * Math.pow(1 + r, termMonths)) / (Math.pow(1 + r, termMonths) - 1);
};

// Months remaining given current balance, original rate, original payment
const calculateMonthsRemaining = (balance, annualRate, monthlyPayment) => {
  if (!balance || !monthlyPayment) return 0;
  const r = (annualRate || 0) / 100 / 12;
  if (r === 0) return Math.ceil(balance / monthlyPayment);
  // n = -log(1 - balance*r/payment) / log(1+r)
  const inner = 1 - (balance * r) / monthlyPayment;
  if (inner <= 0) return Infinity; // payment too small to ever pay off
  return Math.ceil(-Math.log(inner) / Math.log(1 + r));
};

// Split a payment into principal + interest portions for a given balance
const splitPayment = (balance, annualRate, payment) => {
  const r = (annualRate || 0) / 100 / 12;
  const interest = balance * r;
  const principal = Math.max(0, payment - interest);
  return { interest, principal };
};

// ============ BALANCE COMPUTATIONS ============
// Single source of truth for live balance math. The starting balance + all transaction
// effects, expressed in the account/card's own currency. Used by Dashboard stats,
// Dashboard forecast, Accounts tab, and Cards tab — keep behaviour identical across
// all of them by calling these helpers, never by inlining the math.

// Live balance of a deposit-like account (cash/checking/savings/etc.).
// Income increases it. Expense decreases it (refund reverses). Transfers in/out adjust.
// M1 fix: balance math now keys on stable uuids (account.id), not display names.
// Name-based matching breaks when the user renames an account or has two
// accounts with the same name. Falls back to name matching only for legacy
// rows where the FK wasn't backfilled.
const computeAccountLiveBalance = (account, transactions) => {
  const today = todayStr();
  let change = 0;
  transactions.forEach(t => {
    if (t.excludesFromBalances) return;
    if (t.date && t.date > today) return;
    if (t.type === 'transfer') {
      const fromMatch = t.fromAccountId ? t.fromAccountId === account.id : t.fromAccount === account.name;
      const toMatch   = t.toAccountId   ? t.toAccountId   === account.id : t.toAccount   === account.name;
      if (fromMatch) change -= convert(t.amount, t.currency, account.currency);
      if (toMatch)   change += convert(t.amount, t.currency, account.currency);
    } else {
      const owns = t.accountId ? t.accountId === account.id : t.account === account.name;
      if (!owns) return;
      const amt = convert(t.amount, t.currency, account.currency);
      if (t.type === 'income') change += amt;
      else if (t.type === 'expense') change += t.isRefund ? amt : -amt;
    }
  });
  return (account.balance || 0) + change;
};

// Live balance owed on a credit card. Spending INCREASES debt. Cashback/refund DECREASES.
// Transfer TO the card (paying it down) DECREASES debt. Transfer FROM card INCREASES.
//
// M2.5 Session C: installments contribute as a *drift* (remaining - original),
// not as a raw +remaining. At conversion time remaining = original so the drift
// is 0 — converting a $2000 balance into a $2000 installment leaves the card
// at the same total owed. As payments reduce remaining_balance, the drift goes
// negative and the card's total owed shrinks. The M1 doc captures this as two
// derived numbers (general balance + total owed) but the practical "what do I
// owe right now" for the UI is best computed as a single drift adjustment.
const computeCardLiveBalance = (card, transactions, installments = []) => {
  // Card owed = opening
  //           + sum(real tx contribs including installment-linked monthly txs)
  //           + sum(future monthly_payment cycles for live installments on
  //                  this card, strictly AFTER today)
  //
  // Two halves of the same idea:
  //   1. Anything that has fired as a transaction (real purchase, recurring
  //      subscription, the installment's monthly auto-rule tx) deducts from
  //      the card on its date. Today's payment_day tx is one of these — it
  //      hits the card today as a normal expense.
  //   2. Everything still ahead in the installment schedule is pre-loaded:
  //      sum of monthly_payments (principal + interest) for cycles strictly
  //      > today. As each cycle's date arrives the materializer fires its
  //      tx — that cycle moves from "future" into "tx contribs" with no
  //      net change in card_owed, exactly mirroring how a bank statement
  //      works.
  //
  // Balance-transfer / easy-cash kinds already create a setup transfer tx
  // that loads the target card at creation, so we skip them here to avoid
  // double-counting with the future-cycles sum.
  //
  // Future-dated transactions still don't count — they only feed forecast.
  const today = todayStr();
  let change = 0;
  transactions.forEach(t => {
    if (t.excludesFromBalances) return;
    if (t.date && t.date > today) return;
    if (t.type === 'transfer') {
      const toMatch   = t.toCardId   ? t.toCardId   === card.id : t.toAccount   === card.name;
      const fromMatch = t.fromCardId ? t.fromCardId === card.id : t.fromAccount === card.name;
      if (toMatch)   change -= convert(t.amount, t.currency, card.currency);
      if (fromMatch) change += convert(t.amount, t.currency, card.currency);
    } else {
      const owns = t.cardId ? t.cardId === card.id : t.account === card.name;
      if (!owns) return;
      const amt = convert(t.amount, t.currency, card.currency);
      if (t.type === 'expense') change += t.isRefund ? -amt : amt;
      else if (t.type === 'income') change -= amt; // cashback reduces what you owe
    }
  });

  let installmentLoad = 0;
  installments.forEach(ci => {
    if (ci.deletedAt) return;
    if (ci.cardId !== card.id) return;
    if (ci.kind === 'balance_transfer' || ci.kind === 'easy_cash') return;
    const term = Number(ci.termMonths) || 0;
    const monthlyPay = Number(ci.monthlyPayment) || 0;
    if (term <= 0 || monthlyPay <= 0) return;
    const cyclesThroughToday = paymentsElapsed(ci.startDate, Number(ci.paymentDay) || 1);
    const futureCycles = Math.max(0, term - cyclesThroughToday);
    installmentLoad += convert(futureCycles * monthlyPay, ci.currency, card.currency);
  });

  return (card.currentBalance || 0) + change + installmentLoad;
};

// A card's balance as of a given date (its statement-close date), EXCLUDING the
// installment future-load. A bank statement bills only what was charged through
// the close date — installment monthly payments show up as their own dated txns
// (captured here), but the remaining installment schedule is NOT billed in one
// statement. The cash forecast uses this to size the statement the user pays at
// payday. Mirrors computeCardLiveBalance's tx math, frozen at `asOf`.
const cardStatementBalanceAsOf = (card, transactions, asOf) => {
  let change = 0;
  transactions.forEach(t => {
    if (t.deletedAt || t.excludesFromBalances) return;
    if (!t.date || t.date > asOf) return;
    if (t.type === 'transfer') {
      const toMatch   = t.toCardId   ? t.toCardId   === card.id : t.toAccount   === card.name;
      const fromMatch = t.fromCardId ? t.fromCardId === card.id : t.fromAccount === card.name;
      if (toMatch)   change -= convert(t.amount, t.currency, card.currency);
      if (fromMatch) change += convert(t.amount, t.currency, card.currency);
    } else {
      const owns = t.cardId ? t.cardId === card.id : t.account === card.name;
      if (!owns) return;
      const amt = convert(t.amount, t.currency, card.currency);
      if (t.type === 'expense') change += t.isRefund ? -amt : amt;
      else if (t.type === 'income') change -= amt; // cashback reduces what you owe
    }
  });
  return (card.currentBalance || 0) + change;
};

// ============ DUPLICATE DETECTION ============
// Suspected-duplicate keys for a transaction. Two txs are likely the same if
// they share ANY key:
//   • ref:<ref_no>  — bank reference numbers are globally unique, so this is
//     the strongest signal and matches at any distance in time.
//   • h:<type|amount|day|instrument|merchant>  — same type, AED amount,
//     account/card, merchant/description, on the SAME calendar day. Tight on
//     purpose: same-day never flags legitimate repeats (daily Noon orders,
//     weekly, monthly subscriptions are all on different days), but always
//     catches an accidental re-import (which keeps the original tx date).
const normDupeStr = (s) => (s ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
const txInstrument = (t) =>
  t.cardId || t.accountId || t.fromCardId || t.toCardId || t.fromAccountId || t.toAccountId || t.toLoanId || '';
const txDupeKeys = (t) => {
  const keys: string[] = [];
  const ref = normDupeStr(t.refNo);
  if (ref) keys.push(`ref:${ref}`);
  // Amount component: prefer the ORIGINAL foreign amount/currency when present.
  // It's stable across re-imports; the AED settled amount is re-derived (FX /
  // available-limit delta) and can drift cent-to-cent, which would otherwise
  // break the match for foreign txs. Falls back to the AED amount.
  const hasOrig = t.originalCurrency && Number(t.originalAmount) > 0;
  const amt = hasOrig
    ? `${normDupeStr(t.originalCurrency)}${Math.round(Number(t.originalAmount) * 100)}`
    : `aed${Math.round((toBase(Number(t.amount) || 0, t.currency || 'AED')) * 100)}`;
  const hasAmt = hasOrig || (Number(t.amount) || 0) > 0;
  if (hasAmt && t.date) keys.push(`h:${t.type}|${amt}|${t.date}|${txInstrument(t)}|${normDupeStr(t.description)}`);
  return keys;
};
// Group non-deleted txs into suspected-duplicate clusters. Returns the set of
// tx ids that share a key with ≥1 other tx, plus the distinct clusters.
const computeDuplicateTxGroups = (transactions) => {
  const byKey = new Map<string, string[]>();
  const ackById = new Map<string, boolean>();
  for (const t of transactions) {
    if (t.deletedAt) continue;
    ackById.set(t.id, !!t.dupeAck);
    for (const k of txDupeKeys(t)) {
      const arr = byKey.get(k);
      if (arr) arr.push(t.id); else byKey.set(k, [t.id]);
    }
  }
  const dupeIds = new Set<string>();
  const groups: string[][] = [];
  const seen = new Set<string>();
  for (const ids of byKey.values()) {
    const uniq = [...new Set(ids)];
    if (uniq.length < 2) continue;
    // Suppress a cluster only when every member is acknowledged ("Keep both").
    // A fresh, un-acked copy joining the cluster re-flags the whole thing.
    if (uniq.every(id => ackById.get(id))) continue;
    uniq.forEach(id => dupeIds.add(id));
    const gk = uniq.slice().sort().join(',');
    if (!seen.has(gk)) { seen.add(gk); groups.push(uniq); }
  }
  return { dupeIds, groups };
};

// Transaction-driven net worth (cash − card debt) in BASE currency, reconstructed
// as of `asOf` (YYYY-MM-DD) from the CURRENT transaction list. Deliberately
// mirrors the pg_cron snapshot's accounts+cards math (sum of amount_base tx
// contributions + opening_balance→base), but applies a strict date<=asOf cutoff.
// Used to refresh the FROZEN tx portion of stored net-worth snapshots so the Net
// Worth Trail reflects later edits/deletes/redates of past transactions — while
// the snapshot's market-priced parts (investments/assets) stay as real history.
const txDrivenNetWorthBaseAsOf = (accounts, cards, transactions, asOf) => {
  let accountsBase = 0, cardsBase = 0;
  for (const a of accounts) {
    if (a.deletedAt) continue;
    let contrib = 0;
    for (const t of transactions) {
      if (t.deletedAt || t.excludesFromBalances) continue;
      if (t.date && t.date > asOf) continue;
      const base = Number(t.amountBase ?? toBase(t.amount, t.currency)) || 0;
      if (t.type === 'transfer') {
        if (t.toAccountId === a.id) contrib += base;
        if (t.fromAccountId === a.id) contrib -= base;
      } else if (t.accountId === a.id) {
        if (t.type === 'income') contrib += base;
        else if (t.type === 'expense') contrib += t.isRefund ? base : -base;
      }
    }
    accountsBase += toBase(Number(a.balance) || 0, a.currency) + contrib;
  }
  for (const c of cards) {
    if (c.deletedAt) continue;
    let contrib = 0;
    for (const t of transactions) {
      if (t.deletedAt || t.excludesFromBalances) continue;
      if (t.date && t.date > asOf) continue;
      const base = Number(t.amountBase ?? toBase(t.amount, t.currency)) || 0;
      if (t.type === 'transfer') {
        if (t.toCardId === c.id) contrib -= base;   // paying the card down lowers debt
        if (t.fromCardId === c.id) contrib += base;
      } else if (t.cardId === c.id) {
        if (t.type === 'expense') contrib += t.isRefund ? -base : base;
        else if (t.type === 'income') contrib -= base; // cashback/refund lowers debt
      }
    }
    cardsBase += toBase(Number(c.currentBalance) || 0, c.currency) + contrib;
  }
  return accountsBase - cardsBase;
};

// ============ FORECAST CORE (shared by Dashboard chart + notifications) ============
const computeCashOnHand = (accounts, transactions) =>
  accounts.reduce((s, a) => a.deletedAt ? s : s + toBase(computeAccountLiveBalance(a, transactions), a.currency), 0);

// Interest-earning accounts pooled into one savings balance + balance-weighted
// rate (any account with an interest rule, regardless of active/auto-log).
const computeInterestParams = (accounts, transactions, recurring) => {
  const rules = recurring.filter(r => r.isInterestRule && !r.deletedAt && r.accountId);
  let savingsBal = 0, rateNum = 0, postDay = 31;
  const seen = new Set();
  rules.forEach(r => {
    const acc = accounts.find(a => a.id === r.accountId);
    if (!acc || acc.deletedAt || seen.has(acc.id)) return;
    seen.add(acc.id);
    const bal = toBase(computeAccountLiveBalance(acc, transactions), acc.currency);
    if (bal <= 0) return;
    savingsBal += bal;
    rateNum += bal * (Number(r.interestAnnualRatePct) || 0);
    const pd = Number(r.dayOfMonth) || 31;
    if (pd < postDay) postDay = pd;
  });
  const annualPct = savingsBal > 0 ? rateNum / savingsBal : 0;
  return { savingsBal, monthlyRate: annualPct / 100 / 12, postDay, active: savingsBal > 0 && annualPct > 0 };
};

// Project month-end CASH (settlement basis) for `horizon` months. Returns a
// series of { key, label, cash, net, cardPay, interest }. Pure function so both
// the Dashboard chart and the liquidity notification use one implementation.
// ============ Forecast learning layers (see FORECASTING.md §6) ============
// Each learns from logged history with recency weighting and falls back to a
// safe default when there isn't enough data, so a brand-new account still gets
// a sane forecast and it sharpens automatically as history accumulates.
const FORECAST_WINDOW_MONTHS = 6;      // trailing window the learners look at
const RECENCY_DECAY = 0.85;            // weight multiplier per month older
const UAE_DEFAULT_APR_PCT = 39;        // fallback credit-card APR when none set

const monthsAgoOf = (dateStr, todayD) => {
  const d = strToDate(dateStr);
  return (todayD.getFullYear() - d.getFullYear()) * 12 + (todayD.getMonth() - d.getMonth());
};

// §6a — per-category card-vs-account split, recency- and amount-weighted.
// Returns a fn(categoryName) -> fraction of that category's spend that goes on a
// CARD (deferred to a statement). Falls back: category history → portfolio-wide
// history → 1 (treat as card, the dominant real pattern and the prior engine's
// implicit assumption). Net worth is instrument-agnostic, so this only refines
// the cash line's timing (immediate debit vs deferred card).
const learnCategorySplit = (transactions, cards, todayD) => {
  const cardNames = new Set((cards || []).filter(c => !c.deletedAt).map(c => c.name));
  const tStr = dateToStr(todayD);
  const perCat = new Map();
  let pCard = 0, pAcct = 0;
  (transactions || []).forEach(t => {
    if (t.deletedAt || t.excludesFromBalances || t.isRefund || t.type !== 'expense') return;
    if (!t.date || t.date > tStr) return;
    const ma = monthsAgoOf(t.date, todayD);
    if (ma < 0 || ma > FORECAST_WINDOW_MONTHS) return;
    const val = (Number(t.amount) || 0) * Math.pow(RECENCY_DECAY, ma);
    const isCard = !!t.cardId || (t.account && cardNames.has(t.account));
    const cat = t.category || '(uncat)';
    if (!perCat.has(cat)) perCat.set(cat, { card: 0, acct: 0 });
    const e = perCat.get(cat);
    if (isCard) { e.card += val; pCard += val; } else { e.acct += val; pAcct += val; }
  });
  const portfolio = (pCard + pAcct) > 0 ? pCard / (pCard + pAcct) : null;
  return (catName) => {
    const e = perCat.get(catName);
    if (e && (e.card + e.acct) > 0) return e.card / (e.card + e.acct);
    if (portfolio != null) return portfolio;
    return 1;
  };
};

// §6d — learned variable income. Median (not mean, so one big month can't
// inflate it) of monthly NON-recurring income over the window. Returns 0 unless
// such income appears in >=3 distinct months — that guard stops a one-off bonus
// being extrapolated into every future month. Declared recurring income and
// already-entered future-dated income are handled separately by the engine.
const learnVariableIncome = (transactions, todayD) => {
  const tStr = dateToStr(todayD);
  const byMonth = new Map();
  (transactions || []).forEach(t => {
    if (t.deletedAt || t.excludesFromBalances || t.recurringId || t.type !== 'income') return;
    if (!t.date || t.date > tStr) return;
    const ma = monthsAgoOf(t.date, todayD);
    if (ma < 0 || ma > FORECAST_WINDOW_MONTHS) return;
    const mk = t.date.slice(0, 7);
    byMonth.set(mk, (byMonth.get(mk) || 0) + toBase(Number(t.amount) || 0, t.currency));
  });
  if (byMonth.size < 3) return 0;
  const vals = [...byMonth.values()].sort((a, b) => a - b);
  return vals[Math.floor(vals.length / 2)];
};

// The amount actually DUE this cycle for a statement. Prefers the parsed Total
// Payment Due (excludes not-yet-due installment/EPP balances that inflate the
// closing balance); falls back to the closing balance when we didn't capture it.
// Credits (CR / negative Total Payment Due) clamp to 0 — nothing to pay.
const statementAmountDue = (s) => {
  if (!s) return 0;
  const tpd = s.totalPaymentDue;
  const base = (tpd != null && Number.isFinite(Number(tpd))) ? Number(tpd) : (Number(s.closingBalance) || 0);
  return Math.max(0, Math.round(base * 100) / 100);
};

// §6b — learned card payment-ratio: payments toward the card ÷ statement closing
// balance, recency-weighted across cycles. Only counts statements whose due date
// has already PASSED (so a freshly-uploaded, not-yet-due statement with no
// payment can't be mistaken for a 0% revolver). Needs >=2 such cycles; otherwise
// defaults to 1.0 (pay in full).
const learnPaymentRatio = (card, transactions, cardStatements, todayD) => {
  const tStr = dateToStr(todayD);
  const stmts = (cardStatements || [])
    .filter(s => s.cardId === card.id && !s.deletedAt && s.statementDate
      && s.dueDate && s.dueDate < tStr && Number(s.closingBalance) > 0)
    .sort((a, b) => cmpDateStr(a.statementDate, b.statementDate));
  if (stmts.length < 2) return 1;
  let wRatio = 0, wSum = 0;
  stmts.forEach((s, i) => {
    const closing = Number(s.closingBalance) || 0;
    if (closing <= 0) return;
    const nextStmt = stmts[i + 1];
    const until = nextStmt ? nextStmt.statementDate : tStr;
    const paid = (transactions || []).filter(t => !t.deletedAt && t.type === 'transfer'
      && (t.toCardId ? t.toCardId === card.id : t.toAccount === card.name)
      && t.date > s.statementDate && t.date <= until
    ).reduce((sum, t) => sum + convert(t.amount, t.currency, card.currency), 0);
    const ratio = Math.min(1, Math.max(0, paid / closing));
    const w = Math.pow(RECENCY_DECAY, Math.max(0, monthsAgoOf(s.statementDate, todayD)));
    wRatio += ratio * w; wSum += w;
  });
  return wSum > 0 ? wRatio / wSum : 1;
};

const projectCashSeries = ({ accounts, transactions, recurring, budgets, cards, cardStatements = [], baseCurrency, horizon, investments = [], assets = [], loans = [], cardInstallments = [] }) => {
  const cashOnHand = computeCashOnHand(accounts, transactions);
  const interestParams = computeInterestParams(accounts, transactions, recurring);
  const now = new Date();
  const tStr = todayStr();
  const todayD = strToDate(tStr);
  const BUFFER_DAYS = 1;
  const addDays = (dt, n) => { const d = new Date(dt); d.setDate(d.getDate() + n); return d; };
  const monthKeyOf = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;

  const salaryRule = recurring
    .filter(r => r.active && !r.deletedAt && r.type === 'income' && !r.isInterestRule && r.startDate)
    .sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0))[0] || null;
  const paydayInMonth = (yr, mo) => {
    const last = lastDayOfMonth(yr, mo);
    if (salaryRule) {
      const ms = `${yr}-${String(mo + 1).padStart(2, '0')}-01`;
      const me = `${yr}-${String(mo + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
      const occs = generateOccurrences(salaryRule, ms, me);
      if (occs.length) return occs[occs.length - 1];
    }
    return new Date(yr, mo, last);
  };
  const firstPaydayOnOrAfter = (dt) => {
    let y = dt.getFullYear(), m = dt.getMonth();
    for (let k = 0; k < 14; k++) {
      const pd = paydayInMonth(y, m);
      if (pd >= dt) return pd;
      m++; if (m > 11) { m = 0; y++; }
    }
    return null;
  };
  // Learned card-payment day-of-month: the owner's typical payment day inferred
  // from logged card payments (transfers into a card). Needs >=2 samples; until
  // then it's null and we fall back to the statement DUE DATE (the default).
  const cardPays = (transactions || []).filter(t => !t.deletedAt && t.type === 'transfer'
    && (t.toCardId || (t.toAccount && cards.some(c => c.name === t.toAccount))) && t.date);
  let learnedPayDay = null;
  if (cardPays.length >= 2) {
    const days = cardPays.map(t => strToDate(t.date).getDate());
    learnedPayDay = Math.min(28, Math.max(1, Math.round(days.reduce((s, d) => s + d, 0) / days.length)));
  }
  // The statement's due date: the card's payment_due_day on the first occurrence
  // strictly after the close (due is always after close).
  const dueDateFromClose = (card, closeDt) => {
    const dd = Number(card.paymentDueDay) || 0;
    if (!dd) return firstPaydayOnOrAfter(addDays(closeDt, BUFFER_DAYS));
    let y = closeDt.getFullYear(), m = closeDt.getMonth();
    for (let k = 0; k < 14; k++) {
      const d = new Date(y, m, Math.min(dd, lastDayOfMonth(y, m)));
      if (d > closeDt) return d;
      m++; if (m > 11) { m = 0; y++; }
    }
    return firstPaydayOnOrAfter(addDays(closeDt, BUFFER_DAYS));
  };
  // Date a statement closing on `closeDt` is paid. DEFAULT = the due date
  // (uploaded due_date if present, else payment_due_day). Once we've learned the
  // owner pays earlier (>=2 logged payments), use that learned day-of-month
  // instead. So: due-date by default, learn-pays-early over time (§6c).
  const payDateFromClose = (card, closeDt, uploadedDue) => {
    if (learnedPayDay != null) {
      const minDt = addDays(closeDt, BUFFER_DAYS);
      let y = minDt.getFullYear(), m = minDt.getMonth();
      for (let k = 0; k < 14; k++) {
        const d = new Date(y, m, Math.min(learnedPayDay, lastDayOfMonth(y, m)));
        if (d >= minDt) return d;
        m++; if (m > 11) { m = 0; y++; }
      }
    }
    if (uploadedDue) { const d = strToDate(uploadedDue); if (d > closeDt) return d; }
    return dueDateFromClose(card, closeDt);
  };
  const rangeFor = (offset) => {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const yr = d.getFullYear(), mo = d.getMonth();
    const mStr = `${yr}-${String(mo + 1).padStart(2, '0')}`;
    return {
      yr, mo, mStr,
      mStart: `${mStr}-01`,
      mEnd: `${mStr}-${String(lastDayOfMonth(yr, mo)).padStart(2, '0')}`,
      label: d.toLocaleDateString('en', { month: 'short', year: '2-digit' }),
    };
  };
  const incomeForRange = (fromStr, toStr) => {
    let v = 0;
    recurring.filter(r => r.active && !r.deletedAt && r.type === 'income' && r.accountId && !r.isInterestRule).forEach(r => {
      const occs = generateOccurrences(r, fromStr, toStr);
      if (occs.length) v += toBase((Number(r.amount) || 0) * occs.length, r.currency);
    });
    transactions.filter(t => !t.deletedAt && !t.excludesFromBalances && !t.recurringId
      && t.type === 'income' && t.date >= fromStr && t.date <= toStr
    ).forEach(t => { v += toBase(Number(t.amount) || 0, t.currency); });
    return v;
  };
  const directOutForRange = (fromStr, toStr) => {
    let v = 0;
    recurring.filter(r => r.active && !r.deletedAt && r.type === 'expense' && r.accountId && !r.cardId).forEach(r => {
      const occs = generateOccurrences(r, fromStr, toStr);
      if (occs.length) v += toBase((Number(r.amount) || 0) * occs.length, r.currency);
    });
    recurring.filter(r => r.active && !r.deletedAt && r.type === 'transfer' && r.linkedLoanId).forEach(r => {
      const occs = generateOccurrences(r, fromStr, toStr);
      if (occs.length) v += toBase((Number(r.amount) || 0) * occs.length, r.currency);
    });
    transactions.filter(t => !t.deletedAt && !t.excludesFromBalances && !t.recurringId
      && t.type === 'expense' && !t.isRefund && t.accountId && t.date >= fromStr && t.date <= toStr
    ).forEach(t => { v += toBase(Number(t.amount) || 0, t.currency); });
    return v;
  };
  const cardShareFor = learnCategorySplit(transactions, cards, todayD);
  const learnedVarIncome = learnVariableIncome(transactions, todayD);

  // Budget lines for a month, falling back to the most recent prior budgeted
  // month so projections continue past the last explicitly-budgeted month.
  const budgetsForMonth = (mStr) => {
    let mb = budgets.filter(b => b.month === mStr && !b.deletedAt);
    if (!mb.length) {
      const bms = [...new Set(budgets.filter(b => !b.deletedAt && b.month).map(b => b.month))].sort();
      let base = null; for (const m of bms) if (m < mStr) base = m;
      if (base != null) mb = budgets.filter(b => b.month === base && !b.deletedAt);
    }
    return mb;
  };
  // Recurring expense/loan occurrences in [from,to], grouped by category and
  // split by declared instrument (card vs account).
  const recurringByCatForRange = (fromStr, toStr) => {
    const m = new Map();
    const add = (cat, key, val) => { const c = cat || '(uncat)'; if (!m.has(c)) m.set(c, { acct: 0, card: 0 }); m.get(c)[key] += val; };
    recurring.filter(r => r.active && !r.deletedAt && r.type === 'expense').forEach(r => {
      const occs = generateOccurrences(r, fromStr, toStr);
      if (occs.length) add(r.category, r.cardId ? 'card' : 'acct', toBase((Number(r.amount) || 0) * occs.length, r.currency));
    });
    recurring.filter(r => r.active && !r.deletedAt && r.type === 'transfer' && r.linkedLoanId).forEach(r => {
      const occs = generateOccurrences(r, fromStr, toStr);
      if (occs.length) add(r.category, 'acct', toBase((Number(r.amount) || 0) * occs.length, r.currency));
    });
    return m;
  };
  // §6a/§7 — route a future month's spend into immediate (account) vs deferred
  // (card) buckets: recurring goes by its declared instrument; the discretionary
  // remainder (budget − recurring, per category) is split by the learned card
  // share. Budget is the envelope; recurring is a floor.
  const routingCache = new Map();
  const monthSpendRouting = (mStr, mStart, mEnd) => {
    if (routingCache.has(mStr)) return routingCache.get(mStr);
    const mb = budgetsForMonth(mStr);
    const rec = recurringByCatForRange(mStart, mEnd);
    let accountOut = 0, cardSpend = 0;
    const seen = new Set();
    mb.forEach(b => {
      const cat = b.category || '(uncat)'; seen.add(cat);
      const budgetAmt = toBase(Number(b.amount) || 0, baseCurrency);
      const r = rec.get(cat) || { acct: 0, card: 0 };
      const disc = Math.max(0, budgetAmt - r.acct - r.card);
      const cs = cardShareFor(cat);
      accountOut += r.acct + disc * (1 - cs);
      cardSpend += r.card + disc * cs;
    });
    rec.forEach((r, cat) => { if (!seen.has(cat)) { accountOut += r.acct; cardSpend += r.card; } });
    const out = { accountOut, cardSpend };
    routingCache.set(mStr, out);
    return out;
  };
  const cardEstimateFor = (offset) => {
    const { mStr, mStart, mEnd } = rangeFor(offset);
    if (offset === 0) {
      // current month keeps the simpler budget − account-actuals estimate.
      const mb = budgetsForMonth(mStr);
      if (!mb.length) return 0;
      const total = mb.reduce((s, b) => s + toBase(Number(b.amount) || 0, baseCurrency), 0);
      return Math.max(0, total - directOutForRange(mStart, mEnd));
    }
    return monthSpendRouting(mStr, mStart, mEnd).cardSpend;
  };
  const accountEstimateFor = (offset) => {
    const { mStr, mStart, mEnd } = rangeFor(offset);
    return monthSpendRouting(mStr, mStart, mEnd).accountOut;
  };
  const closeOn = (card, yr, mo) => new Date(yr, mo, Math.min(Number(card.statementDay) || 1, lastDayOfMonth(yr, mo)));
  const paymentsTowardCardAfter = (card, afterStr) =>
    transactions.filter(t => !t.deletedAt && t.type === 'transfer'
      && (t.toCardId ? t.toCardId === card.id : t.toAccount === card.name)
      && t.date > afterStr && t.date <= tStr
    ).reduce((s, t) => s + convert(t.amount, t.currency, card.currency), 0);
  const offsetOfMonth = (dt) => (dt.getFullYear() - now.getFullYear()) * 12 + (dt.getMonth() - now.getMonth());

  const liveCards = cards.filter(c => !c.deletedAt && c.statementDay);
  const bnowByCard = new Map();
  let totalBnow = 0;
  liveCards.forEach(c => {
    const b = Math.max(0, toBase(cardStatementBalanceAsOf(c, transactions, tStr), c.currency));
    bnowByCard.set(c.id, b); totalBnow += b;
  });
  const shareOf = (c) => totalBnow > 0 ? (bnowByCard.get(c.id) || 0) / totalBnow : (liveCards.length ? 1 / liveCards.length : 0);

  const cardPayByMonth = {};
  const revolveInterestByMonth = {};
  const addPay = (payday, amtBase) => {
    if (!payday || payday <= todayD || amtBase <= 0.5) return;
    const k = monthKeyOf(payday);
    cardPayByMonth[k] = (cardPayByMonth[k] || 0) + amtBase;
  };
  const addInterest = (closeDt, amtBase) => {
    if (amtBase <= 0.5) return;
    const k = monthKeyOf(closeDt);
    revolveInterestByMonth[k] = (revolveInterestByMonth[k] || 0) + amtBase;
  };
  liveCards.forEach(c => {
    // §6b — learned pay-in-full vs revolve behaviour, + APR on any carried balance.
    const ratio = learnPaymentRatio(c, transactions, cardStatements, todayD);
    const mAPR = ((Number(c.apr) > 0 ? Number(c.apr) : UAE_DEFAULT_APR_PCT) / 100) / 12;
    const minPayBase = toBase(Number(c.minimumPayment) || 0, c.currency);
    let revolve = 0; // carried unpaid balance (base AED)
    // Bill `billedBase` onto the statement closing at closeDt: accrue APR on any
    // prior carried balance, pay the learned ratio of what's due (never below the
    // minimum, never above the balance), carry the rest. Pay-in-full (ratio 1)
    // keeps revolve at 0 — the default until a card's history shows otherwise.
    const billAndPay = (billedBase, payday, closeDt) => {
      const interest = revolve * mAPR;
      if (interest > 0) addInterest(closeDt, interest);
      const due = Math.max(0, billedBase) + revolve + interest;
      let payment = ratio >= 1 ? due : ratio * due;
      if (minPayBase > 0 && payment < minPayBase) payment = minPayBase;
      payment = Math.min(due, Math.max(0, payment));
      revolve = Math.max(0, due - payment);
      addPay(payday, payment);
    };
    let lc = closeOn(c, now.getFullYear(), now.getMonth());
    if (lc > todayD) { const p = new Date(now.getFullYear(), now.getMonth() - 1, 1); lc = closeOn(c, p.getFullYear(), p.getMonth()); }
    const lcStr = dateToStr(lc);
    let billed = cardStatementBalanceAsOf(c, transactions, lcStr);
    const uploaded = (cardStatements || []).filter(s => s.cardId === c.id && !s.deletedAt && s.statementDate
      && s.statementDate.slice(0, 7) === lcStr.slice(0, 7)).sort((a, b) => cmpDateStr(b.statementDate, a.statementDate))[0];
    // Prefer Total Payment Due (what's actually due this cycle) over the closing
    // balance (which can include not-yet-due installments); credits clamp to 0.
    if (uploaded && (uploaded.totalPaymentDue != null || uploaded.closingBalance != null)) {
      billed = uploaded.totalPaymentDue != null ? Number(uploaded.totalPaymentDue) : (Number(uploaded.closingBalance) || 0);
    }
    const statementedBase = toBase(Math.max(0, billed - paymentsTowardCardAfter(c, lcStr)), c.currency);
    const openSoFarBase = Math.max(0, (bnowByCard.get(c.id) || 0) - statementedBase);
    billAndPay(statementedBase, payDateFromClose(c, lc, uploaded && uploaded.dueDate), lc);
    let prevClose = lc;
    let isOpenCycle = true;
    for (let k = 0; k <= horizon + 1; k++) {
      const cm = new Date(now.getFullYear(), now.getMonth() + k, 1);
      const C = closeOn(c, cm.getFullYear(), cm.getMonth());
      if (C <= todayD || C <= prevClose) continue;
      const payday = payDateFromClose(c, C);
      const aggEst = cardEstimateFor(Math.min(Math.max(0, offsetOfMonth(C)), horizon));
      const perCardEst = aggEst * shareOf(c);
      if (isOpenCycle) {
        const cycleLen = Math.max(1, (C - prevClose) / MS_PER_DAY);
        const fracRemaining = Math.min(1, Math.max(0, (C - todayD) / MS_PER_DAY / cycleLen));
        billAndPay(openSoFarBase + perCardEst * fracRemaining, payday, C);
        isOpenCycle = false;
      } else {
        billAndPay(perCardEst, payday, C);
      }
      prevClose = C;
    }
  });

  let running = cashOnHand;
  let savings = interestParams.savingsBal;
  // Net-worth baseline (accrual): same composition as the Dashboard chart, so
  // the projected net-worth line here matches it. Accrues income − accrual
  // expense (directOut + the budget-based card estimate) + interest each month.
  const investmentsNow = (investments || []).reduce((s, i) => s + toBase((Number(i.shares) || 0) * (Number(i.currentPrice) || 0), i.currency), 0);
  const assetsNow = (assets || []).reduce((s, a) => s + toBase(getAssetCurrentValue(a), a.currency || 'AED'), 0);
  const accountsNow = (accounts || []).reduce((s, a) => a.deletedAt ? s : s + toBase(computeAccountLiveBalance(a, transactions), a.currency), 0);
  const cardsNow = (cards || []).reduce((s, c) => c.deletedAt ? s : s + toBase(computeCardLiveBalance(c, transactions, cardInstallments), c.currency), 0);
  const loansNow = (loans || []).reduce((s, l) => l.deletedAt ? s : s + toBase(computeLoanLiveBalance(l, transactions), l.currency), 0);
  let nwRunning = accountsNow + investmentsNow + assetsNow - cardsNow - loansNow;
  // Principal add-back: project loan + installment debt each month so we can
  // credit the PRINCIPAL portion of each EMI back into net worth (paydown is
  // wealth-neutral; only interest is a real expense).
  const loanBase = (loans || []).map(l => ({ l, base: paymentsElapsed(l.startDate, l.paymentDay) }));
  let prevLoanDebt = 0, prevInstDebt = 0;
  const series: any[] = [];
  for (let offset = 0; offset <= horizon; offset++) {
    const { mStr, mStart, mEnd, label } = rangeFor(offset);
    const effStart = offset === 0 ? dateToStr(addDays(todayD, 1)) : mStart;
    let income = incomeForRange(effStart, mEnd);
    // §6d — add learned variable income to future months that don't already
    // carry an explicit future-dated income tx (so a known bonus isn't doubled).
    if (offset >= 1 && learnedVarIncome > 0
      && !transactions.some(t => !t.deletedAt && !t.recurringId && t.type === 'income'
        && t.date >= mStart && t.date <= mEnd && t.date > tStr)) {
      income += learnedVarIncome;
    }
    // Future months route their account-vs-card split through the learned engine;
    // the current month uses actual logged outflows for the remaining days.
    const directOut = offset === 0 ? directOutForRange(effStart, mEnd) : accountEstimateFor(offset);
    const cardPay = cardPayByMonth[mStr] || 0;
    const net = income - directOut - cardPay;
    let interest = 0;
    if (interestParams.active) {
      // Interest accrues on the SAVINGS balance only (≈ its monthly average,
      // since we don't model intra-month savings transfers), NOT on the month's
      // cash surplus — that surplus sits in a non-interest account until moved.
      if (!(offset === 0 && todayD.getDate() >= interestParams.postDay)) {
        interest = Math.max(0, savings * interestParams.monthlyRate);
      }
      savings = savings + interest; // compounds on the savings balance
    }
    running += net + interest;
    let projLoanDebt = 0;
    loanBase.forEach(({ l, base }) => {
      projLoanDebt += toBase(amortizedBalance(l.originalPrincipal, l.interestRate || 0, l.termMonths || 1, base + offset), l.currency);
    });
    let projInstDebt = 0;
    (cardInstallments || []).forEach(inst => {
      if (inst.deletedAt) return;
      projInstDebt += toBase(installmentBalanceAfter(inst, offset), inst.currency || 'AED');
    });
    const debtPrincipalPaid = offset === 0 ? 0
      : Math.max(0, prevLoanDebt - projLoanDebt) + Math.max(0, prevInstDebt - projInstDebt);
    prevLoanDebt = projLoanDebt; prevInstDebt = projInstDebt;
    // Accrual net worth. For the CURRENT month only the spend not yet incurred
    // counts — what's already been spent is baked into the baseline
    // (cardsNow/accountsNow), so subtracting the full month's estimate here would
    // double-count it and push net worth below cash. Use budget − actual-spend-
    // so-far. Future months use the full routed budget (directOut + cardEstimate).
    // Principal repaid is wealth-neutral, so add it back — only interest bites.
    let nwExpense;
    if (offset === 0) {
      const monthBudgetTotal = budgetsForMonth(mStr).reduce((s, b) => s + toBase(Number(b.amount) || 0, baseCurrency), 0);
      const spentSoFar = transactions.filter(t => !t.deletedAt && !t.excludesFromBalances
        && t.type === 'expense' && !t.isRefund && t.date >= mStart && t.date <= tStr)
        .reduce((s, t) => s + toBase(Number(t.amount) || 0, t.currency), 0);
      nwExpense = Math.max(0, monthBudgetTotal - spentSoFar);
    } else {
      nwExpense = directOut + cardEstimateFor(offset);
    }
    nwRunning += income - nwExpense - (revolveInterestByMonth[mStr] || 0) + interest + debtPrincipalPaid;
    series.push({
      label: offset === 0 ? 'Now' : label,
      key: mStr,
      cash: Math.round(running),
      netWorth: Math.round(nwRunning),
      net: Math.round(net + interest),
      cardPay: Math.round(cardPay),
      interest: Math.round(interest),
      isEstimated: offset !== 0,
      noBaseline: false,
    });
  }
  return series;
};

// Compute remaining loan balance after N payments using exact amortization schedule
const amortizedBalance = (originalPrincipal, annualRate, termMonths, paymentsMade) => {
  if (!originalPrincipal || !termMonths) return originalPrincipal || 0;
  const r = (annualRate || 0) / 100 / 12;
  if (r === 0) return Math.max(0, originalPrincipal - (originalPrincipal / termMonths) * paymentsMade);
  const payment = (originalPrincipal * r * Math.pow(1 + r, termMonths)) / (Math.pow(1 + r, termMonths) - 1);
  const balance = originalPrincipal * Math.pow(1 + r, paymentsMade) - payment * (Math.pow(1 + r, paymentsMade) - 1) / r;
  return Math.max(0, balance);
};

// Remaining balance on a card installment after `monthsForward` more payments.
// Walks the declared monthly_payment down: each month interest = balance×rate/12,
// principal = payment − interest. 0%-interest installments (the common case)
// reduce by the full payment until cleared. Used to credit the PRINCIPAL portion
// of installment payments back into the net-worth line (paydown is neutral; only
// interest is a real expense).
const installmentBalanceAfter = (inst, monthsForward) => {
  let bal = Number(inst.remainingBalance ?? inst.originalAmount ?? 0) || 0;
  const pay = Number(inst.monthlyPayment) || 0;
  const r = (Number(inst.interestRate) || 0) / 100 / 12;
  for (let i = 0; i < monthsForward && bal > 0.005 && pay > 0; i++) {
    const interest = bal * r;
    const principal = Math.max(0, pay - interest);
    bal = Math.max(0, bal - principal);
  }
  return bal;
};

// Payment-driven loan balance: walk the (frozen) starting balance down by the
// PRINCIPAL portion of each logged payment — a transfer with to_loan_id. Each
// payment's interest = balance × rate/12, principal = amount − interest, so the
// loan ticks down exactly as it amortises, reflecting what was actually paid
// (early/extra/missed payments included) rather than assuming an on-time schedule.
const computeLoanLiveBalance = (loan, transactions = []) => {
  let bal = Number(loan.currentBalance ?? loan.originalPrincipal ?? 0);
  const r = (Number(loan.interestRate) || 0) / 100 / 12;
  const payments = (transactions || [])
    .filter(t => !t.deletedAt && t.toLoanId === loan.id && (Number(t.amount) || 0) > 0)
    .sort((a, b) => String(a.date).localeCompare(String(b.date))
      || String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
  for (const p of payments) {
    const interest = bal * r;
    const principal = Math.max(0, (Number(p.amount) || 0) - interest);
    bal = Math.max(0, bal - principal);
  }
  return Math.round(bal * 100) / 100;
};

// Count how many monthly payments have fired for a loan given its startDate up to today
// Count of scheduled payments due between the loan's start and `asOfStr`
// (defaults to today). Passing a past date gives the payments elapsed AS OF that
// day — used to amortize a loan's balance historically for the net-worth trail.
const paymentsElapsed = (startDateStr, paymentDay, asOfStr = null) => {
  if (!startDateStr) return 0;
  const start = strToDate(startDateStr);
  const end = asOfStr ? strToDate(asOfStr) : new Date();
  end.setHours(0, 0, 0, 0);
  let count = 0;
  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor <= end) {
    const day = Math.min(paymentDay || 1, lastDayOfMonth(cursor.getFullYear(), cursor.getMonth()));
    const payDate = new Date(cursor.getFullYear(), cursor.getMonth(), day);
    if (payDate >= start && payDate <= end) count++;
    cursor.setMonth(cursor.getMonth() + 1);
    if (count > 600) break; // safety
  }
  return count;
};

// Next payment date given a loan: based on payment day of month
const nextLoanPaymentDate = (loan) => {
  if (!loan?.paymentDay) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = Math.min(loan.paymentDay, lastDayOfMonth(today.getFullYear(), today.getMonth()));
  let next = new Date(today.getFullYear(), today.getMonth(), day);
  if (next < today) {
    const nm = today.getMonth() + 1;
    const nmY = nm > 11 ? today.getFullYear() + 1 : today.getFullYear();
    const nmM = nm > 11 ? 0 : nm;
    next = new Date(nmY, nmM, Math.min(loan.paymentDay, lastDayOfMonth(nmY, nmM)));
  }
  return next;
};

// Splits a pasted batch of bank SMSes into one body per SMS. Tries the
// documented "blank line between each" format first; if that yields a
// single chunk (or one giant chunk) but the text has multiple lines, it
// falls back to grouping line-by-line on common bank-SMS opening
// patterns: "POS Purchase", "Online Purchase", "Card Purchase",
// "A transaction", "Dear Customer/Cardmember", "Cash Withdrawal", etc.
// Each line matching one of these markers starts a new SMS; lines that
// don't are appended to the current one (for SMSes that wrapped onto
// multiple lines when copied).
const SMS_START_MARKER = /^(?:POS Purchase|Online Purchase|Card Purchase|Credit Card|Purchase of|Payment of|(?:An )?Amount of|A transaction|A txn|Txn on|Transaction (?:on|of|to)|Cash Withdrawal|Withdrawal of|Deposit of|(?:A )?Credit of|(?:A )?Debit of|(?:A )?Refund\b|Your (?:payment|card|account)|Dear (?:Customer|Cardmember)|Transfer of|Request received|Charge of)/i;
function splitSmsBatch(text: string): string[] {
  const byBlanks = text.split(/\n\s*\n+/).map(s => s.trim()).filter(s => s.length > 10);
  if (byBlanks.length > 1) return byBlanks;
  const lines = text.split(/\r?\n/).map(s => s.trim()).filter(s => s.length > 0);
  if (lines.length <= 1) return byBlanks;
  const chunks: string[] = [];
  let current = '';
  for (const line of lines) {
    if (SMS_START_MARKER.test(line)) {
      if (current.trim().length > 10) chunks.push(current.trim());
      current = line;
    } else {
      // Preserve the newline between continuation lines — multi-line banks
      // (EI's "At:" label, FAB's positional merchant line) rely on the
      // line structure. Joining with a space collapses it and breaks merchant
      // extraction.
      current = current ? `${current}\n${line}` : line;
    }
  }
  if (current.trim().length > 10) chunks.push(current.trim());
  return chunks.length > 0 ? chunks : byBlanks;
}

// ============ PARSERS ============
const parseSMS = (smsText) => {
  const txs = [];
  const messages = smsText.split(/\n\n+|\r\n\r\n|(?=Dear Customer|Dear Cardmember|Your account|Purchase of)/gi).filter(m => m.trim().length > 20);

  messages.forEach((msg) => {
    const cleaned = msg.trim();
    const amountMatch = cleaned.match(/(?:AED|USD|EUR|SAR)\s*([0-9,]+\.?\d*)/i);
    if (!amountMatch) return;

    const amount = parseFloat(amountMatch[1].replace(/,/g, ''));
    if (!amount || isNaN(amount)) return;

    const currencyMatch = cleaned.match(/(AED|USD|EUR|SAR)/i);
    const currency = currencyMatch ? currencyMatch[1].toUpperCase() : 'AED';

    const isCredit = /credited|received|salary|refund|deposit|reversal/i.test(cleaned);
    const isDebit = /debited|spent|purchase|paid|withdrawn|payment|charge|atm/i.test(cleaned);
    if (!isCredit && !isDebit) return;

    let description = '';
    const patterns = [
      /at\s+([A-Z][A-Z0-9\s&'.-]+?)(?:\s+on|\s+ON|\.|$)/,
      /to\s+([A-Z][A-Z0-9\s&'.-]+?)(?:\s+on|\s+ON|\.|$)/,
      /from\s+([A-Z][A-Z0-9\s&'.-]+?)(?:\s+on|\s+ON|\.|$)/,
    ];
    for (const p of patterns) {
      const m = cleaned.match(p);
      if (m) { description = m[1].trim().replace(/\s+/g, ' '); break; }
    }
    if (!description) description = isCredit ? 'Income' : 'Purchase';

    let date = todayStr();
    let dateFound = false;
    const dateMatch = cleaned.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (dateMatch) {
      const day = dateMatch[1].padStart(2, '0');
      const month = dateMatch[2].padStart(2, '0');
      let year = dateMatch[3];
      if (year.length === 2) year = '20' + year;
      date = `${year}-${month}-${day}`;
      dateFound = true;
    }

    let category = isCredit ? 'Other Income' : 'Other';
    const lower = (description + ' ' + cleaned).toLowerCase();
    if (/salary|payroll/i.test(lower)) category = 'Salary';
    else if (/carrefour|spinneys|lulu|waitrose|union coop|grocery/i.test(lower)) category = 'Groceries';
    else if (/talabat|deliveroo|careem food|zomato|noon food|restaurant|cafe|starbucks|costa/i.test(lower)) category = 'Food & Dining';
    else if (/uber|careem|taxi|rta|salik|petrol|enoc|adnoc|emarat/i.test(lower)) category = 'Transport';
    else if (/netflix|spotify|amazon prime|youtube|apple|icloud|subscription/i.test(lower)) category = 'Subscriptions';
    else if (/dewa|du|etisalat|electricity|water|internet/i.test(lower)) category = 'Utilities';
    else if (/amazon|noon|namshi|sharaf|shopping|mall/i.test(lower)) category = 'Shopping';
    else if (/cinema|vox|reel|cineplex|theme park/i.test(lower)) category = 'Entertainment';
    else if (/pharmacy|hospital|clinic|doctor|medical/i.test(lower)) category = 'Healthcare';
    else if (/emirates|flydubai|etihad|booking|airbnb|hotel/i.test(lower)) category = 'Travel';
    else if (/rent|ejari/i.test(lower)) category = 'Rent';

    txs.push({
      id: uid('sms'),
      date, description, amount, currency, category,
      type: isCredit ? 'income' : 'expense',
      account: 'Default',
      notes: dateFound ? 'Auto-imported from SMS' : 'Auto-imported from SMS (date not found, set to today — please verify)',
      source: 'sms',
    });
  });

  return txs;
};

// Robust CSV row parser handling quoted fields with embedded commas
const parseCSVRow = (row) => {
  const cells = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (inQuotes) {
      if (ch === '"' && row[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else cur += ch;
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',') { cells.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
  }
  cells.push(cur.trim());
  return cells;
};

const parseCSV = (csvText) => {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  const headers = parseCSVRow(lines[0]).map(h => h.toLowerCase());
  const txs = [];

  const dateIdx = headers.findIndex(h => /date|posted/.test(h));
  const descIdx = headers.findIndex(h => /desc|narration|details|particulars|memo/.test(h));
  const amountIdx = headers.findIndex(h => /^amount$|^value$/.test(h));
  const debitIdx = headers.findIndex(h => /debit|withdrawal|out/.test(h));
  const creditIdx = headers.findIndex(h => /credit|deposit|^in$/.test(h));
  const currencyIdx = headers.findIndex(h => /currency|ccy/.test(h));

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVRow(lines[i]);
    if (cols.length < 2) continue;

    const dateStr = dateIdx >= 0 ? cols[dateIdx] : '';
    const description = descIdx >= 0 ? cols[descIdx] : 'Imported';
    const currency = currencyIdx >= 0 ? cols[currencyIdx]?.toUpperCase() : 'AED';

    let amount = 0;
    let type = 'expense';

    if (debitIdx >= 0 && creditIdx >= 0) {
      const debit = parseFloat(cols[debitIdx]?.replace(/,/g, '')) || 0;
      const credit = parseFloat(cols[creditIdx]?.replace(/,/g, '')) || 0;
      if (debit > 0) { amount = debit; type = 'expense'; }
      else if (credit > 0) { amount = credit; type = 'income'; }
    } else if (amountIdx >= 0) {
      const val = parseFloat(cols[amountIdx]?.replace(/,/g, '')) || 0;
      amount = Math.abs(val);
      type = val < 0 ? 'expense' : 'income';
    }

    if (amount === 0) continue;

    let date = todayStr();
    if (dateStr) {
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) date = dateToStr(parsed);
    }

    txs.push({
      id: uid('csv'),
      date,
      description: description || 'Imported',
      amount,
      currency: CURRENCIES.includes(currency) ? currency : 'AED',
      category: type === 'income' ? 'Other Income' : 'Other',
      type,
      account: 'Default',
      notes: 'Imported from CSV',
      source: 'csv',
    });
  }

  return txs;
};

// ============ DATA STORE ============
// M4: the persistence layer now lives in src/lib/dataStore.ts and talks to
// Supabase. The interface this file consumes is unchanged from the M2-era
// shape (list/get/set/remove/clear plus the fetchAllByPrefix convenience),
// so the rest of this file didn't need rewiring. See the module for the
// snake↔camel mapping, the synthesized name-string fields, and the FX
// rate fill-in on transaction writes.
import { dataStore, fetchAllByPrefix, listDeletedByPrefix, runInstallmentDecrement, subscribeToUserChanges } from './lib/dataStore';
import { supabase } from './lib/supabase';
import { registerDialogs, confirmDialog, toast } from './lib/dialogs';
import {
  BUDGET_CUTOVER_MONTH, FREQUENCIES, QUARTERLY_MONTHS, round10,
  computeMonth as computeBudgetMonth, resolveTemplateForMonth, evaluateFrequency,
  countWeekdayInMonth, daysInMonth,
  findReconciliationTargets, shouldInsertNewVersion,
} from './lib/budget-engine';

// ============ B2 — ID RESOLVERS ============
// Pure functions that map name-string FKs to _id FKs against the categories /
// accounts / cards in scope. After M4, the load-time backfill is gone; these
// still run in the save handlers so any new in-app writes carry resolved IDs.
// Lossless: when a name string has no match, the id is null and the string is
// preserved. The Supabase-backed dataStore strips the strings on write and
// re-synthesizes them on read via PostgREST embedded selects.
const resolveCategoryId = (name, categories) => {
  if (!name) return null;
  const c = categories.find(x => x.name === name);
  return c ? c.id : null;
};

// Returns { accountId, cardId } where exactly one is non-null when the name
// matches a live account/card, both null otherwise. Account match wins over
// card match if (somehow) both exist with identical names.
const resolveAccountOrCardId = (name, accounts, cards) => {
  if (!name) return { accountId: null, cardId: null };
  const a = accounts.find(x => x.name === name);
  if (a) return { accountId: a.id, cardId: null };
  const c = cards.find(x => x.name === name);
  if (c) return { accountId: null, cardId: c.id };
  return { accountId: null, cardId: null };
};

const resolveTxIds = (tx, { accounts, cards, categories }) => {
  const out = { ...tx };
  if (tx.type === 'transfer') {
    const fromRes = resolveAccountOrCardId(tx.fromAccount, accounts, cards);
    const toRes = resolveAccountOrCardId(tx.toAccount, accounts, cards);
    out.fromAccountId = fromRes.accountId;
    out.fromCardId = fromRes.cardId;
    out.toAccountId = toRes.accountId;
    out.toCardId = toRes.cardId;
    // Investment legs (buy/sell a holding) and loan legs (loan payment /
    // disbursement) are passed through pre-resolved as IDs — there is no name
    // string to resolve. An investment/loan endpoint is exclusive, so clear the
    // account/card on that side to satisfy the one-endpoint-per-side CHECK.
    out.fromInvestmentId = tx.fromInvestmentId || null;
    out.toInvestmentId = tx.toInvestmentId || null;
    out.fromLoanId = tx.fromLoanId || null;
    out.toLoanId = tx.toLoanId || null;
    if (out.toLoanId || out.toInvestmentId) { out.toAccountId = null; out.toCardId = null; }
    if (out.fromLoanId || out.fromInvestmentId) { out.fromAccountId = null; out.fromCardId = null; }
    out.accountId = null;
    out.cardId = null;
  } else {
    const res = resolveAccountOrCardId(tx.account, accounts, cards);
    out.accountId = res.accountId;
    out.cardId = res.cardId;
    out.fromAccountId = null;
    out.fromCardId = null;
    out.toAccountId = null;
    out.toCardId = null;
    out.fromInvestmentId = null;
    out.toInvestmentId = null;
    out.fromLoanId = null;
    out.toLoanId = null;
  }
  out.categoryId = resolveCategoryId(tx.category, categories);
  return out;
};

const resolveRecurringIds = (r, { accounts, cards, categories }) => {
  // If the caller has pre-set categoryId (e.g. it just created a new category
  // whose row hasn't propagated to the closure-captured `categories` yet),
  // respect it. Otherwise fall back to resolving by name.
  const resolvedCategoryId = r.categoryId ?? resolveCategoryId(r.category, categories);
  if (r.type === 'transfer') {
    const fromRes = resolveAccountOrCardId(r.fromAccount, accounts, cards);
    const toRes   = resolveAccountOrCardId(r.toAccount,   accounts, cards);
    return {
      ...r,
      // For transfer rules the DB CHECK requires account_id/card_id null and
      // from_*/to_* set; the resolver mirrors that shape.
      accountId: null,
      cardId: null,
      fromAccountId: fromRes.accountId,
      fromCardId:    fromRes.cardId,
      toAccountId:   toRes.accountId,
      toCardId:      toRes.cardId,
      categoryId: resolvedCategoryId,
    };
  }
  const res = resolveAccountOrCardId(r.account, accounts, cards);
  return {
    ...r,
    accountId: res.accountId,
    cardId: res.cardId,
    fromAccountId: null,
    fromCardId: null,
    toAccountId: null,
    toCardId: null,
    categoryId: resolveCategoryId(r.category, categories),
  };
};

const resolveBudgetIds = (b, { categories }) => ({
  ...b,
  categoryId: resolveCategoryId(b.category, categories),
});

// ============ MAIN APP ============
// Valid tab ids — also the URL path segments (e.g. /transactions, /assets).
// Debt lives under /loans (its tab id). Anything else falls back to dashboard.
const TAB_IDS = ['dashboard', 'calendar', 'accounts', 'assets', 'investments', 'cards', 'loans', 'recurring', 'budgets', 'savings', 'transactions', 'settings'];
const pathToTab = (p) => {
  const seg = (p || '/').replace(/^\/+/, '').split('/')[0];
  return TAB_IDS.includes(seg) ? seg : 'dashboard';
};

export default function FinancePlatform() {
  const { signOut, session } = useAuth();
  // Tab state is mirrored in the URL path so refresh / deep-links land on the
  // right tab and browser back/forward work. pushState on change; popstate syncs.
  const [activeTab, _setActiveTab] = useState(() =>
    typeof window !== 'undefined' ? pathToTab(window.location.pathname) : 'dashboard'
  );
  const setActiveTab = useCallback((tab) => {
    _setActiveTab(tab);
    if (typeof window !== 'undefined') {
      const path = `/${tab}`;
      if (window.location.pathname !== path) window.history.pushState({ tab }, '', path);
    }
  }, []);
  useEffect(() => {
    // Normalize bare "/" to "/dashboard" so the URL always names a tab.
    if (typeof window !== 'undefined' && window.location.pathname === '/') {
      window.history.replaceState({ tab: 'dashboard' }, '', '/dashboard');
    }
    const onPop = () => _setActiveTab(pathToTab(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [highlightIds, setHighlightIds] = useState<string[]>([]);
  const [txFilter, setTxFilter] = useState<{ source?: string; cat?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const baseCurrency = 'AED';
  // User timezone — drives the server's day-boundary jobs (snapshot at 23:59
  // local, new-day work at 00:01 local). Defaults to Asia/Dubai until loaded.
  const [timezone, setTimezone] = useState('Asia/Dubai');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('user_settings').select('timezone').maybeSingle();
      if (!cancelled && data?.timezone) setTimezone(data.timezone);
    })();
    return () => { cancelled = true; };
  }, [session]);

  const saveTimezone = async (tz) => {
    setTimezone(tz);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { error } = await supabase.from('user_settings').upsert(
      { user_id: user.id, timezone: tz, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' },
    );
    if (error) { toast('Could not save timezone — try again.', 'error'); return; }
    toast(`Timezone set to ${tz}.`, 'success');
  };

  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [budgets, setBudgets] = useState([]);
  // Budget Engine (2026-07 cutover): versioned template rule lines + per-month
  // override diffs. Category totals are computed on demand as the sum of lines;
  // nothing computed is stored. The legacy `budgets` array above still serves
  // pre-cutover months (frozen). See src/lib/budget-engine.ts.
  const [budgetTemplateLines, setBudgetTemplateLines] = useState([]);
  const [budgetOverrides, setBudgetOverrides] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [cards, setCards] = useState([]);
  const [loans, setLoans] = useState([]);
  const [assets, setAssets] = useState([]);
  // M2.5 Session C: per-card installment products (balance_transfer / easy_payment_plan
  // / balance_installment / easy_cash). Server-side table exists since M3 migration 0008.
  const [cardInstallments, setCardInstallments] = useState([]);
  // M2.5 Session D: virtual savings goals overlaying account/investment balances.
  // Goals own a target; allocations slice a source's balance (% or fixed) onto goals.
  const [savingsGoals, setSavingsGoals] = useState([]);
  const [savingsGoalAllocations, setSavingsGoalAllocations] = useState([]);
  // M2.5 Session D: reimbursement_settlements link an income (reimbursement)
  // to a reimbursable expense with an amount. transactions.reimbursed_amount
  // is kept in sync by a server-side trigger (M3 migration 0010).
  const [reimbursementSettlements, setReimbursementSettlements] = useState([]);
  // Net-worth snapshot history, written nightly server-side. Used by the
  // Dashboard chart.
  const [netWorthHistory, setNetWorthHistory] = useState([]);
  // SMS-ingest plumbing — tokens for the iPhone Shortcut, reconciliation
  // alerts surfaced when bank's available limit drifts from Floosi's.
  const [smsTokens, setSmsTokens] = useState([]);
  const [smsReconAlerts, setSmsReconAlerts] = useState([]);
  const [smsPending, setSmsPending] = useState([]); // sms_ingest_raw rows awaiting user completion
  const [cardStatements, setCardStatements] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  // "Read" marker for the bell badge — the signature of the notification set the
  // last time the panel was opened. The badge only lights when the current set
  // differs (something new), and clears once the bell is opened. Per-device.
  const [lastSeenNotif, setLastSeenNotif] = useState(() => {
    try { return localStorage.getItem('floosi_notif_seen') || ''; } catch { return ''; }
  });
  // Keys of view-only items already moved to "Recent" (mirrors the panel). The
  // bell badge counts only items still ACTIVE — i.e. not yet in Recent — so the
  // number matches what's actually shown, not the cumulative total incl. Recent.
  const [tidySeen, setTidySeen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('floosi_tidy_seen') || '[]')); } catch { return new Set(); }
  });
  // Occurrences of recurring-reminder rules the user marked "handled" (paid
  // early) — suppresses the remaining reminders for that one occurrence. Keyed
  // `${ruleId}:${occurrenceDate}` in localStorage.
  const [recRemindDone, setRecRemindDone] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('floosi_recremind_done') || '[]')); } catch { return new Set(); }
  });
  const markRecReminderHandled = (ruleId, occKey) => {
    setRecRemindDone(prev => {
      const next = new Set(prev); next.add(`${ruleId}:${occKey}`);
      try { localStorage.setItem('floosi_recremind_done', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };
  const [completingSms, setCompletingSms] = useState(null); // raw row being filled in
  // ============ B1a — schema state (seeded on first load, not yet read by UI) ============
  // These three arrays exist in parallel to the existing DEFAULT_CATEGORIES / EXCHANGE_RATES
  // constants. Constants stay the source of truth for now; state is plumbing for B1b/B2
  // when categories become user-editable and rates become refreshable. No render path
  // reads from these yet — that flip happens in a later session.
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);

  const [showTxModal, setShowTxModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showInvestmentModal, setShowInvestmentModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [convertEppTx, setConvertEppTx] = useState(null); // tx being turned into a card installment (EPP)

  // ============ MATERIALIZE RECURRING (pure function — no closure dependencies) ============
  const materializeRecurring = useCallback(async (rules, existingTxs, loansData = [], accountsData = []) => {
    const today = todayStr();
    const existingKeys = new Set(
      existingTxs.filter(t => t.recurringId && t.occurrenceDate).map(t => `${t.recurringId}:${t.occurrenceDate}`)
    );
    const newTxs = [];
    const loanUpdates = []; // collect loan balance updates

    // ---------- Interest-rule helpers ----------
    // MAB for a given account over [periodStart, periodEnd]: average of
    // end-of-day balances. Each end-of-day balance is opening_balance +
    // signed sum of all non-excluded txs for the account with date ≤ d.
    const endOfDayBalance = (account, allTxs, dayStr) => {
      let change = 0;
      for (const t of allTxs) {
        if (t.deletedAt) continue;
        if (t.excludesFromBalances) continue;
        if (!t.date || t.date > dayStr) continue;
        if (t.type === 'transfer') {
          if (t.fromAccountId === account.id) change -= Number(t.amount) || 0;
          if (t.toAccountId   === account.id) change += Number(t.amount) || 0;
        } else if (t.accountId === account.id) {
          const amt = Number(t.amount) || 0;
          if (t.type === 'income') change += amt;
          else if (t.type === 'expense') change += t.isRefund ? amt : -amt;
        }
      }
      return (Number(account.balance) || Number(account.openingBalance) || 0) + change;
    };
    const monthlyAvgBalance = (account, allTxs, periodStart, periodEnd) => {
      const start = strToDate(periodStart);
      const end   = strToDate(periodEnd);
      const days  = Math.round((end - start) / MS_PER_DAY) + 1;
      if (days <= 0) return 0;
      let sum = 0;
      const cur = new Date(start);
      for (let i = 0; i < days; i++) {
        sum += endOfDayBalance(account, allTxs, dateToStr(cur));
        cur.setDate(cur.getDate() + 1);
      }
      return sum / days;
    };
    const transfersOutCount = (accountId, allTxs, periodStart, periodEnd) => {
      let n = 0;
      for (const t of allTxs) {
        if (t.deletedAt) continue;
        if (t.type !== 'transfer') continue;
        if (t.fromAccountId !== accountId) continue;
        if (!t.date || t.date < periodStart || t.date > periodEnd) continue;
        n++;
      }
      return n;
    };

    for (const rule of rules) {
      // active = rule in effect; autoLog = should we auto-create its txs.
      // autoLog === false → visible/forecasted only (e.g. SMS-covered), never materialised.
      if (!rule.active || rule.autoLog === false) continue;
      // Start from materializedThrough (the bookmark) when set, else startDate.
      // Turning auto-log on stamps materializedThrough=today, so a re-enabled
      // rule only logs occurrences from that point forward — never backfills a
      // window you were logging by SMS.
      const occurrences = generateOccurrences(rule, rule.materializedThrough || rule.startDate, today);
      dlog(`[Materialize] Rule "${rule.description}" — startDate=${rule.startDate}, today=${today}, occurrences=${occurrences.length}`);

      // ---------- Interest rule branch ----------
      // For each occurrence date, the "period" is the previous calendar
      // month relative to the occurrence. Compute MAB on the rule's
      // account, count transfer-OUT txs in the period, and either skip
      // (forfeit) or create an income tx with amount = MAB × rate/12.
      if (rule.isInterestRule) {
        const rate = (Number(rule.interestAnnualRatePct) || 0) / 100;
        const maxW = rule.maxWithdrawalsPerMonth == null ? null : Number(rule.maxWithdrawalsPerMonth);
        const account = accountsData.find(a => a.id === rule.accountId);
        if (!account || rate <= 0) continue;
        for (const occ of occurrences) {
          const occKey = dateToStr(occ);
          const dedupKey = `${rule.id}:${occKey}`;
          if (existingKeys.has(dedupKey)) continue;
          // Prior month relative to occ.
          const periodMonth = new Date(occ.getFullYear(), occ.getMonth() - 1, 1);
          const periodStart = dateToStr(periodMonth);
          const periodEnd   = dateToStr(new Date(occ.getFullYear(), occ.getMonth(), 0));
          const withdrawals = transfersOutCount(account.id, existingTxs, periodStart, periodEnd);
          if (maxW != null && withdrawals > maxW) {
            dlog(`[Materialize] Interest rule "${rule.description}" SKIP ${occKey}: ${withdrawals} withdrawals > limit ${maxW}`);
            continue; // forfeit — no tx
          }
          const mab = monthlyAvgBalance(account, existingTxs, periodStart, periodEnd);
          if (mab <= 0) continue;
          const amount = Math.round(mab * (rate / 12) * 100) / 100;
          if (amount <= 0) continue;
          existingKeys.add(dedupKey);
          const periodLabel = periodMonth.toLocaleDateString('en', { month: 'long', year: 'numeric' });
          newTxs.push({
            id: uid('tx'),
            date: occKey,
            description: rule.description || `Interest — ${periodLabel}`,
            amount,
            currency: rule.currency,
            category: rule.category,
            type: 'income',
            account: rule.account,
            notes: rule.notes
              ? `${rule.notes} | MAB ${mab.toFixed(2)} × ${(rate * 100).toFixed(2)}% / 12`
              : `MAB ${mab.toFixed(2)} × ${(rate * 100).toFixed(2)}% / 12 = ${amount.toFixed(2)}`,
            source: 'recurring',
            recurringId: rule.id,
            occurrenceDate: occKey,
            accountId: rule.accountId ?? null,
            cardId: null,
            categoryId: rule.categoryId ?? null,
            excludesFromBalances: false,
          });
        }
        continue;
      }
      // Installment-linked monthly txs DO contribute to card_owed — they're
      // normal txs in every sense. The installment's load on the card is
      // computed as the sum of FUTURE monthly_payment cycles (cycles
      // strictly after today). When a cycle's date arrives the materializer
      // fires its tx, that 212.46 moves from "future load" into "tx
      // contribs", and card_owed stays flat — mirroring how a bank
      // statement actually works. See computeCardLiveBalance.
      const installmentInformational = false;
      for (const occ of occurrences) {
        const occKey = dateToStr(occ);
        const dedupKey = `${rule.id}:${occKey}`;
        if (existingKeys.has(dedupKey)) continue;
        existingKeys.add(dedupKey);
        if (rule.type === 'transfer') {
          newTxs.push({
            id: uid('tx'),
            date: occKey,
            description: rule.description,
            amount: rule.amount,
            currency: rule.currency,
            category: 'Transfer',
            type: 'transfer',
            fromAccount: rule.fromAccount,
            toAccount: rule.toAccount,
            notes: rule.notes || `Recurring: ${describeRule(rule)}`,
            source: 'recurring',
            recurringId: rule.id,
            occurrenceDate: occKey,
            accountId: null,
            cardId: null,
            fromAccountId: rule.fromAccountId ?? null,
            fromCardId:    rule.fromCardId    ?? null,
            toAccountId:   rule.toAccountId   ?? null,
            toCardId:      rule.toCardId      ?? null,
            categoryId: rule.categoryId ?? null,
            excludesFromBalances: installmentInformational,
          });
        } else {
          newTxs.push({
            id: uid('tx'),
            date: occKey,
            description: rule.description,
            amount: rule.amount,
            currency: rule.currency,
            category: rule.category,
            type: rule.type,
            account: rule.account,
            notes: rule.notes || `Recurring: ${describeRule(rule)}`,
            source: 'recurring',
            recurringId: rule.id,
            occurrenceDate: occKey,
            accountId: rule.accountId ?? null,
            cardId: rule.cardId ?? null,
            categoryId: rule.categoryId ?? null,
            excludesFromBalances: installmentInformational,
          });
        }
      }

      // Auto-reduce linked loan balance using amortization math
      if (rule.linkedLoanId && loansData.length > 0) {
        const loan = loansData.find(l => l.id === rule.linkedLoanId);
        if (loan && loan.originalPrincipal && loan.startDate) {
          const n = paymentsElapsed(loan.startDate, loan.paymentDay);
          if (n > 0) {
            const newBalance = amortizedBalance(
              loan.originalPrincipal,
              loan.interestRate || 0,
              loan.termMonths || 1,
              n
            );
            if (Math.abs(newBalance - (loan.currentBalance || 0)) > 1) {
              loanUpdates.push({ ...loan, currentBalance: Math.round(newBalance * 100) / 100 });
            }
          }
        }
      }
    }

    dlog(`[Materialize] Created ${newTxs.length} new transactions, ${loanUpdates.length} loan updates`);

    // Note: previously we mirrored a per-rule installment decrement on local
    // state here, claiming a DB trigger backed it. There's no such trigger —
    // the canonical mechanism is the daily `decrement_due_installments_all`
    // cron (M2.5 Session C). saveCardInstallment now calls that RPC directly
    // and refetches, so the local mirror was both incorrect (lost on reload)
    // and misleading. Dropped.

    const saves = newTxs.map(t =>
      dataStore.set(`tx:${t.id}`, t).catch(e => console.error('Save tx failed:', e))
    );
    const loanSaves = loanUpdates.map(l =>
      dataStore.set(`loan:${l.id}`, l).catch(e => console.error('Save loan failed:', e))
    );
    if (saves.length > 0 || loanSaves.length > 0) {
      await Promise.all([...saves, ...loanSaves]);
    }

    return { newTxs, loanUpdates };
  }, []);

  // ============ LOAD ON MOUNT ============
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // M4: client-side B1a seed (categories/currencies/exchange_rates) and
        // B2 backfill (name-string -> _id) have been retired. Both jobs now
        // live in the SQL migrations under supabase/migrations/ — currencies +
        // FX rates seed via migration 0002 and per-user categories seed via
        // the auth.users trigger in migration 0003. Old window.storage rate
        // limits no longer apply, so the chunked() helper is gone too.

        const [txData, accData, budData, invData, recData, cardData, loanData, assetData, catData, currData, fxData, ciData, sgData, sgaData, rsData, nwData, sitData, sraData, smrData, cstData, btData, boData] = await Promise.all([
          fetchAllByPrefix('tx:'),
          fetchAllByPrefix('acc:'),
          fetchAllByPrefix('bud:'),
          fetchAllByPrefix('inv:'),
          fetchAllByPrefix('rec:'),
          fetchAllByPrefix('card:'),
          fetchAllByPrefix('loan:'),
          fetchAllByPrefix('asset:'),
          fetchAllByPrefix('cat:'),
          fetchAllByPrefix('curr:'),
          fetchAllByPrefix('fx:'),
          fetchAllByPrefix('ci:'),
          fetchAllByPrefix('sg:'),
          fetchAllByPrefix('sga:'),
          fetchAllByPrefix('rs:'),
          fetchAllByPrefix('nw:'),
          fetchAllByPrefix('sit:'),
          fetchAllByPrefix('sra:'),
          fetchAllByPrefix('smr:'),
          fetchAllByPrefix('cst:'),
          fetchAllByPrefix('bt:'),
          fetchAllByPrefix('bo:'),
        ]);

        if (cancelled) return;

        // ============ B1b — soft delete filter ============
        // Storage retains soft-deleted records (deletedAt set). State never sees them.
        // Doing this at the load boundary means every read site downstream is automatically
        // correct without per-call-site filtering. Internal cleanup paths (orphan removal,
        // materializer) also operate on filtered data — soft-deleted rules don't materialize
        // and soft-deleted loans don't get balance updates.
        const isLive = (x) => !x?.deletedAt;
        const txDataLive = txData.filter(isLive);
        const accDataLive = accData.filter(isLive);
        const budDataLive = budData.filter(isLive);
        const invDataLive = invData.filter(isLive);
        const recDataLive = recData.filter(isLive);
        const cardDataLive = cardData.filter(isLive);
        const loanDataLive = loanData.filter(isLive);
        const assetDataLive = assetData.filter(isLive);
        const catDataLive = catData.filter(isLive);
        const currDataLive = currData.filter(isLive);
        const fxDataLive = fxData.filter(isLive);
        const ciDataLive = ciData.filter(isLive);
        const sgDataLive = sgData.filter(isLive);
        const sgaDataLive = sgaData.filter(isLive);
        const rsDataLive = rsData.filter(isLive);
        const btDataLive = btData.filter(isLive);
        const boDataLive = boData.filter(isLive);

        // M4: the B2 _id-resolution backfill is no longer needed. Rows
        // arrive from Supabase already keyed by uuid; resolveTxIds /
        // resolveRecurringIds / resolveBudgetIds still run in the save
        // handlers as a defensive belt-and-suspenders for in-app writes,
        // but the load path no longer does a one-shot rewrite.
        const txDataForLoad = txDataLive;
        const recDataForLoad = recDataLive;
        const budDataForLoad = budDataLive;

        // Audit cleanup: orphan-cleanup pass removed. The DB's FK on
        // transactions.recurring_id is ON DELETE SET NULL — if a rule is
        // hard-deleted, dependent transactions stay around with null
        // recurring_id, no orphan can exist. And we hard-delete via cascade
        // anyway in deleteRecurring's "alsoDeleteOccurrences" path.
        const sortedTx = [...txDataForLoad].sort((a, b) => cmpDateStr(b.date, a.date));
        setTransactions(sortedTx);
        // No placeholder fallback: an empty accounts list is a valid state for a
        // fresh user. Injecting a phantom "Default" account caused edits to fail
        // because its id ('default-1') is not a valid uuid in the DB.
        setAccounts(accDataLive);
        setBudgets(budDataForLoad);
        setBudgetTemplateLines(btDataLive);
        setBudgetOverrides(boDataLive);
        setInvestments(invDataLive);
        setRecurring(recDataForLoad);
        setCards(cardDataLive);
        setLoans(loanDataLive);
        setAssets(assetDataLive);
        setCategories(catDataLive);
        setCurrencies(currDataLive);
        // Repoint the module-level currency list at the full DB set so every
        // currency dropdown offers all of them (not just the 9 seed defaults).
        if (currDataLive?.length) CURRENCIES = currDataLive.map((c: any) => c.code).filter(Boolean).sort();
        setExchangeRates(fxDataLive);
        // Refresh module-level FX cache before anything else renders.
        setLiveFxRates(fxDataLive);
        setCardInstallments(ciDataLive);
        setSavingsGoals(sgDataLive);
        setSavingsGoalAllocations(sgaDataLive);
        setReimbursementSettlements(rsDataLive);
        // net_worth_snapshots aren't soft-deletable; the dataStore already
        // skips the deleted_at filter for this table. Sort chronologically.
        setNetWorthHistory(
          (nwData || []).slice().sort((a, b) => (a.snapshotDate || '').localeCompare(b.snapshotDate || '')),
        );
        setSmsTokens(((sitData || []) as any[]).filter((t) => !t.revokedAt));
        // Load both active and recently resolved alerts so the bell panel
        // can show a history section. Bell badge counts only unresolved.
        setSmsReconAlerts((sraData || []) as any[]);
        // A pending SMS that already has a transaction linked to it (sms_raw_id)
        // has been dealt with — don't show it again even if the pending flag
        // didn't clear (e.g. a transient RLS/update miss).
        const linkedSmsIds = new Set(((txDataForLoad || []) as any[]).filter((t) => !t.deletedAt && t.smsRawId).map((t) => t.smsRawId));
        setSmsPending(((smrData || []) as any[]).filter((s) => s.pendingCompletion && !linkedSmsIds.has(s.id)));
        setCardStatements(((cstData || []) as any[]).filter((s) => !s.deletedAt));

        // M4-era client-side load-time materialization removed. Per the
        // audit: it duplicated the M3 server cron (0 1 * * * UTC), and even
        // running fire-and-forget it issued one Supabase write per occurrence.
        // saveRecurring still runs the in-memory materializer when creating a
        // new rule so today's occurrences appear instantly.
      } catch (e) {
        console.error('Load error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ============ Realtime sync ============
  // The initial load is a one-shot snapshot. Anything that mutates the DB from
  // OUTSIDE this browser tab — the SMS-ingest edge function, the nightly crons,
  // or another device — used to require a manual refresh to show up (new
  // transactions, bank-drift alerts, statement/limit changes → new bell
  // notifications). Subscribe to those tables and refetch the affected
  // collections live so notifications appear without a reload. Debounced so a
  // single SMS (which writes a tx + raw row + maybe an alert + a card update in
  // one burst) coalesces into one reload.
  const reloadLiveCollections = useCallback(async () => {
    try {
      const [txData, cardData, cstData, ciData, sraData, smrData, recData] = await Promise.all([
        fetchAllByPrefix('tx:'), fetchAllByPrefix('card:'), fetchAllByPrefix('cst:'),
        fetchAllByPrefix('ci:'), fetchAllByPrefix('sra:'), fetchAllByPrefix('smr:'), fetchAllByPrefix('rec:'),
      ]);
      const isLive = (x) => !x?.deletedAt;
      const txLive = txData.filter(isLive);
      setTransactions([...txLive].sort((a, b) => cmpDateStr(b.date, a.date)));
      setCards(cardData.filter(isLive));
      setCardInstallments(ciData.filter(isLive));
      setCardStatements(((cstData || []) as any[]).filter((s) => !s.deletedAt));
      setSmsReconAlerts((sraData || []) as any[]);
      setRecurring(recData.filter(isLive));
      const linkedSmsIds = new Set(txLive.filter((t) => t.smsRawId).map((t) => t.smsRawId));
      setSmsPending(((smrData || []) as any[]).filter((s) => s.pendingCompletion && !linkedSmsIds.has(s.id)));
    } catch (e) {
      console.error('Realtime reload error:', e);
    }
  }, []);

  useEffect(() => {
    let timer = null;
    const schedule = () => { if (timer) clearTimeout(timer); timer = setTimeout(reloadLiveCollections, 400); };
    const unsub = subscribeToUserChanges(
      ['transactions', 'cards', 'card_statements', 'card_installments',
       'sms_reconciliation_alerts', 'sms_ingest_raw', 'recurring_rules'],
      schedule,
    );
    return () => { if (timer) clearTimeout(timer); unsub(); };
  }, [reloadLiveCollections]);

  // ============ CRUD ============
  const saveTransaction = async (tx) => {
    try {
      // B2: dual-write — keep name strings AND resolved _id fields in sync.
      const resolved = resolveTxIds(tx, { accounts, cards, categories });
      // Any edit on an SMS-imported tx counts as "user reviewed it" — clear
      // the needs_review flag so the badge disappears even when the edit
      // was just to the date / amount / description (not just category).
      const prior = transactions.find(t => t.id === resolved.id);
      if (resolved.smsRawId && resolved.smsNeedsReview) {
        resolved.smsNeedsReview = false;
      }
      // FX estimate: the marker stays until the user enters the real amount.
      // It clears the moment the amount changes (they've put in the settled
      // value); a category/date-only edit leaves it flagged.
      resolved.fxEstimated = !!(prior?.fxEstimated) && Number(prior?.amount) === Number(resolved.amount);
      // Merchant→category learning: when the user changes the category on
      // an SMS-imported tx, save the (merchant, category) pair so the
      // next SMS from the same merchant auto-categorises correctly.
      if (
        resolved.smsRawId
        && resolved.categoryId
        && resolved.description
        && prior && prior.categoryId !== resolved.categoryId
      ) {
        const merchantKey = String(resolved.description)
          .toUpperCase()
          .replace(/[^A-Z0-9]+/g, '')
          .slice(0, 40);
        if (merchantKey) {
          supabase.auth.getUser().then(({ data: { user } }) => {
            if (!user) return;
            return supabase.from('merchant_category_map').upsert({
              user_id: user.id,
              merchant_key: merchantKey,
              category_id: resolved.categoryId,
              updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id,merchant_key' });
          }).catch(e => console.warn('Merchant-category save failed (non-fatal):', e));
        }
      }
      await dataStore.set(`tx:${resolved.id}`, resolved);
      setTransactions(prev => {
        const filtered = prev.filter(t => t.id !== resolved.id);
        return [...filtered, resolved].sort((a, b) => cmpDateStr(b.date, a.date));
      });
      // Cashback redemption: decrement the card's cashbackBalance when a
      // cashback-redemption income tx is saved against a card. Mirrors how
      // statement upload auto-sets the balance — keeps the running total accurate
      // for manually-logged redemptions too.
      if (resolved.type === 'income' && resolved.cardId && /CASHBACK.*REDEMPTION|FAB.*REWARDS.*REDEMPTION/i.test(resolved.description || '')) {
        const c = cards.find(card => card.id === resolved.cardId && !card.deletedAt);
        if (c && Number(c.cashbackBalance) > 0) {
          const updated = { ...c, cashbackBalance: Math.max(0, Math.round(((Number(c.cashbackBalance) || 0) - (Number(resolved.amount) || 0)) * 100) / 100) };
          dataStore.set(`card:${c.id}`, updated).catch(e => console.warn('Cashback balance update failed:', e));
          setCards(prev => prev.map(card => card.id === c.id ? updated : card));
        }
      }
    } catch (e) { console.error('Save tx failed:', e); throw e; }
  };

  const saveTransactions = async (txList) => {
    try {
      const resolved = txList.map(tx => resolveTxIds(tx, { accounts, cards, categories }));
      await Promise.all(resolved.map(tx => dataStore.set(`tx:${tx.id}`, tx)));
      setTransactions(prev => {
        const ids = new Set(resolved.map(t => t.id));
        const filtered = prev.filter(t => !ids.has(t.id));
        return [...filtered, ...resolved].sort((a, b) => cmpDateStr(b.date, a.date));
      });
    } catch (e) { console.error('Bulk save failed:', e); throw e; }
  };

  // Toggle the user's review flag on a transaction.
  const toggleTxFlag = async (tx) => {
    const next = !tx.flagged;
    try {
      await dataStore.set(`tx:${tx.id}`, { ...tx, flagged: next });
      setTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, flagged: next } : t));
    } catch (e) { console.error('Toggle flag failed:', e); throw e; }
  };

  // Mark a suspected-duplicate cluster as intentional ("Keep both"): set
  // dupe_ack on every tx in the cluster so detection stops flagging it.
  const markDuplicatesIntentional = async (txIds) => {
    const idSet = new Set(txIds);
    const targets = transactions.filter(t => idSet.has(t.id) && !t.deletedAt && !t.dupeAck);
    if (targets.length === 0) return;
    try {
      await Promise.all(targets.map(t => dataStore.set(`tx:${t.id}`, { ...t, dupeAck: true })));
      setTransactions(prev => prev.map(t => idSet.has(t.id) ? { ...t, dupeAck: true } : t));
    } catch (e) { console.error('Mark intentional failed:', e); throw e; }
  };

  const deleteTransaction = async (id) => {
    try {
      // Soft delete: keep record in storage, hide from state.
      const existing = transactions.find(t => t.id === id);
      if (!existing) return;
      const now = new Date().toISOString();
      await dataStore.set(`tx:${id}`, { ...existing, deletedAt: now });
      // Cascade to derived children (e.g. an auto FX VAT line) so they don't orphan.
      const children = transactions.filter(t => t.parentTxId === id && !t.deletedAt);
      for (const c of children) await dataStore.set(`tx:${c.id}`, { ...c, deletedAt: now });
      const removeIds = new Set([id, ...children.map(c => c.id)]);
      setTransactions(prev => prev.filter(t => !removeIds.has(t.id)));
    } catch (e) { console.error('Delete tx failed:', e); }
  };

  const saveAccount = async (acc) => {
    try {
      await dataStore.set(`acc:${acc.id}`, acc);
      setAccounts(prev => {
        const filtered = prev.filter(a => a.id !== acc.id);
        return [...filtered, acc];
      });
    } catch (e) { console.error('Save account failed:', e); throw e; }
  };

  const deleteAccount = async (id, alsoDeleteTxs) => {
    try {
      const acc = accounts.find(a => a.id === id);
      if (!acc) return;
      const now = new Date().toISOString();
      // Soft-delete the account
      await dataStore.set(`acc:${id}`, { ...acc, deletedAt: now });
      setAccounts(prev => prev.filter(a => a.id !== id));
      // Cascade: user opted in to also soft-delete associated transactions
      if (alsoDeleteTxs) {
        const orphans = transactions.filter(t => t.account === acc.name);
        await Promise.all(orphans.map(t =>
          dataStore.set(`tx:${t.id}`, { ...t, deletedAt: now })
        ));
        setTransactions(prev => prev.filter(t => t.account !== acc.name));
      }
    } catch (e) { console.error('Delete account failed:', e); }
  };

  const saveBudget = async (b) => {
    try {
      const resolved = resolveBudgetIds(b, { categories });
      // Defensive: if a live budget already exists for this (category, month)
      // — e.g. the dropdown was stale or two windows are open — update that
      // row instead of inserting a new id. Avoids hitting the
      // budgets_user_category_month_unique index with a fresh id (23505).
      const isEditingExisting = budgets.some((x) => x.id === resolved.id);
      if (!isEditingExisting && resolved.categoryId) {
        const dupe = budgets.find((x) =>
          !x.deletedAt &&
          x.categoryId === resolved.categoryId &&
          x.month === resolved.month,
        );
        if (dupe) resolved.id = dupe.id;
      }
      if (!resolved.categoryId) {
        throw new Error(`Category "${b.category}" not found — re-pick it from the dropdown.`);
      }
      await dataStore.set(`bud:${resolved.id}`, resolved);
      setBudgets(prev => {
        const filtered = prev.filter(x => x.id !== resolved.id);
        return [...filtered, resolved];
      });
    } catch (e) { console.error('Save budget failed:', e); throw e; }
  };

  const deleteBudget = async (id) => {
    try {
      const existing = budgets.find(b => b.id === id);
      if (!existing) return;
      await dataStore.set(`bud:${id}`, { ...existing, deletedAt: new Date().toISOString() });
      setBudgets(prev => prev.filter(b => b.id !== id));
    } catch (e) { console.error('Delete budget failed:', e); }
  };

  // ============ Budget Engine handlers (2026-07 cutover) ============
  // Template lines are versioned: a line's identity is `lineGroupId`; an edit or
  // delete writes a NEW version effective from the current real month forward, so
  // past months (which resolve an older version) stay frozen. Overrides are
  // per-month diffs that ride on top. See src/lib/budget-engine.ts.

  // The real current calendar month — template edits always take effect from here
  // forward, regardless of which month the user is viewing.
  const budgetCurrentMonth = todayStr().slice(0, 7);

  // Soft-delete a set of override rows (release / re-sync to template).
  const releaseBudgetOverrides = async (ids) => {
    const idSet = new Set(ids.filter(Boolean));
    if (!idSet.size) return;
    const now = new Date().toISOString();
    const rows = budgetOverrides.filter(o => idSet.has(o.id));
    for (const o of rows) await dataStore.set(`bo:${o.id}`, { ...o, deletedAt: now });
    setBudgetOverrides(prev => prev.filter(o => !idSet.has(o.id)));
  };

  // Create or edit a template line, then apply reconciliation resolutions.
  // payload: { lineGroupId|null, categoryId, name, amount, frequency, freqConfig,
  //            templateNote, sortOrder, resolutions: [{overrideId, action}] }
  const commitTemplateLine = async (payload) => {
    try {
      const eff = budgetCurrentMonth;
      const isNew = !payload.lineGroupId;
      const lineGroupId = payload.lineGroupId || uid('btg');
      // Which existing version, if any, is active for the effective month?
      const groupVersions = budgetTemplateLines.filter(v => v.lineGroupId === lineGroupId && !v.deletedAt);
      const active = resolveTemplateForMonth(groupVersions, eff).get(lineGroupId)?.def;
      const insertNew = isNew || shouldInsertNewVersion(active, eff);
      const rowId = insertNew ? uid('bt') : active.id;
      const row = {
        id: rowId,
        lineGroupId,
        categoryId: payload.categoryId,
        name: payload.name,
        amount: Number(payload.amount) || 0,
        frequency: payload.frequency,
        freqConfig: payload.freqConfig ?? null,
        templateNote: payload.templateNote ?? null,
        effectiveFrom: eff,
        isDeleted: false,
        sortOrder: payload.sortOrder ?? active?.sortOrder ?? budgetTemplateLines.length,
      };
      await dataStore.set(`bt:${rowId}`, row);
      setBudgetTemplateLines(prev => [...prev.filter(v => v.id !== rowId), row]);
      // Apply reconciliation choices: "resync" drops the locked override.
      const toRelease = (payload.resolutions || []).filter(r => r.action === 'resync').map(r => r.overrideId);
      if (toRelease.length) await releaseBudgetOverrides(toRelease);
    } catch (e) { console.error('Save template line failed:', e); throw e; }
  };

  // Remove a template line from the current month forward (writes an isDeleted
  // version). Past months keep the old version. Applies reconciliation choices.
  const deleteTemplateLine = async (lineGroupId, resolutions = []) => {
    try {
      const eff = budgetCurrentMonth;
      const groupVersions = budgetTemplateLines.filter(v => v.lineGroupId === lineGroupId && !v.deletedAt);
      const active = resolveTemplateForMonth(groupVersions, eff).get(lineGroupId)?.def;
      if (!active) return;
      const insertNew = shouldInsertNewVersion(active, eff);
      const rowId = insertNew ? uid('bt') : active.id;
      const row = { ...active, id: rowId, effectiveFrom: eff, isDeleted: true };
      await dataStore.set(`bt:${rowId}`, row);
      setBudgetTemplateLines(prev => [...prev.filter(v => v.id !== rowId), row]);
      const toRelease = (resolutions || []).filter(r => r.action === 'resync').map(r => r.overrideId);
      if (toRelease.length) await releaseBudgetOverrides(toRelease);
    } catch (e) { console.error('Delete template line failed:', e); throw e; }
  };

  // Upsert a per-month override diff. For line diffs (lineGroupId set) there's at
  // most one live row per (month, line) — reuse it if present. Standalone lines
  // (added / adjustment) always get their own id.
  const saveBudgetOverride = async (o) => {
    try {
      let id = o.id;
      if (!id) {
        if (o.lineGroupId) {
          const existing = budgetOverrides.find(x => !x.deletedAt && x.month === o.month && x.lineGroupId === o.lineGroupId);
          id = existing?.id || uid('bo');
        } else {
          id = uid('bo');
        }
      }
      const row = {
        id,
        month: o.month,
        lineGroupId: o.lineGroupId ?? null,
        lockedAmount: o.lockedAmount ?? null,
        deletedThisMonth: !!o.deletedThisMonth,
        categoryId: o.categoryId ?? null,
        name: o.name ?? null,
        addedAmount: o.addedAmount ?? null,
        isAdjustment: !!o.isAdjustment,
        monthNote: o.monthNote ?? null,
        sortOrder: o.sortOrder ?? budgetOverrides.length,
      };
      await dataStore.set(`bo:${id}`, row);
      setBudgetOverrides(prev => [...prev.filter(x => x.id !== id), row]);
      return row;
    } catch (e) { console.error('Save budget override failed:', e); throw e; }
  };

  // Release every diff for a month (snap the whole month back to template), or
  // just the diffs for one line in that month.
  const releaseBudgetMonth = async (month, lineGroupId = null) => {
    const ids = budgetOverrides
      .filter(o => o.month === month && (lineGroupId ? o.lineGroupId === lineGroupId : true))
      .map(o => o.id);
    await releaseBudgetOverrides(ids);
  };

  const saveInvestment = async (i) => {
    try {
      await dataStore.set(`inv:${i.id}`, i);
      setInvestments(prev => {
        const filtered = prev.filter(x => x.id !== i.id);
        return [...filtered, i];
      });
    } catch (e) { console.error('Save investment failed:', e); throw e; }
  };

  const deleteInvestment = async (id) => {
    try {
      const existing = investments.find(i => i.id === id);
      if (!existing) return;
      await dataStore.set(`inv:${id}`, { ...existing, deletedAt: new Date().toISOString() });
      setInvestments(prev => prev.filter(i => i.id !== id));
    } catch (e) { console.error('Delete investment failed:', e); }
  };

  // ============ buy / sell holdings ============
  // A purchase moves cash from an account/card into a holding; a sale moves the
  // proceeds back. Both are a transfer with one investment leg (migration 0020).
  // The investment row is created/averaged here; the transfer transaction
  // debits/credits the funding side through the normal balance math, and —
  // being a transfer — never counts as spending.

  const buyHolding = async ({ symbol, name, type, currency, shares, pricePerShare, fee, fundingId, fundingKind, fundingName, date }) => {
    const sh = Number(shares) || 0;
    const px = Number(pricePerShare) || 0;
    const fees = Number(fee) || 0;
    if (sh <= 0 || px <= 0) throw new Error('Shares and price must be positive.');
    const sym = String(symbol).trim().toUpperCase();
    const totalCost = sh * px + fees; // cash leaving the funding source
    const when = date || todayStr();

    // Average into an existing live holding of the same symbol + currency, else create.
    const existing = investments.find(
      (i) => !i.deletedAt && i.symbol === sym && i.currency === currency,
    );
    let inv;
    if (existing) {
      const totalShares = (Number(existing.shares) || 0) + sh;
      const oldCostBasis = (Number(existing.shares) || 0) * (Number(existing.avgCost) || 0);
      inv = { ...existing, shares: totalShares, avgCost: (oldCostBasis + sh * px + fees) / totalShares };
    } else {
      inv = {
        id: uid('inv'), symbol: sym, name: name?.trim() || '', type: type || 'stock',
        currency, shares: sh, avgCost: totalCost / sh, currentPrice: px,
      };
    }
    await saveInvestment(inv);

    const tx = {
      id: uid('tx'), type: 'transfer', date: when,
      description: `Buy ${sh} ${sym}`, amount: totalCost, currency,
      category: 'Transfer', categoryId: resolveCategoryId('Transfer', categories),
      accountId: null, cardId: null,
      fromAccountId: fundingKind === 'account' ? fundingId : null,
      fromCardId:    fundingKind === 'card'    ? fundingId : null,
      fromInvestmentId: null,
      toAccountId: null, toCardId: null, toInvestmentId: inv.id,
      fromAccount: fundingName, toAccount: sym,
      notes: fees > 0 ? `Includes ${currency} ${fees} fee` : '',
    };
    await dataStore.set(`tx:${tx.id}`, tx);
    setTransactions((prev) => [tx, ...prev]);
    return inv;
  };

  const sellHolding = async ({ investmentId, shares, pricePerShare, fee, destId, destKind, destName, date }) => {
    const inv = investments.find((i) => i.id === investmentId && !i.deletedAt);
    if (!inv) throw new Error('Holding not found.');
    const sh = Number(shares) || 0;
    const px = Number(pricePerShare) || 0;
    const fees = Number(fee) || 0;
    const held = Number(inv.shares) || 0;
    if (sh <= 0 || px <= 0) throw new Error('Shares and price must be positive.');
    if (sh > held + 1e-9) throw new Error('Cannot sell more shares than held.');
    const when = date || todayStr();

    const netProceeds = sh * px - fees;
    if (netProceeds <= 0) throw new Error('Fee exceeds sale proceeds.');
    const realisedGain = netProceeds - sh * (Number(inv.avgCost) || 0);

    // Reduce the holding; a full sale soft-deletes it (schema forbids shares = 0).
    const remaining = held - sh;
    if (remaining <= 1e-9) await deleteInvestment(inv.id);
    else await saveInvestment({ ...inv, shares: remaining }); // avgCost unchanged

    const tx = {
      id: uid('tx'), type: 'transfer', date: when,
      description: `Sell ${sh} ${inv.symbol}`, amount: netProceeds, currency: inv.currency,
      category: 'Transfer', categoryId: resolveCategoryId('Transfer', categories),
      accountId: null, cardId: null,
      fromAccountId: null, fromCardId: null, fromInvestmentId: inv.id,
      toAccountId: destKind === 'account' ? destId : null,
      toCardId:    destKind === 'card'    ? destId : null,
      toInvestmentId: null,
      fromAccount: inv.symbol, toAccount: destName,
      notes: `Realised ${realisedGain >= 0 ? 'gain' : 'loss'}: ${inv.currency} ${realisedGain.toFixed(2)}`
        + (fees > 0 ? ` (after ${inv.currency} ${fees} fee)` : ''),
    };
    await dataStore.set(`tx:${tx.id}`, tx);
    setTransactions((prev) => [tx, ...prev]);
    return { realisedGain, netProceeds };
  };

  const saveRecurring = async (r) => {
    try {
      // Interest rules use an existing "Interest" income category if the user
      // created one; otherwise the rule stays uncategorised (surfaced in the
      // bell nudge). We never auto-create the category.
      // B2: dual-write resolved IDs on the rule. Materializer downstream copies these
      // to each generated tx, so occurrences inherit the rule's resolved IDs.
      const resolved = resolveRecurringIds(r, { accounts, cards, categories });

      // 1. Persist the rule
      await dataStore.set(`rec:${resolved.id}`, resolved);

      // 2. Update rules state
      let updatedRules;
      setRecurring(prev => {
        const filtered = prev.filter(x => x.id !== resolved.id);
        updatedRules = [...filtered, resolved];
        return updatedRules;
      });

      // 3. Materialize using a snapshot of current transactions (read via state setter sync trick)
      if (resolved.active && resolved.autoLog !== false) {
        let currentTxs = [];
        setTransactions(prev => { currentTxs = prev; return prev; });
        let currentLoans = [];
        setLoans(prev => { currentLoans = prev; return prev; });
        let currentAccounts = [];
        setAccounts(prev => { currentAccounts = prev; return prev; });
        await new Promise(resolve => setTimeout(resolve, 0));

        const result = await materializeRecurring([resolved], currentTxs, currentLoans, currentAccounts);
        const { newTxs, loanUpdates } = result;
        dlog('[saveRecurring] About to update state with', newTxs.length, 'new transactions');

        if (newTxs.length > 0) {
          setTransactions(prev => {
            const ids = new Set(newTxs.map(t => t.id));
            const filtered = prev.filter(t => !ids.has(t.id));
            const merged = [...filtered, ...newTxs].sort((a, b) => cmpDateStr(b.date, a.date));
            dlog('[saveRecurring] Merged transactions count:', merged.length);
            return merged;
          });
        }
        if (loanUpdates.length > 0) {
          setLoans(prev => prev.map(l => {
            const upd = loanUpdates.find(u => u.id === l.id);
            return upd || l;
          }));
        }
      }
    } catch (e) {
      console.error('Save recurring failed:', e);
      throw e;
    }
  };

  const deleteRecurring = async (id, alsoDeleteOccurrences) => {
    try {
      const existing = recurring.find(r => r.id === id);
      if (!existing) return;
      const now = new Date().toISOString();
      await dataStore.set(`rec:${id}`, { ...existing, deletedAt: now });
      setRecurring(prev => prev.filter(r => r.id !== id));
      if (alsoDeleteOccurrences) {
        const toDelete = transactions.filter(t => t.recurringId === id);
        await Promise.all(toDelete.map(t =>
          dataStore.set(`tx:${t.id}`, { ...t, deletedAt: now })
        ));
        setTransactions(prev => prev.filter(t => t.recurringId !== id));
      }
    } catch (e) { console.error('Delete recurring failed:', e); }
  };

  // Fire a recurring rule's NEXT scheduled occurrence early. Creates a tx
  // dated today but stamped with that occurrence_date, so the materialiser
  // (which dedupes by recurring_id + occurrence_date) won't re-fire it on
  // the scheduled day. Skips interest rules (need MAB compute that's not
  // wired into this path yet) and rules with no upcoming occurrence.
  const fireRecurringNow = async (id) => {
    try {
      const rule = recurring.find(r => r.id === id);
      if (!rule || rule.deletedAt) return;
      if (rule.isInterestRule) {
        toast('Fire-now isn\'t supported for interest rules — they auto-compute at month boundaries.');
        return;
      }
      const today = todayStr();
      // Look 14 months ahead to catch yearly rules.
      const horizonD = new Date();
      horizonD.setMonth(horizonD.getMonth() + 14);
      const occs = generateOccurrences(rule, today, dateToStr(horizonD));
      const next = occs.find(d => dateToStr(d) >= today);
      if (!next) {
        toast('No upcoming occurrence found for this rule (ended or out of horizon).');
        return;
      }
      const occKey = dateToStr(next);
      const dedupKey = `${rule.id}:${occKey}`;
      const already = transactions.some(t =>
        !t.deletedAt && t.recurringId === rule.id && t.occurrenceDate === occKey,
      );
      if (already) {
        toast(`The ${occKey} occurrence has already been materialised. Nothing to fire.`);
        return;
      }
      if (!(await confirmDialog(
        `Generate this rule's ${occKey} transaction now (dated ${today})? `
        + `It won't fire again on ${occKey}.`,
      ))) return;
      const baseTx = rule.type === 'transfer' ? {
        id: uid('tx'),
        date: today,
        description: rule.description,
        amount: rule.amount,
        currency: rule.currency,
        category: 'Transfer',
        type: 'transfer',
        fromAccount: rule.fromAccount,
        toAccount: rule.toAccount,
        fromAccountId: rule.fromAccountId ?? null,
        fromCardId: rule.fromCardId ?? null,
        toAccountId: rule.toAccountId ?? null,
        toCardId: rule.toCardId ?? null,
        accountId: null,
        cardId: null,
        categoryId: rule.categoryId ?? null,
        notes: rule.notes || `Fired early from recurring rule (was scheduled ${occKey})`,
        source: 'recurring',
        recurringId: rule.id,
        occurrenceDate: occKey,
        excludesFromBalances: false,
      } : {
        id: uid('tx'),
        date: today,
        description: rule.description,
        amount: rule.amount,
        currency: rule.currency,
        category: rule.category,
        type: rule.type,
        account: rule.account,
        accountId: rule.accountId ?? null,
        cardId: rule.cardId ?? null,
        categoryId: rule.categoryId ?? null,
        notes: rule.notes || `Fired early from recurring rule (was scheduled ${occKey})`,
        source: 'recurring',
        recurringId: rule.id,
        occurrenceDate: occKey,
        excludesFromBalances: false,
      };
      await dataStore.set(`tx:${baseTx.id}`, baseTx);
      setTransactions(prev => [baseTx, ...prev].sort((a, b) => cmpDateStr(b.date, a.date)));
    } catch (e) {
      console.error('Fire recurring failed:', e);
      toast('Fire failed: ' + (e?.message || String(e)));
    }
  };

  const saveCard = async (c, autoCreateAnnualFeeRule = false, annualFeeCategoryName = 'Card Fees', annualFeeStartDate = null) => {
    try {
      const oldCard = cards.find((x) => x.id === c.id);
      await dataStore.set(`card:${c.id}`, c);
      setCards(prev => {
        const filtered = prev.filter(x => x.id !== c.id);
        return [...filtered, c];
      });

      const newFee = Number(c.annualFee) || 0;
      const oldFee = Number(oldCard?.annualFee) || 0;
      // Find any existing yearly fee rule for this card.
      const existingFeeRule = recurring.find(
        (r) => !r.deletedAt
          && r.type === 'expense'
          && r.unit === 'year'
          && (r.cardId === c.id || r.account === c.name)
          && (r.description || '').toLowerCase().includes('annual fee'),
      );

      if (existingFeeRule && newFee > 0 && oldFee !== newFee) {
        // Fee changed and a rule already exists — offer to update it so
        // future occurrences carry the new amount. Past materialised
        // transactions stay as-is (history doesn't rewrite itself).
        const ok = await confirmDialog(
          `The annual fee changed from ${c.currency} ${oldFee.toLocaleString()} to ${c.currency} ${newFee.toLocaleString()}.\n\n` +
          `Update the existing recurring rule "${existingFeeRule.description}" to charge ${c.currency} ${newFee.toLocaleString()} going forward?\n\n` +
          `(Past materialised transactions stay as-is.)`,
          { title: 'Annual fee changed', confirmLabel: 'Update rule' }
        );
        if (ok) {
          await saveRecurring({ ...existingFeeRule, amount: newFee });
        }
      } else if (autoCreateAnnualFeeRule && newFee > 0 && !existingFeeRule) {
        // First-time create: bank posts the fee to the card; rule mirrors that.
        // Use the picked category if it exists; else fall back to the user's
        // own "Other"/"Miscellaneous" if present; else leave uncategorised
        // (bell nudge). Never auto-create.
        const feeCat = findCategory(annualFeeCategoryName, 'expense')
          || findCategory('Other', 'expense') || findCategory('Miscellaneous', 'expense');
        const start = annualFeeStartDate || todayStr();
        const rule = {
          id: uid('rec'),
          description: `${c.name} — annual fee`,
          amount: newFee,
          currency: c.currency,
          type: 'expense',
          category: feeCat?.name ?? null,
          categoryId: feeCat?.id ?? null,
          account: c.name,
          pattern: 'day_of_month',
          dayOfMonth: parseInt(start.slice(8, 10), 10) || 1,
          weekday: 0, occurrence: 1, nthLastWorkingDay: 1,
          interval: 1,
          unit: 'year',
          startDate: start,
          endDate: null,
          active: true,
          notes: `Auto-created annual fee for ${c.name}`,
        };
        await saveRecurring(rule);
      }
    } catch (e) { console.error('Save card failed:', e); throw e; }
  };

  const deleteCard = async (id) => {
    try {
      const existing = cards.find(c => c.id === id);
      if (!existing) return;
      await dataStore.set(`card:${id}`, { ...existing, deletedAt: new Date().toISOString() });
      setCards(prev => prev.filter(c => c.id !== id));
    } catch (e) { console.error('Delete card failed:', e); }
  };

  const saveCardStatement = async (stmt) => {
    try {
      await dataStore.set(`cst:${stmt.id}`, stmt);
      setCardStatements(prev => {
        const filtered = prev.filter(s => s.id !== stmt.id);
        return [...filtered, stmt];
      });
    } catch (e) { console.error('Save statement failed:', e); throw e; }
  };

  const deleteCardStatement = async (id) => {
    try {
      const existing = cardStatements.find(s => s.id === id);
      if (!existing) return;
      await dataStore.set(`cst:${id}`, { ...existing, deletedAt: new Date().toISOString() });
      setCardStatements(prev => prev.filter(s => s.id !== id));
    } catch (e) { console.error('Delete statement failed:', e); }
  };

  // ============ M2.5 Session C — card installments ============
  // Save creates the installment row plus the side effects mandated by the
  // kind discriminator (per M1):
  //   balance_transfer:     transfer (this card -> source card) + fee expense
  //   easy_payment_plan:    fee expense; original tx untouched
  //   balance_installment:  fee expense
  //   easy_cash:            transfer (this card -> source account) + fee expense
  // Side effects share the installment's currency. They post on the installment's
  // start_date so the dashboard sees them immediately.
  //
  // If the user picks a payment source account in the modal, we also auto-
  // create a monthly recurring expense (linked_card_installment_id pointing
  // at this installment) so the future installment payments materialize on
  // their own. The recurring rule ends after `term_months` so it stops on
  // its own when the installment is paid off.
  const saveCardInstallment = async (ci, autoCreateRecurring = false, recurringCategoryName = 'Loan Payment', backfillFromStart = false) => {
    try {
      const installmentId = ci.id || uid('ci');

      // Detect "is this an edit of an installment that already lives in state"
      // BEFORE we mutate state. Can't gate on ci.id alone because the modal
      // pre-generates an id when it mounts, so a fresh save always carries one.
      const isEdit = cardInstallments.some((x) => x.id === installmentId);

      // On edit we reset remaining_balance to originalAmount and clear
      // last_decremented_through, then let the decrement RPC re-walk the
      // amortisation schedule from scratch. Without this, prior decrements
      // taken at an older monthly_payment / term linger as principal that
      // was never actually paid, and the UI's payments-made formula
      // (round((original - remaining) / monthly)) reports the wrong count.
      const installmentForDb = {
        ...ci,
        id: installmentId,
        remainingBalance: isEdit
          ? (Number(ci.originalAmount) || 0)
          : (ci.remainingBalance ?? ci.originalAmount ?? 0),
        ...(isEdit ? { lastDecrementedThrough: null } : {}),
      };

      await dataStore.set(`ci:${installmentId}`, installmentForDb);
      setCardInstallments((prev) => {
        const filtered = prev.filter((x) => x.id !== installmentId);
        return [...filtered, installmentForDb];
      });

      const card = cards.find((c) => c.id === ci.cardId);
      const cur = ci.currency || card?.currency || 'AED';
      const startDate = ci.startDate || todayStr();
      const fee = Number(ci.feeAmount) || 0;
      const amount = Number(ci.originalAmount) || 0;

      // EDIT path: don't re-fire fee / transfer / new-rule creation. But DO
      // sync the linked recurring rule's amount / payment day / end date so a
      // term change or monthly-payment correction propagates to future
      // materialisations.
      if (isEdit) {
        const linkedRule = recurring.find(
          (r) => !r.deletedAt && r.linkedCardInstallmentId === installmentId,
        );
        if (linkedRule) {
          const newMonthly = Number(ci.monthlyPayment) || 0;
          const newTerm    = Number(ci.termMonths)     || 0;
          const newPayDay  = Number(ci.paymentDay)     || 1;
          const startParts = startDate.split('-').map(Number);
          const endD = new Date(startParts[0], startParts[1] - 1 + Math.max(0, newTerm - 1), newPayDay);
          const endDateStr = `${endD.getFullYear()}-${String(endD.getMonth() + 1).padStart(2, '0')}-${String(endD.getDate()).padStart(2, '0')}`;
          await saveRecurring({
            ...linkedRule,
            amount: newMonthly,
            currency: cur,
            dayOfMonth: newPayDay,
            endDate: endDateStr,
          });
        }
        // Run the schedule-walk decrement too so any forward-effect on the
        // amortisation curve is reflected, then refetch.
        await runInstallmentDecrement();
        const fresh = await fetchAllByPrefix('ci:');
        setCardInstallments(fresh.filter((x) => !x.deletedAt));
        return installmentForDb;
      }

      if (!card) return installmentForDb;

      // Fee expense lands on this card (all four kinds).
      if (fee > 0) {
        const feeTx = resolveTxIds({
          id: uid('tx'),
          date: startDate,
          description: `${ci.name || 'Installment'} — fee`,
          amount: fee,
          currency: cur,
          category: 'Other',
          type: 'expense',
          account: card.name,
          cardId: card.id,
          notes: `Auto-created for ${ci.kind} "${ci.name}"`,
        }, { accounts, cards, categories });
        await dataStore.set(`tx:${feeTx.id}`, feeTx);
        setTransactions((prev) => [feeTx, ...prev]);
      }

      // Transfer side effects: BT pays off another card; easy_cash pushes cash to an account.
      // Notably NOT for balance_installment / easy_payment_plan — those just
      // convert existing debt and don't move money around.
      const transferToTarget = (toCard, toAccount) => {
        const fromName = card.name;
        const toName = toCard?.name || toAccount?.name;
        if (!toName) return null;
        return resolveTxIds({
          id: uid('tx'),
          date: startDate,
          description: `${ci.name || 'Installment'} — ${ci.kind === 'easy_cash' ? 'cash advance' : 'pay-off transfer'}`,
          amount,
          currency: cur,
          category: 'Transfer',
          type: 'transfer',
          fromAccount: fromName,
          toAccount: toName,
          notes: `Auto-created for ${ci.kind} "${ci.name}"`,
        }, { accounts, cards, categories });
      };

      if (amount > 0 && ci.kind === 'balance_transfer' && ci.linkedSourceCardId) {
        const sourceCard = cards.find((c) => c.id === ci.linkedSourceCardId);
        const tx = transferToTarget(sourceCard, null);
        if (tx) {
          await dataStore.set(`tx:${tx.id}`, tx);
          setTransactions((prev) => [tx, ...prev]);
        }
      } else if (amount > 0 && ci.kind === 'easy_cash' && ci.linkedSourceAccountId) {
        const sourceAccount = accounts.find((a) => a.id === ci.linkedSourceAccountId);
        const tx = transferToTarget(null, sourceAccount);
        if (tx) {
          await dataStore.set(`tx:${tx.id}`, tx);
          setTransactions((prev) => [tx, ...prev]);
        }
      }

      // Opt-in monthly recurring rule, targeting the CARD (not an account).
      // Each fire records an expense on the card for monthlyPayment — that's
      // the bank charging your card as part of the statement. Combined with
      // the auto-decrement that drops remaining_balance by the principal
      // portion, the net change in card-owed each month is the interest
      // portion only, which is what you actually owe extra. No cash-account
      // event is fabricated because none happens at this step (the cash
      // leaves your hand when you pay the card statement, separately).
      const monthly = Number(ci.monthlyPayment) || 0;
      const term    = Number(ci.termMonths)     || 0;
      if (autoCreateRecurring && monthly > 0 && term > 0 && card) {
        const startParts = startDate.split('-').map(Number);
        const endD = new Date(startParts[0], startParts[1] - 1 + (term - 1), Number(ci.paymentDay) || 1);
        const endDateStr = `${endD.getFullYear()}-${String(endD.getMonth() + 1).padStart(2, '0')}-${String(endD.getDate()).padStart(2, '0')}`;
        // Use the chosen existing category (required in the modal); null-safe
        // if somehow blank → uncategorised + bell nudge. Never auto-create.
        const ruleCat = findCategory(recurringCategoryName, 'expense');
        const rule = {
          id: uid('rec'),
          description: `${ci.name} — installment payment`,
          amount: monthly,
          currency: cur,
          type: 'expense',
          category: ruleCat?.name ?? null,
          categoryId: ruleCat?.id ?? null,
          // resolveRecurringIds picks card_id when this name matches a card.
          account: card.name,
          pattern: 'day_of_month',
          dayOfMonth: Number(ci.paymentDay) || 1,
          weekday: 0, occurrence: 1, nthLastWorkingDay: 1,
          interval: 1, unit: 'month',
          // Backfill opt-in: anchor at the installment's own start_date so
          // the materialiser walks forward through missed months. Default off
          // -> today -> rule fires only from now on.
          startDate: backfillFromStart ? startDate : todayStr(),
          endDate: endDateStr,
          active: true,
          notes: `Auto-created for ${ci.kind} installment "${ci.name}".`,
          linkedCardInstallmentId: installmentId,
        };
        await saveRecurring(rule);
      }

      // Fire the schedule-walk decrement immediately so a freshly-created
      // installment (with or without backfill) shows accurate
      // remaining_balance / last_decremented_through right away — no waiting
      // for the next 01:15 UTC cron tick. The DB is the source of truth;
      // refetch installments after the RPC so local state matches.
      await runInstallmentDecrement();
      const fresh = await fetchAllByPrefix('ci:');
      setCardInstallments(fresh.filter((x) => !x.deletedAt));

      return installmentForDb;
    } catch (e) {
      console.error('Save installment failed:', e);
      throw e;
    }
  };

  // ============ M2.5 Session D — savings goals + allocations ============
  const saveSavingsGoal = async (g) => {
    try {
      const id = g.id || uid('sg');
      const goal = { ...g, id };
      await dataStore.set(`sg:${id}`, goal);
      setSavingsGoals((prev) => {
        const filtered = prev.filter((x) => x.id !== id);
        return [...filtered, goal];
      });
      return goal;
    } catch (e) { console.error('Save savings goal failed:', e); throw e; }
  };

  const deleteSavingsGoal = async (id) => {
    try {
      const existing = savingsGoals.find((g) => g.id === id);
      if (!existing) return;
      const now = new Date().toISOString();
      await dataStore.set(`sg:${id}`, { ...existing, deletedAt: now });
      // Cascade: soft-delete this goal's allocations too. The schema's ON DELETE
      // CASCADE only fires on hard deletes; soft-delete needs explicit cascade.
      const linked = savingsGoalAllocations.filter((a) => a.goalId === id);
      if (linked.length > 0) {
        await Promise.all(linked.map((a) => dataStore.set(`sga:${a.id}`, { ...a, deletedAt: now })));
      }
      setSavingsGoals((prev) => prev.filter((g) => g.id !== id));
      setSavingsGoalAllocations((prev) => prev.filter((a) => a.goalId !== id));
    } catch (e) { console.error('Delete savings goal failed:', e); }
  };

  const saveSavingsGoalAllocation = async (a) => {
    try {
      const id = a.id || uid('sga');
      const alloc = { ...a, id };
      await dataStore.set(`sga:${id}`, alloc);
      setSavingsGoalAllocations((prev) => {
        const filtered = prev.filter((x) => x.id !== id);
        return [...filtered, alloc];
      });
      return alloc;
    } catch (e) { console.error('Save allocation failed:', e); throw e; }
  };

  const deleteSavingsGoalAllocation = async (id) => {
    try {
      const existing = savingsGoalAllocations.find((a) => a.id === id);
      if (!existing) return;
      await dataStore.set(`sga:${id}`, { ...existing, deletedAt: new Date().toISOString() });
      setSavingsGoalAllocations((prev) => prev.filter((a) => a.id !== id));
    } catch (e) { console.error('Delete allocation failed:', e); }
  };

  // Categories CRUD — the schema's been user-editable since M3 but the UI
  // sourced from constants. Now picker dropdowns read from `categories` state
  // and the Settings tab manages adds/edits/deletes.
  const saveCategory = async (c) => {
    try {
      const id = c.id || uid('cat');
      const cat = { ...c, id };
      await dataStore.set(`cat:${id}`, cat);
      setCategories((prev) => {
        const filtered = prev.filter((x) => x.id !== id);
        return [...filtered, cat];
      });
      return cat;
    } catch (e) { console.error('Save category failed:', e); throw e; }
  };

  // Returns an existing category by name (+kind), or null. NEVER creates —
  // categories are user-created only (owner's standing rule). Callers must
  // handle null by leaving the item uncategorised; it then surfaces in the
  // bell's "uncategorised" nudge for the user to assign one.
  const findCategory = (name, kind = null) =>
    categories.find((c) => c.name === name && (kind == null || c.kind === kind) && !c.deletedAt) || null;
  const deleteCategory = async (id) => {
    try {
      const existing = categories.find((c) => c.id === id);
      if (!existing) return;
      await dataStore.set(`cat:${id}`, { ...existing, deletedAt: new Date().toISOString() });
      setCategories((prev) => prev.filter((c) => c.id !== id));
    } catch (e) { console.error('Delete category failed:', e); }
  };

  // Create a new SMS-ingest token. DB default fills in token (uuid),
  // created_at, etc. Caller provides only the label.
  const createSmsToken = async (label) => {
    const id = uid('sit');
    const row = { id, label: label || 'iPhone' };
    await dataStore.set(`sit:${id}`, row);
    const fresh = await dataStore.get(`sit:${id}`);
    if (fresh) setSmsTokens((prev) => [fresh, ...prev]);
    return fresh;
  };
  const revokeSmsToken = async (id) => {
    const existing = smsTokens.find((t) => t.id === id);
    if (!existing) return;
    const updated = { ...existing, revokedAt: new Date().toISOString() };
    await dataStore.set(`sit:${id}`, updated);
    setSmsTokens((prev) => prev.filter((t) => t.id !== id));
  };

  // Reimbursement settlements: the M3 trigger keeps transactions.reimbursed_amount
  // in sync as settlements land. We refetch the affected tx after writing a
  // settlement so the UI sees the new reimbursed_amount immediately.
  const refetchTx = async (id) => {
    const fresh = await dataStore.get(`tx:${id}`);
    if (fresh) {
      setTransactions((prev) => prev.map((t) => (t.id === id ? fresh : t)));
    }
  };
  const saveReimbursementSettlement = async (s) => {
    try {
      const id = s.id || uid('rs');
      const settlement = { ...s, id };
      await dataStore.set(`rs:${id}`, settlement);
      setReimbursementSettlements((prev) => {
        const filtered = prev.filter((x) => x.id !== id);
        return [...filtered, settlement];
      });
      await refetchTx(settlement.expenseTxId);
      return settlement;
    } catch (e) { console.error('Save settlement failed:', e); throw e; }
  };
  const deleteReimbursementSettlement = async (id) => {
    try {
      const existing = reimbursementSettlements.find((r) => r.id === id);
      if (!existing) return;
      await dataStore.set(`rs:${id}`, { ...existing, deletedAt: new Date().toISOString() });
      setReimbursementSettlements((prev) => prev.filter((r) => r.id !== id));
      await refetchTx(existing.expenseTxId);
    } catch (e) { console.error('Delete settlement failed:', e); }
  };

  const deleteCardInstallment = async (id) => {
    try {
      const existing = cardInstallments.find((c) => c.id === id);
      if (!existing) return;
      const now = new Date().toISOString();
      await dataStore.set(`ci:${id}`, { ...existing, deletedAt: now });
      setCardInstallments((prev) => prev.filter((c) => c.id !== id));

      // Cascade: soft-delete any recurring rule auto-linked to this installment.
      // The historical fee / transfer transactions stay — those were real
      // events that already happened, not future plans.
      const linkedRules = recurring.filter((r) => r.linkedCardInstallmentId === id);
      if (linkedRules.length > 0) {
        await Promise.all(
          linkedRules.map((r) => dataStore.set(`rec:${r.id}`, { ...r, deletedAt: now })),
        );
        const linkedIds = new Set(linkedRules.map((r) => r.id));
        setRecurring((prev) => prev.filter((r) => !linkedIds.has(r.id)));
      }
    } catch (e) {
      console.error('Delete installment failed:', e);
    }
  };

  const saveLoan = async (l, autoCreateRecurring = false, paymentAccountId = null, recurringCategoryName = 'Loan Payment', backfillFromStart = false) => {
    try {
      await dataStore.set(`loan:${l.id}`, l);
      setLoans(prev => {
        const filtered = prev.filter(x => x.id !== l.id);
        return [...filtered, l];
      });
      // Auto-create recurring rule if requested and payment > 0.
      // Needs a real payment-from account (DB's XOR constraint rejects nulls).
      const sourceAccount = paymentAccountId ? accounts.find((a) => a.id === paymentAccountId) : null;
      if (autoCreateRecurring && (l.monthlyPayment || 0) > 0 && sourceAccount) {
        // Mirrors saveCardInstallment: the link is one-way from the rule to the
        // loan (linked_loan_id on recurring_rules). The loan itself doesn't
        // need to store the rule id — find it via the linked column.
        // Use the chosen existing category (required in the modal); null-safe
        // if blank → uncategorised + bell nudge. Never auto-create.
        const ruleCat = findCategory(recurringCategoryName, 'expense');
        const rule = {
          id: uid('rec'),
          description: `${l.name} Payment`,
          amount: l.monthlyPayment,
          currency: l.currency,
          type: 'expense',
          category: ruleCat?.name ?? null,
          categoryId: ruleCat?.id ?? null,
          account: sourceAccount.name,
          pattern: 'day_of_month',
          dayOfMonth: l.paymentDay || 1,
          weekday: 0, occurrence: 1,
          interval: 1, unit: 'month',
          // Backfill opt-in: when the user ticked it, anchor the rule's
          // startDate at the loan's own start_date so the materialiser walks
          // forward and creates past-month transactions. Default off → today
          // → no backfill.
          startDate: (backfillFromStart && l.startDate) ? l.startDate : todayStr(),
          endDate: null,
          active: true,
          notes: `Auto-created from loan: ${l.name}`,
          linkedLoanId: l.id,
        };
        await saveRecurring(rule);
      }
    } catch (e) { console.error('Save loan failed:', e); throw e; }
  };

  const deleteLoan = async (id, alsoDeleteRule = false) => {
    try {
      const existing = loans.find(l => l.id === id);
      if (!existing) return;
      const now = new Date().toISOString();
      await dataStore.set(`loan:${id}`, { ...existing, deletedAt: now });
      setLoans(prev => prev.filter(l => l.id !== id));
      // Cascade: when the user opts in, soft-delete the auto-created payment
      // rule(s) for this loan and their materialised future transactions.
      if (alsoDeleteRule) {
        const linkedRules = recurring.filter(r => r.linkedLoanId === id);
        for (const rule of linkedRules) {
          await deleteRecurring(rule.id, true);
        }
      }
    } catch (e) { console.error('Delete loan failed:', e); }
  };

  const saveAsset = async (a) => {
    try {
      await dataStore.set(`asset:${a.id}`, a);
      setAssets(prev => {
        const filtered = prev.filter(x => x.id !== a.id);
        return [...filtered, a];
      });
    } catch (e) { console.error('Save asset failed:', e); throw e; }
  };

  const deleteAsset = async (id) => {
    try {
      const existing = assets.find(a => a.id === id);
      if (!existing) return;
      await dataStore.set(`asset:${id}`, { ...existing, deletedAt: new Date().toISOString() });
      setAssets(prev => prev.filter(a => a.id !== id));
    } catch (e) { console.error('Delete asset failed:', e); }
  };

  // Export all data as JSON. In a regular browser this triggers a file download.
  // In sandboxed contexts (e.g. the Claude artifact iframe), <a download>.click()
  // can crash the iframe — so we wrap it and fall back to clipboard.
  const exportData = async () => {
    const payload = {
      exportedAt: new Date().toISOString(),
      transactions, accounts, budgets, investments, recurring, cards, loans, assets,
      // M2.5 entities — audit caught these being silently dropped from backups.
      cardInstallments, savingsGoals, savingsGoalAllocations, reimbursementSettlements,
      // Budget Engine (2026-07 cutover): versioned template lines + month diffs.
      budgetTemplateLines, budgetOverrides,
    };
    const json = JSON.stringify(payload, null, 2);
    try {
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `finance-export-${todayStr()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export download failed, trying clipboard fallback:', e);
      try {
        await navigator.clipboard.writeText(json);
        toast('Download blocked by sandbox. JSON copied to clipboard instead — paste it into a text file to save.');
      } catch (e2) {
        console.error('Clipboard fallback also failed:', e2);
        toast('Export failed. Check the console for details.');
      }
    }
  };

  const [showClearConfirm, setShowClearConfirm] = useState(false);
  // Diagnostic: surfaces errors from clearAllData / loadDemoData inside the modal,
  // since toast() is suppressed in the artifact sandbox. Temporary — remove once
  // demo loading is verified working.
  const [demoError, setDemoError] = useState(null);

  const clearAllData = async () => {
    setDemoError(null);
    try {
      const prefixes = ['tx:', 'acc:', 'bud:', 'bt:', 'bo:', 'inv:', 'rec:', 'card:', 'loan:', 'asset:', 'ci:', 'sg:', 'sga:', 'rs:', 'nw:'];
      // Serial clear: parallel DELETEs deadlock on Supabase because the FK
      // graph forces shared row locks across these tables. One-by-one is
      // slightly slower but eliminates the 40P01 deadlock errors.
      for (const p of prefixes) { await dataStore.clear(p); }
      setTransactions([]);
      setAccounts([]);
      setBudgets([]);
      setBudgetTemplateLines([]);
      setBudgetOverrides([]);
      setInvestments([]);
      setRecurring([]);
      setCards([]);
      setLoans([]);
      setAssets([]);
      setCardInstallments([]);
      setSavingsGoals([]);
      setSavingsGoalAllocations([]);
      setReimbursementSettlements([]);
      setNetWorthHistory([]);
      setShowClearConfirm(false);
    } catch (e) {
      console.error('Clear failed:', e);
      setDemoError(`Clear failed: ${e?.message || String(e)}`);
    }
  };

  const loadDemoData = async () => {
    setDemoError(null);
    // Per-step wrapper: lets us pinpoint which storage write throws.
    let currentStep = 'init';
    try {
      currentStep = 'clear existing data';
      const prefixes = ['tx:', 'acc:', 'bud:', 'bt:', 'bo:', 'inv:', 'rec:', 'card:', 'loan:', 'asset:', 'ci:', 'sg:', 'sga:', 'rs:', 'nw:'];
      // Serial clear: parallel DELETEs deadlock on Supabase because the FK
      // graph forces shared row locks across these tables. One-by-one is
      // slightly slower but eliminates the 40P01 deadlock errors.
      for (const p of prefixes) { await dataStore.clear(p); }

      const now = new Date();
      const cm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`; // current month YYYY-MM
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lmStr = `${lm.getFullYear()}-${String(lm.getMonth() + 1).padStart(2, '0')}`;
      const lm2 = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      const lm2Str = `${lm2.getFullYear()}-${String(lm2.getMonth() + 1).padStart(2, '0')}`;
      const lm3 = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const lm3Str = `${lm3.getFullYear()}-${String(lm3.getMonth() + 1).padStart(2, '0')}`;

      // Helper to build a date string
      const dt = (monthStr, day) => `${monthStr}-${String(day).padStart(2, '0')}`;

      // ============ ACCOUNTS ============
      const demoAccounts = [
        { id: 'acc-enbd-salary', name: 'ENBD Salary', type: 'checking', currency: 'AED', balance: 12500 },
        { id: 'acc-enbd-savings', name: 'ENBD Savings', type: 'savings', currency: 'AED', balance: 85000 },
        { id: 'acc-cash', name: 'Cash Wallet', type: 'cash', currency: 'AED', balance: 850 },
        { id: 'acc-wise-usd', name: 'Wise USD', type: 'checking', currency: 'USD', balance: 3200 },
      ];

      // ============ CARDS ============
      const demoCards = [
        {
          id: 'card-enbd-platinum', name: 'ENBD Platinum', network: 'Visa', lastFour: '4521',
          currency: 'AED', creditLimit: 50000, currentBalance: 8240, statementBalance: 6150,
          minimumPayment: 615, statementDay: 25, paymentDueDay: 15, apr: 35.99,
        },
        {
          id: 'card-mashreq-titanium', name: 'Mashreq Titanium', network: 'Mastercard', lastFour: '8832',
          currency: 'AED', creditLimit: 30000, currentBalance: 14200, statementBalance: 14200,
          minimumPayment: 710, statementDay: 5, paymentDueDay: 30, apr: 39.50,
        },
        {
          id: 'card-amex-gold', name: 'Amex Gold', network: 'American Express', lastFour: '2014',
          currency: 'AED', creditLimit: 25000, currentBalance: 3450, statementBalance: 0,
          minimumPayment: 0, statementDay: 15, paymentDueDay: 5, apr: 29.99,
        },
      ];

      // ============ LOANS ============
      const demoLoans = [
        {
          id: 'loan-mortgage', name: 'Apartment Mortgage', loanType: 'mortgage', lender: 'Emirates NBD',
          currency: 'AED', originalPrincipal: 1200000, currentBalance: 945000,
          interestRate: 4.25, termMonths: 300, monthlyPayment: 6510,
          startDate: '2021-08-01', paymentDay: 1,
        },
        {
          id: 'loan-car', name: 'Car Loan', loanType: 'auto', lender: 'ADCB',
          currency: 'AED', originalPrincipal: 145000, currentBalance: 62500,
          interestRate: 3.50, termMonths: 60, monthlyPayment: 2640,
          startDate: '2023-03-15', paymentDay: 5,
        },
      ];

      // ============ ASSETS ============
      const demoAssets = [
        {
          id: 'asset-apartment', name: 'Dubai Apartment', assetType: 'real_estate',
          currency: 'AED', purchasePrice: 1500000, purchaseDate: '2021-08-01',
          appreciationRate: 3, linkedLoanId: 'loan-mortgage', notes: 'JVC - 2BR',
        },
        {
          id: 'asset-car', name: 'Toyota Land Cruiser', assetType: 'vehicle',
          currency: 'AED', purchasePrice: 180000, purchaseDate: '2023-03-15',
          appreciationRate: -12, linkedLoanId: 'loan-car', notes: '2023 GXR',
        },
      ];
      const demoInvestments = [
        { id: 'inv-vti', symbol: 'VTI', name: 'Vanguard Total Stock Market ETF', shares: 45, avgCost: 215.30, currentPrice: 287.40, currency: 'USD', type: 'etf' },
        { id: 'inv-vxus', symbol: 'VXUS', name: 'Vanguard Total Intl Stock ETF', shares: 80, avgCost: 56.20, currentPrice: 64.85, currency: 'USD', type: 'etf' },
        { id: 'inv-aapl', symbol: 'AAPL', name: 'Apple Inc.', shares: 25, avgCost: 168.50, currentPrice: 232.10, currency: 'USD', type: 'stock' },
        { id: 'inv-emaar', symbol: 'EMAAR.AE', name: 'Emaar Properties', shares: 1500, avgCost: 5.85, currentPrice: 8.42, currency: 'AED', type: 'stock' },
        { id: 'inv-btc', symbol: 'BTC', name: 'Bitcoin', shares: 0.15, avgCost: 42000, currentPrice: 96500, currency: 'USD', type: 'crypto' },
      ];

      // ============ BUDGETS (current month) ============
      const demoBudgets = [
        { id: 'bud-groceries', category: 'Groceries', amount: 2500, month: cm },
        { id: 'bud-food', category: 'Food & Dining', amount: 2000, month: cm },
        { id: 'bud-transport', category: 'Transport', amount: 1200, month: cm },
        { id: 'bud-shopping', category: 'Shopping', amount: 1500, month: cm },
        { id: 'bud-entertainment', category: 'Entertainment', amount: 800, month: cm },
        { id: 'bud-utilities', category: 'Utilities', amount: 1000, month: cm },
        // Also a budget for last month so you can see a "past" month
        { id: 'bud-groceries-lm', category: 'Groceries', amount: 2500, month: lmStr },
        { id: 'bud-food-lm', category: 'Food & Dining', amount: 2000, month: lmStr },
      ];

      // ============ RECURRING RULES ============
      const demoRecurring = [
        {
          id: 'rec-salary', description: 'Monthly Salary', amount: 32000, currency: 'AED',
          type: 'income', category: 'Salary', account: 'ENBD Salary',
          pattern: 'last_working_day', dayOfMonth: 1, weekday: 4, occurrence: -1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: '',
        },
        {
          id: 'rec-rent', description: 'Apartment Rent', amount: 8500, currency: 'AED',
          type: 'expense', category: 'Rent', account: 'ENBD Salary',
          pattern: 'day_of_month', dayOfMonth: 1, weekday: 0, occurrence: 1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: '',
        },
        {
          id: 'rec-mortgage', description: 'Mortgage Payment', amount: 6510, currency: 'AED',
          type: 'expense', category: 'Other', account: 'ENBD Salary',
          pattern: 'day_of_month', dayOfMonth: 1, weekday: 0, occurrence: 1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: 'Auto-deducted from salary account',
        },
        {
          id: 'rec-car-loan', description: 'Car Loan Payment', amount: 2640, currency: 'AED',
          type: 'expense', category: 'Transport', account: 'ENBD Salary',
          pattern: 'day_of_month', dayOfMonth: 5, weekday: 0, occurrence: 1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: '',
        },
        {
          id: 'rec-spotify', description: 'Spotify Family', amount: 9.99, currency: 'USD',
          type: 'expense', category: 'Subscriptions', account: 'ENBD Salary',
          pattern: 'day_of_month', dayOfMonth: 12, weekday: 0, occurrence: 1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: '',
        },
        {
          id: 'rec-netflix', description: 'Netflix', amount: 56, currency: 'AED',
          type: 'expense', category: 'Subscriptions', account: 'ENBD Salary',
          pattern: 'day_of_month', dayOfMonth: 18, weekday: 0, occurrence: 1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: '',
        },
        {
          id: 'rec-icloud', description: 'iCloud+ 200GB', amount: 11.99, currency: 'AED',
          type: 'expense', category: 'Subscriptions', account: 'ENBD Salary',
          pattern: 'day_of_month', dayOfMonth: 22, weekday: 0, occurrence: 1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: '',
        },
        {
          id: 'rec-dewa', description: 'DEWA (Electricity & Water)', amount: 480, currency: 'AED',
          type: 'expense', category: 'Utilities', account: 'ENBD Salary',
          pattern: 'day_of_month', dayOfMonth: 15, weekday: 0, occurrence: 1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: '',
        },
        {
          id: 'rec-du', description: 'du Mobile + Home Internet', amount: 425, currency: 'AED',
          type: 'expense', category: 'Utilities', account: 'ENBD Salary',
          pattern: 'day_of_month', dayOfMonth: 8, weekday: 0, occurrence: 1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: '',
        },
        {
          id: 'rec-gym', description: 'Gym Membership', amount: 350, currency: 'AED',
          type: 'expense', category: 'Personal Care', account: 'ENBD Salary',
          pattern: 'day_of_month', dayOfMonth: 1, weekday: 0, occurrence: 1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: '',
        },
        {
          id: 'rec-savings-transfer', description: 'Auto-Save to Savings', amount: 5000, currency: 'AED',
          type: 'expense', category: 'Other', account: 'ENBD Salary',
          pattern: 'day_of_month', dayOfMonth: 2, weekday: 0, occurrence: 1,
          interval: 1, unit: 'month', startDate: '2024-01-01', endDate: null,
          active: true, notes: 'Automatic transfer to savings account',
        },
      ];

      // ============ ONE-OFF TRANSACTIONS for the current month ============
      // These are realistic ad-hoc transactions on top of the recurring ones
      const day = now.getDate();
      const oneOffs = [
        // Groceries — multiple visits this month
        { date: dt(cm, Math.max(1, day - 1)), description: 'Carrefour MOE', amount: 387.50, category: 'Groceries', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(cm, Math.max(1, day - 4)), description: 'Spinneys', amount: 245.80, category: 'Groceries', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(cm, Math.max(1, day - 8)), description: 'Lulu Hypermarket', amount: 412.30, category: 'Groceries', type: 'expense', account: 'Mashreq Titanium' },
        { date: dt(cm, Math.max(1, day - 12)), description: 'Carrefour Express', amount: 89.50, category: 'Groceries', type: 'expense', account: 'Cash Wallet' },

        // Food & Dining
        { date: dt(cm, Math.max(1, day - 1)), description: 'Talabat - Dinner', amount: 78.50, category: 'Food & Dining', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(cm, Math.max(1, day - 2)), description: 'Starbucks DIFC', amount: 32, category: 'Food & Dining', type: 'expense', account: 'Amex Gold' },
        { date: dt(cm, Math.max(1, day - 3)), description: 'Operation Falafel', amount: 45, category: 'Food & Dining', type: 'expense', account: 'Cash Wallet' },
        { date: dt(cm, Math.max(1, day - 5)), description: 'Zuma Dubai', amount: 680, category: 'Food & Dining', type: 'expense', account: 'Amex Gold', notes: 'Anniversary dinner with Ibtissam' },
        { date: dt(cm, Math.max(1, day - 7)), description: 'Deliveroo - Lunch', amount: 52, category: 'Food & Dining', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(cm, Math.max(1, day - 9)), description: 'Ravi Restaurant', amount: 95, category: 'Food & Dining', type: 'expense', account: 'Cash Wallet' },
        { date: dt(cm, Math.max(1, day - 11)), description: 'Costa Coffee', amount: 28, category: 'Food & Dining', type: 'expense', account: 'Amex Gold' },

        // Transport
        { date: dt(cm, Math.max(1, day - 1)), description: 'Salik', amount: 24, category: 'Transport', type: 'expense', account: 'ENBD Salary' },
        { date: dt(cm, Math.max(1, day - 4)), description: 'ADNOC - Petrol', amount: 240, category: 'Transport', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(cm, Math.max(1, day - 8)), description: 'Careem - Airport', amount: 85, category: 'Transport', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(cm, Math.max(1, day - 14)), description: 'ENOC - Petrol', amount: 220, category: 'Transport', type: 'expense', account: 'Mashreq Titanium' },

        // Shopping
        { date: dt(cm, Math.max(1, day - 2)), description: 'Amazon.ae', amount: 215, category: 'Shopping', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(cm, Math.max(1, day - 6)), description: 'Namshi', amount: 480, category: 'Shopping', type: 'expense', account: 'Mashreq Titanium' },
        { date: dt(cm, Math.max(1, day - 10)), description: 'Sharaf DG - AirPods', amount: 899, category: 'Shopping', type: 'expense', account: 'Amex Gold' },

        // Entertainment
        { date: dt(cm, Math.max(1, day - 3)), description: 'VOX Cinemas', amount: 120, category: 'Entertainment', type: 'expense', account: 'Amex Gold' },
        { date: dt(cm, Math.max(1, day - 9)), description: 'Coca-Cola Arena - Concert', amount: 450, category: 'Entertainment', type: 'expense', account: 'ENBD Platinum' },

        // Healthcare
        { date: dt(cm, Math.max(1, day - 6)), description: 'Aster Pharmacy', amount: 145, category: 'Healthcare', type: 'expense', account: 'Amex Gold' },

        // Personal Care
        { date: dt(cm, Math.max(1, day - 5)), description: 'Salon - Haircut', amount: 80, category: 'Personal Care', type: 'expense', account: 'Cash Wallet' },

        // Income beyond salary
        { date: dt(cm, Math.max(1, day - 7)), description: 'Freelance Consulting - Tech advisory', amount: 4500, category: 'Freelance', type: 'income', account: 'ENBD Salary' },

        // Last month — fewer entries to give a realistic comparison
        { date: dt(lmStr, 28), description: 'Carrefour MOE', amount: 425, category: 'Groceries', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(lmStr, 22), description: 'Spinneys', amount: 380, category: 'Groceries', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(lmStr, 18), description: 'Lulu', amount: 290, category: 'Groceries', type: 'expense', account: 'Mashreq Titanium' },
        { date: dt(lmStr, 15), description: 'Talabat', amount: 95, category: 'Food & Dining', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(lmStr, 12), description: 'Brunch at Bushman\'s', amount: 520, category: 'Food & Dining', type: 'expense', account: 'Amex Gold' },
        { date: dt(lmStr, 10), description: 'ADNOC', amount: 230, category: 'Transport', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(lmStr, 8), description: 'Salik recharge', amount: 100, category: 'Transport', type: 'expense', account: 'ENBD Salary' },
        { date: dt(lmStr, 5), description: 'Mall of the Emirates - Shopping', amount: 720, category: 'Shopping', type: 'expense', account: 'Mashreq Titanium' },

        // Two months ago
        { date: dt(lm2Str, 25), description: 'Carrefour', amount: 410, category: 'Groceries', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(lm2Str, 20), description: 'Restaurants & Cafes', amount: 850, category: 'Food & Dining', type: 'expense', account: 'Amex Gold' },
        { date: dt(lm2Str, 14), description: 'Petrol & Salik', amount: 320, category: 'Transport', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(lm2Str, 7), description: 'Birthday Gift for Mom', amount: 600, category: 'Gifts', type: 'expense', account: 'Amex Gold' },

        // Three months ago — vacation month
        { date: dt(lm3Str, 28), description: 'Emirates Flights to Casablanca', amount: 4200, category: 'Travel', type: 'expense', account: 'Amex Gold', notes: 'Trip to visit family' },
        { date: dt(lm3Str, 25), description: 'Hotel - Marrakech', amount: 2800, category: 'Travel', type: 'expense', account: 'Amex Gold' },
        { date: dt(lm3Str, 20), description: 'Restaurants in Morocco', amount: 1200, category: 'Food & Dining', type: 'expense', account: 'Amex Gold' },
        { date: dt(lm3Str, 15), description: 'Carrefour', amount: 380, category: 'Groceries', type: 'expense', account: 'ENBD Platinum' },
        { date: dt(lm3Str, 10), description: 'Souvenirs & Shopping in Morocco', amount: 950, category: 'Shopping', type: 'expense', account: 'Amex Gold' },
      ].map(t => ({
        id: uid('tx'),
        currency: 'AED',
        notes: t.notes || '',
        source: 'manual',
        ...t,
      }));

      // M4: remap every hardcoded demo ID to a fresh uuid before anything
      // hits Supabase (the schema's uuid columns reject the artifact's old
      // 'acc-enbd-salary'-style strings). One Map<oldId, newId> covers the
      // cross-references too — assets.linkedLoanId, transactions.recurringId,
      // and the resolver outputs (which read accounts/cards by name and emit
      // their .id, now the new uuid) all stay consistent.
      const idFor = new Map();
      const fresh = (oldId) => {
        if (!idFor.has(oldId)) idFor.set(oldId, uid('any'));
        return idFor.get(oldId);
      };
      demoAccounts.forEach((a) => { a.id = fresh(a.id); });
      demoCards.forEach((c) => { c.id = fresh(c.id); });
      demoLoans.forEach((l) => { l.id = fresh(l.id); });
      demoAssets.forEach((a) => {
        a.id = fresh(a.id);
        if (a.linkedLoanId) a.linkedLoanId = fresh(a.linkedLoanId);
      });
      demoInvestments.forEach((i) => { i.id = fresh(i.id); });
      demoBudgets.forEach((b) => { b.id = fresh(b.id); });
      demoRecurring.forEach((r) => { r.id = fresh(r.id); });

      // ============ MATERIALIZE RECURRING for past months ============
      // Generate the recurring transactions from rules
      const today = todayStr();
      const recurringTxs = [];
      for (const rule of demoRecurring) {
        if (!rule.active) continue;
        const occurrences = generateOccurrences(rule, rule.startDate, today);
        for (const occ of occurrences) {
          const occKey = dateToStr(occ);
          recurringTxs.push({
            id: uid('tx'),
            date: occKey,
            description: rule.description,
            amount: rule.amount,
            currency: rule.currency,
            category: rule.category,
            type: rule.type,
            account: rule.account,
            notes: rule.notes || `Recurring: ${describeRule(rule)}`,
            source: 'recurring',
            recurringId: rule.id,
            occurrenceDate: occKey,
          });
        }
      }

      const allTxs = [...oneOffs, ...recurringTxs];

      // ============ B2: resolve IDs before persisting ============
      // Categories come from current state (seeded at app boot). Accounts/cards are the
      // ones we're about to persist — we resolve against the in-memory arrays before any
      // of them hit storage. Future loads see fully-resolved records.
      const resolverCtx = { accounts: demoAccounts, cards: demoCards, categories };
      const resolvedRecurring = demoRecurring.map(r => resolveRecurringIds(r, resolverCtx));
      const resolvedBudgets = demoBudgets.map(b => resolveBudgetIds(b, resolverCtx));
      const resolvedTxs = allTxs.map(t => resolveTxIds(t, resolverCtx));

      // ============ PERSIST EVERYTHING (sequential, per-entity, so failures pinpoint) ============
      currentStep = 'persist accounts';
      await Promise.all(demoAccounts.map(a => dataStore.set(`acc:${a.id}`, a)));
      currentStep = 'persist cards';
      await Promise.all(demoCards.map(c => dataStore.set(`card:${c.id}`, c)));
      currentStep = 'persist loans';
      await Promise.all(demoLoans.map(l => dataStore.set(`loan:${l.id}`, l)));
      currentStep = 'persist assets';
      await Promise.all(demoAssets.map(a => dataStore.set(`asset:${a.id}`, a)));
      currentStep = 'persist investments';
      await Promise.all(demoInvestments.map(i => dataStore.set(`inv:${i.id}`, i)));
      currentStep = 'persist budgets';
      await Promise.all(resolvedBudgets.map(b => dataStore.set(`bud:${b.id}`, b)));
      currentStep = 'persist recurring rules';
      await Promise.all(resolvedRecurring.map(r => dataStore.set(`rec:${r.id}`, r)));
      currentStep = `persist transactions (${resolvedTxs.length} rows)`;
      // Chunk into batches of 20 so we don't fan out hundreds of concurrent
      // upserts at Supabase's rate limit. The audit flagged this as a
      // future-429 once demo + import data combine.
      await chunkedAll(resolvedTxs, 20, (t) => dataStore.set(`tx:${t.id}`, t));

      // ============ UPDATE STATE ============
      currentStep = 'update state';
      setAccounts(demoAccounts);
      setCards(demoCards);
      setLoans(demoLoans);
      setAssets(demoAssets);
      setInvestments(demoInvestments);
      setBudgets(resolvedBudgets);
      setRecurring(resolvedRecurring);
      setTransactions(resolvedTxs.sort((a, b) => cmpDateStr(b.date, a.date)));

      setShowClearConfirm(false);
    } catch (e) {
      console.error(`Demo data load failed at step "${currentStep}":`, e);
      setDemoError(`Demo load failed at step "${currentStep}": ${e?.message || String(e)}`);
    }
  };

  // Decorate loans with a payment-driven live balance (separate field so the
  // edit modal still reads the frozen `currentBalance` starting point).
  // MUST be above the early `loading` return — hooks run unconditionally.
  const loansWithLive = useMemo(
    () => loans.map(l => ({ ...l, liveBalance: computeLoanLiveBalance(l, transactions) })),
    [loans, transactions],
  );

  // "Review & tidy" notification sources — surfaced centrally in the bell
  // (not on the dashboard). Each is one bell item; counts live inside it.
  const notifTidy = useMemo(() => {
    const uncatRulesList = recurring.filter(r => r.active && !r.deletedAt && !r.category);
    const uncatRules = uncatRulesList.length;
    const uncatRuleIds = uncatRulesList.map(r => r.id);
    const uncatTxs = transactions.filter(t => !t.deletedAt && (t.type === 'expense' || t.type === 'income') && !t.category).length;
    const fxEst = transactions.filter(t => !t.deletedAt && t.fxEstimated).length;
    const review = transactions.filter(t => !t.deletedAt && t.smsNeedsReview).length;
    const { dupeIds, groups: dupeGroups } = computeDuplicateTxGroups(transactions);
    return { uncatRules, uncatRuleIds, uncatTxs, fxEst, review, dupes: dupeGroups.length, dupeIds: [...dupeIds] };
  }, [recurring, transactions]);

  // Statement payment reminders for the bell: a card's latest statement that's
  // still partly unpaid and due within a week (or already overdue).
  const notifStatements = useMemo(() => {
    const out: any[] = [];
    for (const c of cards) {
      if (c.deletedAt) continue;
      const latest = cardStatements
        .filter(s => s.cardId === c.id && !s.deletedAt)
        .sort((a, b) => cmpDateStr(b.statementDate, a.statementDate))[0];
      if (!latest || !latest.dueDate) continue;
      const due = statementAmountDue(latest);
      const paid = transactions
        .filter(t => !t.deletedAt && t.type === 'transfer' && t.toCardId === c.id && cmpDateStr(t.date, latest.statementDate) >= 0)
        .reduce((s, t) => s + (Number(t.amount) || 0), 0);
      const remaining = Math.max(0, Math.round((due - paid) * 100) / 100);
      if (remaining <= 0.01) continue;
      const days = Math.round((strToDate(latest.dueDate).getTime() - strToDate(todayStr()).getTime()) / MS_PER_DAY);
      const minUnmet = latest.minimumPayment != null && paid < (Number(latest.minimumPayment) || 0);
      if (days < 0 || days <= 7) out.push({ cardId: c.id, cardName: c.name, currency: c.currency, due, paid, remaining, dueDate: latest.dueDate, days, overdue: days < 0, minUnmet });
    }
    return out;
  }, [cards, cardStatements, transactions]);

  // Cash-flow view of loan payments: a payment is a transfer → loan (so the
  // loan amortises and net worth is correct), but if the loan carries a budget
  // category we surface the FULL payment as spending under it. These synthetic
  // entries are category-only (no account/card, excludesFromBalances) so the
  // budget/spending sums pick them up while balances + net worth ignore them.
  const loanSpendTx = useMemo(() => {
    const out: any[] = [];
    for (const t of transactions) {
      if (t.deletedAt || t.type !== 'transfer' || !t.toLoanId) continue;
      const loan = loans.find(l => l.id === t.toLoanId);
      if (!loan?.budgetCategoryId) continue;
      const cat = categories.find(c => c.id === loan.budgetCategoryId);
      if (!cat) continue;
      out.push({
        id: `loanspend-${t.id}`, type: 'expense', category: cat.name, categoryId: loan.budgetCategoryId,
        // Carry the payment's name/source so it doesn't render blank ("—") when
        // this synthetic spend row shows in the budget category's tx list.
        description: t.description || `${loan.name || 'Loan'} payment`,
        account: t.fromAccount || t.account || loan.name || '',
        amount: t.amount, currency: t.currency || loan.currency || 'AED', date: t.date,
        isRefund: false, _loanSpend: true, excludesFromBalances: true,
      });
    }
    return out;
  }, [transactions, loans, categories]);
  // Augmented set for the budget/spending views only (NOT the tx list / balances).
  const budgetTx = useMemo(() => [...transactions, ...loanSpendTx], [transactions, loanSpendTx]);

  // Unified budget rows for forecasting + bell alerts. The Budget Engine owns
  // 2026-07 onward: each such month's per-category total is the SUM OF ITS LINES,
  // computed on demand from the template + overrides. Pre-cutover months keep
  // their legacy `budgets` rows (frozen). Everything downstream (Dashboard
  // forecast, Cash Calendar, savings ETAs, liquidity + over-budget alerts) reads
  // this one array in the legacy shape, so there's no split brain.
  const effectiveBudgets = useMemo(() => {
    const catName = (id) => categories.find(c => c.id === id && !c.deletedAt)?.name || '';
    const rows = budgets.filter(b => !b.deletedAt && b.month && b.month < BUDGET_CUTOVER_MONTH).map(b => ({ ...b }));
    const [cy, cm] = budgetCurrentMonth.split('-').map(Number);
    const endD = new Date(cy, cm - 1 + 30, 1);
    const endKey = `${endD.getFullYear()}-${String(endD.getMonth() + 1).padStart(2, '0')}`;
    const [sy, sm] = BUDGET_CUTOVER_MONTH.split('-').map(Number);
    for (let i = 0; ; i++) {
      const d = new Date(sy, sm - 1 + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (key > endKey) break;
      const res = computeBudgetMonth(key, budgetTemplateLines, budgetOverrides);
      for (const cr of res.categories) {
        if (cr.total === 0) continue;
        rows.push({ id: `eng-${key}-${cr.categoryId}`, month: key, categoryId: cr.categoryId, category: catName(cr.categoryId), amount: cr.total, currency: baseCurrency, deletedAt: null });
      }
    }
    return rows;
  }, [budgets, budgetTemplateLines, budgetOverrides, categories, budgetCurrentMonth, baseCurrency]);

  // Current-month budget categories at/over 100% spent — surfaced in the bell
  // as an informational alert (no action needed; it clears itself when spend
  // drops back under or the month rolls over). Mirrors the Budgets page's spend
  // math exactly, including loan payments via budgetTx.
  const budgetAlerts = useMemo(() => {
    const curMonth = todayStr().slice(0, 7);
    const today = todayStr();
    return effectiveBudgets
      .filter(b => !b.deletedAt && (b.month || curMonth) === curMonth)
      .map(b => {
        const spent = budgetTx
          .filter(t => t.type === 'expense' && t.category === b.category && (t.date || '').startsWith(curMonth) && t.date <= today)
          .reduce((s, t) => s + (t.isRefund ? -toBase(t.amount, t.currency) : toBase(t.amount, t.currency)), 0);
        const clamped = Math.max(0, spent);
        const pct = b.amount > 0 ? (clamped / b.amount) * 100 : 0;
        // Bands: warn (approaching), full (100–109), over (110+). The notif
        // re-fires only when a category crosses INTO a new band.
        const band = pct >= 110 ? 'over' : pct >= 100 ? 'full' : pct >= 85 ? 'warn' : null;
        return { category: b.category, pct, band };
      })
      .filter(b => b.band)
      .sort((a, b) => b.pct - a.pct);
  }, [effectiveBudgets, budgetTx]);

  // Additional bell alerts (informational; clear themselves when resolved).
  const moreNotifs = useMemo(() => {
    const today = todayStr();
    const todayD = strToDate(today);
    const daysSince = (dateStr) => Math.round((todayD.getTime() - strToDate(dateStr).getTime()) / MS_PER_DAY);

    // 1) Liquidity warning — earliest projected month where CASH dips below the
    //    buffer (default 0 = negative-only) and/or NET WORTH goes negative. Both
    //    come from the same forecast engine that feeds the Dashboard chart.
    let liquidity = null;
    try {
      const buffer = Number(localStorage.getItem('floosi_cash_buffer')) || 0;
      const series = projectCashSeries({ accounts, transactions, recurring, budgets: effectiveBudgets, cards, cardStatements, baseCurrency, horizon: 6, investments, assets, loans, cardInstallments });
      const cashHit = series.find(m => m.cash < buffer);
      const nwHit = series.find(m => m.netWorth < 0);
      if (cashHit || nwHit) liquidity = {
        buffer,
        cash: cashHit ? { label: cashHit.label, amount: cashHit.cash } : null,
        netWorth: nwHit ? { label: nwHit.label, amount: nwHit.netWorth } : null,
      };
    } catch { /* ignore */ }

    // 2) Statement closed but not yet uploaded / amount-due entered. Escalates
    //    at 2 / 7 / 14 / 21 days after the close and keeps nagging until a
    //    statement record exists for that month (upload OR manual amount-due).
    const stmtUpload: any[] = [];
    cards.filter(c => !c.deletedAt && c.statementDay).forEach(c => {
      let lc = new Date(todayD.getFullYear(), todayD.getMonth(), Math.min(Number(c.statementDay), lastDayOfMonth(todayD.getFullYear(), todayD.getMonth())));
      if (lc > todayD) { const p = new Date(todayD.getFullYear(), todayD.getMonth() - 1, 1); lc = new Date(p.getFullYear(), p.getMonth(), Math.min(Number(c.statementDay), lastDayOfMonth(p.getFullYear(), p.getMonth()))); }
      const lcStr = dateToStr(lc);
      const d = daysSince(lcStr);
      if (d < 2) return; // give it ~2 days after close to arrive/post
      const has = (cardStatements || []).some(s => s.cardId === c.id && !s.deletedAt && s.statementDate && s.statementDate.slice(0, 7) === lcStr.slice(0, 7));
      if (!has) stmtUpload.push({ name: c.name, days: d });
    });

    // 3) Reimbursables not yet settled — remind from the 3rd-to-last working
    //    day of the month AFTER the expense (reimbursements usually land the
    //    following month), and keep reminding every month until settled.
    const reimbReminderDue = (dateStr) => {
      const d = strToDate(dateStr);
      let y = d.getFullYear(), m = d.getMonth() + 1; // the month after the expense
      if (m > 11) { m = 0; y += 1; }
      return getNthLastWorkingDay(y, m, 3);
    };
    const reimbTxs = transactions.filter(t => {
      if (t.deletedAt || !t.isReimbursable) return false;
      if (((Number(t.amount) || 0) - (Number(t.reimbursedAmount) || 0)) <= 0.01) return false; // settled
      const due = reimbReminderDue(t.date);
      return due && todayD >= due;
    });
    const reimbAging = reimbTxs.length
      ? {
          count: reimbTxs.length,
          total: reimbTxs.reduce((s, t) => s + toBase((Number(t.amount) || 0) - (Number(t.reimbursedAmount) || 0), t.currency), 0),
          names: reimbTxs.map(t => t.description).slice(0, 4),
          month: today.slice(0, 7), // re-surfaces each month's reminder cycle
        }
      : null;

    // (Statement closing-balance reconciliation removed — Floosi can't reliably
    //  reconstruct a historical card balance, so it produced phantom mismatches.)

    // 5) Savings goal progress milestones: 25 / 50 / 75 / 90 / 100% of target
    //    (real value, no projected pool). Re-fires only on crossing into a band.
    const goalAlerts = (savingsGoals || []).map(g => {
      if (g.deletedAt) return null;
      const target = Number(g.targetAmount) || 0;
      if (target <= 0) return null;
      const current = computeGoalProgress(g, savingsGoalAllocations, accounts, investments, transactions, 0);
      const pct = (current / target) * 100;
      const band = pct >= 100 ? 'done' : pct >= 90 ? 'm90' : pct >= 75 ? 'm75' : pct >= 50 ? 'm50' : pct >= 25 ? 'm25' : null;
      return band ? { name: g.name, pct, band } : null;
    }).filter(Boolean);

    // 6) Loan EMI due within 7 days. Reminds at 7/3/1/today, and checks that
    //    BOTH the withdrawn-from account and (if set) the direct-debit feeder
    //    account have enough to cover the payment — warning per account.
    const loanDue = (loans || []).filter(l => !l.deletedAt).map(l => {
      const d = nextLoanPaymentDate(l);
      if (!d) return null;
      const days = Math.ceil((d.getTime() - todayD.getTime()) / MS_PER_DAY);
      if (days < 0 || days > 7) return null;
      const pay = Number(l.monthlyPayment) || 0;
      const shortfalls: string[] = [];
      if (pay > 0) {
        const payAcc = accounts.find(a => a.id === l.paymentAccountId && !a.deletedAt);
        const ddAcc = accounts.find(a => a.id === l.directDebitAccountId && !a.deletedAt);
        if (payAcc) {
          const need = convert(pay, l.currency, payAcc.currency);
          const bal = computeAccountLiveBalance(payAcc, transactions);
          if (bal < need) shortfalls.push(`${payAcc.name} short ${fmtMoney(need - bal, payAcc.currency, false)} for the payment`);
        }
        if (ddAcc) {
          const need = convert(pay, l.currency, ddAcc.currency);
          const bal = computeAccountLiveBalance(ddAcc, transactions);
          if (bal < need) shortfalls.push(`${ddAcc.name} short ${fmtMoney(need - bal, ddAcc.currency, false)} for the direct debit`);
        }
      }
      return { name: l.name, days, shortfalls };
    }).filter(Boolean);

    // 7) Forecast months where you'd spend more than you make (net < 0 → dip
    //    into savings). Fires on first detection, then re-surfaces on the
    //    3rd-to-last working day of each following month until it's no longer
    //    negative (the "period" advances the notification key on that day).
    const savingsNeg: any[] = [];
    try {
      const ns = projectMonthlyNetSeries({ accounts, transactions, recurring, budgets: effectiveBudgets, baseCurrency, horizon: 6 });
      const tlbd = getNthLastWorkingDay(todayD.getFullYear(), todayD.getMonth() + 1, 3);
      const period = (todayD >= tlbd)
        ? `${todayD.getFullYear()}-${String(todayD.getMonth() + 1).padStart(2, '0')}`
        : (() => { const p = new Date(todayD.getFullYear(), todayD.getMonth() - 1, 1); return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}`; })();
      ns.forEach(m => {
        if (m.net < 0 && !m.noBaseline) savingsNeg.push({ label: m.label, key: m.key, net: m.net, income: m.income, expense: m.expense, isEstimated: m.isEstimated, period });
      });
    } catch { /* ignore */ }

    // 8) Upcoming recurring-payment reminders (opt-in per rule via `remind`).
    //    Cadence scales with frequency: weekly → 1d; monthly → 7d + 1d;
    //    multi-month / yearly → 30d + 7d + 1d before the next occurrence.
    //    Auto-log rules self-advance (the reminder always targets the next
    //    un-materialized occurrence); for any rule the user can mark a specific
    //    occurrence "handled" (paid early) to mute its remaining reminders.
    const recReminders: any[] = [];
    const recRemindersPassed: any[] = []; // occurrence just passed → shows in Recent for a few days
    (recurring || []).filter(r => r.remind && !r.deletedAt && r.startDate && !r.isInterestRule).forEach(r => {
      const horizon = new Date(todayD); horizon.setMonth(horizon.getMonth() + 14);
      const occ = generateOccurrences(r, today, dateToStr(horizon))[0];
      const unit = r.unit || 'month';
      const interval = Math.max(1, Number(r.interval) || 1);
      const milestones = unit === 'week' ? [1]
        : (unit === 'month' && interval === 1) ? [7, 1]
        : [30, 7, 1];
      // Upcoming: a single reminder at the current milestone (it advances
      // 1M → 1W → 1D as the date nears; only one shows at a time).
      if (occ) {
        const days = Math.ceil((occ.getTime() - todayD.getTime()) / MS_PER_DAY);
        const occKey = dateToStr(occ);
        if (days >= 0 && days <= milestones[0] && !recRemindDone.has(`${r.id}:${occKey}`)) {
          const bucket = days <= 1 ? 'd1' : days <= 7 ? 'd7' : 'd30';
          recReminders.push({ ruleId: r.id, desc: r.description, amount: Number(r.amount) || 0, currency: r.currency, type: r.type, days, occKey, bucket });
        }
      }
      // Just-passed (within the last 3 days): drops to Recent the day after.
      const fromD = new Date(todayD); fromD.setDate(fromD.getDate() - 3);
      const yest = new Date(todayD); yest.setDate(yest.getDate() - 1);
      const passed = generateOccurrences(r, dateToStr(fromD), dateToStr(yest));
      const lastPassed = passed[passed.length - 1];
      if (lastPassed) {
        const occKey = dateToStr(lastPassed);
        if (!recRemindDone.has(`${r.id}:${occKey}`)) {
          recRemindersPassed.push({ ruleId: r.id, desc: r.description, amount: Number(r.amount) || 0, currency: r.currency, type: r.type, occKey, daysAgo: Math.round((todayD.getTime() - lastPassed.getTime()) / MS_PER_DAY) });
        }
      }
    });

    return { liquidity, stmtUpload, reimbAging, goalAlerts, loanDue, savingsNeg, recReminders, recRemindersPassed };
  }, [accounts, transactions, recurring, budgets, cards, cardStatements, cardInstallments, loans, assets, savingsGoals, savingsGoalAllocations, investments, baseCurrency, recRemindDone]);

  // The LIVE drift for a card right now: Floosi's current available (credit limit
  // − live owed, installments included) vs the bank's last SMS-reported available.
  // This is exactly what the Cards "Bank sync" card shows — recomputed from
  // current data, NOT the value frozen on the stored alert row (which goes stale
  // as soon as a tx is added/removed or a newer SMS lands). Positive = Floosi
  // shows more available = LESS spend logged than the bank.
  const liveCardDrift = (cardId) => {
    const card = cards.find(c => c.id === cardId);
    if (!card || card.lastSmsAvailableLimit == null) return null;
    const owed = computeCardLiveBalance(card, transactions, cardInstallments);
    const available = (Number(card.creditLimit) || 0) - owed;
    return { drift: available - Number(card.lastSmsAvailableLimit), owed: Math.max(0, owed), card };
  };

  // A bank-drift alert only earns a notification when the CURRENT drift is
  // material AND not snoozed: > 1% of live owed and >= 1 unit. Using the live
  // drift (not the stored delta) means an alert auto-clears once the card comes
  // back in sync, and always shows the current figure.
  const driftQualifies = (a) => {
    const card = cards.find(c => c.id === a.cardId);
    if (!card) return true; // can't evaluate → don't hide
    try {
      const until = Number(localStorage.getItem('floosi_drift_snooze_' + a.cardId)) || 0;
      if (until > Date.now()) return false;
    } catch { /* ignore */ }
    const live = liveCardDrift(a.cardId);
    if (!live) { // no last-SMS baseline → fall back to the stored delta
      const owed = (Number(card.creditLimit) || 0) - Number(a.floosiLimit);
      const drift = Math.abs(Number(a.delta) || 0);
      return !(owed > 0 && drift <= owed * 0.01);
    }
    const drift = Math.abs(live.drift);
    if (drift < 1) return false;                              // now in sync
    if (live.owed > 0 && drift <= live.owed * 0.01) return false; // under 1% → noise
    return true;
  };

  // A bank-drift alert is a CURRENT-STATE signal, so only the newest unresolved
  // alert per card is actionable — older ones for the same card are superseded
  // noise. Each shown alert is re-stamped with the LIVE drift/owed so the bell
  // matches the Cards page instead of the stale stored value.
  const shownReconAlerts = useMemo(() => {
    const byCard = new Map();
    for (const a of smsReconAlerts) {
      if (a.resolvedAt || !driftQualifies(a)) continue;
      const prev = byCard.get(a.cardId);
      if (!prev || (a.createdAt || '') > (prev.createdAt || '')) byCard.set(a.cardId, a);
    }
    return [...byCard.values()].map(a => {
      const live = liveCardDrift(a.cardId);
      if (!live) return a;
      // Override the fields the panel reads (delta, floosiLimit) so it renders
      // the live drift and % without touching the panel's math.
      return { ...a, delta: Math.round(live.drift * 100) / 100, floosiLimit: (Number(live.card.creditLimit) || 0) - live.owed };
    });
  }, [smsReconAlerts, cards, transactions, cardInstallments]);

  // Stable signature of the current notification set. Changes when something
  // new appears (a new alert, another budget maxed out, etc.) so the bell can
  // re-light; stays identical while nothing's changed so "read" sticks.
  const notifSignature = useMemo(() => {
    const parts: string[] = [];
    shownReconAlerts.forEach(a => parts.push('al:' + a.id));
    smsPending.forEach(p => parts.push('pe:' + p.id));
    notifStatements.forEach(s => {
      // Re-badge at each milestone (7 → 3 → 2 → 1 → today → overdue) so the
      // reminder escalates; between milestones it stays "read".
      const m = s.days < 0 ? 'overdue' : s.days <= 0 ? 'd0' : s.days <= 1 ? 'd1' : s.days <= 2 ? 'd2' : s.days <= 3 ? 'd3' : 'd7';
      parts.push('st:' + (s.cardId || s.id) + ':' + m);
    });
    if ((notifTidy.uncatRules + notifTidy.uncatTxs) > 0) parts.push(`uncat:${notifTidy.uncatRules}/${notifTidy.uncatTxs}`);
    if (notifTidy.review > 0) parts.push('rev:' + notifTidy.review);
    if (notifTidy.dupes > 0) parts.push('dup:' + notifTidy.dupes);
    budgetAlerts.forEach(b => parts.push('bud:' + b.category + ':' + b.band));
    if (moreNotifs.liquidity) {
      const L = moreNotifs.liquidity;
      parts.push('liq:' + (L.cash ? L.cash.label + (L.cash.amount < 0 ? 'neg' : 'low') : '-') + ':' + (L.netWorth ? L.netWorth.label : '-'));
    }
    // First nudge at day 2, then re-light the bell every 7 days (week 1, 2, 3…)
    // indefinitely until the statement is recorded — no cap. Between buckets the
    // signature is stable, so the item just sits in Review & Tidy without re-ringing.
    if (moreNotifs.stmtUpload.length) parts.push('stup:' + moreNotifs.stmtUpload.map(s => s.name + ':' + (s.days >= 7 ? 'w' + Math.floor(s.days / 7) : 'd2')).sort().join(','));
    if (moreNotifs.reimbAging) parts.push('reimb:' + moreNotifs.reimbAging.month + ':' + moreNotifs.reimbAging.count);
    moreNotifs.goalAlerts.forEach(g => parts.push('goal:' + g.name + ':' + g.band));
    moreNotifs.loanDue.forEach(l => {
      const m = l.days <= 0 ? 'd0' : l.days <= 1 ? 'd1' : l.days <= 3 ? 'd3' : 'd7';
      parts.push('loan:' + l.name + ':' + m + ':' + (l.shortfalls.length ? 'short' : 'ok'));
    });
    (moreNotifs.savingsNeg || []).forEach(m => parts.push('savings:' + m.key + ':' + m.period));
    (moreNotifs.recReminders || []).forEach(rr => parts.push('recremind:' + rr.ruleId + ':' + rr.occKey + ':' + rr.bucket));
    return parts.sort().join('|');
  }, [shownReconAlerts, smsReconAlerts, smsPending, notifStatements, notifTidy, budgetAlerts, moreNotifs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Loading your finances...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'accounts', label: 'Accounts', icon: Wallet },
    { id: 'assets', label: 'Assets', icon: Home },
    { id: 'investments', label: 'Investments', icon: TrendingUp },
    { id: 'cards', label: 'Cards', icon: CreditCard },
    { id: 'loans', label: 'Debt', icon: Landmark },
    { id: 'recurring', label: 'Recurring', icon: Repeat },
    { id: 'budgets', label: 'Budgets', icon: Target },
    { id: 'savings', label: 'Savings', icon: Target },
    { id: 'transactions', label: 'Transactions', icon: Receipt },
    { id: 'settings', label: 'Settings', icon: Edit2 },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Finance</h1>
              <p className="text-xs text-slate-400">Base: {baseCurrency}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button onClick={exportData} aria-label="Export all data as JSON" className="px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center gap-2 transition" title="Export all data as JSON">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={() => setShowClearConfirm(true)} aria-label="Clear all data" className="px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-rose-900/50 flex items-center gap-2 transition" title="Clear all data">
              <Trash2 className="w-4 h-4 text-rose-400" />
            </button>
            <button onClick={() => setShowImportModal(true)} className="px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center gap-2 transition">
              <Upload className="w-4 h-4" /> Import
            </button>
            <button onClick={() => { setEditingTx(null); setShowTxModal(true); }} className="px-3 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 flex items-center gap-2 transition">
              <Plus className="w-4 h-4" /> Add
            </button>
            {(() => {
              // Count only items still ACTIVE (mirrors the panel): action items
              // always count; view-only items stop counting once they've moved to
              // Recent (their key is in tidySeen). So the badge matches what's
              // actually shown, not the cumulative total including Recent.
              const tidyAll = buildTidyItems({ ...notifTidy, budgetAlerts, ...moreNotifs }, baseCurrency);
              const activeCount = smsPending.length
                + shownReconAlerts.length
                + notifStatements.filter(s => !tidySeen.has(stmtKey(s))).length
                + tidyAll.filter(it => !isMovableTidy(it) || !tidySeen.has(it.key)).length;
              const unread = activeCount > 0 && notifSignature !== lastSeenNotif;
              const markSeen = () => {
                setLastSeenNotif(notifSignature);
                try { localStorage.setItem('floosi_notif_seen', notifSignature); } catch { /* ignore */ }
              };
              return (
                <button
                  onClick={() => { setShowNotifications(true); markSeen(); }}
                  aria-label={`${activeCount} notification${activeCount === 1 ? '' : 's'}`}
                  className="relative px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center gap-2 transition"
                  title={`${activeCount} notification${activeCount === 1 ? '' : 's'}`}
                >
                  <Bell className="w-4 h-4" />
                  {unread && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {activeCount}
                    </span>
                  )}
                </button>
              );
            })()}
            <button
              onClick={signOut}
              className="px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center gap-2 transition"
              title={session?.user?.email ? `Sign out (${session.user.email})` : 'Sign out'}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto no-scrollbar">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-3 py-2.5 text-sm flex items-center gap-2 border-b-2 transition whitespace-nowrap ${
                  activeTab === t.id
                    ? 'border-emerald-500 text-emerald-400'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'dashboard' && <Dashboard transactions={budgetTx} accounts={accounts} investments={investments} recurring={recurring} cards={cards} loans={loansWithLive} assets={assets} budgets={effectiveBudgets} cardInstallments={cardInstallments} netWorthHistory={netWorthHistory} cardStatements={cardStatements} goals={savingsGoals} allocations={savingsGoalAllocations} baseCurrency={baseCurrency} />}
        {activeTab === 'calendar' && <CashCalendar accounts={accounts} transactions={budgetTx} recurring={recurring} budgets={effectiveBudgets} cards={cards} loans={loansWithLive} cardInstallments={cardInstallments} cardStatements={cardStatements} investments={investments} assets={assets} netWorthHistory={netWorthHistory} baseCurrency={baseCurrency} />}
        {activeTab === 'transactions' && <Transactions transactions={transactions} accounts={accounts} cards={cards} loans={loans} categories={categories} onEdit={tx => { setEditingTx(tx); setShowTxModal(true); }} onDelete={deleteTransaction} onMarkIntentional={markDuplicatesIntentional} onToggleFlag={toggleTxFlag} onConvertToEpp={tx => setConvertEppTx(tx)} reimbursementSettlements={reimbursementSettlements} onSaveSettlement={saveReimbursementSettlement} onDeleteSettlement={deleteReimbursementSettlement} baseCurrency={baseCurrency} initialFilter={txFilter} onClearFilter={() => setTxFilter(null)} />}
        {activeTab === 'settings' && (
          <Settings
            categories={categories}
            smsTokens={smsTokens}
            timezone={timezone}
            onSaveTimezone={saveTimezone}
            onCreateSmsToken={createSmsToken}
            onRevokeSmsToken={revokeSmsToken}
            onSaveCategory={saveCategory}
            onDeleteCategory={deleteCategory}
            onRefreshFx={async () => {
              const fx = await fetchAllByPrefix('fx:');
              const live = fx.filter((x) => !x.deletedAt);
              setLiveFxRates(live);
              setExchangeRates(live);
              toast(`Refreshed FX rates: ${live.length} currencies.`);
            }}
          />
        )}
        {activeTab === 'recurring' && <Recurring recurring={recurring} transactions={transactions} accounts={accounts} cards={cards} categories={categories} onSave={saveRecurring} onDelete={deleteRecurring} onFireNow={fireRecurringNow} showModal={showRecurringModal} setShowModal={setShowRecurringModal} baseCurrency={baseCurrency} highlightIds={highlightIds} onClearHighlight={() => setHighlightIds([])} />}
        {activeTab === 'accounts' && <Accounts accounts={accounts} transactions={transactions} onSave={saveAccount} onDelete={deleteAccount} showModal={showAccountModal} setShowModal={setShowAccountModal} baseCurrency={baseCurrency} />}
        {activeTab === 'cards' && <Cards cards={cards} transactions={transactions} cardInstallments={cardInstallments} accounts={accounts} categories={categories} cardStatements={cardStatements} onSave={saveCard} onDelete={deleteCard} onSaveStatement={saveCardStatement} onDeleteStatement={deleteCardStatement} onImportTransactions={saveTransactions} onDeleteTransaction={deleteTransaction} showModal={showCardModal} setShowModal={setShowCardModal} baseCurrency={baseCurrency} />}
        {activeTab === 'loans' && <Loans loans={loansWithLive} transactions={transactions} accounts={accounts} cards={cards} cardInstallments={cardInstallments} recurring={recurring} categories={categories} onSave={saveLoan} onDelete={deleteLoan} onSaveAsset={saveAsset} onSaveInstallment={saveCardInstallment} onDeleteInstallment={deleteCardInstallment} showModal={showLoanModal} setShowModal={setShowLoanModal} baseCurrency={baseCurrency} />}
        {activeTab === 'assets' && <Assets assets={assets} loans={loansWithLive} onSave={saveAsset} onDelete={deleteAsset} showModal={showAssetModal} setShowModal={setShowAssetModal} baseCurrency={baseCurrency} />}
        {activeTab === 'budgets' && <Budgets
          legacyBudgets={budgets}
          transactions={budgetTx}
          categories={categories}
          templateLines={budgetTemplateLines}
          overrides={budgetOverrides}
          currentMonth={budgetCurrentMonth}
          onCommitTemplate={commitTemplateLine}
          onDeleteTemplate={deleteTemplateLine}
          onSaveOverride={saveBudgetOverride}
          onReleaseOverrides={releaseBudgetOverrides}
          onReleaseMonth={releaseBudgetMonth}
          baseCurrency={baseCurrency}
        />}
        {activeTab === 'savings' && (
          <SavingsGoals
            goals={savingsGoals}
            allocations={savingsGoalAllocations}
            accounts={accounts}
            investments={investments}
            transactions={transactions}
            recurring={recurring}
            budgets={effectiveBudgets}
            onSaveGoal={saveSavingsGoal}
            onDeleteGoal={deleteSavingsGoal}
            onSaveAllocation={saveSavingsGoalAllocation}
            onDeleteAllocation={deleteSavingsGoalAllocation}
            baseCurrency={baseCurrency}
          />
        )}
        {activeTab === 'investments' && <Investments investments={investments} accounts={accounts} cards={cards} onSave={saveInvestment} onDelete={deleteInvestment} onBuy={buyHolding} onSell={sellHolding} showModal={showInvestmentModal} setShowModal={setShowInvestmentModal} baseCurrency={baseCurrency} />}
      </main>

      {showTxModal && (
        <TransactionModal
          tx={editingTx}
          accounts={accounts}
          cards={cards}
          categories={categories}
          loans={loansWithLive}
          onSave={saveTransaction}
          onClose={() => { setShowTxModal(false); setEditingTx(null); }}
        />
      )}
      {convertEppTx && (
        <CardInstallmentModal
          prefill={{
            name: convertEppTx.description || 'Installment plan',
            originalAmount: convertEppTx.amount,
            currency: convertEppTx.currency || 'AED',
            cardId: convertEppTx.cardId,
            kind: 'easy_payment_plan',
            notes: `Converted from transaction "${convertEppTx.description || 'charge'}" dated ${convertEppTx.date}.`,
          }}
          cards={cards}
          accounts={accounts}
          categories={categories}
          onSave={async (ci, autoCreateRecurring, recurringCategoryName, backfillFromStart) => {
            await saveCardInstallment(ci, autoCreateRecurring, recurringCategoryName, backfillFromStart);
            // The charge now lives as an installment — drop the original tx so it
            // isn't double-counted against the card balance.
            await deleteTransaction(convertEppTx.id);
          }}
          onClose={() => setConvertEppTx(null)}
        />
      )}
      {showNotifications && (
        <NotificationPanel
          alerts={[...smsReconAlerts.filter(a => a.resolvedAt), ...shownReconAlerts]}
          pending={smsPending}
          cards={cards}
          transactions={transactions}
          tidy={{ ...notifTidy, budgetAlerts, ...moreNotifs }}
          statements={notifStatements}
          onNavigate={(tab, opts?: { highlightIds?: string[]; filter?: { source?: string; cat?: string; card?: string; reimb?: string; showFilters?: boolean } }) => {
            setActiveTab(tab);
            setHighlightIds(opts?.highlightIds || []);
            // Tolerate callers that pass filter fields at the top level of opts
            // (not nested under `filter`) so a missing wrapper can't silently drop the filter.
            const f = opts?.filter || (opts && (opts.source || opts.cat || opts.card || opts.reimb) ? opts : null);
            setTxFilter(f);
            setShowNotifications(false);
          }}
          onAddTransaction={() => { setEditingTx(null); setShowTxModal(true); setShowNotifications(false); }}
          onMarkDuplicatesIntentional={markDuplicatesIntentional}
          onMarkRecReminderHandled={markRecReminderHandled}
          onSeenUpdate={(keys) => setTidySeen(new Set(keys))}
          onCompletePending={(raw) => { setCompletingSms(raw); setShowNotifications(false); }}
          onClose={() => setShowNotifications(false)}
          onResolve={async (id) => {
            const existing = smsReconAlerts.find(a => a.id === id);
            if (!existing) return;
            const resolvedAt = new Date().toISOString();
            const note = `Dismissed on ${resolvedAt.slice(0, 10)}`;
            const merged = { ...existing, resolvedAt, resolutionNote: note };
            await dataStore.set(`sra:${id}`, merged);
            // Keep the alert in state so it shows in the panel's history.
            setSmsReconAlerts(prev => prev.map(a => a.id === id ? merged : a));
          }}
          onApplyMarkup={async (alertId, markupPct, txIds) => {
            const factor = 1 + markupPct / 100;
            const updated: any[] = [];
            for (const txId of txIds) {
              const tx = transactions.find(t => t.id === txId);
              if (!tx) continue;
              const baseAmt = Number(tx.amount) || 0;
              const baseBase = Number(tx.amountBase ?? tx.amount) || 0;
              const merged = {
                ...tx,
                amount: Math.round(baseAmt * factor * 100) / 100,
                amountBase: Math.round(baseBase * factor * 100) / 100,
                fxMarkupPctApplied: markupPct,
              };
              await dataStore.set(`tx:${txId}`, merged);
              updated.push(merged);
            }
            setTransactions(prev => prev.map(t => {
              const u = updated.find(x => x.id === t.id);
              return u || t;
            }));
            const existing = smsReconAlerts.find(a => a.id === alertId);
            if (existing) {
              const resolvedAt = new Date().toISOString();
              const note = `Applied ${markupPct.toFixed(2)}% FX markup to ${txIds.length} tx${txIds.length === 1 ? '' : 's'} on ${resolvedAt.slice(0, 10)}`;
              const mergedAlert = { ...existing, resolvedAt, resolutionNote: note };
              await dataStore.set(`sra:${alertId}`, mergedAlert);
              setSmsReconAlerts(prev => prev.map(a => a.id === alertId ? mergedAlert : a));
            }
          }}
          baseCurrency={baseCurrency}
        />
      )}
      {completingSms && (
        <CompleteSmsModal
          raw={completingSms}
          cards={cards}
          accounts={accounts}
          categories={categories}
          loans={loansWithLive}
          onClose={() => setCompletingSms(null)}
          onSave={async (filled) => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const aedAmt = parseFloat(filled.amountAed);
            const parse = (completingSms.parseResult || {}) as any;
            const commonNotes = filled.notes?.trim() || `From SMS${parse.refNo ? ` · ref ${parse.refNo}` : ''}`;
            const base = {
              user_id: user.id,
              date: filled.date,
              description: filled.description.trim(),
              amount: aedAmt,
              currency_code: parse.ccy || 'AED',
              amount_base: aedAmt,
              fx_rate: 1,
              sms_raw_id: completingSms.id,
              sms_needs_review: false,
              ref_no: parse.refNo || null,
              notes: commonNotes,
            };
            const insertPayload: any = filled.txType === 'income' ? {
              ...base, type: 'income', account_id: filled.toAccountId, category_id: filled.categoryId,
            } : filled.txType === 'expense' ? {
              // Money out to a party we don't track → an expense from the source account.
              ...base, type: 'expense', account_id: filled.fromAccountId, category_id: filled.categoryId,
            } : {
              ...base, type: 'transfer',
              from_account_id: filled.fromAccountId || null,
              from_card_id: filled.fromCardId || null,
              to_account_id: filled.toAccountId || null,
              to_card_id: filled.toCardId || null,
              to_loan_id: filled.toLoanId || null,
            };
            await supabase.from('transactions').insert(insertPayload);
            await supabase.from('sms_ingest_raw').update({
              pending_completion: false,
              parsed_at: new Date().toISOString(),
            }).eq('id', completingSms.id);
            setSmsPending(prev => prev.filter(p => p.id !== completingSms.id));
            const freshTx = await fetchAllByPrefix('tx:');
            setTransactions(freshTx.filter((t) => !t.deletedAt).sort((a, b) => cmpDateStr(b.date, a.date)));
            setCompletingSms(null);
          }}
          onDismiss={async () => {
            await supabase.from('sms_ingest_raw').update({ pending_completion: false }).eq('id', completingSms.id);
            setSmsPending(prev => prev.filter(p => p.id !== completingSms.id));
            setCompletingSms(null);
          }}
        />
      )}
      {showImportModal && <ImportModal onImport={saveTransactions} smsTokens={smsTokens} cards={cards} accounts={accounts} categories={categories} loans={loansWithLive} transactions={transactions} onReloadTxs={async () => {
        const [freshTx, freshAlerts, freshCards] = await Promise.all([
          fetchAllByPrefix('tx:'),
          fetchAllByPrefix('sra:'),
          fetchAllByPrefix('card:'),
        ]);
        setTransactions(freshTx.filter((t) => !t.deletedAt).sort((a, b) => cmpDateStr(b.date, a.date)));
        setSmsReconAlerts(freshAlerts);
        setCards(freshCards.filter((c) => !c.deletedAt));
      }} onClose={() => setShowImportModal(false)} />}
      {showClearConfirm && (
        <Modal title="Clear All Data" onClose={() => { setShowClearConfirm(false); setDemoError(null); }}>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30">
              <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-200">
                This will permanently delete <strong>everything</strong>: all transactions, accounts, cards, loans, budgets, recurring rules, and investments.
                <div className="text-xs text-slate-400 mt-2">This cannot be undone. To keep a backup first, click Cancel and use the Export button.</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setShowClearConfirm(false); setDemoError(null); }} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
              <button onClick={clearAllData} className="flex-1 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-medium">Clear Everything</button>
            </div>
            <div className="pt-3 border-t border-slate-800">
              <div className="text-xs text-slate-400 mb-2">
                Or replace everything with a realistic demo dataset — useful for trying out the app or resetting after testing.
              </div>
              <button onClick={loadDemoData} className="w-full py-2 rounded-lg bg-cyan-600/80 hover:bg-cyan-500 text-white text-sm font-medium">
                Load Demo Data (overwrites everything)
              </button>
            </div>
            {demoError && (
              <div className="mt-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-rose-300 mb-1">Error</div>
                    <div className="text-xs text-slate-200 font-mono break-words whitespace-pre-wrap">{demoError}</div>
                    <button
                      onClick={() => { try { navigator.clipboard.writeText(demoError); } catch {} }}
                      className="mt-2 text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ DASHBOARD ============

// Net Worth Trail range windows. Each tab reveals only once the data span is
// long enough that it would differ from the next-shorter window (no point
// offering "1Y" when you have two weeks of data). Snapshots are one-per-day, so
// short windows plot daily points and longer ones bucket to the weekly/monthly
// *closing* value so the line stays readable and fits without scrolling.
const NW_RANGES = [
  { key: '1W', label: '1W', days: 7,    revealAfter: 8,    bucket: 'day'   },
  { key: '1M', label: '1M', days: 31,   revealAfter: 8,    bucket: 'day'   },
  { key: '1Y', label: '1Y', days: 366,  revealAfter: 32,   bucket: 'week'  },
  { key: '3Y', label: '3Y', days: 1096, revealAfter: 366,  bucket: 'month' },
  { key: '5Y', label: '5Y', days: 1826, revealAfter: 1096, bucket: 'month' },
];
const BUCKET_WORD = { day: 'daily', week: 'weekly', month: 'monthly' };

const nwBucketKey = (d, bucket) => {
  if (bucket === 'month') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  if (bucket === 'week') {
    const onejan = new Date(d.getFullYear(), 0, 1);
    const week = Math.floor(((d - onejan) / 86400000 + onejan.getDay()) / 7);
    return `${d.getFullYear()}-w${week}`;
  }
  return dateToStr(d);
};

// Window history to the selected range and keep the last (closing) snapshot per
// bucket. Assumes history is sorted ascending by snapshotDate.
const buildNetWorthTrail = (history, range) => {
  if (!history?.length) return [];
  const last = strToDate(history[history.length - 1].snapshotDate);
  const cutoff = new Date(last);
  if (isFinite(range.days)) cutoff.setDate(cutoff.getDate() - range.days);
  else cutoff.setFullYear(cutoff.getFullYear() - 100);
  const byBucket = new Map();
  for (const s of history) {
    const d = strToDate(s.snapshotDate);
    if (d < cutoff) continue;
    byBucket.set(nwBucketKey(d, range.bucket), s);
  }
  return [...byBucket.values()].map((s) => {
    const d = strToDate(s.snapshotDate);
    const label = range.bucket === 'month'
      ? d.toLocaleDateString('en', { month: 'short', year: '2-digit' })
      : (s.snapshotDate?.slice(5) ?? '');
    return { date: label, netWorth: Number(s.netWorth) || 0 };
  });
};

// ============ Dashboard UI primitives ============
// One definition each so every section shares identical styling.
function SectionHeader({ title, action }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-500">{title}</h2>
      {action || null}
    </div>
  );
}
// Slim horizontal progress bar with an optional vertical "pace" marker.
function ProgressBar({ pct, color = 'bg-cyan-500', height = 'h-2', marker }) {
  const w = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className={`relative ${height} bg-slate-800 rounded-full overflow-hidden`}>
      <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${w}%` }} />
      {marker != null && Number.isFinite(marker) && (
        <div className="absolute top-0 bottom-0 w-0.5 bg-slate-200/80" style={{ left: `${Math.max(0, Math.min(100, marker))}%` }} title="Today / month pace" />
      )}
    </div>
  );
}
// Tiny inline SVG sparkline (no recharts overhead) for KPI trend.
function Sparkline({ data, color = '#10b981', width = 110, height = 30 }) {
  const vals = (data || []).map(d => Number(d) || 0);
  if (vals.length < 2) return null;
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = (max - min) || 1;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// Shared list row for the Recent Transactions + Upcoming dashboard widgets — a
// SINGLE definition so the two are guaranteed identical in style and size (no
// drift between two near-copies). The shell (card, icon box, two-line layout,
// right-aligned amount) is fixed here; callers pass pre-shaped content.
function DashRow({ icon, title, badge, subtitleIcon, subtitle, amount, amountPositive, amountSub }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-700">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate flex items-center gap-1.5">
            <span className="truncate">{title}</span>
            {badge}
          </div>
          <div className="text-xs text-slate-400 flex items-center gap-1 min-w-0">
            {subtitleIcon}
            <span className="truncate">{subtitle}</span>
          </div>
        </div>
      </div>
      <div className="text-right flex-shrink-0 pl-2">
        <div className={`text-sm font-semibold ${amountPositive ? 'text-emerald-400' : ''}`}>{amount}</div>
        {amountSub ? <div className="text-xs text-slate-500 truncate max-w-[140px]">{amountSub}</div> : null}
      </div>
    </div>
  );
}

function Dashboard({ transactions, accounts, investments, recurring, cards, loans, assets, budgets, cardInstallments = [], netWorthHistory = [], cardStatements = [], goals = [], allocations = [], baseCurrency }) {
  const [nwRangeKey, setNwRangeKey] = useState(null);
  const nwSpanDays = netWorthHistory.length >= 2
    ? Math.round((strToDate(netWorthHistory[netWorthHistory.length - 1].snapshotDate) - strToDate(netWorthHistory[0].snapshotDate)) / 86400000)
    : 0;
  const nwRanges = NW_RANGES.filter((r) => nwSpanDays >= r.revealAfter);
  // No tab qualifies yet (< ~a week of data) → show everything, daily.
  const nwActiveRange = nwRanges.find((r) => r.key === nwRangeKey)
    || nwRanges[nwRanges.length - 1]
    || { key: 'ALL', days: Infinity, bucket: 'day' };
  // Stored snapshots freeze each day's net worth, so editing/deleting/redating a
  // PAST transaction would otherwise never correct that day on the trail. Refresh
  // only the transaction-driven slice (cash − card debt) of each snapshot from the
  // CURRENT transactions as-of that day, keeping the market-priced parts
  // (investments/assets/loans/installments) from the stored breakdown as real
  // history. Result: the trail reacts to any tx edit, no server round-trip, and
  // can't miss an edit path because it's derived. Snapshots without a breakdown
  // (pre-breakdown rows) fall back to their stored value untouched.
  const correctedNwHistory = useMemo(() => {
    return (netWorthHistory || []).map((s) => {
      const bd = s.breakdown;
      if (!bd || bd.accounts == null || bd.cards == null) return s;
      const storedTxDriven = (Number(bd.accounts) || 0) - (Number(bd.cards) || 0);
      const liveTxDriven = txDrivenNetWorthBaseAsOf(accounts, cards, transactions, s.snapshotDate);
      // Loans are a LIABILITY whose stored value is only refreshed lazily (when the
      // app processes the EMI rule), so the snapshot lags the real paydown by up to
      // a month. Swap it for the amortized balance AS OF this day, which steps down
      // on each payment date with no lag. Guard on a stored loan existing so we
      // don't retroactively add a loan to days before it was tracked.
      const storedLoans = Number(bd.loans) || 0;
      let loanAdj = 0;
      if (storedLoans > 0) {
        const liveLoans = (loans || []).reduce((sum, l) => {
          if (l.deletedAt) return sum;
          const n = paymentsElapsed(l.startDate, l.paymentDay, s.snapshotDate);
          const bal = amortizedBalance(l.originalPrincipal, l.interestRate || 0, l.termMonths || 1, n);
          return sum + toBase(bal, l.currency || baseCurrency);
        }, 0);
        loanAdj = storedLoans - liveLoans; // remove frozen liability, add as-of-day one
      }
      return { ...s, netWorth: (Number(s.netWorth) || 0) - storedTxDriven + liveTxDriven + loanAdj };
    });
  }, [netWorthHistory, accounts, cards, transactions, loans, baseCurrency]);
  const nwTrail = useMemo(
    () => buildNetWorthTrail(correctedNwHistory, nwActiveRange),
    [correctedNwHistory, nwActiveRange.key],
  );
  const today = todayStr();
  const thisMonth = today.slice(0, 7);
  const d = new Date();
  const lm = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const lastMonthStr = `${lm.getFullYear()}-${String(lm.getMonth() + 1).padStart(2, '0')}`;
  const [reportPeriod, setReportPeriod] = useState('6m');

  const stats = useMemo(() => {
    // Future-dated transactions don't count toward "current state" rollups —
    // they only feed the forecast. Same convention as compute*LiveBalance.
    const monthTx = transactions.filter(t => t.date.startsWith(thisMonth) && t.date <= today);
    const lastMonthTx = transactions.filter(t => t.date.startsWith(lastMonthStr) && t.date <= today);

    // Income & expense totals — refunds count as negative expenses, transfers don't count
    const income = monthTx.filter(t => t.type === 'income').reduce((s, t) => s + toBase(t.amount, t.currency), 0);
    const expense = monthTx.filter(t => t.type === 'expense').reduce((s, t) => s + (t.isRefund ? -toBase(t.amount, t.currency) : toBase(t.amount, t.currency)), 0);
    const lastExpense = lastMonthTx.filter(t => t.type === 'expense').reduce((s, t) => s + (t.isRefund ? -toBase(t.amount, t.currency) : toBase(t.amount, t.currency)), 0);

    // Account balances incl. transactions
    const accountsTotal = accounts.reduce((s, a) => {
      const live = computeAccountLiveBalance(a, transactions);
      const val = toBase(live, a.currency);
      return s + val;
    }, 0);

    const investmentsTotal = investments.reduce((s, i) => s + toBase((i.shares || 0) * (i.currentPrice || 0), i.currency), 0);

    // Assets — apply simple yearly appreciation/depreciation to get current value
    const assetsTotal = (assets || []).reduce((s, a) =>
      s + toBase(getAssetCurrentValue(a), a.currency || 'AED'), 0);

    // Card debt incl. transactions
    const cardsDebt = cards.reduce((s, c) => {
      const live = computeCardLiveBalance(c, transactions, cardInstallments);
      return s + toBase(live, c.currency);
    }, 0);

    const loansDebt = loans.reduce((s, l) => s + toBase((l.liveBalance ?? l.currentBalance) || 0, l.currency), 0);
    const totalDebt = cardsDebt + loansDebt;
    return {
      income, expense, net: income - expense, lastExpense,
      expenseChange: lastExpense > 0 ? ((expense - lastExpense) / lastExpense) * 100 : 0,
      netWorth: accountsTotal + investmentsTotal + assetsTotal - totalDebt,
      accountsTotal, investmentsTotal, assetsTotal, cardsDebt, loansDebt, totalDebt,
    };
  }, [transactions, accounts, investments, assets, cards, loans, cardInstallments, thisMonth, lastMonthStr]);

  const trendData = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const label = dt.toLocaleDateString('en', { month: 'short' });
      const monthTx = transactions.filter(t => t.date.startsWith(key) && t.date <= today);
      const inc = monthTx.filter(t => t.type === 'income').reduce((s, t) => s + toBase(t.amount, t.currency), 0);
      const exp = monthTx.filter(t => t.type === 'expense').reduce((s, t) => s + (t.isRefund ? -toBase(t.amount, t.currency) : toBase(t.amount, t.currency)), 0);
      months.push({ month: label, income: Math.round(inc), expense: Math.round(Math.max(0, exp)) });
    }
    return months;
  }, [transactions]);

  // Recent = already happened. Future-dated txs (e.g. a scheduled bonus) belong
  // in Upcoming, not here, so exclude anything dated after today. Show 6 to fill
  // the box to the same height as Upcoming.
  const recentTx = transactions.filter(t => t.date <= today).slice(0, 6);

  // Reports data — depends on selected period
  const { reportTrend, reportCategories } = useMemo(() => {
    const now = new Date();
    let months = 6;
    if (reportPeriod === '3m') months = 3;
    if (reportPeriod === '12m') months = 12;
    if (reportPeriod === 'ytd') months = now.getMonth() + 1;

    const trend = [];
    for (let i = months - 1; i >= 0; i--) {
      const dd = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}`;
      const label = dd.toLocaleDateString('en', { month: 'short', year: '2-digit' });
      const monthTx = transactions.filter(t => t.date.startsWith(key) && t.date <= today);
      const inc = monthTx.filter(t => t.type === 'income').reduce((s, t) => s + toBase(t.amount, t.currency), 0);
      const exp = monthTx.filter(t => t.type === 'expense').reduce((s, t) => s + (t.isRefund ? -toBase(t.amount, t.currency) : toBase(t.amount, t.currency)), 0);
      const expClamp = Math.max(0, exp);
      trend.push({ month: label, Income: Math.round(inc), Expense: Math.round(expClamp), Savings: Math.round(inc - expClamp) });
    }

    const cutoff = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
    const cutoffStr = dateToStr(cutoff);
    const tx = transactions.filter(t => t.type === 'expense' && t.date >= cutoffStr && t.date <= today);
    const byCat = {};
    tx.forEach(t => {
      const amt = t.isRefund ? -toBase(t.amount, t.currency) : toBase(t.amount, t.currency);
      byCat[t.category] = (byCat[t.category] || 0) + amt;
    });
    const breakdown = Object.entries(byCat)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => {
        return { name, value: Math.round(value), color: '#64748b' };
      })
      .sort((a, b) => b.value - a.value);

    return { reportTrend: trend, reportCategories: breakdown };
  }, [transactions, reportPeriod]);

  const totalSpentReport = reportCategories.reduce((s, c) => s + c.value, 0);

  // Upcoming
  const upcoming = useMemo(() => {
    const items = [];
    const todayDate = strToDate(today);
    const horizon = new Date(todayDate);
    horizon.setDate(horizon.getDate() + 30);
    const horizonStr = dateToStr(horizon);

    recurring.filter(r => r.active).forEach(r => {
      const occs = generateOccurrences(r, today, horizonStr);
      if (occs.length > 0) {
        const next = occs[0];
        const days = Math.ceil((next - todayDate) / (MS_PER_DAY));
        items.push({
          id: 'rec-' + r.id, kind: 'recurring',
          name: r.description, amount: r.amount, currency: r.currency,
          days, frequency: describeRule(r), type: r.type, category: r.category,
        });
      }
    });

    cards.forEach(c => {
      const next = nextCardPaymentDate(c);
      if (!next) return;
      const days = Math.ceil((next - todayDate) / (MS_PER_DAY));
      if (days < 0 || days > 30) return;
      items.push({
        id: 'card-' + c.id, kind: 'card_payment',
        name: `${c.name} payment`, amount: c.statementBalance || c.currentBalance || 0,
        currency: c.currency, days, frequency: 'Card payment due', type: 'expense',
      });
    });

    // Future-dated one-off transactions (not recurring — those are handled above)
    // that fall within the horizon, e.g. a scheduled bonus or planned purchase.
    transactions.filter(t => !t.deletedAt && !t.recurringId
      && (t.type === 'income' || t.type === 'expense')
      && t.date > today && t.date <= horizonStr).forEach(t => {
      const days = Math.ceil((strToDate(t.date) - todayDate) / (MS_PER_DAY));
      items.push({
        id: 'tx-' + t.id, kind: 'planned',
        name: t.description, amount: t.amount, currency: t.currency,
        days, frequency: 'Scheduled', type: t.type, category: t.category,
      });
    });

    return items.sort((a, b) => a.days - b.days).slice(0, 6);
  }, [recurring, cards, transactions, today]);

  // ============ FORECAST ============
  // ============ CASH ON HAND + RUNWAY ============
  // Cash = sum of live account balances in base ccy. Strips cards, installments,
  // assets, investments — only what's spendable RIGHT NOW.
  const cashOnHand = useMemo(() => computeCashOnHand(accounts, transactions), [accounts, transactions]);

  // Runway has two modes:
  //   - 'budget'  → forward-looking burn = avg of next-6-month projected
  //                 expense from the budgets-only forecast (with smart
  //                 estimate fallback for unbudgeted months). Aligned with
  //                 the cash forecast and the user's planned spending.
  //   - 'history' → trailing burn = avg of actual expense (non-refund)
  //                 across the last 6 calendar months. Lagging but
  //                 reflects what you've actually been doing.
  // Both return null if there's nothing to compute against.
  const runway = useMemo(() => {
    const now = new Date();

    // Historical burn from past 6 months of real expense txs.
    const histMonthly = [];
    for (let i = 1; i <= 6; i++) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const sum = transactions
        .filter(t => !t.deletedAt && t.type === 'expense' && !t.isRefund && t.date?.startsWith(key))
        .reduce((s, t) => s + toBase(t.amount, t.currency), 0);
      if (sum > 0) histMonthly.push(sum);
    }
    const histAvg = histMonthly.length > 0
      ? histMonthly.reduce((a, b) => a + b, 0) / histMonthly.length : 0;

    // Forward-looking burn: average of next 6 months of projected expense
    // using the same budget→smart-estimate path the forecast uses.
    const budgetMonths = [...new Set(
      budgets.filter(b => !b.deletedAt && b.month).map(b => b.month),
    )].sort();
    const baselineMonthFor = (target) => {
      let best = null;
      for (const m of budgetMonths) {
        if (m < target) best = m;
      }
      return best;
    };
    const isLumpy = (r) => {
      const interval = Math.max(1, Number(r.interval) || 1);
      if (r.unit === 'year') return true;
      if (r.unit === 'month' && interval > 1) return true;
      return false;
    };
    const projMonthly = [];
    for (let offset = 1; offset <= 6; offset++) {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      const yr = d.getFullYear(), mo = d.getMonth();
      const mStr = `${yr}-${String(mo + 1).padStart(2, '0')}`;
      const mStart = `${mStr}-01`;
      const mEnd = `${mStr}-${String(lastDayOfMonth(yr, mo)).padStart(2, '0')}`;
      const monthBudgets = budgets.filter(b => b.month === mStr && !b.deletedAt);
      let expense = 0;
      if (monthBudgets.length > 0) {
        expense = monthBudgets.reduce(
          (s, b) => s + toBase(Number(b.amount) || 0, baseCurrency), 0,
        );
      } else {
        const baselineMonth = baselineMonthFor(mStr);
        if (baselineMonth) {
          const baselineBudgets = budgets.filter(b => b.month === baselineMonth && !b.deletedAt);
          const baselineCats = new Set(baselineBudgets.map(b => b.category));
          expense += baselineBudgets.reduce(
            (s, b) => s + toBase(Number(b.amount) || 0, baseCurrency), 0,
          );
          recurring.filter(r =>
            r.active && !r.deletedAt && r.type === 'expense'
            && isLumpy(r) && !baselineCats.has(r.category)
          ).forEach(r => {
            const occs = generateOccurrences(r, mStart, mEnd);
            if (occs.length > 0) {
              expense += toBase((Number(r.amount) || 0) * occs.length, r.currency);
            }
          });
          transactions.filter(t =>
            !t.deletedAt && !t.excludesFromBalances && !t.recurringId
            && t.type === 'expense' && !t.isRefund
            && t.date >= mStart && t.date <= mEnd
            && !baselineCats.has(t.category)
          ).forEach(t => {
            expense += toBase(Number(t.amount) || 0, t.currency);
          });
        }
      }
      if (expense > 0) projMonthly.push(expense);
    }
    const projAvg = projMonthly.length > 0
      ? projMonthly.reduce((a, b) => a + b, 0) / projMonthly.length : 0;

    return {
      history: {
        avgMonthlyExpense: histAvg,
        months: histAvg > 0 ? cashOnHand / histAvg : null,
        samples: histMonthly.length,
      },
      budget: {
        avgMonthlyExpense: projAvg,
        months: projAvg > 0 ? cashOnHand / projAvg : null,
        samples: projMonthly.length,
      },
    };
  }, [cashOnHand, transactions, budgets, recurring, baseCurrency]);

  const [runwayMode, setRunwayMode] = useState('budget');
  const runwayActive = runway[runwayMode];

  // Combined forecast: horizon (months ahead) + which lines to show.
  const [forecastHorizon, setForecastHorizon] = useState(6); // 3, 6, 12, 24
  const [showCashLine, setShowCashLine] = useState(true);
  const [showNetWorthLine, setShowNetWorthLine] = useState(true);

  // Cash-on-hand forecast: today's cash + 6 forward months.
  //
  // Expense projection follows BUDGETS ONLY. If a future month has at
  // least one budget row, the projected expense is the sum of those rows
  // — full stop, no recurring / historical-avg / tx fallback. The user
  // is responsible for making the budget reflect everything (recurring
  // subs, planned one-offs, etc.).
  //
  // For unbudgeted future months, a smart estimate fills the gap so the
  // forecast doesn't pretend nothing's being spent:
  //   baseline = sum of the most recent fully-budgeted month's
  //              allocations (last "regular monthly burn" plan)
  //   spikes   = active recurring expense rules that fire in this month
  //              and have sub-monthly cadence (yearly/quarterly/etc.),
  //              skipping categories already in the baseline
  //   planned  = one-off future-dated expense txs in this month,
  //              skipping categories already in the baseline
  //   total    = baseline + spikes + planned
  // Marked with isEstimated=true so the UI can badge it. If no prior
  // budgeted month exists at all, falls back to 0 with a noBaseline
  // warning flag.
  //
  // Income stays deterministic and is NOT subject to budgets:
  // recurring income on accounts + interest-rule MAB approximations +
  // one-off future-dated income txs.
  // Remainder of the CURRENT month: what's still expected to flow between
  // today and month-end, so the first forecast point lands on the projected
  // month-CLOSE rather than a "today" snapshot. Income = recurring rules +
  // future-dated income txs still to come; expense = the unspent portion of
  // this month's budget (or the baseline estimate) plus any lumpy recurring
  // bills due in the remaining days. Live balances already hold the actuals,
  // so we only add what hasn't happened yet.
  const currentMonthTail = useMemo(() => {
    const now = new Date();
    const yr = now.getFullYear(), mo = now.getMonth();
    const mStr = `${yr}-${String(mo + 1).padStart(2, '0')}`;
    const mEnd = `${mStr}-${String(lastDayOfMonth(yr, mo)).padStart(2, '0')}`;
    const tStr = todayStr();
    const afterStr = dateToStr(new Date(yr, mo, now.getDate() + 1)); // first day still ahead
    const isLumpy = (r) => r.unit === 'year' || (r.unit === 'month' && Math.max(1, Number(r.interval) || 1) > 1);

    // INCOME still to come (skip interest rules — they may already have posted).
    let income = 0;
    recurring.filter(r => r.active && !r.deletedAt && r.type === 'income' && r.accountId && !r.isInterestRule).forEach(r => {
      const occs = generateOccurrences(r, afterStr, mEnd);
      if (occs.length > 0) income += toBase((Number(r.amount) || 0) * occs.length, r.currency);
    });
    transactions.filter(t =>
      !t.deletedAt && !t.excludesFromBalances && !t.recurringId
      && t.type === 'income' && t.date > tStr && t.date <= mEnd
    ).forEach(t => { income += toBase(Number(t.amount) || 0, t.currency); });

    // EXPENSE still to come — unspent budget (or baseline) for the month.
    const actualSoFar = transactions.filter(t =>
      !t.deletedAt && !t.excludesFromBalances && t.type === 'expense' && !t.isRefund
      && t.date.startsWith(mStr) && t.date <= tStr
    ).reduce((s, t) => s + toBase(Number(t.amount) || 0, t.currency), 0);

    const monthBudgets = budgets.filter(b => b.month === mStr && !b.deletedAt);
    const budgetCats = new Set();
    let target = 0;
    let hasProjection = false;
    if (monthBudgets.length > 0) {
      monthBudgets.forEach(b => { target += toBase(Number(b.amount) || 0, baseCurrency); budgetCats.add(b.category); });
      hasProjection = true;
    } else {
      const budgetMonths = [...new Set(budgets.filter(b => !b.deletedAt && b.month).map(b => b.month))].sort();
      let baselineMonth = null;
      for (const m of budgetMonths) if (m < mStr) baselineMonth = m;
      if (baselineMonth) {
        budgets.filter(b => b.month === baselineMonth && !b.deletedAt).forEach(b => {
          target += toBase(Number(b.amount) || 0, baseCurrency); budgetCats.add(b.category);
        });
        hasProjection = true;
      }
    }
    let expense = Math.max(0, target - actualSoFar);
    // Lumpy recurring bills (annual/multi-month) due in the remaining days,
    // for categories the budget doesn't already cover.
    recurring.filter(r =>
      r.active && !r.deletedAt && r.type === 'expense' && isLumpy(r) && !budgetCats.has(r.category)
    ).forEach(r => {
      const occs = generateOccurrences(r, afterStr, mEnd);
      if (occs.length > 0) expense += toBase((Number(r.amount) || 0) * occs.length, r.currency);
    });

    return { income, expense, net: income - expense, hasProjection };
  }, [transactions, recurring, budgets, baseCurrency]);

  // Interest-earning accounts (the savings sweep). An account earns interest if
  // it has an interest rule, REGARDLESS of the rule's active/auto-log state —
  // the rule is paused for auto-posting (interest arrives via SMS) but its rate
  // is still economically real, so the forecast reads it unconditionally. We
  // pool all interest accounts into one savings balance with a balance-weighted
  // annual rate, and take the earliest post-day across rules for the
  // already-credited-this-month guard.
  const interestParams = useMemo(() => computeInterestParams(accounts, transactions, recurring), [accounts, transactions, recurring]);

  // CASH forecast — SETTLEMENT basis. Cash only moves when money actually
  // leaves an account: income deposits, direct-from-account spend, loan EMIs,
  // and credit-card STATEMENT PAYMENTS (the float). Card spend itself doesn't
  // touch cash until its statement is paid at payday. Plus compounding interest
  // on the savings sweep. See PROJECT_STATE.md "Forecasting model" for the spec.
  const cashForecast = useMemo(
    () => projectCashSeries({ accounts, transactions, recurring, budgets, cards, cardStatements, baseCurrency, horizon: forecastHorizon, investments, assets, loans, cardInstallments }),
    [accounts, transactions, recurring, budgets, cards, cardStatements, baseCurrency, forecastHorizon, investments, assets, loans, cardInstallments],
  );

  const forecastData = useMemo(() => {
    const now = new Date();
    const months = [];

    const monthKey = (offset) => {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const monthLabel = (offset) => {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return d.toLocaleDateString('en', { month: 'short', year: '2-digit' });
    };

    // Most-recent-budgeted-month lookup for the smart-estimate fallback.
    const budgetMonths = [...new Set(
      budgets.filter(b => !b.deletedAt && b.month).map(b => b.month)
    )].sort();
    const baselineMonthFor = (target) => {
      let best = null;
      for (const m of budgetMonths) {
        if (m < target) best = m;
      }
      return best;
    };
    const isLumpy = (r) => {
      const interval = Math.max(1, Number(r.interval) || 1);
      if (r.unit === 'year') return true;
      if (r.unit === 'month' && interval > 1) return true;
      return false;
    };

    // Current net worth as baseline
    const investmentsNow = investments.reduce((s, i) => s + toBase((i.shares || 0) * (i.currentPrice || 0), i.currency), 0);
    const assetsNow = (assets || []).reduce((s, a) =>
      s + toBase(getAssetCurrentValue(a), a.currency || 'AED'), 0);
    const accountsNow = accounts.reduce((s, a) => {
      const live = computeAccountLiveBalance(a, transactions);
      const val = toBase(live, a.currency);
      return s + val;
    }, 0);
    const cardsNow = cards.reduce((s, c) => {
      const live = computeCardLiveBalance(c, transactions, cardInstallments);
      return s + toBase(live, c.currency);
    }, 0);
    const loansNow = loans.reduce((s, l) => s + toBase((l.liveBalance ?? l.currentBalance) || 0, l.currency), 0);
    let runningNetWorth = accountsNow + investmentsNow + assetsNow - cardsNow - loansNow;
    // Savings sweep for compounding interest (mirrors the cash line). The
    // monthly accrual surplus accumulates in savings and earns interest, which
    // is income that also lifts net worth.
    let nwSavings = interestParams.savingsBal;

    // Pre-compute loan base payments so we can project reduction
    const loanProjections = loans.map(l => ({ loan: l, basePayments: paymentsElapsed(l.startDate, l.paymentDay) }));
    // Track end-of-month loan + installment debt so we can credit the PRINCIPAL
    // portion of each EMI back into net worth (debt paydown is wealth-neutral;
    // only interest is a real expense). Seeded at offset 0 so the current month
    // contributes no add-back.
    let prevLoanDebt = 0, prevInstDebt = 0;

    for (let offset = 0; offset <= forecastHorizon; offset++) {
      const key = monthKey(offset);
      const label = monthLabel(offset);
      const isCurrentMonth = offset === 0;

      // INCOME
      let projectedIncome = 0;
      const incomeBreakdown = [];

      if (isCurrentMonth) {
        const monthTx = transactions.filter(t => t.date.startsWith(key) && t.date <= today && t.type === 'income');
        projectedIncome = monthTx.reduce((s, t) => s + toBase(t.amount, t.currency), 0);
        incomeBreakdown.push({ label: 'Actual this month', amount: projectedIncome, source: 'actual' });
      } else {
        const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        const yr = d.getFullYear(), mo = d.getMonth();
        const mStart = `${yr}-${String(mo+1).padStart(2,'0')}-01`;
        const mEnd = `${yr}-${String(mo+1).padStart(2,'0')}-${String(lastDayOfMonth(yr,mo)).padStart(2,'0')}`;
        const recTotal = recurring.filter(r => r.active && r.type === 'income').reduce((s, r) => {
          const occs = generateOccurrences(r, mStart, mEnd);
          return occs.length > 0 ? s + toBase(r.amount * occs.length, r.currency) : s;
        }, 0);
        const futureIncomeTotal = transactions.filter(t =>
          !t.deletedAt && !t.excludesFromBalances && !t.recurringId
          && t.type === 'income'
          && t.date >= mStart && t.date <= mEnd
        ).reduce((s, t) => s + toBase(Number(t.amount) || 0, t.currency), 0);
        projectedIncome = recTotal + futureIncomeTotal;
        // §6d — learned variable income for months with no explicit future income.
        const learnedVar = learnVariableIncome(transactions, strToDate(today));
        if (learnedVar > 0 && futureIncomeTotal === 0) {
          projectedIncome += learnedVar;
          incomeBreakdown.push({ label: 'Variable income (learned avg)', amount: learnedVar, source: 'learned' });
        }
        if (recTotal > 0) incomeBreakdown.push({ label: 'Recurring income rules', amount: recTotal, source: 'recurring' });
        if (futureIncomeTotal > 0) incomeBreakdown.push({ label: 'Anticipated income (future-dated)', amount: futureIncomeTotal, source: 'planned' });
        if (projectedIncome === 0) incomeBreakdown.push({ label: 'No income projected', amount: 0, source: 'none' });
      }

      // EXPENSES
      let projectedExpense = 0;
      const expenseBreakdown = [];

      if (isCurrentMonth) {
        const monthTx = transactions.filter(t => t.date.startsWith(key) && t.date <= today && t.type === 'expense' && !t.isRefund);
        projectedExpense = monthTx.reduce((s, t) => s + toBase(t.amount, t.currency), 0);
        expenseBreakdown.push({ label: 'Actual spend so far', amount: projectedExpense, source: 'actual' });
      } else {
        const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        const yr = d.getFullYear(), mo = d.getMonth();
        const mStr = `${yr}-${String(mo+1).padStart(2,'0')}`;
        const mStart = `${mStr}-01`;
        const mEnd = `${mStr}-${String(lastDayOfMonth(yr,mo)).padStart(2,'0')}`;

        // Budgets-only model with smart estimate when missing.
        const monthBudgets = budgets.filter(b => b.month === mStr && !b.deletedAt);
        if (monthBudgets.length > 0) {
          monthBudgets.forEach(b => {
            const amt = toBase(b.amount, baseCurrency);
            projectedExpense += amt;
            expenseBreakdown.push({ label: `${b.category}`, amount: amt, source: 'budget' });
          });
        } else {
          const baselineMonth = baselineMonthFor(mStr);
          if (!baselineMonth) {
            expenseBreakdown.push({ label: 'No baseline budget yet — set one to enable projection', amount: 0, source: 'no-baseline' });
          } else {
            const baselineBudgets = budgets.filter(b => b.month === baselineMonth && !b.deletedAt);
            const baselineCats = new Set(baselineBudgets.map(b => b.category));
            const baselineTotal = baselineBudgets.reduce(
              (s, b) => s + toBase(Number(b.amount) || 0, baseCurrency), 0,
            );
            projectedExpense += baselineTotal;
            expenseBreakdown.push({ label: `Baseline (from ${baselineMonth})`, amount: baselineTotal, source: 'baseline' });
            recurring.filter(r =>
              r.active && r.type === 'expense' && isLumpy(r) && !baselineCats.has(r.category)
            ).forEach(r => {
              const occs = generateOccurrences(r, mStart, mEnd);
              if (occs.length === 0) return;
              const amt = toBase((Number(r.amount) || 0) * occs.length, r.currency);
              projectedExpense += amt;
              expenseBreakdown.push({ label: `Spike: ${r.description}`, amount: amt, source: 'spike' });
            });
            transactions.filter(t =>
              !t.deletedAt && !t.excludesFromBalances && !t.recurringId
              && t.type === 'expense' && !t.isRefund
              && t.date >= mStart && t.date <= mEnd
              && !baselineCats.has(t.category)
            ).forEach(t => {
              const amt = toBase(Number(t.amount) || 0, t.currency);
              projectedExpense += amt;
              expenseBreakdown.push({ label: `Planned: ${t.description}`, amount: amt, source: 'planned' });
            });
          }
        }
      }

      // LOAN DEBT at end of this month
      let projectedLoanDebt = 0;
      loanProjections.forEach(lp => {
        const l = lp.loan;
        const totalPayments = lp.basePayments + offset;
        const bal = amortizedBalance(l.originalPrincipal, l.interestRate || 0, l.termMonths || 1, totalPayments);
        projectedLoanDebt += toBase(bal, l.currency);
      });

      // INSTALLMENT DEBT at end of this month (for the principal add-back).
      let projectedInstDebt = 0;
      (cardInstallments || []).forEach(inst => {
        if (inst.deletedAt) return;
        projectedInstDebt += toBase(installmentBalanceAfter(inst, offset), inst.currency || 'AED');
      });

      // Principal repaid this month is net-worth-NEUTRAL (cash down, debt down
      // equally) — only interest is a real expense. projectedExpense already
      // subtracts the full EMI (it sits inside the budget), so add the principal
      // back. offset 0 seeds prev* with no add-back (current month is settling).
      const debtPrincipalPaid = offset === 0
        ? 0
        : Math.max(0, prevLoanDebt - projectedLoanDebt) + Math.max(0, prevInstDebt - projectedInstDebt);
      prevLoanDebt = projectedLoanDebt;
      prevInstDebt = projectedInstDebt;

      // NET WORTH
      let projectedNetWorth;
      const nwNet = isCurrentMonth ? currentMonthTail.net : (projectedIncome - projectedExpense);
      // Interest on the savings balance only (≈ monthly average), not the cash
      // surplus — same correct model as the cash line.
      let nwInterest = 0;
      if (interestParams.active) {
        if (!(isCurrentMonth && today && strToDate(today).getDate() >= interestParams.postDay)) {
          nwInterest = Math.max(0, nwSavings * interestParams.monthlyRate);
        }
        nwSavings = nwSavings + nwInterest;
      }
      if (isCurrentMonth) {
        // Close out the current month: today's net worth plus the cash flows
        // still expected before month-end, so this point is the projected
        // month-CLOSE (matching the cash line) and future months chain off it.
        runningNetWorth = runningNetWorth + currentMonthTail.net + nwInterest + debtPrincipalPaid;
        projectedNetWorth = runningNetWorth;
      } else {
        runningNetWorth = runningNetWorth + (projectedIncome - projectedExpense) + nwInterest + debtPrincipalPaid;
        projectedNetWorth = runningNetWorth;
      }

      // CONFIDENCE
      let confidence, confidenceColor;
      if (isCurrentMonth) {
        confidence = 'Actual'; confidenceColor = 'text-cyan-400';
      } else {
        const nBudgets = budgets.filter(b => b.month === key && !b.deletedAt).length;
        const hasRecIncome = recurring.some(r => r.active && r.type === 'income');
        const hasBaseline = !!baselineMonthFor(key);
        if (nBudgets >= 3 && hasRecIncome) { confidence = 'High'; confidenceColor = 'text-emerald-400'; }
        else if (nBudgets >= 1) { confidence = 'Medium'; confidenceColor = 'text-amber-400'; }
        else if (hasBaseline) { confidence = 'Estimated'; confidenceColor = 'text-orange-400'; }
        else { confidence = 'No budget'; confidenceColor = 'text-rose-400'; }
      }

      months.push({
        label, key, offset, isCurrentMonth,
        income: Math.round(projectedIncome),
        expense: Math.round(projectedExpense),
        netWorth: Math.round(projectedNetWorth),
        loanDebt: Math.round(projectedLoanDebt),
        incomeBreakdown, expenseBreakdown,
        confidence, confidenceColor,
      });
    }

    return months;
  }, [transactions, accounts, investments, assets, cards, loans, recurring, budgets, cardInstallments, baseCurrency, forecastHorizon, currentMonthTail, interestParams]);

  // Combined series for the unified Forecast widget: each entry has both
  // a cash and a net-worth value, aligned month-by-month. forecastData[i]
  // (offset 0..horizon) lines up with cashForecast[i] (Now + offsets
  // 1..horizon), so we read them in lockstep.
  const combinedForecast = useMemo(() => {
    return forecastData.map((m, i) => ({
      label: m.label,
      key: m.key,
      cash: cashForecast[i]?.cash ?? 0,
      // Net worth comes from the same enriched engine as cash (principal
      // add-back, learned split/ratio/income), so the bell and the chart agree.
      // Fall back to forecastData's value if the cash series is short.
      netWorth: cashForecast[i]?.netWorth ?? m.netWorth,
      isCurrentMonth: m.isCurrentMonth,
      confidence: m.confidence,
      confidenceColor: m.confidenceColor,
      cashNet: cashForecast[i]?.net ?? 0,
      cashEstimated: !!cashForecast[i]?.isEstimated,
      cashNoBaseline: !!cashForecast[i]?.noBaseline,
    }));
  }, [forecastData, cashForecast]);

  // ============ Dashboard v2 derived metrics ============
  // Net worth headline: live value, month-over-month delta, 30-pt sparkline.
  const nwSummary = useMemo(() => {
    const hist = (correctedNwHistory || []).filter(s => s && s.snapshotDate && s.netWorth != null)
      .slice().sort((a, b) => String(a.snapshotDate).localeCompare(String(b.snapshotDate)));
    const current = stats.netWorth;
    const cut = new Date(); cut.setDate(cut.getDate() - 30);
    const cutStr = dateToStr(cut);
    let prior = null, priorDate = null;
    for (const s of hist) { if (s.snapshotDate <= cutStr) { prior = Number(s.netWorth) || 0; priorDate = s.snapshotDate; } }
    if (prior == null && hist.length) { prior = Number(hist[0].netWorth) || 0; priorDate = hist[0].snapshotDate; }
    const delta = prior != null ? current - prior : 0;
    const days = priorDate ? Math.max(1, Math.round((strToDate(dateToStr(new Date())).getTime() - strToDate(priorDate).getTime()) / MS_PER_DAY)) : 0;
    const spark = hist.slice(-30).map(s => Number(s.netWorth) || 0);
    if (spark.length) spark[spark.length - 1] = current;
    return { current, delta, pct: prior ? (delta / prior) * 100 : 0, spark, days, hasPrior: prior != null && hist.length > 1 };
  }, [correctedNwHistory, stats.netWorth]);

  // This-month cash flow + savings rate + how far through the month we are.
  const monthFlow = useMemo(() => {
    const now = new Date();
    const daysInMonth = lastDayOfMonth(now.getFullYear(), now.getMonth());
    return {
      income: stats.income, expense: stats.expense, net: stats.net,
      savingsRate: stats.income > 0 ? (stats.net / stats.income) * 100 : 0,
      monthPct: (now.getDate() / daysInMonth) * 100,
    };
  }, [stats]);

  // Budget vs actual for the current month (overall + per category, pace-aware).
  const budgetVsActual = useMemo(() => {
    const mb = budgets.filter(b => (b.month || thisMonth) === thisMonth && !b.deletedAt);
    const byCat = {};
    mb.forEach(b => { byCat[b.category] = { budget: toBase(Number(b.amount) || 0, baseCurrency), spent: 0 }; });
    transactions.filter(t => !t.deletedAt && t.type === 'expense' && t.date.startsWith(thisMonth) && t.date <= today)
      .forEach(t => {
        const a = (t.isRefund ? -1 : 1) * toBase(t.amount, t.currency);
        if (!byCat[t.category]) byCat[t.category] = { budget: 0, spent: 0 };
        byCat[t.category].spent += a;
      });
    const cats = Object.entries(byCat)
      .map(([name, v]) => ({ name, budget: v.budget, spent: Math.max(0, v.spent), pct: v.budget > 0 ? (Math.max(0, v.spent) / v.budget) * 100 : (v.spent > 0 ? 100 : 0), noBudget: v.budget <= 0 }))
      .filter(c => c.budget > 0 || c.spent > 0)
      .sort((a, b) => b.pct - a.pct);
    const totalBudget = mb.reduce((s, b) => s + toBase(Number(b.amount) || 0, baseCurrency), 0);
    const totalSpent = cats.reduce((s, c) => s + c.spent, 0);
    return { totalBudget, totalSpent, pct: totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0, cats, hasBudget: mb.length > 0 };
  }, [budgets, transactions, thisMonth, today, baseCurrency]);

  // This-month spend by category with month-over-month delta.
  const spendingByCat = useMemo(() => {
    const cur = {}, prev = {};
    transactions.filter(t => !t.deletedAt && t.type === 'expense' && t.date <= today).forEach(t => {
      const a = (t.isRefund ? -1 : 1) * toBase(t.amount, t.currency);
      if (t.date.startsWith(thisMonth)) cur[t.category] = (cur[t.category] || 0) + a;
      else if (t.date.startsWith(lastMonthStr)) prev[t.category] = (prev[t.category] || 0) + a;
    });
    const total = Object.values(cur).reduce((s, v) => s + Math.max(0, v), 0);
    return {
      total,
      cats: Object.entries(cur).filter(([, v]) => v > 0)
        .map(([name, v]) => ({ name, value: v, delta: v - (prev[name] || 0), pct: total > 0 ? (v / total) * 100 : 0 }))
        .sort((a, b) => b.value - a.value),
    };
  }, [transactions, thisMonth, lastMonthStr, today, baseCurrency]);

  // Recurring monthly card commitments (subscriptions), excluding installments.
  const subsSummary = useMemo(() => {
    const subs = recurring.filter(r => !r.deletedAt && r.active && r.type === 'expense' && r.unit === 'month'
      && (Number(r.interval) || 1) === 1 && r.cardId && !r.linkedCardInstallmentId && !r.isInterestRule);
    const items = subs.map(r => ({ name: r.description, amount: toBase(Number(r.amount) || 0, r.currency) })).sort((a, b) => b.amount - a.amount);
    return { count: items.length, monthly: items.reduce((s, i) => s + i.amount, 0), items };
  }, [recurring, baseCurrency]);

  // Credit-card utilization (overall + per card).
  const cardUtil = useMemo(() => {
    const list = cards.filter(c => !c.deletedAt).map(c => {
      const owed = Math.max(0, toBase(computeCardLiveBalance(c, transactions, cardInstallments), c.currency));
      const limit = toBase(Number(c.creditLimit) || 0, c.currency);
      return { name: c.name, owed, limit, pct: limit > 0 ? (owed / limit) * 100 : 0 };
    });
    const totalOwed = list.reduce((s, c) => s + c.owed, 0);
    const totalLimit = list.reduce((s, c) => s + c.limit, 0);
    return { totalOwed, totalLimit, pct: totalLimit > 0 ? (totalOwed / totalLimit) * 100 : 0, cards: list.filter(c => c.limit > 0).sort((a, b) => b.pct - a.pct) };
  }, [cards, transactions, cardInstallments, baseCurrency]);

  // Loan payoff progress.
  const loanPayoff = useMemo(() => loans.filter(l => !l.deletedAt).map(l => {
    const orig = Number(l.originalPrincipal) || 0;
    const bal = (l.liveBalance ?? l.currentBalance) || 0;
    const start = l.startDate ? strToDate(l.startDate) : null;
    const payoff = (start && Number(l.termMonths)) ? new Date(start.getFullYear(), start.getMonth() + Number(l.termMonths), Math.min(Number(l.paymentDay) || 1, 28)) : null;
    return { name: l.name, paidPct: orig > 0 ? Math.max(0, Math.min(100, ((orig - bal) / orig) * 100)) : 0, bal: toBase(bal, l.currency), emi: toBase(Number(l.monthlyPayment) || 0, l.currency), payoff };
  }), [loans, baseCurrency]);

  // Savings-goal progress.
  const goalsProgress = useMemo(() => (goals || []).filter(g => !g.deletedAt && (Number(g.targetAmount) || 0) > 0).map(g => {
    const current = computeGoalProgress(g, allocations, accounts, investments, transactions, 0);
    const target = Number(g.targetAmount) || 0;
    return { name: g.name, current: toBase(current, g.currency || 'AED'), target: toBase(target, g.currency || 'AED'), pct: target > 0 ? Math.min(100, (current / target) * 100) : 0 };
  }).sort((a, b) => b.pct - a.pct), [goals, allocations, accounts, investments, transactions, baseCurrency]);

  // Outstanding reimbursables (owed to you).
  const reimbOwed = useMemo(() => {
    let total = 0, count = 0;
    transactions.filter(t => !t.deletedAt && t.isReimbursable && t.date <= today).forEach(t => {
      const rem = (Number(t.amount) || 0) - (Number(t.reimbursedAmount) || 0);
      if (rem > 0.01) { total += toBase(rem, t.currency); count++; }
    });
    return { total, count };
  }, [transactions, today, baseCurrency]);

  // Uncategorised rules/txs are surfaced centrally in the bell now
  // (NotificationPanel "Review & tidy"), not as a dashboard banner.

  return (
    <div className="space-y-8">

      {/* ============ HERO ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Net Worth */}
        <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Net Worth</span>
            <Wallet className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-3xl font-bold mt-1 truncate">{fmtMoney(nwSummary.current, baseCurrency)}</div>
          <div className="flex items-end justify-between mt-2 gap-2">
            <span className={`text-xs font-medium ${nwSummary.delta >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {nwSummary.hasPrior
                ? `${nwSummary.delta >= 0 ? '▲' : '▼'} ${fmtMoney(Math.abs(nwSummary.delta), baseCurrency)} (${nwSummary.pct >= 0 ? '+' : ''}${nwSummary.pct.toFixed(1)}%) · ${nwSummary.days}d`
                : 'building history…'}
            </span>
            {nwSummary.spark.length >= 2 && <Sparkline data={nwSummary.spark} color={nwSummary.delta >= 0 ? '#10b981' : '#f43f5e'} />}
          </div>
        </div>

        {/* Cash on Hand + runway */}
        {(() => {
          const m = runwayActive.months;
          const tone = m == null ? 'text-slate-400' : m < 2 ? 'text-rose-400' : m < 6 ? 'text-amber-400' : 'text-emerald-400';
          const sub = m == null
            ? (runwayMode === 'budget' ? 'Set a budget to project runway' : 'Need ≥1 month of history')
            : `at ${fmtMoney(runwayActive.avgMonthlyExpense, baseCurrency)}/mo · ${runwayMode === 'budget' ? `${runwayActive.samples}-mo plan` : `${runwayActive.samples}-mo actual`}`;
          return (
            <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">Cash on Hand</span>
                <span className="text-[11px] text-slate-500">{accounts.filter(a => !a.deletedAt).length} accounts</span>
              </div>
              <div className="text-3xl font-bold mt-1 truncate">{fmtMoney(cashOnHand, baseCurrency)}</div>
              <div className="flex items-center justify-between mt-2 gap-2">
                <span className={`text-xs font-semibold ${tone}`}>{m == null ? 'runway —' : `${m.toFixed(1)} mo runway`}</span>
                <div className="flex items-center gap-0.5 bg-slate-800/60 rounded p-0.5">
                  <button type="button" onClick={() => setRunwayMode('budget')} className={`text-[10px] px-1.5 py-0.5 rounded ${runwayMode === 'budget' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}>Budget</button>
                  <button type="button" onClick={() => setRunwayMode('history')} className={`text-[10px] px-1.5 py-0.5 rounded ${runwayMode === 'history' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}>History</button>
                </div>
              </div>
              <div className="text-[11px] text-slate-500 mt-1 truncate">{sub}</div>
            </div>
          );
        })()}

        {/* This Month — net + savings rate */}
        <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">This Month — Net</span>
            <TrendingUp className={`w-4 h-4 ${monthFlow.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} />
          </div>
          <div className={`text-3xl font-bold mt-1 truncate ${monthFlow.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {monthFlow.net >= 0 ? '+' : '−'}{fmtMoney(Math.abs(monthFlow.net), baseCurrency)}
          </div>
          <div className="text-xs text-slate-400 mt-2 truncate">
            In {fmtMoney(monthFlow.income, baseCurrency)} · Out {fmtMoney(monthFlow.expense, baseCurrency)}
          </div>
          <div className="text-[11px] text-slate-500 mt-1 truncate">
            {monthFlow.income > 0 && monthFlow.net >= 0 ? `${monthFlow.savingsRate.toFixed(0)}% saved`
              : monthFlow.income <= 0 ? 'awaiting income this month'
              : 'spending ahead of income so far'}{reimbOwed.total > 0 ? ` · ${fmtMoney(reimbOwed.total, baseCurrency)} owed to you` : ''}
          </div>
        </div>
      </div>

      {/* ============ THIS MONTH — budget health ============ */}
      <div>
        <SectionHeader title="This Month" />
        <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
          {!budgetVsActual.hasBudget ? (
            <div className="text-slate-500 text-sm py-6 text-center">No budget set for this month — add one in Budgets to track spending against a plan.</div>
          ) : (() => {
            const over = budgetVsActual.pct > 100;
            const ahead = budgetVsActual.pct > monthFlow.monthPct + 5;
            const barColor = budgetVsActual.pct > 100 ? 'bg-rose-500' : budgetVsActual.pct >= 85 ? 'bg-amber-500' : 'bg-emerald-500';
            return (
              <>
                <div className="flex items-end justify-between mb-2 gap-2">
                  <div className="min-w-0">
                    <div className="text-2xl font-bold truncate">{fmtMoney(budgetVsActual.totalSpent, baseCurrency)} <span className="text-sm font-normal text-slate-500">of {fmtMoney(budgetVsActual.totalBudget, baseCurrency)}</span></div>
                    <div className="text-xs text-slate-400 mt-0.5">{budgetVsActual.pct.toFixed(0)}% of budget · {monthFlow.monthPct.toFixed(0)}% through the month</div>
                  </div>
                  <span className={`text-xs font-medium flex-shrink-0 ${over ? 'text-rose-400' : ahead ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {over ? 'over budget' : ahead ? 'ahead of pace' : 'on track'}
                  </span>
                </div>
                <ProgressBar pct={budgetVsActual.pct} color={barColor} height="h-2.5" marker={monthFlow.monthPct} />
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-5">
                  {budgetVsActual.cats.slice(0, 8).map(c => {
                    const cc = c.pct > 100 ? 'bg-rose-500' : c.pct >= 85 ? 'bg-amber-500' : 'bg-cyan-500';
                    const cat = ALL_CATEGORIES.find(x => x.name === c.name);
                    return (
                      <div key={c.name || 'uncat'}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="truncate flex items-center gap-1.5"><span>{cat?.icon || '📦'}</span>{c.name || 'Uncategorised'}</span>
                          <span className="text-slate-400 flex-shrink-0">{fmtMoney(c.spent, baseCurrency)}{c.budget > 0 ? ` / ${fmtMoney(c.budget, baseCurrency)}` : ''}</span>
                        </div>
                        <ProgressBar pct={c.pct} color={cc} height="h-1.5" />
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* ============ SPENDING ============ */}
      <div>
        <SectionHeader title="Spending" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-300">By Category</h3>
              <span className="text-[11px] text-slate-500">this month vs last</span>
            </div>
            {spendingByCat.cats.length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center">No spending logged this month yet.</div>
            ) : (
              <div className="space-y-3">
                {spendingByCat.cats.slice(0, 7).map(c => {
                  const cat = ALL_CATEGORIES.find(x => x.name === c.name);
                  const up = c.delta > 0;
                  return (
                    <div key={c.name || 'uncat'}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-2 truncate"><span>{cat?.icon || '📦'}</span>{c.name || 'Uncategorised'}</span>
                        <span className="text-slate-300 flex-shrink-0">
                          {fmtMoney(c.value, baseCurrency)}
                          {Math.abs(c.delta) >= 1 && <span className={`ml-1.5 text-[11px] ${up ? 'text-rose-400' : 'text-emerald-400'}`}>{up ? '▲' : '▼'}{fmtMoney(Math.abs(c.delta), baseCurrency)}</span>}
                        </span>
                      </div>
                      <ProgressBar pct={c.pct} color="bg-cyan-500" height="h-1.5" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-300">Subscriptions</h3>
              <span className="text-[11px] text-slate-500">{subsSummary.count} active</span>
            </div>
            {subsSummary.count === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center">No recurring subscriptions detected.</div>
            ) : (
              <>
                <div className="text-2xl font-bold">{fmtMoney(subsSummary.monthly, baseCurrency)}<span className="text-sm font-normal text-slate-500">/mo</span></div>
                <div className="text-[11px] text-slate-500 mb-3">≈ {fmtMoney(subsSummary.monthly * 12, baseCurrency)}/yr across {subsSummary.count} services</div>
                <div className="space-y-1.5">
                  {subsSummary.items.slice(0, 6).map(s => (
                    <div key={s.name} className="flex items-center justify-between text-xs">
                      <span className="truncate text-slate-300">{s.name}</span>
                      <span className="text-slate-400 flex-shrink-0">{fmtMoney(s.amount, baseCurrency)}</span>
                    </div>
                  ))}
                  {subsSummary.items.length > 6 && <div className="text-[11px] text-slate-600">+{subsSummary.items.length - 6} more</div>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ============ MONEY & DEBT ============ */}
      <div>
        <SectionHeader title="Money & Debt" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <MiniStatCard label="Cash & Bank" value={fmtMoney(stats.accountsTotal, baseCurrency)} positive />
          <MiniStatCard label="Investments" value={fmtMoney(stats.investmentsTotal, baseCurrency)} positive />
          <MiniStatCard label="Card Debt" value={fmtMoney(stats.cardsDebt, baseCurrency)} negative={stats.cardsDebt > 0} />
          <MiniStatCard label="Loan Debt" value={fmtMoney(stats.loansDebt, baseCurrency)} negative={stats.loansDebt > 0} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-300">Card Utilization</h3>
              <span className={`text-xs font-semibold ${cardUtil.pct >= 70 ? 'text-rose-400' : cardUtil.pct >= 30 ? 'text-amber-400' : 'text-emerald-400'}`}>{cardUtil.pct.toFixed(0)}%</span>
            </div>
            {cardUtil.cards.length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center">No card limits set.</div>
            ) : (
              <>
                <div className="text-xs text-slate-400 mb-3">{fmtMoney(cardUtil.totalOwed, baseCurrency)} of {fmtMoney(cardUtil.totalLimit, baseCurrency)} limit</div>
                <div className="space-y-2.5">
                  {cardUtil.cards.slice(0, 6).map(c => {
                    const cc = c.pct >= 70 ? 'bg-rose-500' : c.pct >= 30 ? 'bg-amber-500' : 'bg-emerald-500';
                    return (
                      <div key={c.name}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="truncate">{c.name}</span>
                          <span className="text-slate-400 flex-shrink-0">{c.pct.toFixed(0)}%</span>
                        </div>
                        <ProgressBar pct={c.pct} color={cc} height="h-1.5" />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Loan Payoff</h3>
            {loanPayoff.length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center">No active loans.</div>
            ) : (
              <div className="space-y-4">
                {loanPayoff.map(l => (
                  <div key={l.name}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="truncate">{l.name}</span>
                      <span className="text-slate-400 flex-shrink-0">{l.paidPct.toFixed(0)}% paid</span>
                    </div>
                    <ProgressBar pct={l.paidPct} color="bg-emerald-500" height="h-1.5" />
                    <div className="text-[11px] text-slate-500 mt-1 flex justify-between gap-2">
                      <span className="truncate">{fmtMoney(l.bal, baseCurrency)} left · {fmtMoney(l.emi, baseCurrency)}/mo</span>
                      {l.payoff && <span className="flex-shrink-0">payoff {l.payoff.toLocaleDateString('en', { month: 'short', year: 'numeric' })}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============ FORECAST ============ */}
      <div>
        <SectionHeader title="Forecast" />
        <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-slate-300">Cash &amp; Net Worth</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1 bg-slate-800/60 rounded p-0.5">
                {[
                  { label: '3M', val: 3 },
                  { label: '6M', val: 6 },
                  { label: '12M', val: 12 },
                  { label: '2Yr', val: 24 },
                ].map(opt => (
                  <button
                    key={opt.val}
                    type="button"
                    onClick={() => setForecastHorizon(opt.val)}
                    className={`text-[11px] px-2 py-0.5 rounded ${forecastHorizon === opt.val ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                <button
                  type="button"
                  onClick={() => setShowCashLine(v => !v)}
                  className={`flex items-center gap-1.5 px-2 py-0.5 rounded ${showCashLine ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-800/60 text-slate-500'}`}
                >
                  <span className="w-3 h-0.5 bg-emerald-400 inline-block" /> Cash
                </button>
                <button
                  type="button"
                  onClick={() => setShowNetWorthLine(v => !v)}
                  className={`flex items-center gap-1.5 px-2 py-0.5 rounded ${showNetWorthLine ? 'bg-cyan-500/15 text-cyan-300' : 'bg-slate-800/60 text-slate-500'}`}
                >
                  <span className="w-3 h-0.5 bg-cyan-400 inline-block" /> Net Worth
                </button>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={combinedForecast}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                labelStyle={{ color: '#cbd5e1' }}
                itemStyle={{ color: '#e2e8f0' }}
                formatter={(v, name) => [fmtMoney(v, baseCurrency, false), name === 'cash' ? 'Cash' : 'Net Worth']}
              />
              {showCashLine && (
                <Line type="monotone" dataKey="cash" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} />
              )}
              {showNetWorthLine && (
                <Line type="monotone" dataKey="netWorth" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3, fill: '#06b6d4' }} />
              )}
            </LineChart>
          </ResponsiveContainer>
          <div className="text-[10px] text-slate-500 mt-3 leading-relaxed">
            Each point is the projected month-end balance · Cash = accounts only, moving when money actually leaves (direct debits + card statements paid at due date) · card spend hits cash only when its statement is paid · savings interest accrues on the savings balance · Net Worth counts spend when it happens (accrual) and includes assets, investments, debt · far-term card spend estimated from budgets.
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <div className="bg-slate-900 rounded-xl p-5 border border-slate-800 min-w-0 overflow-hidden">
            <h3 className="text-sm font-semibold mb-4 text-slate-300">Upcoming (Next 30 Days)</h3>
            {upcoming.length === 0 ? (
              <div className="text-slate-500 text-sm py-8 text-center">Nothing scheduled in the next 30 days</div>
            ) : (
              <div className="space-y-2">
                {upcoming.map(b => {
                  const cat = ALL_CATEGORIES.find(c => c.name === b.category);
                  const Icon = b.kind === 'card_payment' ? CreditCard : b.kind === 'loan_payment' ? Landmark : Repeat;
                  const iconEl = (b.kind === 'card_payment' || b.kind === 'loan_payment')
                    ? <Icon className="w-4 h-4 text-cyan-400" />
                    : (cat ? <span className="text-sm">{cat.icon}</span> : <Repeat className="w-4 h-4 text-cyan-400" />);
                  return (
                    <DashRow key={b.id}
                      icon={iconEl}
                      title={b.name}
                      subtitleIcon={<Clock className="w-3 h-3 flex-shrink-0" />}
                      subtitle={b.days <= 0 ? 'Today' : b.days === 1 ? 'Tomorrow' : `In ${b.days} days`}
                      amount={`${b.type === 'income' ? '+' : '−'}${fmtMoney(b.amount, b.currency)}`}
                      amountPositive={b.type === 'income'}
                      amountSub={b.frequency}
                    />
                  );
                })}
              </div>
            )}
          </div>
          <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
            <h3 className="text-sm font-semibold mb-4 text-slate-300">Savings Goals</h3>
            {goalsProgress.length === 0 ? (
              <div className="text-slate-500 text-sm py-8 text-center">No savings goals yet — set one in Savings.</div>
            ) : (
              <div className="space-y-4">
                {goalsProgress.slice(0, 5).map(g => (
                  <div key={g.name}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="truncate">{g.name}</span>
                      <span className="text-slate-400 flex-shrink-0">{g.pct.toFixed(0)}%</span>
                    </div>
                    <ProgressBar pct={g.pct} color={g.pct >= 100 ? 'bg-emerald-500' : 'bg-cyan-500'} height="h-1.5" />
                    <div className="text-[11px] text-slate-500 mt-1">{fmtMoney(g.current, baseCurrency)} of {fmtMoney(g.target, baseCurrency)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============ TRENDS & ACTIVITY ============ */}
      <div>
        <SectionHeader title="Trends & Activity" action={
          <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800">
            {[{ id: '3m', label: '3M' }, { id: '6m', label: '6M' }, { id: 'ytd', label: 'YTD' }, { id: '12m', label: '12M' }].map(p => (
              <button key={p.id} onClick={() => setReportPeriod(p.id)} className={`px-3 py-1 text-xs rounded transition ${reportPeriod === p.id ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                {p.label}
              </button>
            ))}
          </div>
        } />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {netWorthHistory.length >= 2 ? (
            <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
              <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-slate-300">Net Worth Trail</h3>
                <div className="flex items-center gap-2">
                  {nwRanges.length > 0 && (
                    <div className="flex gap-0.5 bg-slate-800/60 rounded-lg p-0.5">
                      {nwRanges.map((r) => (
                        <button
                          key={r.key}
                          onClick={() => setNwRangeKey(r.key)}
                          className={`px-2 py-0.5 text-xs rounded transition ${nwActiveRange.key === r.key ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={nwTrail}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={11} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} labelStyle={{ color: '#cbd5e1' }} itemStyle={{ color: '#e2e8f0' }} formatter={(v) => fmtMoney(v, baseCurrency, false)} />
                  <Line type="monotone" dataKey="netWorth" stroke="#06b6d4" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="bg-slate-900 rounded-xl p-5 border border-slate-800 flex items-center justify-center text-slate-500 text-sm">
              Net Worth Trail builds as daily snapshots accumulate.
            </div>
          )}

          <div className="bg-slate-900 rounded-xl p-5 border border-slate-800 min-w-0 overflow-hidden">
            <h3 className="text-sm font-semibold mb-4 text-slate-300">Recent Transactions</h3>
            {recentTx.length === 0 ? (
              <div className="text-slate-500 text-sm py-8 text-center">No transactions yet — add one or import to get started</div>
            ) : (
              <div className="space-y-2">
                {recentTx.map(tx => {
                  const cat = ALL_CATEGORIES.find(c => c.name === tx.category);
                  const hasOrig = tx.originalCurrency && tx.originalCurrency !== baseCurrency
                    && Number.isFinite(Number(tx.originalAmount)) && Number(tx.originalAmount) > 0;
                  const aedValue = toBase(tx.amount, tx.currency);
                  const origAmount = hasOrig ? Number(tx.originalAmount) : tx.amount;
                  const origCurrency = hasOrig ? tx.originalCurrency : tx.currency;
                  const showOrig = origCurrency !== baseCurrency;
                  const pos = tx.type === 'income' || tx.isRefund;
                  const subtitle = `${tx.type === 'transfer' ? `${tx.fromAccount || '—'} → ${tx.toAccount || '—'}` : (tx.category || 'Uncategorised')} · ${strToDate(tx.date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}`;
                  return (
                    <DashRow key={tx.id}
                      icon={<span className="text-sm">{cat?.icon || '📦'}</span>}
                      title={tx.description}
                      badge={<>
                        {tx.smsRawId && (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${tx.smsNeedsReview ? 'bg-amber-500/15 text-amber-300' : 'bg-violet-500/15 text-violet-300'}`}>
                            {tx.smsNeedsReview ? 'SMS ⚠' : 'SMS'}
                          </span>
                        )}
                        {tx.recurringId && <Repeat className="w-3 h-3 text-cyan-400 flex-shrink-0" />}
                      </>}
                      subtitle={subtitle}
                      amount={`${tx.type === 'transfer' ? '' : (pos ? '+' : '−')}${fmtMoney(aedValue, baseCurrency, false)}`}
                      amountPositive={pos}
                      amountSub={showOrig ? fmtMoney(origAmount, origCurrency, false) : null}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
            <h3 className="text-sm font-semibold mb-4 text-slate-300">Income vs Expense</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={reportTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} labelStyle={{ color: '#cbd5e1' }} itemStyle={{ color: '#e2e8f0' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Income" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Expense" fill="#f43f5e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
            <h3 className="text-sm font-semibold mb-4 text-slate-300">Savings Trend</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={reportTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} labelStyle={{ color: '#cbd5e1' }} itemStyle={{ color: '#e2e8f0' }} />
                <Line type="monotone" dataKey="Savings" stroke="#06b6d4" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

    </div>
  );
}

// ============ CASH-FLOW CALENDAR ============
// Per-day cash events + a projected running balance ANCHORED to the monthly
// forecast (projectCashSeries) at each month-end, so the calendar and the
// dashboard forecast always agree. Past days reconstruct the actual balance
// backward from today's live cash; from today forward, discrete scheduled
// events (income, bills, EMIs, card statement payments at due dates, planned
// txs) create visible day-steps and the remaining discretionary change is
// spread evenly to land exactly on the forecast month-end. Verified vs real
// data: today == cashOnHand, and each month-end == projectCashSeries.
//
// Card PURCHASES (and recurring card subs/installments) are also placed, flagged
// `cash:false` — they're shown for context but DON'T move cash on their day
// (the cash leaves when the statement is paid, modelled separately). So the
// balance stays a true cash projection while the calendar shows full activity.
const CAL_KIND = {
  'actual-in': { icon: '💰', pos: true },
  income:      { icon: '💰', pos: true },
  interest:    { icon: '✨', pos: true },
  'actual-out':{ icon: '🧾', pos: false },
  bill:        { icon: '📄', pos: false },
  loan:        { icon: '🏦', pos: false },
  card:        { icon: '💳', pos: false },
  'card-spend':{ icon: '💳', pos: false },
  transfer:    { icon: '↔', pos: null },
};

const buildCashCalendar = ({ year, month, accounts, transactions, recurring = [], budgets = [], cards = [], cardStatements = [], loans = [], cardInstallments = [], investments = [], assets = [], netWorthHistory = [], baseCurrency = 'AED', buffer = 0 }) => {
  const tStr = todayStr();
  const todayD = strToDate(tStr);
  const addDays = (dt, n) => { const x = new Date(dt); x.setDate(x.getDate() + n); return x; };
  const cashNow = computeCashOnHand(accounts, transactions);

  // Monday-first grid (6 weeks / 42 cells).
  const first = new Date(year, month, 1);
  const mondayOffset = (first.getDay() + 6) % 7;
  const gridStart = addDays(first, -mondayOffset);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const gs = dateToStr(cells[0]), ge = dateToStr(cells[41]);

  const ev = new Map();
  const push = (ds, e) => { if (ds < gs || ds > ge) return; if (!ev.has(ds)) ev.set(ds, []); ev.get(ds).push(e); };

  // Card statement close → payment-due mapping. Hoisted here so BOTH the
  // cash-forecast event (future only) and the informational "payment due"
  // overlay (any month being viewed, incl. the past) can use them.
  const closeOn = (c, yr, mo) => new Date(yr, mo, Math.min(Number(c.statementDay) || 1, lastDayOfMonth(yr, mo)));
  const dueFromClose = (c, closeDt, uploadedDue) => {
    if (uploadedDue) { const dd = strToDate(uploadedDue); if (dd > closeDt) return dd; }
    const dq = Number(c.paymentDueDay) || 0; if (!dq) return addDays(closeDt, 21);
    let y = closeDt.getFullYear(), m = closeDt.getMonth();
    for (let k = 0; k < 14; k++) { const dt = new Date(y, m, Math.min(dq, lastDayOfMonth(y, m))); if (dt > closeDt) return dt; m++; if (m > 11) { m = 0; y++; } }
    return addDays(closeDt, 21);
  };
  const paymentsAfter = (c, afterStr) => transactions.filter(t => !t.deletedAt && t.type === 'transfer' && (t.toCardId ? t.toCardId === c.id : t.toAccount === c.name) && t.date > afterStr && t.date <= tStr).reduce((s, t) => s + convert(t.amount, t.currency, c.currency), 0);

  // PAST: actual cash movements (account-affecting) + card purchases (context).
  transactions.forEach(t => {
    if (t.deletedAt || t.excludesFromBalances || !t.date || t.date > tStr) return;
    const amt = toBase(Number(t.amount) || 0, t.currency);
    if (t.type === 'income' && t.accountId) push(t.date, { name: t.description || 'Income', amount: amt, dir: 1, kind: 'actual-in' });
    else if (t.type === 'expense' && t.accountId && !t.cardId) push(t.date, { name: t.description || 'Expense', amount: amt, dir: t.isRefund ? 1 : -1, kind: 'actual-out' });
    else if (t.type === 'expense' && t.cardId) push(t.date, { name: t.description || 'Card spend', amount: amt, dir: t.isRefund ? 1 : -1, kind: 'card-spend', cash: false });
    else if (t.type === 'transfer') {
      const fromAcc = !!t.fromAccountId, toAcc = !!t.toAccountId;
      if (fromAcc && toAcc) return; // internal transfer — net 0 on cash, skip
      if (fromAcc) push(t.date, { name: t.description || 'Transfer out', amount: amt, dir: -1, kind: 'transfer' });
      if (toAcc) push(t.date, { name: t.description || 'Transfer in', amount: amt, dir: 1, kind: 'transfer' });
    }
  });

  // FUTURE: scheduled events.
  const fStart = dateToStr(addDays(todayD, 1));
  if (ge >= fStart) {
    recurring.filter(r => r.active && !r.deletedAt && r.type === 'income' && r.accountId && !r.isInterestRule).forEach(r => {
      generateOccurrences(r, fStart, ge).forEach(o => push(dateToStr(o), { name: r.description || 'Income', amount: toBase(Number(r.amount) || 0, r.currency), dir: 1, kind: 'income' }));
    });
    recurring.filter(r => r.active && !r.deletedAt && r.type === 'expense' && r.accountId && !r.cardId).forEach(r => {
      generateOccurrences(r, fStart, ge).forEach(o => push(dateToStr(o), { name: r.description || 'Bill', amount: toBase(Number(r.amount) || 0, r.currency), dir: -1, kind: r.linkedLoanId ? 'loan' : 'bill' }));
    });
    recurring.filter(r => r.active && !r.deletedAt && r.type === 'expense' && r.cardId).forEach(r => {
      generateOccurrences(r, fStart, ge).forEach(o => push(dateToStr(o), { name: r.description || 'Card spend', amount: toBase(Number(r.amount) || 0, r.currency), dir: -1, kind: 'card-spend', cash: false }));
    });
    recurring.filter(r => r.active && !r.deletedAt && r.type === 'transfer' && r.linkedLoanId).forEach(r => {
      generateOccurrences(r, fStart, ge).forEach(o => push(dateToStr(o), { name: r.description || 'Loan payment', amount: toBase(Number(r.amount) || 0, r.currency), dir: -1, kind: 'loan' }));
    });
    transactions.filter(t => !t.deletedAt && !t.excludesFromBalances && !t.recurringId && t.date > tStr && t.date <= ge).forEach(t => {
      const amt = toBase(Number(t.amount) || 0, t.currency);
      if (t.type === 'income') push(t.date, { name: t.description || 'Income', amount: amt, dir: 1, kind: 'income' });
      else if (t.type === 'expense' && t.accountId && !t.cardId) push(t.date, { name: t.description || 'Expense', amount: amt, dir: -1, kind: 'bill' });
      else if (t.type === 'expense' && t.cardId) push(t.date, { name: t.description || 'Card spend', amount: amt, dir: -1, kind: 'card-spend', cash: false });
    });
    // Card statement payments (most-recent closed statement) at due dates.
    cards.filter(c => !c.deletedAt && c.statementDay).forEach(c => {
      let lc = closeOn(c, todayD.getFullYear(), todayD.getMonth());
      if (lc > todayD) { const p = new Date(todayD.getFullYear(), todayD.getMonth() - 1, 1); lc = closeOn(c, p.getFullYear(), p.getMonth()); }
      const lcStr = dateToStr(lc);
      let billed = cardStatementBalanceAsOf(c, transactions, lcStr);
      const up = (cardStatements || []).filter(s => s.cardId === c.id && !s.deletedAt && s.statementDate && s.statementDate.slice(0, 7) === lcStr.slice(0, 7)).sort((a, b) => cmpDateStr(b.statementDate, a.statementDate))[0];
      if (up && (up.totalPaymentDue != null || up.closingBalance != null)) {
        billed = up.totalPaymentDue != null ? Number(up.totalPaymentDue) : (Number(up.closingBalance) || 0);
      }
      const stmtBase = toBase(Math.max(0, billed - paymentsAfter(c, lcStr)), c.currency);
      if (stmtBase <= 0.5) return;
      const due = dueFromClose(c, lc, up && up.dueDate); const dueStr = dateToStr(due);
      if (dueStr > tStr && dueStr <= ge) push(dueStr, { name: `${c.name} payment`, amount: stmtBase, dir: -1, kind: 'card' });
    });
  }

  // ── Card statement payment-due overlay (informational; never touches the
  // cash math above) ────────────────────────────────────────────────────────
  // Mark every card's payment due date that lands in the visible grid. Show the
  // amount ONLY when a statement for that cycle is on file (uploaded or entered);
  // otherwise just the card (the cycle may not have closed yet). Past due dates
  // in view show their statement amount, tagged "paid" once settled.
  const cardDueByDay = new Map();
  const seenDue = new Set();
  const pushDue = (ds, e) => {
    if (ds < gs || ds > ge) return;
    const k = e.cardId + '|' + ds; if (seenDue.has(k)) return; seenDue.add(k);
    if (!cardDueByDay.has(ds)) cardDueByDay.set(ds, []); cardDueByDay.get(ds).push(e);
  };
  cards.filter(c => !c.deletedAt && c.statementDay).forEach(c => {
    // Walk close dates spanning the grid (±3 months) → each maps to a due date.
    for (let k = -3; k <= 3; k++) {
      const base = new Date(todayD.getFullYear(), todayD.getMonth() + k, 1);
      const lc = closeOn(c, base.getFullYear(), base.getMonth());
      const lcStr = dateToStr(lc);
      const up = (cardStatements || []).filter(s => s.cardId === c.id && !s.deletedAt && s.statementDate && s.statementDate.slice(0, 7) === lcStr.slice(0, 7)).sort((a, b) => cmpDateStr(b.statementDate, a.statementDate))[0];
      const dueStr = dateToStr(dueFromClose(c, lc, up && up.dueDate));
      if (dueStr < gs || dueStr > ge) continue;
      const hasStatement = !!(up && (up.totalPaymentDue != null || up.closingBalance != null));
      let amount = null;
      if (hasStatement) {
        const billed = up.totalPaymentDue != null ? Number(up.totalPaymentDue) : (Number(up.closingBalance) || 0);
        amount = Math.max(0, billed - paymentsAfter(c, lcStr));
      }
      pushDue(dueStr, { cardId: c.id, cardName: c.name, currency: c.currency, amount, amountBase: amount != null ? toBase(amount, c.currency) : null, hasStatement, paid: hasStatement && amount != null && amount <= 0.5, dueStr });
    }
  });

  // Cash net only (card-spend is cash:false and excluded).
  const netByDay = (ds) => (ev.get(ds) || []).reduce((s, e) => s + (e.cash === false ? 0 : e.dir * e.amount), 0);

  // Forecast anchors (month-ends) so the calendar agrees with the dashboard.
  const vOff = (year - todayD.getFullYear()) * 12 + (month - todayD.getMonth());
  const fc = projectCashSeries({ accounts, transactions, recurring, budgets, cards, cardStatements, baseCurrency, horizon: Math.max(0, vOff) + 2, investments, assets, loans, cardInstallments });
  const anchorEnd = (k) => (fc[k] && fc[k].cash != null) ? fc[k].cash : cashNow;

  const bal = {};
  const offs = [...new Set(cells.filter(dt => dateToStr(dt) > tStr).map(dt => (dt.getFullYear() - todayD.getFullYear()) * 12 + (dt.getMonth() - todayD.getMonth())))].sort((a, b) => a - b);
  offs.forEach(k => {
    const md = new Date(todayD.getFullYear(), todayD.getMonth() + k, 1);
    const y = md.getFullYear(), m = md.getMonth(), lastDay = lastDayOfMonth(y, m);
    const startBal = k === 0 ? cashNow : anchorEnd(k - 1);
    const endBal = anchorEnd(k);
    const startDay = k === 0 ? todayD.getDate() + 1 : 1;
    const pd = []; for (let dd = startDay; dd <= lastDay; dd++) pd.push(dateToStr(new Date(y, m, dd)));
    if (!pd.length) return;
    const totDisc = pd.reduce((s, ds) => s + netByDay(ds), 0);
    const per = ((endBal - startBal) - totDisc) / pd.length;
    let run = startBal; pd.forEach(ds => { run += netByDay(ds) + per; bal[ds] = run; });
  });
  cells.filter(dt => dateToStr(dt) <= tStr).forEach(dt => {
    let b = cashNow;
    for (let x = addDays(dt, 1); dateToStr(x) <= tStr; x = addDays(x, 1)) b -= netByDay(dateToStr(x));
    bal[dateToStr(dt)] = b;
  });

  // Net-worth line: past from daily snapshots, future interpolated between the
  // forecast's month-end net-worth anchors (accrual moves smoothly day to day).
  const investmentsNow = (investments || []).reduce((s, i) => s + toBase((Number(i.shares) || 0) * (Number(i.currentPrice) || 0), i.currency), 0);
  const assetsNow = (assets || []).reduce((s, a) => s + toBase(getAssetCurrentValue(a), a.currency || 'AED'), 0);
  const accountsNow = (accounts || []).reduce((s, a) => a.deletedAt ? s : s + toBase(computeAccountLiveBalance(a, transactions), a.currency), 0);
  const cardsNow = (cards || []).reduce((s, c) => c.deletedAt ? s : s + toBase(computeCardLiveBalance(c, transactions, cardInstallments), c.currency), 0);
  const loansNow = (loans || []).reduce((s, l) => l.deletedAt ? s : s + toBase(computeLoanLiveBalance(l, transactions), l.currency), 0);
  const nwNow = accountsNow + investmentsNow + assetsNow - cardsNow - loansNow;
  const nwAnchor = (k) => (fc[k] && fc[k].netWorth != null) ? fc[k].netWorth : nwNow;
  const snapByDate = {}; (netWorthHistory || []).forEach(sn => { if (sn && sn.snapshotDate) snapByDate[sn.snapshotDate] = Number(sn.netWorth) || 0; });
  const sortedSnap = Object.keys(snapByDate).sort();
  const nwBal = {};
  offs.forEach(k => {
    const md = new Date(todayD.getFullYear(), todayD.getMonth() + k, 1);
    const y = md.getFullYear(), m = md.getMonth(), lastDay = lastDayOfMonth(y, m);
    const startNw = k === 0 ? nwNow : nwAnchor(k - 1), endNw = nwAnchor(k);
    const startDay = k === 0 ? todayD.getDate() + 1 : 1;
    const pd = []; for (let dd = startDay; dd <= lastDay; dd++) pd.push(dateToStr(new Date(y, m, dd)));
    if (!pd.length) return;
    const step = (endNw - startNw) / pd.length;
    let run = startNw; pd.forEach(ds => { run += step; nwBal[ds] = run; });
  });
  cells.filter(dt => dateToStr(dt) <= tStr).forEach(dt => {
    const ds = dateToStr(dt);
    if (snapByDate[ds] != null) { nwBal[ds] = snapByDate[ds]; return; }
    let v = null; for (const k of sortedSnap) { if (k <= ds) v = snapByDate[k]; else break; }
    nwBal[ds] = v != null ? v : nwNow;
  });
  nwBal[tStr] = nwNow;

  const grid = cells.map(dt => {
    const ds = dateToStr(dt);
    const events = (ev.get(ds) || []).slice().sort((a, b) => (b.dir * b.amount) - (a.dir * a.amount));
    const balance = Math.round(bal[ds] ?? 0);
    const nwBalance = Math.round(nwBal[ds] ?? nwNow);
    // Three channels: cash in (income), cash out (anything leaving an account),
    // credit out (card purchases — paid later at statement, don't move cash today).
    const cashIn = events.reduce((s, e) => s + (e.cash !== false && e.dir > 0 ? e.amount : 0), 0);
    const cashOut = events.reduce((s, e) => s + (e.cash !== false && e.dir < 0 ? e.amount : 0), 0);
    const creditOut = events.reduce((s, e) => s + (e.cash === false ? -e.dir * e.amount : 0), 0);
    const net = cashIn - cashOut;
    return {
      ds, day: dt.getDate(), dow: dt.getDay(), inMonth: dt.getMonth() === month,
      isToday: ds === tStr, isPast: ds < tStr, events, net, cashIn, cashOut, creditOut, cardSpend: creditOut,
      cardDue: cardDueByDay.get(ds) || [],
      hasCard: Math.abs(creditOut) > 0.5, balance, nwBalance, below: ds > tStr && balance < buffer, isLow: false,
    };
  });

  const inMonthCells = grid.filter(c => c.inMonth);
  let monthIn = 0, monthOut = 0, monthCredit = 0;
  inMonthCells.forEach(c => { monthIn += c.cashIn; monthOut += c.cashOut; monthCredit += c.creditOut; });
  const future = grid.filter(c => !c.isPast && c.ds <= ge);
  let lowBalance = null, lowDate = null;
  future.forEach(c => { if (lowBalance == null || c.balance < lowBalance) { lowBalance = c.balance; lowDate = c.ds; } });
  const lowCell = grid.find(c => c.ds === lowDate); if (lowCell) lowCell.isLow = true;
  const endCell = inMonthCells[inMonthCells.length - 1];
  const safeToSpend = lowBalance != null ? Math.max(0, lowBalance - buffer) : null;
  let nextIncomeDate = null;
  future.forEach(c => { if (nextIncomeDate) return; if (c.events.some(e => e.cash !== false && e.dir > 0)) nextIncomeDate = c.ds; });

  return {
    grid,
    summary: { monthIn, monthOut, monthCredit, lowBalance, lowDate, endBalance: endCell ? endCell.balance : cashNow, todayBalance: cashNow, buffer, safeToSpend, nextIncomeDate, viewEnd: ge },
  };
};

const CAL_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function CashCalendar({ accounts, transactions, recurring, budgets, cards, cardStatements, loans, cardInstallments, investments, assets, netWorthHistory, baseCurrency }) {
  const now = new Date();
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selDs, setSelDs] = useState(todayStr());
  const [show, setShow] = useState({ income: true, debit: true, credit: true, due: true });
  const [showNW, setShowNW] = useState(true);
  const [showCash, setShowCash] = useState(true);

  const cal = useMemo(() => buildCashCalendar({
    year: view.y, month: view.m, accounts, transactions, recurring, budgets, cards, cardStatements,
    loans, cardInstallments, investments, assets, netWorthHistory, baseCurrency,
  }), [view, accounts, transactions, recurring, budgets, cards, cardStatements, loans, cardInstallments, investments, assets, netWorthHistory, baseCurrency]);

  const monthLabel = new Date(view.y, view.m, 1).toLocaleDateString('en', { month: 'long', year: 'numeric' });
  const prev = () => setView(v => { const d = new Date(v.y, v.m - 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const next = () => setView(v => { const d = new Date(v.y, v.m + 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const goToday = () => { setView({ y: now.getFullYear(), m: now.getMonth() }); setSelDs(todayStr()); };

  const sel = cal.grid.find(c => c.ds === selDs && c.inMonth) || cal.grid.find(c => c.isToday && c.inMonth) || cal.grid.find(c => c.inMonth);
  const stripData = cal.grid.filter(c => c.inMonth).map(c => ({ day: String(c.day), balance: c.balance, netWorth: c.nwBalance }));
  const s = cal.summary;
  const lowBelow = s.lowBalance != null && s.lowBalance < s.buffer;
  const kfmt = (n) => { const a = Math.abs(n); const sign = n < 0 ? '−' : ''; if (a >= 1000) return sign + (a / 1000).toFixed(a >= 10000 ? 0 : 1) + 'k'; return sign + Math.round(a); };
  const lowDateLabel = s.lowDate ? strToDate(s.lowDate).toLocaleDateString('en', { month: 'short', day: 'numeric' }) : '—';
  const selLabel = sel ? strToDate(sel.ds).toLocaleDateString('en', { weekday: 'long', month: 'long', day: 'numeric' }) : '';
  const rows = [];
  for (let i = 0; i < 6; i++) { const row = cal.grid.slice(i * 7, i * 7 + 7); if (row.some(c => c.inMonth)) rows.push(row); }

  const CH = {
    income: { label: 'Income', color: 'text-emerald-400', chip: 'bg-emerald-500/15 text-emerald-300', Icon: ArrowUpRight,   sign: '+' },
    debit:  { label: 'Debit',  color: 'text-rose-400',    chip: 'bg-rose-500/15 text-rose-300',       Icon: ArrowDownRight, sign: '−' },
    credit: { label: 'Credit', color: 'text-orange-400',  chip: 'bg-orange-500/15 text-orange-300',   Icon: CreditCard,     sign: '' },
  };
  const selGroups = {
    income: sel ? sel.events.filter(e => e.cash !== false && e.dir > 0) : [],
    // Card statement payments get their own "Payment due" section below, so
    // exclude them here to avoid showing the same payment twice.
    debit:  sel ? sel.events.filter(e => e.cash !== false && e.dir < 0 && e.kind !== 'card') : [],
    credit: sel ? sel.events.filter(e => e.cash === false) : [],
  };
  const selCardDue = sel && show.due ? (sel.cardDue || []) : [];

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">Money Calendar</h2>
          <p className="text-xs text-slate-400 mt-0.5">Each day's money — <span className="text-emerald-400">income</span> · <span className="text-rose-400">debit (cash out)</span> · <span className="text-orange-400">credit (card spend)</span> · <span className="text-violet-400">card payment due</span> — with your projected cash &amp; net worth, anchored to the forecast.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prev} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-semibold w-32 text-center">{monthLabel}</span>
          <button onClick={next} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={goToday} className="text-xs px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300">Today</button>
        </div>
      </div>

      {/* channel toggles */}
      <div className="flex items-center gap-2 flex-wrap">
        {['income', 'debit', 'credit'].map(k => {
          const ch = CH[k]; const on = show[k];
          return (
            <button key={k} onClick={() => setShow(p => ({ ...p, [k]: !p[k] }))} className={`text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition ${on ? ch.chip : 'bg-slate-800 text-slate-500'}`}>
              <ch.Icon className="w-3.5 h-3.5" /> {ch.label}
            </button>
          );
        })}
        <button onClick={() => setShow(p => ({ ...p, due: !p.due }))} className={`text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition ${show.due ? 'bg-violet-500/15 text-violet-300' : 'bg-slate-800 text-slate-500'}`}>
          <Landmark className="w-3.5 h-3.5" /> Card due
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* grid */}
        <div className="lg:col-span-2 bg-slate-900 rounded-xl p-3 border border-slate-800">
          <div className="grid grid-cols-7 gap-1 mb-1">
            {CAL_WEEKDAYS.map(d => <div key={d} className="text-center text-[11px] font-medium text-slate-500 py-1">{d}</div>)}
          </div>
          <div className="space-y-1">
            {rows.map((row, ri) => (
              <div key={ri} className="grid grid-cols-7 gap-1">
                {row.map((c, ci) => {
                  if (!c.inMonth) return <div key={ci} />;
                  const selected = c.ds === selDs;
                  const cls = [
                    'rounded-lg p-1.5 text-left transition border min-h-[90px] flex flex-col overflow-hidden',
                    'bg-slate-800/40 border-slate-800 hover:border-slate-600',
                    c.below ? 'bg-rose-500/10 border-rose-500/40' : '',
                    c.isLow && !c.below ? 'border-amber-500/40' : '',
                    c.isToday ? 'ring-1 ring-cyan-500' : '',
                    selected ? 'ring-2 ring-emerald-500 border-emerald-500/40' : '',
                  ].join(' ');
                  return (
                    <button key={ci} onClick={() => setSelDs(c.ds)} className={cls}>
                      <span className={`text-xs ${c.isToday ? 'font-bold text-cyan-400' : 'text-slate-300'}`}>{c.day}</span>
                      <div className="leading-tight font-semibold mt-0.5 space-y-px">
                        {show.income && c.cashIn > 0.5 && <div className="flex items-center gap-0.5 text-[10px] text-emerald-400 truncate"><ArrowUpRight className="w-3 h-3 flex-shrink-0" />+{kfmt(c.cashIn)}</div>}
                        {show.debit && c.cashOut > 0.5 && <div className="flex items-center gap-0.5 text-[10px] text-rose-400 truncate"><ArrowDownRight className="w-3 h-3 flex-shrink-0" />−{kfmt(c.cashOut)}</div>}
                        {show.credit && c.creditOut > 0.5 && <div className="flex items-center gap-0.5 text-[10px] text-orange-400 truncate"><CreditCard className="w-3 h-3 flex-shrink-0" />{kfmt(c.creditOut)}</div>}
                        {show.due && c.cardDue.length > 0 && (() => {
                          const known = c.cardDue.reduce((s, d) => s + (d.amountBase || 0), 0);
                          const allPaid = c.cardDue.every(d => d.paid);
                          return <div className={`flex items-center gap-0.5 text-[10px] truncate ${allPaid ? 'text-slate-500' : 'text-violet-400'}`} title="Card payment due"><Landmark className="w-3 h-3 flex-shrink-0" />{allPaid ? 'paid' : (known > 0.5 ? kfmt(known) : `${c.cardDue.length} due`)}</div>;
                        })()}
                      </div>
                      <div className="mt-auto pt-0.5 flex items-center justify-between gap-1 text-[9px]">
                        {showCash && <span className={`flex items-center gap-0.5 min-w-0 ${c.below ? 'text-rose-400 font-semibold' : 'text-slate-100'}`} title={`Cash ${fmtMoney(c.balance, baseCurrency)}`}><Wallet className="w-2.5 h-2.5 flex-shrink-0" />{kfmt(c.balance)}</span>}
                        {showNW && <span className="flex items-center gap-0.5 min-w-0 text-slate-100" title={`Net worth ${fmtMoney(c.nwBalance, baseCurrency)}`}><TrendingUp className="w-2.5 h-2.5 flex-shrink-0" />{kfmt(c.nwBalance)}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* day detail — sticky */}
        <div className="lg:col-span-1 lg:sticky lg:top-4 bg-slate-900 rounded-xl p-5 border border-slate-800">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="text-sm font-semibold text-slate-200">{selLabel}</h3>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${sel && sel.isPast ? 'bg-slate-800 text-slate-400' : sel && sel.isToday ? 'bg-cyan-500/15 text-cyan-300' : 'bg-emerald-500/15 text-emerald-300'}`}>{sel && sel.isPast ? 'Actual' : sel && sel.isToday ? 'Today' : 'Projected'}</span>
          </div>
          {sel && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-slate-800/40 rounded-lg p-3">
                  <div className="text-[11px] text-cyan-400/80">Cash balance</div>
                  <div className={`text-lg font-bold ${sel.below ? 'text-rose-400' : ''}`}>{fmtMoney(sel.balance, baseCurrency)}</div>
                </div>
                <div className="bg-slate-800/40 rounded-lg p-3">
                  <div className="text-[11px] text-emerald-400/80">Net worth</div>
                  <div className="text-lg font-bold">{fmtMoney(sel.nwBalance, baseCurrency)}</div>
                </div>
              </div>
              {(['income', 'debit', 'credit'].every(k => !show[k] || selGroups[k].length === 0) && selCardDue.length === 0) ? (
                <div className="text-slate-500 text-sm py-2">Nothing scheduled. Everyday spending is smoothed into the balance.</div>
              ) : (
                <div className="space-y-4">
                  {['income', 'debit', 'credit'].map(k => {
                    if (!show[k] || selGroups[k].length === 0) return null;
                    const ch = CH[k];
                    const total = selGroups[k].reduce((sum, e) => sum + e.amount, 0);
                    return (
                      <div key={k}>
                        <div className="flex items-center justify-between text-[11px] mb-1.5">
                          <span className={`flex items-center gap-1 font-medium ${ch.color}`}><ch.Icon className="w-3.5 h-3.5" />{ch.label}</span>
                          <span className={`font-semibold ${ch.color}`}>{ch.sign}{fmtMoney(total, baseCurrency)}</span>
                        </div>
                        <div className="space-y-1.5">
                          {selGroups[k].map((e, i) => (
                            <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/40">
                              <span className="text-sm truncate min-w-0">{e.name}</span>
                              <span className={`text-sm flex-shrink-0 ml-2 ${k === 'income' ? 'text-emerald-400 font-semibold' : k === 'credit' ? 'text-orange-400' : 'text-slate-200'}`}>{ch.sign}{fmtMoney(e.amount, baseCurrency)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {selCardDue.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between text-[11px] mb-1.5">
                        <span className="flex items-center gap-1 font-medium text-violet-400"><Landmark className="w-3.5 h-3.5" />Card payment due</span>
                      </div>
                      <div className="space-y-1.5">
                        {selCardDue.map((d, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/40">
                            <span className="text-sm truncate min-w-0 flex items-center gap-1.5">{d.cardName}{d.paid && <span className="text-[10px] text-emerald-400 flex items-center gap-0.5 flex-shrink-0"><CheckCircle2 className="w-3 h-3" />paid</span>}</span>
                            <span className="text-sm flex-shrink-0 ml-2">
                              {d.hasStatement && d.amount != null
                                ? <span className={d.paid ? 'text-slate-400' : 'text-violet-300 font-semibold'}>{fmtMoney(d.amount, d.currency)}</span>
                                : <span className="text-slate-500 text-xs italic">no statement yet</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className={`rounded-xl p-4 border ${s.safeToSpend != null && s.safeToSpend <= 0 ? 'bg-rose-500/10 border-rose-500/30' : 'bg-emerald-500/10 border-emerald-500/25'}`}>
          <div className="text-xs text-slate-300">Safe to spend now</div>
          <div className={`text-xl font-bold mt-1 truncate ${s.safeToSpend != null && s.safeToSpend <= 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{s.safeToSpend != null ? fmtMoney(s.safeToSpend, baseCurrency) : '—'}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">extra, and stay above buffer through {monthLabel.split(' ')[0]}</div>
        </div>
        <div className={`rounded-xl p-4 border ${lowBelow ? 'bg-rose-500/10 border-rose-500/30' : 'bg-slate-900 border-slate-800'}`}>
          <div className="text-xs text-slate-400">Lowest projected</div>
          <div className={`text-xl font-bold mt-1 truncate ${lowBelow ? 'text-rose-400' : ''}`}>{s.lowBalance != null ? fmtMoney(s.lowBalance, baseCurrency) : '—'}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{s.lowDate ? `on ${lowDateLabel}${lowBelow ? ' · below buffer' : ''}` : 'no projection'}</div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="text-xs text-slate-400">Money in</div>
          <div className="text-xl font-bold mt-1 text-emerald-400 truncate">+{fmtMoney(s.monthIn, baseCurrency)}</div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="text-xs text-slate-400">Money out <span className="text-slate-600">(cash)</span></div>
          <div className="text-xl font-bold mt-1 text-rose-400 truncate">−{fmtMoney(Math.abs(s.monthOut), baseCurrency)}</div>
          {s.monthCredit > 0.5 && <div className="text-[11px] text-orange-400 mt-0.5 truncate">💳 {fmtMoney(s.monthCredit, baseCurrency)} charged to cards</div>}
        </div>
      </div>

      {/* balance + net worth strip — below the calendar */}
      <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <span className="text-[11px] text-slate-500">Projected through {monthLabel}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCash(v => !v)} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded ${showCash ? 'bg-cyan-500/15 text-cyan-300' : 'bg-slate-800 text-slate-500'}`}><Wallet className="w-3.5 h-3.5" /> Cash</button>
            <button onClick={() => setShowNW(v => !v)} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded ${showNW ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-800 text-slate-500'}`}><TrendingUp className="w-3.5 h-3.5" /> Net Worth</button>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={130}>
          <AreaChart data={stripData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="calBal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.32} />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="day" stroke="#64748b" fontSize={10} interval={4} tickLine={false} />
            <YAxis stroke="#64748b" fontSize={10} width={40} tickFormatter={(v) => Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
            <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} labelFormatter={(l) => `${monthLabel.split(' ')[0]} ${l}`} formatter={(v, n) => [fmtMoney(v, baseCurrency, false), n === 'balance' ? 'Cash' : 'Net Worth']} />
            {showCash && <Area type="monotone" dataKey="balance" stroke="#06b6d4" strokeWidth={2} fill="url(#calBal)" />}
            {showNW && <Area type="monotone" dataKey="netWorth" stroke="#10b981" strokeWidth={2} fill="transparent" dot={false} />}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="text-[11px] text-slate-500 leading-relaxed px-1">
        Up to today everything is your actual logged activity; from tomorrow it's projected from recurring rules, planned txs, and card statement due-dates, with everyday spending smoothed so each month-end matches the Dashboard forecast. Credit (card) purchases are shown for context but only hit cash when their statement is paid. Net worth (accrual) uses your daily snapshots in the past and the forecast ahead. Days where projected cash falls below your buffer are flagged red.
      </div>
    </div>
  );
}

// ============ SETTINGS (M2.5+) — category management ============
// First piece of a Settings surface. Lets the user add / rename / delete
// categories beyond the seeded defaults. Grouped by kind; transfer kind has
// exactly one row (the seeded "Transfer") and isn't user-editable.
function Settings({ categories = [], smsTokens = [], timezone = 'Asia/Dubai', onSaveTimezone, onCreateSmsToken, onRevokeSmsToken, onSaveCategory, onDeleteCategory, onRefreshFx }) {
  // Full IANA zone list where supported; fall back to a curated set otherwise.
  const tzList = (() => {
    try { return (Intl as any).supportedValuesOf('timeZone'); } catch { return null; }
  })() || ['Asia/Dubai', 'Asia/Riyadh', 'Asia/Karachi', 'Asia/Kolkata', 'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles', 'UTC'];
  const deviceTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; } })();
  const SMS_ENDPOINT = `${(import.meta as any).env?.VITE_SUPABASE_URL || ''}/functions/v1/sms-ingest`;
  const [smsLabel, setSmsLabel] = useState('');
  const [smsCreating, setSmsCreating] = useState(false);
  const [revealedToken, setRevealedToken] = useState(null);
  const handleCreateSmsToken = async () => {
    setSmsCreating(true);
    try {
      const t = await onCreateSmsToken(smsLabel.trim() || 'iPhone');
      setSmsLabel('');
      if (t?.token) setRevealedToken({ id: t.id, token: t.token, label: t.label });
    } catch (e) { toast('Failed to create token: ' + (e?.message || String(e))); }
    finally { setSmsCreating(false); }
  };

  // (Bulk SMS import moved into the top-bar Import Transactions button —
  // it now uses a preview-edit-confirm flow with an editable table. The
  // Settings panel just manages tokens.)
  const live = (categories || []).filter((c) => !c.deletedAt);
  const byKind = {
    expense: live.filter((c) => c.kind === 'expense').sort((a, b) => a.name.localeCompare(b.name)),
    income: live.filter((c) => c.kind === 'income').sort((a, b) => a.name.localeCompare(b.name)),
    transfer: live.filter((c) => c.kind === 'transfer'),
  };
  const [editing, setEditing] = useState(null);
  const [showFor, setShowFor] = useState(null); // kind or null
  const [trashOpen, setTrashOpen] = useState(false);
  const [trash, setTrash] = useState({}); // prefix -> rows[]
  const [trashLoading, setTrashLoading] = useState(false);
  const TRASH_PREFIXES = [
    { prefix: 'tx:', label: 'Transactions', titleField: 'description' },
    { prefix: 'acc:', label: 'Accounts', titleField: 'name' },
    { prefix: 'card:', label: 'Cards', titleField: 'name' },
    { prefix: 'loan:', label: 'Loans', titleField: 'name' },
    { prefix: 'asset:', label: 'Assets', titleField: 'name' },
    { prefix: 'inv:', label: 'Investments', titleField: 'symbol' },
    { prefix: 'rec:', label: 'Recurring rules', titleField: 'description' },
    { prefix: 'bud:', label: 'Budgets', titleField: 'category' },
    { prefix: 'cat:', label: 'Categories', titleField: 'name' },
    { prefix: 'ci:', label: 'Installments', titleField: 'name' },
    { prefix: 'sg:', label: 'Savings goals', titleField: 'name' },
  ];
  const openTrash = async () => {
    setTrashLoading(true);
    setTrashOpen(true);
    const results = {};
    for (const { prefix } of TRASH_PREFIXES) {
      results[prefix] = await listDeletedByPrefix(prefix);
    }
    setTrash(results);
    setTrashLoading(false);
  };
  const restoreRow = async (prefix, row) => {
    await dataStore.set(`${prefix}${row.id}`, { ...row, deletedAt: null });
    setTrash((prev) => ({ ...prev, [prefix]: prev[prefix].filter((r) => r.id !== row.id) }));
    toast(`Restored. Reload the page to see it back in the live lists.`);
  };
  const hardDelete = async (prefix, row) => {
    if (!(await confirmDialog(`Permanently delete? This can't be undone.`, { danger: true, confirmLabel: 'Delete' }))) return;
    await dataStore.remove(`${prefix}${row.id}`);
    setTrash((prev) => ({ ...prev, [prefix]: prev[prefix].filter((r) => r.id !== row.id) }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold">Settings</h2>
        <p className="text-sm text-slate-400">Manage the lists Floosi pulls from when you log a transaction.</p>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 space-y-3">
        <h3 className="text-sm font-semibold">Timezone</h3>
        <p className="text-xs text-slate-400">
          Your day boundary. The daily net-worth snapshot is taken at <strong>23:59</strong> your time (end-of-day close), and new-day work (recurring transactions, installment payments, FX refresh) runs at <strong>00:01</strong> your time. Set this to wherever you are.
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <select
            value={timezone}
            onChange={(e) => onSaveTimezone?.(e.target.value)}
            className="input sm:max-w-xs text-sm"
          >
            {!tzList.includes(timezone) && <option value={timezone}>{timezone}</option>}
            {tzList.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
          {deviceTz && deviceTz !== timezone && (
            <button
              onClick={() => onSaveTimezone?.(deviceTz)}
              className="px-2.5 h-9 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs whitespace-nowrap"
            >
              Use device ({deviceTz})
            </button>
          )}
        </div>
        <div className="text-[11px] text-slate-500">
          Now in {timezone}: {(() => { try { return new Intl.DateTimeFormat('en-GB', { timeZone: timezone, dateStyle: 'medium', timeStyle: 'short' }).format(new Date()); } catch { return '—'; } })()}
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">SMS Auto-import</h3>
          <span className="text-[10px] text-slate-500 font-mono">{SMS_ENDPOINT.replace(/^https:\/\//, '')}</span>
        </div>
        <p className="text-xs text-slate-400">
          Generate a token, paste it into an iOS Shortcut that fires on bank SMS. Each SMS hits the endpoint and lands as a transaction with an SMS badge. Foreign-currency txs use the bank's reported available limit to derive the AED amount.
        </p>
        {revealedToken && (
          <div className="text-xs rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
            <div className="text-amber-300 font-semibold">Copy this token now — it won't be shown again.</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-950 rounded px-2 py-1.5 font-mono text-[11px] break-all">{revealedToken.token}</code>
              <button
                onClick={async () => { try { await navigator.clipboard.writeText(revealedToken.token); toast('Copied.'); } catch { toast('Copy failed — select and copy manually.'); } }}
                className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-xs"
              >Copy</button>
              <button
                onClick={() => setRevealedToken(null)}
                className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-xs"
              >Done</button>
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={smsLabel}
            onChange={(e) => setSmsLabel(e.target.value)}
            placeholder="Label (e.g. iPhone, Backup)"
            className="input flex-1 text-sm"
          />
          <button
            onClick={handleCreateSmsToken}
            disabled={smsCreating}
            className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700"
          >
            {smsCreating ? 'Creating…' : 'Generate token'}
          </button>
        </div>
        {smsTokens.length === 0 ? (
          <div className="text-xs text-slate-500 italic">No tokens yet.</div>
        ) : (
          <div className="space-y-1.5">
            {smsTokens.map((t) => (
              <div key={t.id} className="flex items-center justify-between bg-slate-800/40 rounded px-2 py-1.5 text-xs">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{t.label}</div>
                  <div className="text-slate-500 text-[10px]">
                    Created {t.createdAt?.slice(0, 10) || '—'}
                    {t.lastUsedAt ? ` · last used ${t.lastUsedAt.slice(0, 10)}` : ' · never used'}
                  </div>
                </div>
                <button
                  onClick={async () => { if (await confirmDialog(`Revoke "${t.label}"? Any Shortcut using this token will stop working.`)) onRevokeSmsToken(t.id); }}
                  className="ml-2 px-2 py-1 rounded hover:bg-rose-900/40 text-rose-400"
                >Revoke</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {(['expense', 'income'] as const).map((kind) => (
        <div key={kind} className="bg-slate-900 rounded-xl border border-slate-800 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold capitalize">{kind} categories</h3>
            <button onClick={() => { setEditing(null); setShowFor(kind); }} className="px-2 py-1 text-xs rounded bg-slate-800 hover:bg-slate-700">
              <Plus className="inline w-3 h-3" /> Add
            </button>
          </div>
          {byKind[kind].length === 0 ? (
            <div className="text-xs text-slate-500 italic">No custom categories yet — defaults are seeded for new users.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-1.5">
              {byKind[kind].map((c) => (
                <div key={c.id} className="flex items-center justify-between bg-slate-800/40 rounded px-2 py-1.5">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="w-5 h-5 rounded flex items-center justify-center text-sm" style={{ backgroundColor: '#64748b30' }}>{c.icon || '📦'}</span>
                    <span className="text-sm truncate">{c.name}</span>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => { setEditing(c); setShowFor(c.kind); }} className="p-1 hover:bg-slate-700 rounded" title="Edit">
                      <Edit2 className="w-3 h-3 text-slate-400" />
                    </button>
                    <button onClick={async () => { if (await confirmDialog(`Delete category "${c.name}"?`)) onDeleteCategory(c.id); }} className="p-1 hover:bg-slate-700 rounded" title="Delete">
                      <Trash2 className="w-3 h-3 text-rose-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">FX rates</h3>
          <button onClick={onRefreshFx} className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700">Refresh from DB</button>
        </div>
        <p className="text-xs text-slate-400">Refetches public.v_latest_fx. Currency conversions update on the next render. Rates seeded on M3; rotate as needed manually.</p>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Trash</h3>
          <button onClick={openTrash} className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700">{trashOpen ? 'Refresh' : 'Open'}</button>
        </div>
        {!trashOpen ? (
          <p className="text-xs text-slate-400">Soft-deleted rows live forever — open to inspect and restore or permanently delete.</p>
        ) : trashLoading ? (
          <div className="text-xs text-slate-500 italic">Loading…</div>
        ) : (
          <div className="space-y-3 max-h-[480px] overflow-auto">
            {TRASH_PREFIXES.map(({ prefix, label, titleField }) => {
              const rows = trash[prefix] || [];
              if (rows.length === 0) return null;
              return (
                <div key={prefix}>
                  <div className="text-xs font-medium text-slate-400 mb-1">{label} ({rows.length})</div>
                  <div className="space-y-1">
                    {rows.map((row) => (
                      <div key={row.id} className="flex items-center justify-between bg-slate-800/40 rounded px-2 py-1.5 text-xs">
                        <div className="min-w-0 flex-1 truncate">
                          <span className="text-slate-300">{row[titleField] || row.id.slice(0, 8)}</span>
                          <span className="text-slate-500 ml-2">deleted {row.deletedAt?.slice(0, 10)}</span>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => restoreRow(prefix, row)} className="px-2 py-0.5 rounded bg-emerald-600/70 hover:bg-emerald-500 text-white">Restore</button>
                          <button onClick={() => hardDelete(prefix, row)} className="px-2 py-0.5 rounded bg-rose-600/70 hover:bg-rose-500 text-white">Forever</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {Object.values(trash).every((rs) => (rs || []).length === 0) && (
              <div className="text-xs text-slate-500 italic">Trash is empty.</div>
            )}
          </div>
        )}
      </div>

      <div className="bg-slate-900/40 rounded-xl border border-slate-800 p-4 text-xs text-slate-500">
        <p className="font-medium text-slate-400 mb-1">A few notes</p>
        <ul className="space-y-1 list-disc list-inside">
          <li>Deleting a category soft-deletes it — transactions and budgets that reference it stay intact; the picker just hides it going forward.</li>
          <li>Default categories are seeded once when the user is created. Re-adding doesn't duplicate (the DB has a partial unique on name+kind).</li>
          <li>"Transfer" is a single, fixed system category and not user-editable.</li>
          <li>Restoring from Trash needs a page reload to surface back in the live lists — the in-memory state already excluded the row.</li>
        </ul>
      </div>

      {showFor && (
        <CategoryModal
          category={editing}
          kind={showFor}
          onSave={async (c) => { await onSaveCategory(c); }}
          onClose={() => { setShowFor(null); setEditing(null); }}
        />
      )}

    </div>
  );
}

function CategoryModal({ category, kind, onSave, onClose }) {
  const [form, setForm] = useState(category || { id: uid('cat'), kind, name: '', color: '#64748b', icon: '📦' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      await onSave({ ...form, name: form.name.trim(), kind });
      onClose();
    } catch (e) { toast('Save failed.'); } finally { setSaving(false); }
  };

  return (
    <Modal title={category ? `Edit ${kind} category` : `New ${kind} category`} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-5 gap-2">
          <Field label="Icon" className="col-span-1">
            <input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="input text-center" maxLength={2} />
          </Field>
          <Field label="Name" className="col-span-4">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="e.g. Coffee" autoFocus />
          </Field>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name?.trim()} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function MiniStatCard({ label, value, positive, negative }) {
  const color = negative ? 'text-rose-400' : positive ? 'text-slate-100' : 'text-slate-400';
  return (
    <div className="bg-slate-900 rounded-xl p-3 border border-slate-800 min-w-0">
      <div className="text-xs text-slate-400 truncate">{label}</div>
      <div className={`text-base font-bold whitespace-nowrap ${color}`}>{negative ? '−' : ''}{value}</div>
    </div>
  );
}

// Shared "top of tab" summary card (Cards / Debt / Assets / Investments /
// Recurring …). One definition so heights/typography stay consistent forever.
function StatCard({ label, value, valueClass = '' }) {
  return (
    <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 min-w-0">
      <div className="text-xs text-slate-400 truncate">{label}</div>
      <div className={`text-base font-bold whitespace-nowrap ${valueClass}`}>{value}</div>
    </div>
  );
}

function KPICard({ label, value, icon: Icon, accent, sub }) {
  const accents = {
    emerald: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/20 text-emerald-400',
    cyan: 'from-cyan-500/20 to-cyan-500/5 border-cyan-500/20 text-cyan-400',
    rose: 'from-rose-500/20 to-rose-500/5 border-rose-500/20 text-rose-400',
    amber: 'from-amber-500/20 to-amber-500/5 border-amber-500/20 text-amber-400',
  };
  return (
    <div className={`bg-gradient-to-br ${accents[accent]} rounded-xl p-4 border`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-300">{label}</span>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-xl font-bold text-white truncate">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1 truncate">{sub}</div>}
    </div>
  );
}

// ============ TRANSACTIONS ============
function Transactions({ transactions, accounts, cards, loans = [], categories = [], onEdit, onDelete, onMarkIntentional, onToggleFlag, onConvertToEpp, reimbursementSettlements = [], onSaveSettlement, onDeleteSettlement, baseCurrency, initialFilter = null, onClearFilter }) {
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCat, setFilterCat] = useState('all');
  const [filterAcc, setFilterAcc] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterReimb, setFilterReimb] = useState('all'); // all | reimbursable | outstanding | settled | reimbursement
  // Debounced search — keystroke at a time on a 10k tx list was tanking the
  // Transactions tab. 200ms is below perceptual threshold.
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterResetKey, setFilterResetKey] = useState(0); // bump to force date inputs to remount on reset
  const [showFilters, setShowFilters] = useState(false);    // filter panel expand toggle
  const [confirmDelete, setConfirmDelete] = useState(null);
  // M2.5 Session D: matchFor holds the reimbursable expense tx we're settling
  // against an income, or the income tx we're applying to expenses.
  const [matchFor, setMatchFor] = useState(null);

  const { dupeIds: dupeIdSet, groups: dupeGroups } = useMemo(() => computeDuplicateTxGroups(transactions), [transactions]);
  const clusterFor = (id) => dupeGroups.find(g => g.includes(id)) || [id];
  const filtered = useMemo(() => {
    let result = transactions.filter(t => {
      if (filterType !== 'all' && t.type !== filterType) return false;
      if (filterCat === '__uncat__') {
        if ((t.type !== 'expense' && t.type !== 'income') || t.category) return false;
      } else if (filterCat !== 'all' && t.category !== filterCat) return false;
      if (filterAcc !== 'all') {
        // For transfers, match if filterAcc is either source or destination
        if (t.type === 'transfer') {
          if (t.fromAccount !== filterAcc && t.toAccount !== filterAcc) return false;
        } else if (t.account !== filterAcc) return false;
      }
      if (filterSource === 'recurring' && !t.recurringId) return false;
      if (filterSource === 'manual' && (t.recurringId || t.smsRawId)) return false;
      if (filterSource === 'sms' && !t.smsRawId) return false;
      if (filterSource === 'sms_review' && !t.smsNeedsReview) return false;
      if (filterSource === 'fx_estimated' && !t.fxEstimated) return false;
      if (filterSource === 'duplicate' && !dupeIdSet.has(t.id)) return false;
      if (filterSource === 'flagged' && !t.flagged) return false;
      if (filterSource === 'unverified' && t.linkedStatementId) return false;
      if (filterReimb !== 'all') {
        const remaining = (Number(t.amount) || 0) - (Number(t.reimbursedAmount) || 0);
        if (filterReimb === 'reimbursable' && !t.isReimbursable) return false;
        if (filterReimb === 'outstanding' && !(t.isReimbursable && remaining > 0.0001)) return false;
        if (filterReimb === 'settled'     && !(t.isReimbursable && remaining <= 0.0001)) return false;
        if (filterReimb === 'reimbursement' && !t.isReimbursement) return false;
      }
      if (dateFrom && t.date < dateFrom) return false;
      if (dateTo && t.date > dateTo) return false;
      if (search && !`${t.description} ${t.notes || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });

    // Sort
    const sorted = [...result];
    switch (sortBy) {
      case 'date_desc': sorted.sort((a, b) => cmpDateStr(b.date, a.date)); break;
      case 'date_asc': sorted.sort((a, b) => cmpDateStr(a.date, b.date)); break;
      case 'amount_desc': sorted.sort((a, b) => toBase(b.amount, b.currency) - toBase(a.amount, a.currency)); break;
      case 'amount_asc': sorted.sort((a, b) => toBase(a.amount, a.currency) - toBase(b.amount, b.currency)); break;
      case 'name_asc': sorted.sort((a, b) => (a.description || '').localeCompare(b.description || '')); break;
      case 'name_desc': sorted.sort((a, b) => (b.description || '').localeCompare(a.description || '')); break;
    }
    return sorted;
  }, [transactions, search, filterType, filterCat, filterAcc, filterSource, filterReimb, dateFrom, dateTo, sortBy, dupeIdSet]);

  // Debounce: only update the actual filter key once 200ms has elapsed
  // since the last keystroke. Keeps the heavy filter/sort off the keypress.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  // A notification can deep-link here with a filter pre-applied (e.g. "needs
  // review" or "uncategorised") so the user lands on exactly those rows.
  // Reset everything else to a clean slate, apply the requested filter, then
  // tell the parent to drop the request so it doesn't re-fire on re-render.
  useEffect(() => {
    if (!initialFilter) return;
    setSearchInput(''); setSearch(''); setFilterType('all'); setFilterCat('all');
    setFilterAcc('all'); setFilterSource('all'); setFilterReimb('all');
    setDateFrom(''); setDateTo(''); setSortBy('date_desc');
    if (initialFilter.source) setFilterSource(initialFilter.source);
    if (initialFilter.cat) setFilterCat(initialFilter.cat);
    if (initialFilter.reimb) setFilterReimb(initialFilter.reimb);
    if (initialFilter.card) setFilterAcc(initialFilter.card);
    if (initialFilter.showFilters) setShowFilters(true);
    onClearFilter?.();
  }, [initialFilter]);

  const totals = useMemo(() => {
    const inc = filtered.filter(t => t.type === 'income').reduce((s, t) => s + toBase(t.amount, t.currency), 0);
    const exp = filtered.filter(t => t.type === 'expense').reduce((s, t) => s + (t.isRefund ? -toBase(t.amount, t.currency) : toBase(t.amount, t.currency)), 0);
    return { inc, exp };
  }, [filtered]);

  const clearFilters = () => {
    setSearchInput(''); setSearch(''); setFilterType('all'); setFilterCat('all'); setFilterAcc('all');
    setFilterSource('all'); setFilterReimb('all'); setDateFrom(''); setDateTo(''); setSortBy('date_desc');
    setFilterResetKey(k => k + 1);
  };

  const hasActiveFilters = search || filterType !== 'all' || filterCat !== 'all' || filterAcc !== 'all'
    || filterSource !== 'all' || dateFrom || dateTo || sortBy !== 'date_desc';

  const handleDelete = async (tx) => {
    if (tx.recurringId) {
      // Confirm deletion of a single occurrence from a recurring rule
      const ok = await confirmDialog('This is a generated occurrence of a recurring rule. Deleting only removes this one instance — the rule will still generate future ones, and may regenerate this one on next load. To stop the rule entirely, edit it in the Recurring tab. Proceed?', { confirmLabel: 'Delete this one' });
      if (!ok) { setConfirmDelete(null); return; }
    }
    await onDelete(tx.id);
    setConfirmDelete(null);
  };

  // Human labels for chips (the <select> option text lives in JSX; mirror it here).
  const SOURCE_LABELS = { manual: 'Manual', recurring: 'Recurring', sms: 'SMS', sms_review: 'SMS — needs review', fx_estimated: 'FX-estimated (≈)', duplicate: 'Possible duplicates', flagged: '⚑ Flagged' };
  const REIMB_LABELS = { reimbursable: 'Any reimbursable', outstanding: 'Outstanding', settled: 'Fully settled', reimbursement: 'Reimbursement income' };
  // Active, removable filter chips (search + sort are handled elsewhere).
  const activeChips = [];
  if (filterType !== 'all') activeChips.push({ key: 'type', label: filterType.charAt(0).toUpperCase() + filterType.slice(1), clear: () => setFilterType('all') });
  if (filterCat !== 'all') activeChips.push({ key: 'cat', label: filterCat === '__uncat__' ? 'Uncategorised' : filterCat, clear: () => setFilterCat('all') });
  if (filterAcc !== 'all') activeChips.push({ key: 'acc', label: filterAcc, clear: () => setFilterAcc('all') });
  if (filterSource !== 'all') activeChips.push({ key: 'src', label: SOURCE_LABELS[filterSource] || filterSource, clear: () => setFilterSource('all') });
  if (filterReimb !== 'all') activeChips.push({ key: 'reimb', label: REIMB_LABELS[filterReimb] || filterReimb, clear: () => setFilterReimb('all') });
  if (dateFrom) activeChips.push({ key: 'from', label: `From ${dateFrom}`, clear: () => { setDateFrom(''); setFilterResetKey(k => k + 1); } });
  if (dateTo) activeChips.push({ key: 'to', label: `To ${dateTo}`, clear: () => { setDateTo(''); setFilterResetKey(k => k + 1); } });

  // ONE source of truth for every filter control's size/shape. Height is
  // pinned explicitly (h-9) — NOT derived from padding — because a <select>
  // and a native <input type="date"> resolve identical padding to different
  // pixel heights. appearance-none on dates strips the taller native control.
  // Change the height here once and every control (selects + dates) follows.
  const FIELD_BASE = 'h-9 box-border w-full min-w-0 text-xs rounded-lg px-2.5 border outline-none transition-colors';
  const selCls = (active: boolean) =>
    `${FIELD_BASE} cursor-pointer ${active ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-300' : 'bg-slate-800 border-slate-700 text-slate-300'}`;
  const dateCls = (val: string) =>
    `${FIELD_BASE} appearance-none ${val ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-300' : 'bg-slate-800 border-slate-700 text-slate-400'}`;

  return (
    <div className="space-y-4">
      <div className="bg-slate-900 rounded-xl border border-slate-800">

        {/* Search + filter toggle */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700/60 rounded-t-xl overflow-hidden">
          <Search className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search description or notes…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-600"
          />
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-300 flex-shrink-0 transition-colors">
              Clear all
            </button>
          )}
          <button
            onClick={() => setShowFilters(s => !s)}
            className={`flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 border flex-shrink-0 transition-colors ${showFilters || activeChips.length ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-300' : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'}`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Filters
            {activeChips.length > 0 && (
              <span className="min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-semibold flex items-center justify-center">{activeChips.length}</span>
            )}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Active filter chips */}
        {activeChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 px-4 py-2.5 border-b border-slate-800">
            {activeChips.map(c => (
              <button
                key={c.key}
                onClick={c.clear}
                title="Remove filter"
                className="group flex items-center gap-1 text-xs rounded-full pl-2.5 pr-1.5 py-1 bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/20 transition-colors"
              >
                <span className="truncate max-w-[160px]">{c.label}</span>
                <X className="w-3 h-3 text-cyan-300/70 group-hover:text-cyan-100" />
              </button>
            ))}
          </div>
        )}

        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2.5 px-4 py-3 border-b border-slate-800">
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">Type</span>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} className={selCls(filterType !== 'all')}>
                <option value="all">All</option>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
                <option value="transfer">Transfer</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">Category</span>
              <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className={selCls(filterCat !== 'all')}>
                <option value="all">All</option>
                <option value="__uncat__">Uncategorised</option>
                {(categories.length ? categories.filter(c => !c.deletedAt) : ALL_CATEGORIES).map(c => <option key={c.id || c.name}>{c.name}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">Account / Card</span>
              <select value={filterAcc} onChange={e => setFilterAcc(e.target.value)} className={selCls(filterAcc !== 'all')}>
                <option value="all">All</option>
                <optgroup label="Accounts">
                  {accounts.map(a => <option key={`acc-${a.id}`} value={a.name}>{a.name}</option>)}
                </optgroup>
                {cards && cards.length > 0 && (
                  <optgroup label="Cards">
                    {cards.map(c => <option key={`card-${c.id}`} value={c.name}>{c.name}</option>)}
                  </optgroup>
                )}
              </select>
            </label>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">Source</span>
              <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className={selCls(filterSource !== 'all')}>
                <option value="all">All</option>
                <option value="manual">Manual</option>
                <option value="recurring">Recurring</option>
                <option value="sms">SMS</option>
                <option value="sms_review">SMS — needs review</option>
                <option value="fx_estimated">FX-estimated (≈)</option>
                <option value="duplicate">Possible duplicates</option>
                <option value="flagged">⚑ Flagged</option>
                <option value="unverified">Not on a statement</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">Reimbursement</span>
              <select value={filterReimb} onChange={e => setFilterReimb(e.target.value)} className={selCls(filterReimb !== 'all')}>
                <option value="all">All</option>
                <option value="reimbursable">Any reimbursable</option>
                <option value="outstanding">Outstanding</option>
                <option value="settled">Fully settled</option>
                <option value="reimbursement">Reimbursement income</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">From</span>
              <input key={`from-${filterResetKey}`} type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={dateCls(dateFrom)} />
            </label>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">To</span>
              <input key={`to-${filterResetKey}`} type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={dateCls(dateTo)} />
            </label>
            <label className="flex flex-col gap-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">Sort by</span>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className={selCls(sortBy !== 'date_desc')}>
                <option value="date_desc">Newest first</option>
                <option value="date_asc">Oldest first</option>
                <option value="amount_desc">Highest amount</option>
                <option value="amount_asc">Lowest amount</option>
                <option value="name_asc">Name A → Z</option>
                <option value="name_desc">Name Z → A</option>
              </select>
            </label>
          </div>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-4 px-4 py-2 text-xs text-slate-500">
          <span>{filtered.length === transactions.length ? `${transactions.length} transactions` : `${filtered.length} of ${transactions.length}`}</span>
          <span className="text-emerald-400">In: {fmtMoney(totals.inc, baseCurrency)}</span>
          <span className="text-rose-400">Out: {fmtMoney(totals.exp, baseCurrency)}</span>
          <span className={totals.inc - totals.exp >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
            Net: {fmtMoney(totals.inc - totals.exp, baseCurrency)}
          </span>
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">{transactions.length === 0 ? 'No transactions yet' : 'No transactions match your filters'}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {filtered.map(tx => (
              <div key={tx.id} className="p-3 hover:bg-slate-800/30 flex items-center justify-between gap-3 group">
                {confirmDelete === tx.id ? (
                  <div className="flex items-center gap-2 flex-1 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2">
                    <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                    <span className="text-sm flex-1 truncate">Delete "{tx.description}"?</span>
                    <button onClick={() => handleDelete(tx)} className="px-3 py-1 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">
                      Delete
                    </button>
                    <button onClick={() => setConfirmDelete(null)} className="px-3 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <TransactionRow tx={tx} baseCurrency={baseCurrency} loans={loans} categories={categories} />
                    <div className="flex gap-1 flex-shrink-0 items-center">
                      {dupeIdSet.has(tx.id) && (
                        <button
                          onClick={() => onMarkIntentional?.(clusterFor(tx.id))}
                          className="px-2 py-1 text-[10px] rounded bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 border border-rose-500/30"
                          title="Flagged as a possible duplicate. Click 'Keep both' to mark it intentional and stop flagging it — or use the trash to delete the accidental copy."
                        >
                          Keep both
                        </button>
                      )}
                      {tx.isReimbursable && (tx.amount || 0) - (tx.reimbursedAmount || 0) > 0.0001 && (
                        <button
                          onClick={() => setMatchFor(tx)}
                          className="px-2 py-1 text-[10px] rounded bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30"
                          title="Match a reimbursement income to this expense"
                        >
                          Match
                        </button>
                      )}
                      {tx.isReimbursement && (
                        <button
                          onClick={() => setMatchFor(tx)}
                          className="px-2 py-1 text-[10px] rounded bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 border border-emerald-500/30"
                          title="Apply this reimbursement to an outstanding expense"
                        >
                          Apply
                        </button>
                      )}
                      {tx.cardId && tx.type === 'expense' && !tx.isRefund && (
                        <button
                          onClick={() => onConvertToEpp?.(tx)}
                          className="p-1.5 rounded text-indigo-400 hover:bg-indigo-900/30"
                          title="Convert this charge into a card installment plan (EPP) — opens the plan form prefilled, and removes this transaction once the plan is saved."
                        >
                          <CreditCard className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => onToggleFlag?.(tx)}
                        className={`p-1.5 rounded ${tx.flagged ? 'text-amber-400 hover:bg-amber-900/30' : 'text-slate-500 hover:bg-slate-700 hover:text-slate-300'}`}
                        title={tx.flagged ? 'Flagged for review — click to unflag' : 'Flag for review'}
                      >
                        <Flag className="w-3.5 h-3.5" fill={tx.flagged ? 'currentColor' : 'none'} />
                      </button>
                      <button onClick={() => onEdit(tx)} className="p-1.5 hover:bg-slate-700 rounded" title="Edit">
                        <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                      <button onClick={() => setConfirmDelete(tx.id)} className="p-1.5 hover:bg-rose-900/50 rounded" title="Delete">
                        <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {matchFor && (
        <ReimbursementMatchModal
          anchor={matchFor}
          transactions={transactions}
          settlements={reimbursementSettlements}
          onSave={async (s) => { await onSaveSettlement(s); }}
          onDelete={async (id) => { await onDeleteSettlement(id); }}
          onClose={() => setMatchFor(null)}
          baseCurrency={baseCurrency}
        />
      )}
    </div>
  );
}

// M2.5 Session D: reimbursement-match modal. Anchor is whichever side the
// user clicked (a reimbursable expense or a reimbursement income); we surface
// the counterpart options on the other side, with each row showing the amount
// still available to allocate.
function ReimbursementMatchModal({ anchor, transactions, settlements, onSave, onDelete, onClose, baseCurrency }) {
  const liveTx = transactions.filter((t) => !t.deletedAt);
  const liveSettlements = settlements.filter((s) => !s.deletedAt);

  const isAnchorExpense = anchor.type === 'expense';

  // For each candidate counterpart tx, compute the amount still available:
  //   expense remaining = amount - reimbursed_amount
  //   income available  = amount - sum(this income's settlements so far)
  const incomeAllocated = (incomeId) =>
    liveSettlements.filter((s) => s.incomeTxId === incomeId).reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  const expenseRemaining = (tx) => (Number(tx.amount) || 0) - (Number(tx.reimbursedAmount) || 0);

  const candidates = useMemo(() => {
    if (isAnchorExpense) {
      // looking for income reimbursements with budget left
      return liveTx
        .filter((t) => t.type === 'income' && t.isReimbursement)
        .map((t) => ({ tx: t, available: (Number(t.amount) || 0) - incomeAllocated(t.id) }))
        .filter((c) => c.available > 0.0001);
    } else {
      // looking for reimbursable expenses with leftover
      return liveTx
        .filter((t) => t.type === 'expense' && t.isReimbursable)
        .map((t) => ({ tx: t, available: expenseRemaining(t) }))
        .filter((c) => c.available > 0.0001);
    }
  }, [liveTx, liveSettlements, isAnchorExpense]);

  // existing settlements on the anchor side
  const existing = liveSettlements.filter((s) =>
    isAnchorExpense ? s.expenseTxId === anchor.id : s.incomeTxId === anchor.id,
  );

  const [picked, setPicked] = useState({}); // counterpartId -> true. No partials:
  const [saving, setSaving] = useState(false);

  // Selecting a counterpart settles the EXPENSE in full (its whole remaining).
  // Partial reimbursements aren't a thing here — if an income covers less than
  // the expense, the leftover is out-of-pocket (an upgrade/extra), and any
  // income excess is simply ignored.
  const handleApply = async () => {
    setSaving(true);
    try {
      for (const [otherId, on] of Object.entries(picked)) {
        if (!on) continue;
        const expenseTx = isAnchorExpense ? anchor : liveTx.find((t) => t.id === otherId);
        if (!expenseTx) continue;
        const amount = Math.round(expenseRemaining(expenseTx) * 100) / 100;
        if (!(amount > 0)) continue;
        await onSave({
          incomeTxId: isAnchorExpense ? otherId : anchor.id,
          expenseTxId: isAnchorExpense ? anchor.id : otherId,
          amount,
        });
      }
      onClose();
    } catch (e) {
      toast('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isAnchorExpense ? `Match a reimbursement to "${anchor.description}"` : `Apply "${anchor.description}" to outstanding reimbursables`} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="bg-slate-800/40 rounded-lg p-3 text-xs">
          <div className="font-medium">{anchor.description}</div>
          <div className="text-slate-400 mt-0.5">
            {isAnchorExpense
              ? `Reimbursable expense · ${fmtMoney(Number(anchor.amount) || 0, anchor.currency, false)} · ${fmtMoney(expenseRemaining(anchor), anchor.currency, false)} still unsettled`
              : `Reimbursement income · ${fmtMoney(Number(anchor.amount) || 0, anchor.currency, false)} · ${fmtMoney((Number(anchor.amount) || 0) - incomeAllocated(anchor.id), anchor.currency, false)} available to allocate`}
          </div>
        </div>

        {existing.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs text-slate-400">Existing settlements on this {isAnchorExpense ? 'expense' : 'income'}:</div>
            {existing.map((s) => {
              const otherId = isAnchorExpense ? s.incomeTxId : s.expenseTxId;
              const other = liveTx.find((t) => t.id === otherId);
              return (
                <div key={s.id} className="flex items-center justify-between bg-slate-800/40 rounded px-2 py-1.5 text-xs">
                  <div className="min-w-0 flex-1 truncate">
                    {fmtMoney(Number(s.amount) || 0, anchor.currency, false)} ↔ {other?.description ?? 'unknown'}
                  </div>
                  <button
                    onClick={async () => { if (await confirmDialog('Remove this settlement?')) onDelete(s.id); }}
                    className="ml-2 p-1 hover:bg-slate-700 rounded"
                    title="Remove settlement"
                  >
                    <Trash2 className="w-3 h-3 text-rose-400" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="space-y-2">
          <div className="text-xs text-slate-300 font-medium">
            {isAnchorExpense ? 'Available reimbursement incomes:' : 'Outstanding reimbursable expenses:'}
          </div>
          {candidates.length === 0 ? (
            <div className="text-xs text-slate-500 italic">
              {isAnchorExpense
                ? 'No reimbursement incomes available. Add an income marked as a reimbursement first.'
                : 'No outstanding reimbursable expenses.'}
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-auto">
              {candidates.map(({ tx, available }) => (
                <label key={tx.id} className="bg-slate-800/40 rounded p-2 flex items-center gap-2 cursor-pointer hover:bg-slate-800/70">
                  <input
                    type="checkbox"
                    checked={!!picked[tx.id]}
                    onChange={(e) => setPicked({ ...picked, [tx.id]: e.target.checked })}
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{tx.description}</div>
                    <div className="text-[10px] text-slate-400">
                      {strToDate(tx.date).toLocaleDateString('en', { month: 'short', day: 'numeric' })} · {fmtMoney(Number(tx.amount) || 0, tx.currency, false)}{!isAnchorExpense ? ` · ${fmtMoney(available, tx.currency, false)} unsettled` : ''}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
          <div className="text-[10px] text-slate-500">Checking an item settles it in full. Partial reimbursements aren't tracked — any leftover is out-of-pocket and income excess is ignored.</div>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button
            onClick={handleApply}
            disabled={saving || !Object.values(picked).some(Boolean)}
            className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500"
          >
            {saving ? 'Saving…' : 'Settle selected'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function TransactionRow({ tx, compact, baseCurrency, loans = [], categories = [] }) {
  const cat = ALL_CATEGORIES.find(c => c.name === tx.category);
  const isIncome = tx.type === 'income';
  return (
    <div className="flex items-center gap-3 flex-1 min-w-0">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-base flex-shrink-0" style={{ backgroundColor: '#64748b20' }}>
        {cat?.icon || '📦'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-1.5 min-w-0">
          <span className="truncate">{tx.description}</span>
          {tx.recurringId && <Repeat className="w-3 h-3 text-cyan-400 flex-shrink-0" title="Recurring" />}
        </div>
        {/* Pills: stacked below the name on mobile, inline beside it on desktop. */}
        {(tx.smsRawId || tx.fxEstimated || tx.isReimbursable || tx.isReimbursement || tx.linkedStatementId) && (
          <div className="flex items-center gap-1 flex-wrap mt-0.5 sm:mt-0 flex-shrink-0">
            {tx.smsRawId && (
              <span
                className={`text-[9px] px-1.5 py-0.5 rounded-full ${tx.smsNeedsReview ? 'bg-amber-500/15 text-amber-300' : 'bg-violet-500/15 text-violet-300'}`}
                title={tx.smsNeedsReview ? 'SMS-imported · needs review (unmatched merchant or amount)' : 'SMS-imported'}
              >
                {tx.smsNeedsReview ? 'SMS ⚠' : 'SMS'}
              </span>
            )}
            {tx.fxEstimated && (
              <span
                className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300"
                title="AED estimated from the FX rate (the bank's available-limit drop was unreliable). Edit this tx with the exact settled amount once it posts to clear this."
              >
                ≈ est.
              </span>
            )}
            {tx.isReimbursable && (() => {
              const remaining = (Number(tx.amount) || 0) - (Number(tx.reimbursedAmount) || 0);
              const fullySettled = remaining < 0.0001;
              return (
                <span
                  className={`text-[9px] px-1.5 py-0.5 rounded-full ${fullySettled ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}
                  title={fullySettled ? 'Fully reimbursed' : `${fmtMoney(Number(tx.reimbursedAmount) || 0, tx.currency, false)} reimbursed of ${fmtMoney(Number(tx.amount) || 0, tx.currency, false)}`}
                >
                  {fullySettled ? 'REIMB' : `REIMB ${Math.round(((Number(tx.reimbursedAmount) || 0) / (Number(tx.amount) || 1)) * 100)}%`}
                </span>
              );
            })()}
            {tx.isReimbursement && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300" title={tx.reimbursementPayer || 'Reimbursement income'}>
                REPAY
              </span>
            )}
            {tx.linkedStatementId && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-teal-500/15 text-teal-300" title="Verified against an uploaded card statement">
                ✓ Stmt
              </span>
            )}
          </div>
        )}
        </div>
        <div className="text-xs text-slate-400 flex items-center gap-2 truncate">
          {tx.type === 'transfer' ? (
            (() => {
              // A loan-payment transfer carries no spend category itself — the
              // budget category lives on the loan (loan.budgetCategoryId). Surface
              // it here so the row reads "Checking → Loan · Transport · Jun 3".
              let transferCat = (tx.category && tx.category !== 'Transfer') ? tx.category : null;
              if (!transferCat && tx.toLoanId) {
                const loan = loans.find(l => l.id === tx.toLoanId);
                if (loan?.budgetCategoryId) transferCat = categories.find(c => c.id === loan.budgetCategoryId)?.name || null;
              }
              return (
                <>
                  <span className="truncate">{tx.fromAccount || '—'} <span className="text-slate-500">→</span> {tx.toAccount || '—'}</span>
                  {transferCat && <><span className="flex-shrink-0">·</span><span className="flex-shrink-0">{transferCat}</span></>}
                </>
              );
            })()
          ) : (
            <>
              <span>{tx.category}</span>
              {!compact && tx.account && <><span>·</span><span className="truncate">{tx.account}</span></>}
            </>
          )}
          <span className="flex-shrink-0">·</span>
          <span className="flex-shrink-0">{strToDate(tx.date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}</span>
        </div>
        {(() => {
          // Show user-written notes inline; hide the auto-generated boilerplate
          // (SMS import / FX VAT / recurring seed) so it doesn't clutter the list.
          const note = (tx.notes || '').trim();
          const boilerplate = /^(auto-imported from sms|from sms|auto fx vat|auto-deducted from salary)/i.test(note);
          return note && !boilerplate
            ? <div className="text-[11px] text-slate-500 truncate mt-0.5">{note}</div>
            : null;
        })()}
      </div>
      {(() => {
        // Headline in AED (the app's base) so tx sizes are comparable at a glance
        // and consistent with budgets/balances. The currency it was actually
        // logged/charged in shows small beneath for cross-referencing against
        // bank/SMS records. Foreign card txs store the AED settled figure in
        // `amount`/`currency` and the foreign original in `originalAmount`/
        // `originalCurrency`; manually-entered foreign txs carry a non-AED
        // `currency` directly.
        const hasOrig = tx.originalCurrency && tx.originalCurrency !== baseCurrency
          && Number.isFinite(Number(tx.originalAmount)) && Number(tx.originalAmount) > 0;
        const aedValue = toBase(tx.amount, tx.currency); // amount is already in tx.currency
        const origAmount = hasOrig ? Number(tx.originalAmount) : tx.amount;
        const origCurrency = hasOrig ? tx.originalCurrency : tx.currency;
        const showOrig = origCurrency !== baseCurrency;
        return (
          <div className="text-right flex-shrink-0">
            <div className={`text-sm font-semibold ${(isIncome || tx.isRefund) ? 'text-emerald-400' : 'text-slate-100'}`}>
              {(isIncome || tx.isRefund) ? '+' : '−'}{fmtMoney(aedValue, baseCurrency, false)}
            </div>
            {showOrig && (
              <div className="text-[11px] text-slate-500">{fmtMoney(origAmount, origCurrency, false)}</div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ============ TRANSACTION MODAL ============
function TransactionModal({ tx, accounts, cards, categories = [], loans = [], onSave, onClose }) {
  const liveLoans = (loans || []).filter((l: any) => !l.deletedAt);
  const isEditing = !!tx;
  const [form, setForm] = useState(tx || {
    id: uid('tx'),
    date: todayStr(),
    description: '',
    amount: '',
    currency: 'AED',
    category: 'Other',
    type: 'expense',
    account: accounts[0]?.name || cards[0]?.name || 'Default',
    notes: '',
    isRefund: false,
    fromAccount: '',
    toAccount: '',
    toLoanId: '',
    // M2.5 Session D — reimbursables
    isReimbursable: false,
    isReimbursement: false,
    reimbursementPayer: '',
  });
  const [saving, setSaving] = useState(false);

  // Categories now sourced from the live `categories` state so user-managed
  // adds/edits show up in the picker. Falls back to the seeded defaults if the
  // load hasn't populated state yet (cold render).
  const liveCats = (categories || []).filter((c) => !c.deletedAt);
  const cats = (() => {
    const kind = form.type === 'income' ? 'income' : 'expense';
    const fromState = liveCats.filter((c) => c.kind === kind);
    if (fromState.length > 0) return fromState;
    return form.type === 'income' ? DEFAULT_CATEGORIES.income : DEFAULT_CATEGORIES.expense;
  })();

  // Combined account+card list, grouped
  const accountOptions = [
    ...accounts.map(a => ({ name: a.name, kind: 'account', label: `${a.name} (${a.currency})` })),
    ...cards.map(c => ({ name: c.name, kind: 'card', label: `${c.name} (${c.currency} card)` })),
  ];

  const handleSave = async () => {
    if (form.type === 'transfer') {
      if (!form.fromAccount || !form.toAccount || form.fromAccount === form.toAccount) {
        toast('Please select different source and destination.');
        return;
      }
    } else if (!form.description?.trim() || !form.amount) return;

    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) {
      toast('Please enter a valid amount greater than zero.');
      return;
    }
    // Categories are mandatory for expense + income so the forecast /
    // budget rollups stay clean. Transfers don't have a category.
    if (form.type !== 'transfer' && !form.category) {
      toast('Pick a category for this transaction.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        description: form.description?.trim() || (form.type === 'transfer' ? `Transfer: ${form.fromAccount} → ${form.toAccount}` : ''),
        amount,
      };
      if (form.type === 'transfer') {
        payload.category = 'Transfer';
      }
      await onSave(payload);
      onClose();
    } catch (e) {
      console.error('Save failed:', e);
      toast('Save failed. Check the console for details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={tx ? 'Edit Transaction' : 'New Transaction'} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <button onClick={() => setForm({ ...form, type: 'expense', category: 'Other', isRefund: false })} className={`py-2 rounded-lg text-sm font-medium transition ${form.type === 'expense' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40' : 'bg-slate-800 text-slate-400'}`}>Expense</button>
          <button onClick={() => setForm({ ...form, type: 'income', category: 'Salary', isRefund: false })} className={`py-2 rounded-lg text-sm font-medium transition ${form.type === 'income' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400'}`}>Income</button>
          <button onClick={() => setForm({ ...form, type: 'transfer', category: 'Transfer', isRefund: false })} className={`py-2 rounded-lg text-sm font-medium transition ${form.type === 'transfer' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40' : 'bg-slate-800 text-slate-400'}`}>Transfer</button>
        </div>

        {form.type !== 'transfer' && (
          <Field label="Description">
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="input" placeholder="What was this for?" autoFocus />
          </Field>
        )}

        <div className="grid grid-cols-5 gap-2">
          <Field label="Amount" className="col-span-2">
            <input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="input" placeholder="0.00" autoFocus={form.type === 'transfer'} />
          </Field>
          <Field label="Currency">
            <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="input">
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Date" className="col-span-2">
            <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="input" />
          </Field>
        </div>

        {form.type === 'transfer' ? (
          <div className="grid grid-cols-2 gap-2">
            <Field label="From">
              <select value={form.fromAccount} onChange={e => setForm({ ...form, fromAccount: e.target.value })} className="input">
                <option value="">Select source…</option>
                {accountOptions.map(o => <option key={`from-${o.name}`} value={o.name}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="To">
              <select
                value={form.toLoanId ? `loan:${form.toLoanId}` : form.toAccount}
                onChange={e => {
                  const v = e.target.value;
                  if (v.startsWith('loan:')) {
                    const id = v.slice(5);
                    setForm({ ...form, toLoanId: id, toAccount: liveLoans.find(l => l.id === id)?.name || 'Loan' });
                  } else {
                    setForm({ ...form, toAccount: v, toLoanId: '' });
                  }
                }}
                className="input"
              >
                <option value="">Select destination…</option>
                {accountOptions.map(o => <option key={`to-${o.name}`} value={o.name}>{o.label}</option>)}
                {liveLoans.length > 0 && (
                  <optgroup label="Loans — pay down">
                    {liveLoans.map(l => <option key={`to-loan-${l.id}`} value={`loan:${l.id}`}>{l.name}</option>)}
                  </optgroup>
                )}
              </select>
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Account / Card">
              <select value={form.account} onChange={e => setForm({ ...form, account: e.target.value })} className="input">
                {accounts.length > 0 && (
                  <optgroup label="Accounts">
                    {accounts.map(a => <option key={`acc-${a.id}`} value={a.name}>{a.name} ({a.currency})</option>)}
                  </optgroup>
                )}
                {cards.length > 0 && (
                  <optgroup label="Credit Cards">
                    {cards.map(c => <option key={`card-${c.id}`} value={c.name}>{c.name} ({c.currency})</option>)}
                  </optgroup>
                )}
              </select>
            </Field>
            <Field label="Category">
              <select value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })} className="input">
                <option value="">Select a category…</option>
                {cats.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
          </div>
        )}

        {form.type === 'expense' && (
          <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${form.isRefund ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800'}`}>
            <input type="checkbox" checked={form.isRefund} onChange={e => setForm({ ...form, isRefund: e.target.checked })} className="w-4 h-4" />
            <ArrowDownRight className={`w-4 h-4 ${form.isRefund ? 'text-emerald-400' : 'text-slate-400'}`} />
            <div className="flex-1">
              <div className="text-sm font-medium">Refund / Reversal</div>
              <div className="text-xs text-slate-400">Treat as a credit against this category instead of an expense</div>
            </div>
          </label>
        )}

        {/* M2.5 Session D: reimbursable flag on expenses */}
        {form.type === 'expense' && (
          <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${form.isReimbursable ? 'bg-amber-500/10 border-amber-500/40' : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800'}`}>
            <input type="checkbox" checked={!!form.isReimbursable} onChange={(e) => setForm({ ...form, isReimbursable: e.target.checked })} className="w-4 h-4" />
            <Repeat className={`w-4 h-4 ${form.isReimbursable ? 'text-amber-400' : 'text-slate-400'}`} />
            <div className="flex-1">
              <div className="text-sm font-medium">Reimbursable</div>
              <div className="text-xs text-slate-400">Track this expense as awaiting reimbursement (e.g. work expense)</div>
            </div>
          </label>
        )}

        {/* M2.5 Session D: reimbursement flag on income */}
        {form.type === 'income' && (
          <>
            <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${form.isReimbursement ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800'}`}>
              <input type="checkbox" checked={!!form.isReimbursement} onChange={(e) => setForm({ ...form, isReimbursement: e.target.checked })} className="w-4 h-4" />
              <ArrowDownRight className={`w-4 h-4 ${form.isReimbursement ? 'text-emerald-400' : 'text-slate-400'}`} />
              <div className="flex-1">
                <div className="text-sm font-medium">Reimbursement from payer</div>
                <div className="text-xs text-slate-400">Money received against a reimbursable expense — match it via the "Match" button on the Transactions tab after saving.</div>
              </div>
            </label>
            {form.isReimbursement && (
              <Field label="Payer (optional)">
                <input value={form.reimbursementPayer || ''} onChange={(e) => setForm({ ...form, reimbursementPayer: e.target.value })} className="input" placeholder="e.g. Employer, Insurance" />
              </Field>
            )}
          </>
        )}

        <Field label="Notes (optional)">
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="input min-h-[50px]" />
        </Field>

        {isEditing && tx.recurringId && (
          <div className="text-xs px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 flex items-start gap-2">
            <Repeat className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>This was generated by a recurring rule. Edit the rule in the Recurring tab to change future occurrences.</span>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm disabled:opacity-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============ ACCOUNTS ============
function Accounts({ accounts, transactions, onSave, onDelete, showModal, setShowModal, baseCurrency }) {
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, txCount }

  // Sorted high-to-low by live balance, normalised to the base currency so
  // an AED 100 row doesn't outrank a USD 100 row.
  const accountsWithLive = useMemo(() => accounts
    .map(a => ({ ...a, liveBalance: computeAccountLiveBalance(a, transactions) }))
    .sort((a, b) => toBase(b.liveBalance, b.currency) - toBase(a.liveBalance, a.currency)),
    [accounts, transactions]);

  const totalAED = accountsWithLive.reduce((s, a) => {
    const val = toBase(a.liveBalance, a.currency);
    return (a.type === 'credit' || a.type === 'loan') ? s - val : s + val;
  }, 0);

  const handleConfirmDelete = async (alsoDeleteTxs) => {
    if (!confirmDelete) return;
    await onDelete(confirmDelete.id, alsoDeleteTxs);
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Accounts</h2>
          <p className="text-sm text-slate-400">Total: {fmtMoney(totalAED, baseCurrency)}</p>
        </div>
        <button onClick={() => { setEditing(null); setShowModal(true); }} className="px-3 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 flex items-center gap-2 whitespace-nowrap flex-shrink-0">
          <Plus className="w-4 h-4" /> Account
        </button>
      </div>

      {accountsWithLive.length === 0 && (
        <div className="bg-slate-900 rounded-xl p-12 border border-slate-800 text-center">
          <p className="text-sm text-slate-400">No accounts yet. Click <span className="text-emerald-400 font-medium">+ Account</span> to add your first one.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {accountsWithLive.map(a => {
          const type = ACCOUNT_TYPES.find(t => t.value === a.type);
          const isNegative = false; // legacy credit/loan account types removed
          const isConfirming = confirmDelete?.id === a.id;

          if (isConfirming) {
            return (
              <div key={a.id} className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-1">Delete "{a.name}"?</div>
                    {confirmDelete.txCount > 0 ? (
                      <>
                        <div className="text-xs text-slate-400 mb-3">
                          Has {confirmDelete.txCount} transaction{confirmDelete.txCount > 1 ? 's' : ''}.
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          <button onClick={() => handleConfirmDelete(false)} className="px-2 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600">Keep txs</button>
                          <button onClick={() => handleConfirmDelete(true)} className="px-2 py-1 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">Delete all</button>
                          <button onClick={() => setConfirmDelete(null)} className="px-2 py-1 text-xs rounded bg-slate-800 hover:bg-slate-700">Cancel</button>
                        </div>
                      </>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => handleConfirmDelete(false)} className="px-3 py-1.5 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">Delete</button>
                        <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600">Cancel</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={a.id} className="bg-slate-900 rounded-xl p-3 border border-slate-800 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center text-lg flex-shrink-0">{type?.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{a.name}</div>
                <div className="text-[11px] text-slate-400 truncate">{a.bank ? `${a.bank} · ${type?.label}` : type?.label}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-bold whitespace-nowrap">{fmtMoney(a.liveBalance, a.currency)}</div>
                {a.currency !== baseCurrency && (
                  <div className="text-[10px] text-slate-500 whitespace-nowrap">≈ {fmtMoney(toBase(a.liveBalance, a.currency), baseCurrency)}</div>
                )}
              </div>
              <div className="flex gap-0.5 flex-shrink-0 -mr-1.5">
                <button onClick={() => { setEditing(a); setShowModal(true); }} className="p-1.5 hover:bg-slate-700 rounded" title="Edit">
                  <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                </button>
                <button
                  onClick={() => {
                    const txCount = transactions.filter(t => t.account === a.name).length;
                    setConfirmDelete({ id: a.id, txCount });
                  }}
                  className="p-1.5 hover:bg-rose-900/50 rounded"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && <AccountModal account={editing} onSave={onSave} onClose={() => { setShowModal(false); setEditing(null); }} />}
    </div>
  );
}

function AccountModal({ account, onSave, onClose }) {
  const [form, setForm] = useState(account || { id: uid('acc'), name: '', bank: '', type: 'checking', currency: 'AED', balance: 0, accountNumber: '', iban: '', lastFour: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      const accountNumber = (form.accountNumber || '').trim();
      // lastFour holds the DEBIT CARD's last 4 (for debit-purchase SMS
      // matching) — NOT the account number. Account-level SMSes match on the
      // account number itself, so don't auto-derive last_four from it.
      await onSave({
        ...form,
        name: form.name.trim(),
        bank: (form.bank || '').trim(),
        balance: parseFloat(form.balance) || 0,
        accountNumber: accountNumber || null,
        iban: (form.iban || '').trim() || null,
        lastFour: (form.lastFour || '').replace(/\D/g, '').slice(0, 4) || null,
      });
      onClose();
    } catch (e) { toast('Save failed: ' + (e?.message || String(e))); } finally { setSaving(false); }
  };

  return (
    <Modal title={account ? 'Edit Account' : 'New Account'} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Name">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input" placeholder="e.g. Salary" autoFocus />
          </Field>
          <Field label="Bank (optional)">
            <input value={form.bank} onChange={e => setForm({ ...form, bank: e.target.value })} className="input" placeholder="e.g. Emirates NBD, ADCB" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Starting Balance">
            <input type="number" step="0.01" value={form.balance} onChange={e => setForm({ ...form, balance: e.target.value })} className="input" />
          </Field>
          <Field label="Currency">
            <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="input">
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Type">
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="input">
              {ACCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
            </select>
          </Field>
          <Field label="Last 4 digits">
            <input value={form.lastFour || ''} onChange={e => setForm({ ...form, lastFour: e.target.value })} className="input font-mono" maxLength={4} placeholder="6436" />
          </Field>
        </div>
        <div className="grid grid-cols-5 gap-2">
          <Field label="Account number" className="col-span-2">
            <input value={form.accountNumber || ''} onChange={e => setForm({ ...form, accountNumber: e.target.value })} className="input" placeholder="e.g. 1234567890" />
          </Field>
          <Field label="IBAN" className="col-span-3">
            <input value={form.iban || ''} onChange={e => setForm({ ...form, iban: e.target.value })} className="input" placeholder="e.g. AE12 …" />
          </Field>
        </div>
        <div className="text-[10px] text-slate-600">Last 4 / account number / IBAN are used to match SMS auto-imports to this account.</div>
        <div className="text-xs text-slate-400">Live balance updates as transactions are added to this account.</div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name?.trim()} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============ BUDGETS — Budget Engine (2026-07 cutover) ============
// Everything is a line. A category total is ALWAYS the sum of its resolved
// lines (see src/lib/budget-engine.ts). Months are computed on demand from
// versioned template rule lines + per-month override diffs; nothing computed is
// stored. Pre-cutover months (< 2026-07) are served read-only from the legacy
// `budgets` table, frozen. Post-cutover past months are computed but read-only.

// Ordered Mon..Sun (matches the day-selector squares). `v` is the JS weekday
// number (0=Sun..6=Sat) used by the engine's calendar math.
const BUDGET_WEEKDAYS = [
  { v: 1, label: 'Monday', short: 'Mon' }, { v: 2, label: 'Tuesday', short: 'Tue' }, { v: 3, label: 'Wednesday', short: 'Wed' },
  { v: 4, label: 'Thursday', short: 'Thu' }, { v: 5, label: 'Friday', short: 'Fri' }, { v: 6, label: 'Saturday', short: 'Sat' }, { v: 0, label: 'Sunday', short: 'Sun' },
];
const WEEKDAY_ORDER = new Map(BUDGET_WEEKDAYS.map((w, i) => [w.v, i]));
const dayShort = (v) => BUDGET_WEEKDAYS.find(w => w.v === v)?.short || '';
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FREQ_LABELS = {
  monthly: 'Every month',
  per_named_day: 'Specific day(s)',
  specific_months: 'Specific month(s)',
  quarterly: 'Quarterly',
  per_day: 'Every day',
  per_need: 'As needed',
};

// The days of a per_named_day line (multi, with legacy single fallback).
const lineWeekdays = (cfg = {}) =>
  (cfg.weekdays && cfg.weekdays.length) ? cfg.weekdays : (typeof cfg.weekday === 'number' ? [cfg.weekday] : []);

// A compact, human description of a line's frequency — uniformly "Every …".
function freqDescription(line) {
  if (line.source === 'added') return 'One-off';
  if (line.source === 'adjustment') return 'Adjustment';
  const cfg = line.freqConfig || {};
  switch (line.frequency) {
    case 'monthly': return 'Every month';
    case 'per_day': return 'Every day';
    case 'per_named_day': {
      const days = lineWeekdays(cfg);
      if (days.length >= 7) return 'Every day';
      if (!days.length) return 'No days set';
      const names = days.slice().sort((a, b) => WEEKDAY_ORDER.get(a) - WEEKDAY_ORDER.get(b)).map(dayShort);
      return 'Every ' + names.join(', ');
    }
    case 'specific_months':
    case 'quarterly': {
      const months = (cfg.months && cfg.months.length) ? cfg.months.slice().sort((a, b) => a - b) : [];
      if (months.length >= 12) return 'Every month';
      if (!months.length) return 'No months set';
      return 'Every ' + months.map(m => MONTH_ABBR[m - 1]).join(', ');
    }
    case 'per_need': return 'As needed';
    default: return '';
  }
}

function Budgets({ legacyBudgets = [], transactions, categories = [], templateLines = [], overrides = [], currentMonth, onCommitTemplate, onDeleteTemplate, onSaveOverride, onReleaseOverrides, onReleaseMonth, baseCurrency }) {
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  // 'monthly' = track spending vs budget per month; 'template' = define the rules.
  const [view, setView] = useState('monthly');
  // modal state: { type, ...ctx }
  const [modal, setModal] = useState(null);

  const isLegacy = selectedMonth < BUDGET_CUTOVER_MONTH;
  const isPast = selectedMonth < currentMonth;
  const isFuture = selectedMonth > currentMonth;
  const isEditable = selectedMonth >= currentMonth; // current + future only

  const money = (n) => fmtMoney(round10(n), baseCurrency);

  const catById = useMemo(() => {
    const m = new Map();
    for (const c of categories) if (!c.deletedAt) m.set(c.id, c);
    return m;
  }, [categories]);
  const expenseCats = useMemo(
    () => categories.filter(c => !c.deletedAt && c.kind === 'expense'),
    [categories],
  );
  const currentMonthLabel = strToDate(currentMonth + '-01').toLocaleDateString('en', { month: 'long', year: 'numeric' });

  // The template as it stands right now (active version per line group, resolved
  // at the current real month), grouped by category. Drives the Template view.
  // Template edits always take effect from the current month forward, so this is
  // the one canonical "the rules today" picture regardless of the month the
  // Monthly view is parked on.
  const templateByCat = useMemo(() => {
    const defs = resolveTemplateForMonth(templateLines, currentMonth);
    const map = new Map();
    for (const { def, isDeleted } of defs.values()) {
      if (isDeleted) continue;
      if (!map.has(def.categoryId)) map.set(def.categoryId, []);
      map.get(def.categoryId).push(def);
    }
    for (const arr of map.values()) arr.sort((a, b) => (a.sortOrder - b.sortOrder) || (a.name || '').localeCompare(b.name || ''));
    return map;
  }, [templateLines, currentMonth]);

  // Spend per category NAME, from transactions in the selected month up to today.
  const spendByCat = useMemo(() => {
    const today = todayStr();
    const m = new Map();
    for (const t of transactions) {
      if (t.deletedAt || t.type !== 'expense') continue;
      if (!t.date || !t.date.startsWith(selectedMonth)) continue;
      if (t.date > today) continue;
      const v = t.isRefund ? -toBase(t.amount, t.currency) : toBase(t.amount, t.currency);
      m.set(t.category, (m.get(t.category) || 0) + v);
    }
    return m;
  }, [transactions, selectedMonth]);

  const spentForCategory = (categoryId) => {
    const cat = catById.get(categoryId);
    if (!cat) return 0;
    return Math.max(0, spendByCat.get(cat.name) || 0);
  };

  // Actual transactions per category NAME for the selected month (the
  // "Transactions" expander in the Monthly view). Newest first.
  const txByCatName = useMemo(() => {
    const m = new Map();
    for (const t of transactions) {
      if (t.deletedAt || t.type !== 'expense') continue;
      if (!t.date || !t.date.startsWith(selectedMonth)) continue;
      if (!m.has(t.category)) m.set(t.category, []);
      m.get(t.category).push(t);
    }
    for (const arr of m.values()) arr.sort((a, z) => (z.date || '').localeCompare(a.date || ''));
    return m;
  }, [transactions, selectedMonth]);

  // ---- month nav ----
  const shiftMonth = (delta) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const monthOptions = useMemo(() => {
    const [cy, cm] = currentMonth.split('-').map(Number);
    const opts = [];
    for (let i = -24; i <= 12; i++) {
      const d = new Date(cy, cm - 1 + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      opts.push({ key, label: d.toLocaleDateString('en', { month: 'long', year: 'numeric' }) });
    }
    return opts;
  }, [currentMonth]);
  const monthLabel = strToDate(selectedMonth + '-01').toLocaleDateString('en', { month: 'long', year: 'numeric' });

  // ---- computed month (engine) ----
  const monthResult = useMemo(
    () => (isLegacy ? null : computeBudgetMonth(selectedMonth, templateLines, overrides)),
    [isLegacy, selectedMonth, templateLines, overrides],
  );

  // Categories to render: every expense category that has >=1 line this month,
  // plus (when editable) empty categories so the user can start adding lines.
  const renderedCategories = useMemo(() => {
    if (isLegacy || !monthResult) return [];
    const rows = expenseCats.map(c => {
      const res = monthResult.byCategoryId[c.id];
      return { category: c, lines: res?.lines || [], total: res?.total || 0 };
    });
    // A line may reference a category that's since been renamed/deleted — surface
    // it too so its budget isn't invisible.
    for (const cr of monthResult.categories) {
      if (!expenseCats.some(c => c.id === cr.categoryId)) {
        rows.push({ category: catById.get(cr.categoryId) || { id: cr.categoryId, name: '(unknown category)', icon: '📦' }, lines: cr.lines, total: cr.total });
      }
    }
    const visible = rows.filter(r => r.lines.length > 0 || isEditable);
    return visible.sort((a, b) => (b.total - a.total) || a.category.name.localeCompare(b.category.name));
  }, [isLegacy, monthResult, expenseCats, catById, isEditable]);

  // Pre-cutover months (legacy `budgets` table) rendered as the SAME compact card
  // shape: each category is one read-only "line" holding its stored amount, so
  // the utilization bar + Transactions expander work identically. Frozen (never
  // editable), so no line menus or per-month actions show.
  const legacyRows = useMemo(() => {
    if (!isLegacy) return [];
    return legacyBudgets
      .filter(b => !b.deletedAt && (b.month || '').slice(0, 7) === selectedMonth)
      .map(b => {
        const cat = catById.get(b.categoryId)
          || categories.find(c => !c.deletedAt && c.name === b.category)
          || DEFAULT_CATEGORIES.expense.find(c => c.name === b.category)
          || { id: b.categoryId || b.category || b.id, name: b.category || '—', icon: '📦' };
        const line = {
          key: `leg:${b.id}`, lineGroupId: null, overrideId: null,
          categoryId: cat.id, name: b.category || cat.name,
          frequency: null, freqConfig: null,
          rawAmount: Number(b.amount) || 0, amount: round10(Number(b.amount) || 0),
          source: 'template', locked: false, deletedThisMonth: false,
          templateNote: b.notes || null, monthNote: null,
        };
        return { category: cat, lines: [line], total: round10(Number(b.amount) || 0) };
      })
      .sort((a, b) => (b.total - a.total) || (a.category.name || '').localeCompare(b.category.name || ''));
  }, [isLegacy, legacyBudgets, selectedMonth, catById, categories]);

  const monthHasOverrides = useMemo(
    () => overrides.some(o => !o.deletedAt && o.month === selectedMonth),
    [overrides, selectedMonth],
  );

  const totals = useMemo(() => {
    if (isLegacy) {
      const rows = legacyBudgets.filter(b => (b.month || currentMonth) === selectedMonth && !b.deletedAt);
      const budgeted = rows.reduce((s, b) => s + (Number(b.amount) || 0), 0);
      const spent = rows.reduce((s, b) => s + spentForCategory(b.categoryId), 0);
      return { budgeted, spent, count: rows.length };
    }
    const budgeted = monthResult ? monthResult.total : 0;
    const spent = renderedCategories.reduce((s, r) => s + spentForCategory(r.category.id), 0);
    const count = renderedCategories.reduce((s, r) => s + r.lines.length, 0);
    return { budgeted, spent, count };
  }, [isLegacy, monthResult, renderedCategories, legacyBudgets, selectedMonth]);

  const pct = totals.budgeted > 0 ? (totals.spent / totals.budgeted) * 100 : 0;
  const over = totals.spent > totals.budgeted + 0.001;
  const warn = pct > 85 && !over;

  // ---- template edit / delete with reconciliation ----
  const openTemplateEdit = (line) => {
    // Resolve the active template version for this line group, current month.
    const groupVersions = templateLines.filter(v => v.lineGroupId === line.lineGroupId && !v.deletedAt);
    const active = resolveTemplateForMonth(groupVersions, currentMonth).get(line.lineGroupId)?.def;
    setModal({ type: 'template', lineGroupId: line.lineGroupId, initial: active || line });
  };

  const requestTemplateCommit = (payload) => {
    // payload has lineGroupId (null for new). Detect reconciliation targets.
    if (payload.lineGroupId) {
      const targets = findReconciliationTargets(payload.lineGroupId, currentMonth, overrides);
      if (targets.length) {
        setModal({ type: 'reconcile', mode: 'edit', payload, targets, newAmountLabel: money(payload.amount) });
        return;
      }
    }
    onCommitTemplate(payload).then(() => setModal(null));
  };

  const requestTemplateDelete = async (line) => {
    const targets = findReconciliationTargets(line.lineGroupId, currentMonth, overrides);
    if (targets.length) {
      setModal({ type: 'reconcile', mode: 'delete', lineGroupId: line.lineGroupId, targets, lineName: line.name });
      return;
    }
    const ok = await confirmDialog(`Remove "${line.name}" from the template, ${currentMonthLabel} onward? Past months keep it.`);
    if (ok) onDeleteTemplate(line.lineGroupId, []);
  };

  // ---- per-month override quick actions ----
  const skipLineThisMonth = (line) => {
    onSaveOverride({ month: selectedMonth, lineGroupId: line.lineGroupId, deletedThisMonth: true, monthNote: line.monthNote || null });
  };
  const releaseLine = (line) => {
    if (line.overrideId) onReleaseOverrides([line.overrideId]);
  };
  const removeStandalone = (line) => {
    if (line.overrideId) onReleaseOverrides([line.overrideId]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold">Budgets</h2>
          <p className="text-sm text-slate-400">
            {view === 'template'
              ? 'Template — define the rule lines that build every month'
              : isLegacy ? 'Archived month — frozen (pre-engine)'
              : isPast ? 'Past month — frozen snapshot'
              : isFuture ? 'Future month — planning ahead'
              : 'This month — tracking spend against your template'}
          </p>
        </div>
        {/* view toggle */}
        <div className="inline-flex rounded-lg bg-slate-900 border border-slate-800 p-0.5">
          <button onClick={() => setView('monthly')} className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition ${view === 'monthly' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
            <Calendar className="w-4 h-4" /> Monthly
          </button>
          <button onClick={() => setView('template')} className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition ${view === 'template' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
            <SlidersHorizontal className="w-4 h-4" /> Template
          </button>
        </div>
      </div>

      {view === 'template' ? (
        <BudgetTemplateView
          expenseCats={expenseCats}
          templateByCat={templateByCat}
          catById={catById}
          money={money}
          onAddRule={(catId) => setModal({ type: 'template', lineGroupId: null, initial: { categoryId: catId } })}
          onEditRule={(def) => setModal({ type: 'template', lineGroupId: def.lineGroupId, initial: def })}
          onDeleteRule={requestTemplateDelete}
          onGoMonthly={() => setView('monthly')}
        />
      ) : (
      <>
      {/* month switcher + totals */}
      {isEditable && !isLegacy && monthHasOverrides && (
        <div className="flex justify-end">
          <button
            onClick={async () => { if (await confirmDialog(`Discard every this-month change for ${monthLabel} and snap back to the template?`)) onReleaseMonth(selectedMonth); }}
            className="px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center gap-2 whitespace-nowrap"
            title="Re-sync this whole month to the template"
          >
            <RotateCcw className="w-4 h-4" /> Re-sync month to template
          </button>
        </div>
      )}
      <div className="bg-slate-900 rounded-xl px-3 py-2 border border-slate-800 flex items-center gap-x-3 gap-y-2 flex-wrap">
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => shiftMonth(-1)} className="p-1.5 hover:bg-slate-800 rounded-lg transition" title="Previous month"><ChevronLeft className="w-4 h-4" /></button>
          <div className="relative">
            <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="appearance-none bg-slate-800 border border-slate-700 rounded-lg pl-3 pr-8 py-1.5 text-sm font-semibold cursor-pointer hover:bg-slate-700 transition">
              {monthOptions.map(o => <option key={o.key} value={o.key}>{o.label}{o.key === currentMonth ? ' (Current)' : ''}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
          </div>
          <button onClick={() => shiftMonth(1)} className="p-1.5 hover:bg-slate-800 rounded-lg transition" title="Next month"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => setSelectedMonth(currentMonth)} disabled={selectedMonth === currentMonth} className={`ml-1 px-2.5 py-1.5 text-xs rounded-lg transition whitespace-nowrap ${selectedMonth === currentMonth ? 'opacity-40 cursor-default bg-slate-800/50 text-slate-500' : 'bg-slate-800 hover:bg-slate-700 text-slate-200'}`}>This month</button>
        </div>

        {totals.count > 0 && (
          <div className="flex items-baseline gap-x-5 gap-y-1 flex-wrap ml-auto">
            <div className="flex items-baseline gap-1.5"><span className="text-xs text-slate-400">Budgeted</span><span className="text-sm font-bold">{money(totals.budgeted)}</span></div>
            <div className="flex items-baseline gap-1.5"><span className="text-xs text-slate-400">Spent</span><span className="text-sm font-bold">{money(totals.spent)}</span></div>
            <div className="flex items-baseline gap-1.5"><span className="text-xs text-slate-400">{totals.budgeted - totals.spent >= 0 ? 'Remaining' : 'Over by'}</span><span className={`text-sm font-bold ${totals.budgeted - totals.spent >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{money(Math.abs(totals.budgeted - totals.spent))}</span></div>
          </div>
        )}
        {totals.count > 0 && (
          <div className="w-full flex items-center gap-3">
            <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${over ? 'bg-rose-950 ring-1 ring-rose-500/40' : 'bg-slate-800'}`}>
              <div className={`h-full rounded-full transition-all duration-500 ${over ? 'bg-rose-500' : warn ? 'bg-amber-500' : 'bg-emerald-500'}`} style={over ? { width: '100%', backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0 4px, transparent 4px 8px)' } : { width: `${Math.min(pct, 100)}%` }} />
            </div>
            <span className={`text-xs font-semibold flex-shrink-0 tabular-nums ${over ? 'text-rose-400' : warn ? 'text-amber-400' : 'text-slate-400'}`}>{pct.toFixed(0)}%</span>
          </div>
        )}
      </div>

      {(isLegacy ? legacyRows : renderedCategories).length === 0 ? (
        <div className="bg-slate-900 rounded-xl p-12 border border-slate-800 text-center">
          <Target className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-400 mb-4">
            {isEditable ? 'No budget rules yet. Define your rule lines in the Template tab — every month is built from them.' : `No budget was set for ${monthLabel}.`}
          </p>
          {isEditable && expenseCats.length > 0 && (
            <button onClick={() => setView('template')} className="px-4 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 inline-flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Go to Template</button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {(isLegacy ? legacyRows : renderedCategories).map(row => (
            <BudgetCategoryCard
              key={row.category.id}
              row={row}
              spent={spentForCategory(row.category.id)}
              transactions={txByCatName.get(row.category.name) || []}
              editable={isEditable}
              money={money}
              baseCurrency={baseCurrency}
              onOverrideLine={(line) => setModal({ type: 'override', line })}
              onSkipLine={skipLineThisMonth}
              onReleaseLine={releaseLine}
              onNoteLine={(line) => setModal({ type: 'override', line, noteFocus: true })}
              onEditStandalone={(line) => setModal({ type: 'standalone', categoryId: row.category.id, line })}
              onRemoveStandalone={removeStandalone}
              onAddOneOff={() => setModal({ type: 'standalone', categoryId: row.category.id, line: null, isAdjustment: false })}
              onAdjust={() => setModal({ type: 'standalone', categoryId: row.category.id, line: null, isAdjustment: true, existingAdjust: row.lines.find(l => l.source === 'adjustment') || null })}
            />
          ))}
        </div>
      )}
      </>
      )}

      {modal?.type === 'template' && (
        <TemplateLineModal
          initial={modal.initial}
          lineGroupId={modal.lineGroupId}
          categories={expenseCats}
          currentMonth={currentMonth}
          currentMonthLabel={currentMonthLabel}
          money={money}
          onSave={requestTemplateCommit}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'override' && (
        <OverrideLineModal
          line={modal.line}
          month={selectedMonth}
          monthLabel={monthLabel}
          noteFocus={modal.noteFocus}
          money={money}
          onSave={(o) => onSaveOverride(o).then(() => setModal(null))}
          onRelease={() => { if (modal.line.overrideId) onReleaseOverrides([modal.line.overrideId]); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'standalone' && (
        <StandaloneLineModal
          line={modal.line}
          categoryId={modal.categoryId}
          month={selectedMonth}
          monthLabel={monthLabel}
          isAdjustment={modal.line ? modal.line.source === 'adjustment' : modal.isAdjustment}
          existingAdjust={modal.existingAdjust}
          onSave={(o) => onSaveOverride(o).then(() => setModal(null))}
          onRemove={() => { const oid = modal.line?.overrideId || modal.existingAdjust?.overrideId; if (oid) onReleaseOverrides([oid]); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'reconcile' && (
        <BudgetReconcileModal
          mode={modal.mode}
          targets={modal.targets}
          lineName={modal.lineName || modal.payload?.name}
          newAmountLabel={modal.newAmountLabel}
          money={money}
          onSave={(resolutions) => {
            const p = modal.mode === 'edit'
              ? onCommitTemplate({ ...modal.payload, resolutions })
              : onDeleteTemplate(modal.lineGroupId, resolutions);
            p.then(() => setModal(null));
          }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function BudgetCategoryCard({ row, spent, transactions = [], editable, money, baseCurrency, onOverrideLine, onSkipLine, onReleaseLine, onNoteLine, onEditStandalone, onRemoveStandalone, onAddOneOff, onAdjust }) {
  const { category, lines, total } = row;
  const [menuKey, setMenuKey] = useState(null);
  // One expander at a time: 'est' (budget lines) | 'tx' (transactions) | null.
  const [expanded, setExpanded] = useState(null);
  const pct = total > 0 ? (spent / total) * 100 : 0;
  const over = spent > total + 0.001;
  const warn = pct > 85 && !over;
  const togCls = (on) => `p-1 rounded transition ${on ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'}`;

  return (
    <div className="bg-slate-900 rounded-xl border border-slate-800">
      {/* one-line header: icon · name · utilization bar · amount (pct) · toggles */}
      <div className="px-3 py-2 flex items-center gap-3">
        <div className="flex items-center gap-2 min-w-0 shrink-0 max-w-[42%]">
          <span className="text-base leading-none flex-shrink-0">{category.icon || '📦'}</span>
          <span className="text-sm font-medium truncate">{category.name}</span>
        </div>
        <div className={`flex-1 min-w-[24px] h-1.5 rounded-full overflow-hidden ${over ? 'bg-rose-950 ring-1 ring-rose-500/40' : 'bg-slate-800'}`}>
          <div className={`h-full transition-all ${over ? 'bg-rose-500' : warn ? 'bg-amber-500' : 'bg-emerald-500'}`} style={over ? { width: '100%', backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0 4px, transparent 4px 8px)' } : { width: `${Math.min(pct, 100)}%` }} />
        </div>
        <div className="text-xs tabular-nums whitespace-nowrap flex-shrink-0">
          <span className={over ? 'text-rose-400 font-medium' : 'text-slate-300'}>{money(spent)}</span>
          <span className="text-slate-500"> / {money(total)}</span>
          <span className="hidden sm:inline text-slate-500"> ({total > 0 ? `${pct.toFixed(0)}% used` : 'no budget'})</span>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button onClick={() => setExpanded(v => v === 'est' ? null : 'est')} className={togCls(expanded === 'est')} title="Estimated budget lines"><Target className="w-3.5 h-3.5" /></button>
          <button onClick={() => setExpanded(v => v === 'tx' ? null : 'tx')} className={togCls(expanded === 'tx')} title="Transactions this month"><Receipt className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {/* Estimation — the budget lines that build this month's total */}
      {expanded === 'est' && (
        <div className="border-t border-slate-800/70">
          <div className="px-1.5 py-1">
        {lines.length === 0 && <div className="px-2 py-2 text-xs text-slate-500 italic">No lines yet.</div>}
        {lines.map(line => {
          const isOpen = menuKey === line.key;
          // Same 3-column layout as the Template view: name · amount + frequency · note.
          const freq = freqDescription(line) + (line.deletedThisMonth ? ' · skipped' : '');
          const note = line.monthNote || line.templateNote;
          return (
            <div key={line.key}>
              <div className={`group grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_minmax(0,1.1fr)_auto] items-center gap-4 px-2 py-1.5 rounded-lg ${editable ? 'hover:bg-slate-800/50' : ''}`}>
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className={`text-sm truncate ${line.deletedThisMonth ? 'line-through text-slate-500' : ''}`}>{line.name}</span>
                  {line.locked && !line.deletedThisMonth && <span title="Locked this month" className="text-amber-400 text-[10px] shrink-0">●</span>}
                </span>
                <span className="flex items-baseline gap-2 min-w-0">
                  <span className={`text-sm font-medium tabular-nums shrink-0 ${line.deletedThisMonth ? 'text-slate-500 line-through' : line.amount < 0 ? 'text-rose-300' : ''}`}>{money(line.amount)}</span>
                  <span className="text-[11px] text-slate-500 whitespace-nowrap">{freq}</span>
                </span>
                <span className="text-[11px] text-slate-500 italic truncate text-right" title={note || ''}>{note || ''}</span>
                {editable
                  ? <button onClick={() => setMenuKey(isOpen ? null : line.key)} className="p-0.5 rounded hover:bg-slate-800 shrink-0" title="Line actions"><MoreVertical className="w-4 h-4 text-slate-400" /></button>
                  : <span />}
              </div>
              {editable && isOpen && (
                <div className="px-2 pb-2 pt-0.5 flex flex-wrap justify-end gap-1.5">
                  {line.source === 'template' ? (
                    <>
                      <MiniBtn icon={Edit2} label="Edit" onClick={() => { setMenuKey(null); onOverrideLine(line); }} />
                      <MiniBtn icon={FileText} label="Add Note" onClick={() => { setMenuKey(null); onNoteLine(line); }} />
                      {!line.deletedThisMonth && <MiniBtn icon={Trash2} label="Delete" danger onClick={() => { setMenuKey(null); onSkipLine(line); }} />}
                    </>
                  ) : (
                    <>
                      <MiniBtn icon={Edit2} label="Edit" onClick={() => { setMenuKey(null); onEditStandalone(line); }} />
                      <MiniBtn icon={FileText} label="Add Note" onClick={() => { setMenuKey(null); onEditStandalone(line); }} />
                      <MiniBtn icon={Trash2} label="Delete" danger onClick={() => { setMenuKey(null); onRemoveStandalone(line); }} />
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
          </div>
          {editable && (
            <div className="border-t border-slate-800/70 px-3 py-2 flex flex-wrap gap-1.5">
              <MiniBtn icon={Plus} label="One-off" onClick={onAddOneOff} />
              <MiniBtn icon={SlidersHorizontal} label="Adjust" onClick={onAdjust} />
            </div>
          )}
        </div>
      )}

      {/* Transactions — actual spend in this category this month */}
      {expanded === 'tx' && (
        <div className="border-t border-slate-800/70">
          {transactions.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500">No transactions this month</div>
          ) : (
            // Same 3-column layout as Estimation: description · amount + source · date (right).
            <div className="px-1.5 py-1 max-h-80 overflow-y-auto">
              {transactions.map(t => {
                const dateLabel = strToDate(t.date).toLocaleDateString('en', { month: 'short', day: 'numeric' });
                const amt = toBase(t.amount, t.currency || baseCurrency);
                const src = t.account || t.cardName || '';
                return (
                  <div key={t.id} className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_minmax(0,1.1fr)_auto] items-center gap-4 px-2 py-1.5 rounded-lg hover:bg-slate-800/40">
                    <span className="text-sm truncate">{t.description || '—'}</span>
                    <span className="flex items-baseline gap-2 min-w-0">
                      <span className={`text-sm font-medium tabular-nums shrink-0 ${t.isRefund ? 'text-emerald-400' : ''}`}>{t.isRefund ? '+' : ''}{fmtMoney(amt, baseCurrency)}</span>
                      {src && <span className="text-[11px] text-slate-500 truncate">{src}</span>}
                    </span>
                    <span className="text-[11px] text-slate-500 tabular-nums text-right truncate">{dateLabel}</span>
                    <span />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The Template view: the centralized place to define budget rule lines. Not
// month-scoped — edits take effect from the current month forward (past months
// frozen). No spend/tracking here; that lives in the Monthly view.
function BudgetTemplateView({ expenseCats, templateByCat, catById, money, onAddRule, onEditRule, onDeleteRule, onGoMonthly }) {
  // Only categories that actually have rules become blocks (no empty-category
  // noise). Ordered by the category list, with any orphaned groups appended.
  const groups = useMemo(() => {
    const out = [];
    for (const c of expenseCats) { const defs = templateByCat.get(c.id); if (defs && defs.length) out.push({ cat: c, defs }); }
    for (const [id, defs] of templateByCat) {
      if (defs.length && !expenseCats.some(c => c.id === id)) out.push({ cat: catById.get(id) || { id, name: '—', icon: '📦' }, defs });
    }
    return out;
  }, [expenseCats, templateByCat, catById]);
  const ruleCount = groups.reduce((s, g) => s + g.defs.length, 0);

  if (ruleCount === 0) {
    return (
      <div className="bg-slate-900 rounded-xl p-12 border border-slate-800 text-center">
        <Target className="w-12 h-12 mx-auto mb-3 text-slate-600" />
        <p className="text-sm text-slate-400 mb-4">No budget rules yet. Add your first rule line — every month is built from these.</p>
        <button onClick={() => onAddRule(expenseCats[0]?.id)} className="px-4 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add rule line</button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => onAddRule(expenseCats[0]?.id)} className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add rule line</button>
        <div className="text-xs text-slate-500">{ruleCount} rule{ruleCount !== 1 ? 's' : ''} · {groups.length} categor{groups.length !== 1 ? 'ies' : 'y'} · <button onClick={onGoMonthly} className="hover:text-slate-300">months →</button></div>
      </div>

      {/* One flat list: subtle category headers (with a full-width divider) and
          tight full-width rows. The note fills the middle so the width isn't wasted. */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 px-2 py-1.5">
        {groups.map(({ cat, defs }, gi) => (
          <div key={cat.id} className={gi > 0 ? 'mt-1' : ''}>
            {/* category header */}
            <div className="flex items-center gap-2 px-2 pt-2 pb-1 group/cat">
              <span className="text-sm flex-shrink-0">{cat.icon || '📦'}</span>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex-shrink-0">{cat.name}</span>
              <div className="flex-1 h-px bg-slate-800" />
              <button onClick={() => onAddRule(cat.id)} title="Add rule line" className="p-0.5 rounded text-slate-600 hover:text-emerald-400 hover:bg-slate-800 opacity-0 group-hover/cat:opacity-100 transition"><Plus className="w-3.5 h-3.5" /></button>
            </div>
            {/* rows — one per line, aligned into three invisible columns:
                name (left) · amount + frequency (left) · note (right) */}
            {defs.map(def => {
              const freq = freqDescription(def);
              return (
                <div key={def.lineGroupId} onClick={() => onEditRule(def)} title="Edit rule" className="group grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_minmax(0,1.1fr)_auto] items-center gap-4 px-2 py-1.5 rounded-lg hover:bg-slate-800/50 cursor-pointer">
                  <span className="text-sm truncate">{def.name}</span>
                  <span className="flex items-baseline gap-2 min-w-0">
                    <span className="text-sm font-medium tabular-nums shrink-0">{money(def.amount)}</span>
                    <span className="text-[11px] text-slate-500 whitespace-nowrap">{freq}</span>
                  </span>
                  <span className="text-[11px] text-slate-500 italic truncate text-right" title={def.templateNote || ''}>{def.templateNote || ''}</span>
                  <button onClick={(e) => { e.stopPropagation(); onDeleteRule(def); }} title="Remove from template" className="p-0.5 rounded text-slate-600 hover:text-rose-400 hover:bg-rose-900/30 shrink-0 opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniBtn({ icon: Icon, label, onClick, danger }) {
  return (
    <button onClick={onClick} className={`px-2 py-1 text-[11px] rounded-md flex items-center gap-1 transition ${danger ? 'bg-rose-900/30 hover:bg-rose-900/50 text-rose-300' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'}`}>
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

// Create / edit a template rule line. Editing an existing line triggers the
// caller's reconciliation flow via onSave.
function TemplateLineModal({ initial, lineGroupId, categories, currentMonth, currentMonthLabel, money, onSave, onClose }) {
  const isEdit = !!lineGroupId;
  const [form, setForm] = useState(() => ({
    categoryId: initial?.categoryId || categories[0]?.id || '',
    name: initial?.name || '',
    amount: initial?.amount != null ? String(initial.amount) : '',
    // New lines default to Specific month(s); legacy monthly/per_day lines keep
    // their stored frequency (kept selectable below when editing).
    frequency: initial?.frequency || 'specific_months',
    weekdays: lineWeekdays(initial?.freqConfig || {}),
    months: initial?.freqConfig?.months || [],
    templateNote: initial?.templateNote || '',
  }));
  const [saving, setSaving] = useState(false);
  const needsAmount = form.frequency !== 'per_need';
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleMonth = (m) => set('months', form.months.includes(m) ? form.months.filter(x => x !== m) : [...form.months, m].sort((a, b) => a - b));
  const toggleDay = (d) => set('weekdays', form.weekdays.includes(d) ? form.weekdays.filter(x => x !== d) : [...form.weekdays, d]);

  const daysMissing = form.frequency === 'per_named_day' && form.weekdays.length === 0;
  const monthsMissing = form.frequency === 'specific_months' && form.months.length === 0;
  const invalid = daysMissing || monthsMissing;

  // Legacy frequencies (monthly / per_day) aren't in the picker; keep the
  // current value selectable so editing an old line doesn't silently change it.
  const freqOptions = FREQUENCIES.includes(form.frequency) ? FREQUENCIES : [form.frequency, ...FREQUENCIES];

  const build = () => {
    let freqConfig = null;
    if (form.frequency === 'per_named_day') freqConfig = { weekdays: form.weekdays };
    else if (form.frequency === 'specific_months' || form.frequency === 'quarterly') freqConfig = { months: form.months };
    return {
      lineGroupId,
      categoryId: form.categoryId,
      name: form.name.trim() || 'Untitled',
      amount: form.frequency === 'per_need' ? 0 : (Number(form.amount) || 0),
      frequency: form.frequency,
      freqConfig,
      templateNote: form.templateNote.trim() || null,
    };
  };

  const handleSave = () => { setSaving(true); onSave(build()); };

  // Live "what this computes to for the current month" preview for the day-based
  // frequency, so the multiply-by-occurrences behaviour is obvious up front.
  const [cy, cm] = (currentMonth || '2026-07').split('-').map(Number);
  const amt = Number(form.amount) || 0;
  let preview = null;
  if (form.frequency === 'per_named_day' && form.weekdays.length) {
    const parts = form.weekdays.slice().sort((a, b) => WEEKDAY_ORDER.get(a) - WEEKDAY_ORDER.get(b))
      .map(d => ({ d, n: countWeekdayInMonth(cy, cm, d) }));
    const total = parts.reduce((s, p) => s + p.n, 0);
    const breakdown = parts.map(p => `${p.n} ${dayShort(p.d)}`).join(' + ');
    preview = `${currentMonthLabel}: ${breakdown} = ${total} → ${total} × ${money(amt)} = ${money(total * amt)}`;
  }

  return (
    <Modal title={isEdit ? 'Edit rule line' : 'New rule line'} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Category">
          <select value={form.categoryId} onChange={e => set('categoryId', e.target.value)} className="input">
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon ? c.icon + ' ' : ''}{c.name}</option>)}
          </select>
        </Field>
        <Field label="Line name">
          <input value={form.name} onChange={e => set('name', e.target.value)} className="input" placeholder="e.g. Groceries, Rent, Coffee" autoFocus />
        </Field>
        <Field label="Frequency">
          <select value={form.frequency} onChange={e => set('frequency', e.target.value)} className="input">
            {freqOptions.map(f => <option key={f} value={f}>{FREQ_LABELS[f]}</option>)}
          </select>
        </Field>
        {form.frequency === 'per_named_day' && (
          <Field label="Which day(s)">
            <div className="flex items-center gap-1.5 flex-wrap">
              {BUDGET_WEEKDAYS.map(w => {
                const on = form.weekdays.includes(w.v);
                return <button key={w.v} type="button" onClick={() => toggleDay(w.v)} className={`px-2 py-1 text-xs rounded ${on ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>{w.short}</button>;
              })}
              <span className="mx-1 text-slate-700">|</span>
              <button type="button" onClick={() => set('weekdays', BUDGET_WEEKDAYS.map(w => w.v))} className="px-2 py-1 text-[11px] rounded bg-slate-800 text-slate-400 hover:bg-slate-700">All</button>
              <button type="button" onClick={() => set('weekdays', [])} className="px-2 py-1 text-[11px] rounded bg-slate-800 text-slate-400 hover:bg-slate-700">None</button>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">Budget = amount × how many of the selected days fall in each month.</p>
            {daysMissing && <p className="mt-1 text-[11px] text-amber-400/80">Pick at least one day.</p>}
          </Field>
        )}
        {(form.frequency === 'specific_months' || form.frequency === 'quarterly') && (
          <Field label="Which month(s)">
            <div className="flex flex-wrap gap-1.5">
              {MONTH_ABBR.map((m, i) => {
                const on = form.months.includes(i + 1);
                return <button key={m} type="button" onClick={() => toggleMonth(i + 1)} className={`px-2 py-1 text-xs rounded ${on ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>{m}</button>;
              })}
            </div>
            <div className="mt-1.5 flex gap-1.5">
              <button type="button" onClick={() => set('months', MONTH_ABBR.map((_, i) => i + 1))} className="px-2 py-1 text-[11px] rounded bg-slate-800 text-slate-400 hover:bg-slate-700">All (every month)</button>
              <button type="button" onClick={() => set('months', [])} className="px-2 py-1 text-[11px] rounded bg-slate-800 text-slate-400 hover:bg-slate-700">None</button>
            </div>
            {monthsMissing && <p className="mt-1 text-[11px] text-amber-400/80">Pick at least one month.</p>}
          </Field>
        )}
        {needsAmount && (
          <Field label={form.frequency === 'per_named_day' ? 'Amount per occurrence (AED)' : 'Amount (AED)'}>
            <input type="number" step="1" value={form.amount} onChange={e => set('amount', e.target.value)} onWheel={e => e.currentTarget.blur()} className="input" placeholder="0" />
          </Field>
        )}
        {preview && (
          <div className="text-xs text-emerald-300/90 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">{preview}</div>
        )}
        {form.frequency === 'per_need' && (
          <p className="text-xs text-slate-500">A placeholder line — it contributes 0 by default. Fill it in per month with “Edit this month”.</p>
        )}
        <Field label="Why this rule exists (note)">
          <textarea value={form.templateNote} onChange={e => set('templateNote', e.target.value)} className="input min-h-[50px]" placeholder="Persistent note — carries across every month" />
        </Field>
        {isEdit && <p className="text-xs text-amber-400/80">Applies this month onward. Past months stay frozen. Months you’ve manually edited will ask what to do.</p>}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || invalid} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}

// Override a template line for ONE month: lock the amount, skip it, or note it.
function OverrideLineModal({ line, month, monthLabel, noteFocus, money, onSave, onRelease, onClose }) {
  const isOverridden = line.locked || line.deletedThisMonth;
  // Fresh line: default to editing the amount (the common intent), unless we were
  // opened just to add a note. Overridden line: start on its current state, and
  // the "Revert to the template value" option is offered.
  const [mode, setMode] = useState(line.deletedThisMonth ? 'skip' : line.locked ? 'lock' : (noteFocus ? 'template' : 'lock'));
  const [amount, setAmount] = useState(String(line.amount));
  const [note, setNote] = useState(line.monthNote || '');
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    const base = { id: line.overrideId || undefined, month, lineGroupId: line.lineGroupId, monthNote: note.trim() || null };
    if (mode === 'skip') { onSave({ ...base, deletedThisMonth: true, lockedAmount: null }); return; }
    // "Edit the amount" only becomes a real override when it differs from the
    // template — otherwise it's the same as keeping the template value.
    if (mode === 'lock' && round10(Number(amount) || 0) !== round10(line.rawAmount)) {
      onSave({ ...base, deletedThisMonth: false, lockedAmount: Number(amount) || 0 });
      return;
    }
    // Template value (revert / unchanged): keep a note-only override if there's a
    // note, else release any existing override, else there's nothing to do.
    if (note.trim()) onSave({ ...base, deletedThisMonth: false, lockedAmount: null });
    else if (line.overrideId) onRelease();
    else onClose();
  };

  return (
    <Modal title={`“${line.name}” — ${monthLabel}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">Changes here affect only {monthLabel}. The template stays as-is.</p>
        <div className="space-y-2">
          {isOverridden && (
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={mode === 'template'} onChange={() => setMode('template')} /> Revert to the template value <span className="text-slate-500">({money(line.rawAmount)})</span></label>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={mode === 'lock'} onChange={() => setMode('lock')} /> Edit the amount this month</label>
          {mode === 'lock' && <input type="number" step="1" value={amount} onChange={e => setAmount(e.target.value)} onWheel={e => e.currentTarget.blur()} className="input ml-6" placeholder="0" autoFocus />}
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={mode === 'skip'} onChange={() => setMode('skip')} /> Skip this line this month</label>
        </div>
        <Field label="Note for this month (optional)">
          <textarea value={note} onChange={e => setNote(e.target.value)} className="input min-h-[50px]" placeholder="Why the change — does not carry to other months" autoFocus={noteFocus} />
        </Field>
        <div className="flex gap-2 pt-1">
          {line.overrideId && <button onClick={() => { setSaving(true); onRelease(); }} className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm" title="Discard this month's change">Re-sync</button>}
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}

// Add / edit a standalone line: a one-off (rides on top) or an adjustment
// (signed; the "Adjust category" action).
function StandaloneLineModal({ line, categoryId, month, monthLabel, isAdjustment, existingAdjust, onSave, onRemove, onClose }) {
  const editing = line || existingAdjust;
  const adj = isAdjustment;
  const [amount, setAmount] = useState(editing ? String(editing.source === 'adjustment' || existingAdjust ? (editing.rawAmount ?? editing.amount) : editing.amount) : '');
  const [name, setName] = useState(editing ? (editing.name || '') : '');
  const [note, setNote] = useState(editing?.monthNote || '');
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    const amt = Number(amount) || 0;
    const overrideId = line?.overrideId || existingAdjust?.overrideId || undefined;
    const finalName = adj ? (name.trim() || `Manual adjustment: ${amt >= 0 ? '+' : ''}${round10(amt).toLocaleString('en')}`) : (name.trim() || 'One-off');
    onSave({ id: overrideId, month, lineGroupId: null, categoryId, name: finalName, addedAmount: amt, isAdjustment: adj, monthNote: note.trim() || null });
  };

  return (
    <Modal title={adj ? `Adjust category — ${monthLabel}` : `${editing ? 'Edit' : 'Add'} one-off — ${monthLabel}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">{adj ? 'A labeled adjustment line that rides on top of the category total for this month only.' : 'A one-off line for this month only. It never carries to other months.'}</p>
        {!adj && (
          <Field label="Name">
            <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="e.g. Birthday gift" autoFocus />
          </Field>
        )}
        <Field label={adj ? 'Adjustment amount (AED — use a minus for a decrease)' : 'Amount (AED)'}>
          <input type="number" step="1" value={amount} onChange={e => setAmount(e.target.value)} onWheel={e => e.currentTarget.blur()} className="input" placeholder={adj ? 'e.g. 1000 or -500' : '0'} autoFocus={adj} />
        </Field>
        {adj && (
          <Field label="Label (optional)">
            <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="Defaults to “Manual adjustment: …”" />
          </Field>
        )}
        <Field label="Note (optional)">
          <textarea value={note} onChange={e => setNote(e.target.value)} className="input min-h-[50px]" placeholder="Why — this month only" />
        </Field>
        <div className="flex gap-2 pt-1">
          {(line?.overrideId || existingAdjust?.overrideId) && <button onClick={() => { setSaving(true); onRemove(); }} className="px-3 py-2 rounded-lg bg-rose-900/30 hover:bg-rose-900/50 text-rose-300 text-sm">Remove</button>}
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}

// Reconciliation: after a template edit/delete, decide per locked month whether
// to keep the manual value or adopt the new template value. Nothing applies
// until Save.
function BudgetReconcileModal({ mode, targets, lineName, newAmountLabel, money, onSave, onClose }) {
  // action per target.override.id: 'keep' | 'resync'. Default keep (conservative).
  const [actions, setActions] = useState(() => Object.fromEntries(targets.map(t => [t.override.id, 'keep'])));
  const [saving, setSaving] = useState(false);
  const setAll = (a) => setActions(Object.fromEntries(targets.map(t => [t.override.id, a])));
  const monthName = (m) => strToDate(m + '-01').toLocaleDateString('en', { month: 'long', year: 'numeric' });

  return (
    <Modal title="Reconcile edited months" onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-sm text-slate-400">
          {mode === 'edit'
            ? <>You changed the template for <strong>{lineName}</strong>{newAmountLabel ? <> (new value {newAmountLabel})</> : null}. These months have manual changes — choose what to do with each.</>
            : <>You removed <strong>{lineName}</strong> from the template. These months have manual changes to that line — choose what to do with each.</>}
        </p>
        <div className="flex gap-2">
          <button onClick={() => setAll('keep')} className="px-3 py-1.5 text-xs rounded-lg bg-slate-800 hover:bg-slate-700">Keep all my values</button>
          <button onClick={() => setAll('resync')} className="px-3 py-1.5 text-xs rounded-lg bg-emerald-700 hover:bg-emerald-600">Update all to template</button>
        </div>
        <div className="divide-y divide-slate-800 border border-slate-800 rounded-lg max-h-80 overflow-y-auto">
          {targets.map(t => {
            const a = actions[t.override.id];
            const isDel = t.kind === 'deleted';
            return (
              <div key={t.override.id} className="p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{monthName(t.month)}</div>
                  <div className="text-xs text-slate-500">{isDel ? 'You removed this line for this month' : `You locked it at ${money(t.lockedAmount || 0)}`}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setActions(p => ({ ...p, [t.override.id]: 'keep' }))} className={`px-2.5 py-1 text-xs rounded ${a === 'keep' ? 'bg-slate-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>{isDel ? 'Keep removed' : 'Keep my value'}</button>
                  <button onClick={() => setActions(p => ({ ...p, [t.override.id]: 'resync' }))} className={`px-2.5 py-1 text-xs rounded ${a === 'resync' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>{isDel ? 'Re-add at new value' : 'Use new template value'}</button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={() => { setSaving(true); onSave(targets.map(t => ({ overrideId: t.override.id, action: actions[t.override.id] }))); }} disabled={saving} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ============ SAVINGS GOALS (M2.5 Session D) ============
// Option A from M1: virtual allocations. One physical balance per account/
// investment, but the user can mark "60% of ENBD Savings is for House, 40% for
// Emergency" and see each goal's progress without moving money.
//
// allocation_type='percentage'  → value is 0-100; goal share = source_balance * value/100
// allocation_type='fixed_amount' → value is a literal amount in the SOURCE's currency

// Per-month projected net (income − expense) for goal ETAs. Mirrors the
// Dashboard's cashForecast math but stripped to just the net number,
// returned as [{ label, key, net, isEstimated, noBaseline }] for offsets
// 1..horizon (today is index 0, omitted because no contributions accrue
// "now" — the first contribution lands in offset 1).
function projectMonthlyNetSeries({ accounts, transactions, recurring, budgets, baseCurrency, horizon }) {
  const now = new Date();
  const budgetMonths = [...new Set(
    (budgets || []).filter(b => !b.deletedAt && b.month).map(b => b.month),
  )].sort();
  const baselineMonthFor = (target) => {
    let best = null;
    for (const m of budgetMonths) {
      if (m < target) best = m;
    }
    return best;
  };
  const isLumpy = (r) => {
    const interval = Math.max(1, Number(r.interval) || 1);
    if (r.unit === 'year') return true;
    if (r.unit === 'month' && interval > 1) return true;
    return false;
  };
  const series = [];
  for (let offset = 1; offset <= horizon; offset++) {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const yr = d.getFullYear(), mo = d.getMonth();
    const mStr = `${yr}-${String(mo + 1).padStart(2, '0')}`;
    const mStart = `${mStr}-01`;
    const mEnd = `${mStr}-${String(lastDayOfMonth(yr, mo)).padStart(2, '0')}`;
    // INCOME
    let income = 0;
    (recurring || []).filter(r => r.active && !r.deletedAt && r.type === 'income' && r.accountId).forEach(r => {
      if (r.isInterestRule) {
        const acc = (accounts || []).find(a => a.id === r.accountId);
        if (!acc) return;
        const bal = computeAccountLiveBalance(acc, transactions || []);
        income += toBase(bal * ((Number(r.interestAnnualRatePct) || 0) / 100 / 12), acc.currency);
        return;
      }
      const occs = generateOccurrences(r, mStart, mEnd);
      if (occs.length === 0) return;
      income += toBase((Number(r.amount) || 0) * occs.length, r.currency);
    });
    let explicitFuture = 0;
    (transactions || []).filter(t =>
      !t.deletedAt && !t.excludesFromBalances && !t.recurringId
      && t.type === 'income' && t.date >= mStart && t.date <= mEnd
    ).forEach(t => {
      const v = toBase(Number(t.amount) || 0, t.currency);
      income += v; explicitFuture += v;
    });
    // §6d — learned variable income when the month has no explicit future income.
    const learnedVar = learnVariableIncome(transactions, strToDate(todayStr()));
    if (learnedVar > 0 && explicitFuture === 0) income += learnedVar;
    // EXPENSE — budgets first, else smart estimate.
    let expense = 0;
    let isEstimated = false;
    let noBaseline = false;
    const monthBudgets = (budgets || []).filter(b => b.month === mStr && !b.deletedAt);
    if (monthBudgets.length > 0) {
      expense = monthBudgets.reduce(
        (s, b) => s + toBase(Number(b.amount) || 0, baseCurrency), 0,
      );
    } else {
      isEstimated = true;
      const baselineMonth = baselineMonthFor(mStr);
      if (!baselineMonth) {
        noBaseline = true;
      } else {
        const baselineBudgets = (budgets || []).filter(b => b.month === baselineMonth && !b.deletedAt);
        const baselineCats = new Set(baselineBudgets.map(b => b.category));
        expense += baselineBudgets.reduce(
          (s, b) => s + toBase(Number(b.amount) || 0, baseCurrency), 0,
        );
        (recurring || []).filter(r =>
          r.active && !r.deletedAt && r.type === 'expense'
          && isLumpy(r) && !baselineCats.has(r.category)
        ).forEach(r => {
          const occs = generateOccurrences(r, mStart, mEnd);
          if (occs.length > 0) expense += toBase((Number(r.amount) || 0) * occs.length, r.currency);
        });
        (transactions || []).filter(t =>
          !t.deletedAt && !t.excludesFromBalances && !t.recurringId
          && t.type === 'expense' && !t.isRefund
          && t.date >= mStart && t.date <= mEnd
          && !baselineCats.has(t.category)
        ).forEach(t => {
          expense += toBase(Number(t.amount) || 0, t.currency);
        });
      }
    }
    series.push({
      label: d.toLocaleDateString('en', { month: 'short', year: '2-digit' }),
      key: mStr,
      income: Math.round(income),
      expense: Math.round(expense),
      net: Math.round(income - expense),
      isEstimated,
      noBaseline,
    });
  }
  return series;
}

function getAccountBalanceForAllocations(account, transactions) {
  // Mirrors computeAccountLiveBalance but without computeCardLiveBalance's installment
  // wrinkle. Savings goals only allocate against accounts/investments.
  return computeAccountLiveBalance(account, transactions);
}

function getInvestmentBalance(investment) {
  return (Number(investment.shares) || 0) * (Number(investment.currentPrice) || 0);
}

// Months elapsed since startDate (inclusive of today). startDate today → 1.
// startDate 1 month ago → 2. Returns 0 if startDate is in the future.
function monthsElapsedSince(startDateStr) {
  if (!startDateStr) return 0;
  const start = strToDate(startDateStr);
  const now = new Date();
  const months = (now.getFullYear() - start.getFullYear()) * 12
    + (now.getMonth() - start.getMonth()) + 1;
  return Math.max(0, months);
}

// historicalMonthlySavingsPool: average projected monthly net used to
// value monthly_recurring percentage allocations *up to now*. The
// forecast walks future months; for the "contributed so far" we use a
// flat pool estimate (computed by the caller and passed in).
function computeGoalProgress(goal, allocations, accounts, investments, transactions, historicalMonthlyPool) {
  let valueInGoalCurrency = 0;
  for (const alloc of allocations) {
    if (alloc.goalId !== goal.id || alloc.deletedAt) continue;
    const sourceCcy = (() => {
      if (alloc.cadence === 'monthly_recurring') return goal.currency || 'AED';
      if (alloc.accountId) {
        const acc = accounts.find((a) => a.id === alloc.accountId);
        return acc?.currency || 'AED';
      }
      if (alloc.investmentId) {
        const inv = investments.find((i) => i.id === alloc.investmentId);
        return inv?.currency || 'USD';
      }
      return goal.currency || 'AED';
    })();

    let slice = 0;
    if (alloc.cadence === 'monthly_recurring') {
      const months = monthsElapsedSince(alloc.startDate);
      if (months <= 0) continue;
      if (alloc.allocationType === 'percentage') {
        slice = (historicalMonthlyPool || 0) * ((Number(alloc.value) || 0) / 100) * months;
      } else {
        slice = (Number(alloc.value) || 0) * months;
      }
    } else {
      // 'snapshot' (legacy / default)
      let sourceBalance = 0;
      if (alloc.accountId) {
        const acc = accounts.find((a) => a.id === alloc.accountId);
        if (!acc) continue;
        sourceBalance = getAccountBalanceForAllocations(acc, transactions);
      } else if (alloc.investmentId) {
        const inv = investments.find((i) => i.id === alloc.investmentId);
        if (!inv) continue;
        sourceBalance = getInvestmentBalance(inv);
      } else {
        continue;
      }
      slice = alloc.allocationType === 'percentage'
        ? sourceBalance * ((Number(alloc.value) || 0) / 100)
        : Number(alloc.value) || 0;
    }
    valueInGoalCurrency += convert(slice, sourceCcy, goal.currency || 'AED');
  }
  return valueInGoalCurrency;
}

// Walks projected future months (returned by projectMonthlyNetSeries)
// and accumulates each goal's monthly_recurring allocations until the
// goal hits its target. netSeries[i] = months from now i+1.
// Returns { months, etaLabel } when found, { beyondHorizon: true } if
// not found in the series, or null if no recurring allocations exist.
function computeGoalEta(goal, allocations, currentValue, netSeries) {
  const target = Number(goal.targetAmount) || 0;
  if (target <= 0 || currentValue >= target) return null;
  const recurringAllocs = allocations.filter(a =>
    !a.deletedAt && a.goalId === goal.id && a.cadence === 'monthly_recurring'
  );
  if (recurringAllocs.length === 0) return null;
  let value = currentValue;
  for (let i = 0; i < netSeries.length; i++) {
    const pool = Math.max(0, netSeries[i].net || 0);
    let monthContribution = 0;
    for (const a of recurringAllocs) {
      if (a.allocationType === 'percentage') {
        monthContribution += pool * ((Number(a.value) || 0) / 100);
      } else {
        monthContribution += Number(a.value) || 0;
      }
    }
    value += monthContribution;
    if (value >= target) {
      return { months: i + 1, etaLabel: netSeries[i].label };
    }
  }
  return { beyondHorizon: true };
}

function SavingsGoals({ goals, allocations, accounts, investments, transactions, recurring = [], budgets = [], onSaveGoal, onDeleteGoal, onSaveAllocation, onDeleteAllocation, baseCurrency }) {
  const [editingGoal, setEditingGoal] = useState(null);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showAllocModalFor, setShowAllocModalFor] = useState(null); // goal id
  const [editingAlloc, setEditingAlloc] = useState(null); // existing allocation row in edit mode
  const [expanded, setExpanded] = useState({}); // goal id -> bool
  const [confirmDelete, setConfirmDelete] = useState(null);

  const liveGoals = goals.filter((g) => !g.deletedAt);
  const liveAllocs = allocations.filter((a) => !a.deletedAt);

  // 24-month net series powers the ETA walk for every goal.
  const netSeries = useMemo(() => projectMonthlyNetSeries({
    accounts, transactions, recurring, budgets, baseCurrency, horizon: 24,
  }), [accounts, transactions, recurring, budgets, baseCurrency]);

  // Average projected monthly net used to value monthly_recurring %
  // allocations' contributed-so-far. Falls back to 0 if all months are
  // negative (no savings to value % against).
  const avgMonthlyPool = useMemo(() => {
    const positives = netSeries.map(m => Math.max(0, m.net)).slice(0, 6);
    if (positives.length === 0) return 0;
    return positives.reduce((a, b) => a + b, 0) / positives.length;
  }, [netSeries]);

  const goalsWithProgress = useMemo(() => {
    return liveGoals.map((g) => {
      const current = computeGoalProgress(g, liveAllocs, accounts, investments, transactions, avgMonthlyPool);
      const target = Number(g.targetAmount) || 0;
      const pct = target > 0 ? (current / target) * 100 : 0;
      const eta = computeGoalEta(g, liveAllocs, current, netSeries);
      return { ...g, current, pct, eta };
    });
  }, [liveGoals, liveAllocs, accounts, investments, transactions, avgMonthlyPool, netSeries]);

  // Total of all recurring percentage allocations across all goals.
  // Shown as a banner if it exceeds 100% (also enforced at save time).
  const totalRecurringPct = liveAllocs
    .filter(a => a.cadence === 'monthly_recurring' && a.allocationType === 'percentage')
    .reduce((s, a) => s + (Number(a.value) || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Savings Goals</h2>
          <p className="text-sm text-slate-400">
            Virtual allocations on top of your accounts and investments — money doesn't move.
          </p>
        </div>
        <button onClick={() => { setEditingGoal(null); setShowGoalModal(true); }} className="px-3 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 flex items-center gap-2 whitespace-nowrap flex-shrink-0">
          <Plus className="w-4 h-4" /> Goal
        </button>
      </div>

      {totalRecurringPct > 100 && (
        <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-xs flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-rose-200">
            Recurring percentage allocations across all goals total <strong>{totalRecurringPct.toFixed(1)}%</strong> of monthly savings — over 100%. Reduce one before adding more.
          </div>
        </div>
      )}

      {goalsWithProgress.length === 0 ? (
        <div className="bg-slate-900 rounded-xl p-12 border border-slate-800 text-center">
          <Target className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-400">No goals yet. Add one and then attach allocations from your accounts or investments.</p>
        </div>
      ) : (
        (() => {
          const inProgress = goalsWithProgress.filter(g => g.pct < 100);
          const accomplished = goalsWithProgress.filter(g => g.pct >= 100);
          const showHeaders = inProgress.length > 0 && accomplished.length > 0;
          const renderGoal = (g) => {
            const goalAllocs = liveAllocs.filter((a) => a.goalId === g.id);
            const isExpanded = expanded[g.id];
            const isConfirming = confirmDelete === g.id;

            if (isConfirming) {
              return (
                <div key={g.id} className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm font-medium mb-1">Delete "{g.name}"?</div>
                      <div className="text-xs text-slate-400 mb-3">
                        Removes the goal and its {goalAllocs.length} allocation{goalAllocs.length === 1 ? '' : 's'}. Underlying account/investment balances are untouched.
                      </div>
                      <div className="flex gap-2">
                        <button onClick={async () => { await onDeleteGoal(g.id); setConfirmDelete(null); }} className="px-3 py-1.5 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">Delete</button>
                        <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600">Cancel</button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={g.id} className="bg-slate-900 rounded-xl border border-slate-800 p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {g.icon && <span className="text-xl">{g.icon}</span>}
                      <span className="font-semibold truncate">{g.name}</span>
                    </div>
                    {g.targetDate && (
                      <div className="text-xs text-slate-400 mt-0.5">Target: {g.targetDate}</div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingGoal(g); setShowGoalModal(true); }} className="p-1.5 hover:bg-slate-700 rounded" title="Edit goal">
                      <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    <button onClick={() => setConfirmDelete(g.id)} className="p-1.5 hover:bg-slate-700 rounded" title="Delete goal">
                      <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                    </button>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-400">Progress</span>
                    <span className="font-semibold">
                      {fmtMoney(g.current, g.currency, false)} / {fmtMoney(Number(g.targetAmount) || 0, g.currency, false)}
                    </span>
                  </div>
                  <div className="h-2 rounded bg-slate-800 overflow-hidden">
                    <div
                      className={`h-full ${g.pct >= 100 ? 'bg-emerald-400' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(100, g.pct)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <div className={`text-[10px] ${g.pct >= 100 ? 'text-emerald-400 font-semibold' : 'text-slate-500'}`}>
                      {g.pct.toFixed(1)}%
                    </div>
                    {g.eta && (
                      <div className="text-[10px]">
                        {g.eta.beyondHorizon ? (
                          <span className="text-rose-400" title="At current monthly contributions, target won't be reached within 24 months.">
                            &gt; 24 months
                          </span>
                        ) : (
                          <span className="text-cyan-400" title={`Walks each month's projected savings × your recurring allocations until total ≥ target.`}>
                            ~{g.eta.months} mo · ETA {g.eta.etaLabel}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t border-slate-800 pt-2">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setExpanded({ ...expanded, [g.id]: !isExpanded })}
                      className="text-xs text-slate-300 hover:text-slate-100"
                    >
                      Allocations ({goalAllocs.length})
                    </button>
                    <button
                      onClick={() => setShowAllocModalFor(g.id)}
                      className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700"
                    >
                      <Plus className="inline w-3 h-3" /> Add
                    </button>
                  </div>
                  {isExpanded && goalAllocs.length > 0 && (
                    <div className="mt-2 space-y-1.5 text-xs">
                      {goalAllocs.map((a) => {
                        const acc = a.accountId ? accounts.find((x) => x.id === a.accountId) : null;
                        const inv = a.investmentId ? investments.find((x) => x.id === a.investmentId) : null;
                        const source = acc?.name || inv?.symbol || '?';
                        const cur = acc?.currency || inv?.currency || g.currency;
                        const display = a.cadence === 'monthly_recurring'
                          ? (a.allocationType === 'percentage'
                            ? `${a.value}% of monthly savings · since ${a.startDate || 'today'}`
                            : `${fmtMoney(Number(a.value) || 0, g.currency, false)} / month · since ${a.startDate || 'today'}`)
                          : (a.allocationType === 'percentage'
                            ? `${a.value}% of ${source}`
                            : `${fmtMoney(Number(a.value) || 0, cur, false)} from ${source}`);
                        return (
                          <div key={a.id} className="flex items-center justify-between bg-slate-800/40 rounded px-2 py-1.5">
                            <div className="truncate">{display}</div>
                            <div className="flex items-center ml-2 gap-1">
                              <button
                                onClick={() => { setEditingAlloc(a); setShowAllocModalFor(g.id); }}
                                className="p-1 hover:bg-slate-700 rounded"
                                title="Edit allocation"
                              >
                                <Edit2 className="w-3 h-3 text-slate-400" />
                              </button>
                              <button
                                onClick={async () => { if (await confirmDialog(`Remove this allocation?`)) onDeleteAllocation(a.id); }}
                                className="p-1 hover:bg-slate-700 rounded"
                                title="Remove allocation"
                              >
                                <Trash2 className="w-3 h-3 text-rose-400" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {isExpanded && goalAllocs.length === 0 && (
                    <div className="mt-2 text-xs text-slate-500 italic">No allocations yet. Add one to start tracking progress.</div>
                  )}
                </div>
              </div>
            );
          };
          return (
            <div className="space-y-3">
              {inProgress.length > 0 && (
                <>
                  {showHeaders && <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">In Progress</div>}
                  <div className="grid gap-3 md:grid-cols-2 items-start">{inProgress.map(renderGoal)}</div>
                </>
              )}
              {accomplished.length > 0 && (
                <>
                  {showHeaders && <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide pt-2">Accomplished</div>}
                  <div className="grid gap-3 md:grid-cols-2 items-start">{accomplished.map(renderGoal)}</div>
                </>
              )}
            </div>
          );
        })()
      )}

      {showGoalModal && (
        <SavingsGoalModal
          goal={editingGoal}
          onSave={async (g) => { await onSaveGoal(g); }}
          onClose={() => { setShowGoalModal(false); setEditingGoal(null); }}
        />
      )}
      {showAllocModalFor && (
        <SavingsGoalAllocationModal
          goalId={showAllocModalFor}
          goal={liveGoals.find((g) => g.id === showAllocModalFor)}
          accounts={accounts}
          investments={investments}
          allocations={liveAllocs}
          transactions={transactions}
          existing={editingAlloc}
          onSave={async (a) => { await onSaveAllocation(a); }}
          onClose={() => { setShowAllocModalFor(null); setEditingAlloc(null); }}
        />
      )}
    </div>
  );
}

function SavingsGoalModal({ goal, onSave, onClose }) {
  const [form, setForm] = useState(goal || {
    id: uid('sg'),
    name: '',
    targetAmount: '',
    targetDate: '',
    currency: 'AED',
    color: '',
    icon: '🎯',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name?.trim() || !form.targetAmount) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        name: form.name.trim(),
        targetAmount: parseFloat(form.targetAmount) || 0,
        targetDate: form.targetDate || null,
      });
      onClose();
    } catch (e) { toast('Save failed.'); } finally { setSaving(false); }
  };

  return (
    <Modal title={goal ? 'Edit Goal' : 'New Goal'} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <Field label="Icon" className="col-span-1">
            <input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="input text-center" maxLength={2} />
          </Field>
          <Field label="Name" className="col-span-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="e.g. House Down Payment" autoFocus />
          </Field>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Field label="Target Amount" className="col-span-2">
            <input type="number" step="0.01" min="0" value={form.targetAmount} onChange={(e) => setForm({ ...form, targetAmount: e.target.value })} className="input" placeholder="0.00" />
          </Field>
          <Field label="Currency">
            <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="input">
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Date">
            <input type="date" value={form.targetDate || ''} onChange={(e) => setForm({ ...form, targetDate: e.target.value })} className="input" />
          </Field>
        </div>
        <Field label="Notes (optional)">
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" />
        </Field>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name?.trim() || !form.targetAmount} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SavingsGoalAllocationModal({ goalId, goal, accounts, investments, allocations = [], transactions = [], existing = null, onSave, onClose }) {
  const liveAccounts = accounts.filter((a) => !a.deletedAt);
  const liveInvestments = investments.filter((i) => !i.deletedAt);
  const isEdit = !!existing;
  const [form, setForm] = useState(existing ? {
    id: existing.id,
    goalId: existing.goalId,
    cadence: existing.cadence || 'snapshot',
    accountId: existing.accountId || '',
    investmentId: existing.investmentId || '',
    allocationType: existing.allocationType || 'percentage',
    value: String(existing.value ?? ''),
    sourceKind: existing.investmentId ? 'investment' : 'account',
    startDate: existing.startDate || todayStr(),
  } : {
    id: uid('sga'),
    goalId,
    cadence: 'snapshot',
    accountId: liveAccounts[0]?.id || '',
    investmentId: '',
    allocationType: 'percentage',
    value: '',
    sourceKind: 'account',
    startDate: todayStr(),
  });
  const [saving, setSaving] = useState(false);
  const isRecurring = form.cadence === 'monthly_recurring';

  // Capacity check only applies to snapshot allocations. Recurring claims
  // from the monthly savings pool and is bounded by the 100% % cap (banner
  // on the Savings Goals page + handleSave guard below).
  const sourceMeta = useMemo(() => {
    if (isRecurring) return null;
    if (form.sourceKind === 'account') {
      const acc = liveAccounts.find((a) => a.id === form.accountId);
      if (!acc) return null;
      const balance = getAccountBalanceForAllocations(acc, transactions);
      const others = allocations.filter((a) =>
        a.id !== form.id && !a.deletedAt && a.cadence !== 'monthly_recurring' && a.accountId === acc.id,
      );
      const usedByOthers = others.reduce((s, a) => {
        const v = Number(a.value) || 0;
        return s + (a.allocationType === 'percentage' ? balance * (v / 100) : v);
      }, 0);
      return { kind: 'account', name: acc.name, currency: acc.currency, balance, usedByOthers };
    }
    const inv = liveInvestments.find((i) => i.id === form.investmentId);
    if (!inv) return null;
    const balance = getInvestmentBalance(inv);
    const others = allocations.filter((a) =>
      a.id !== form.id && !a.deletedAt && a.cadence !== 'monthly_recurring' && a.investmentId === inv.id,
    );
    const usedByOthers = others.reduce((s, a) => {
      const v = Number(a.value) || 0;
      return s + (a.allocationType === 'percentage' ? balance * (v / 100) : v);
    }, 0);
    return { kind: 'investment', name: inv.symbol, currency: inv.currency, balance, usedByOthers };
  }, [isRecurring, form.sourceKind, form.accountId, form.investmentId, form.id, allocations, transactions, liveAccounts, liveInvestments]);

  const proposedSlice = useMemo(() => {
    const v = parseFloat(form.value);
    if (!Number.isFinite(v) || v <= 0 || !sourceMeta) return 0;
    return form.allocationType === 'percentage' ? sourceMeta.balance * (v / 100) : v;
  }, [form.value, form.allocationType, sourceMeta]);

  const availableForThis = sourceMeta ? Math.max(0, sourceMeta.balance - sourceMeta.usedByOthers) : 0;
  const overAllocated = sourceMeta ? proposedSlice > availableForThis + 0.005 : false;

  // Existing recurring % across all OTHER allocations (any goal).
  const existingRecurringPctOthers = allocations
    .filter(a => !a.deletedAt && a.id !== form.id && a.cadence === 'monthly_recurring' && a.allocationType === 'percentage')
    .reduce((s, a) => s + (Number(a.value) || 0), 0);

  const handleSave = async () => {
    const v = parseFloat(form.value);
    if (!(v > 0)) return;
    if (form.allocationType === 'percentage' && v > 100) { toast('Percentage cannot exceed 100.'); return; }
    if (isRecurring) {
      if (!form.startDate) { toast('Pick a start date for the recurring allocation.'); return; }
      if (form.allocationType === 'percentage'
        && existingRecurringPctOthers + v > 100) {
        toast(
          `Recurring percentage allocations already total ${existingRecurringPctOthers.toFixed(1)}% across other goals. ` +
          `Adding ${v.toFixed(1)}% would push it to ${(existingRecurringPctOthers + v).toFixed(1)}% — over 100%. Reduce another allocation first.`,
        );
        return;
      }
    } else {
      if (!sourceMeta) { toast('Pick a source account or investment.'); return; }
      if (overAllocated) {
        toast(
          `That would over-allocate ${sourceMeta.name}. Available: ${fmtMoney(availableForThis, sourceMeta.currency, false)}. ` +
          `This allocation would claim ${fmtMoney(proposedSlice, sourceMeta.currency, false)}.`,
        );
        return;
      }
    }
    setSaving(true);
    try {
      const payload = isRecurring ? {
        id: form.id,
        goalId,
        cadence: 'monthly_recurring',
        accountId: null,
        investmentId: null,
        allocationType: form.allocationType,
        value: v,
        startDate: form.startDate,
      } : {
        id: form.id,
        goalId,
        cadence: 'snapshot',
        accountId: form.sourceKind === 'account' ? (form.accountId || null) : null,
        investmentId: form.sourceKind === 'investment' ? (form.investmentId || null) : null,
        allocationType: form.allocationType,
        value: v,
        startDate: null,
      };
      await onSave(payload);
      onClose();
    } catch (e) { toast('Save failed.'); } finally { setSaving(false); }
  };

  return (
    <Modal title={`${isEdit ? 'Edit' : 'New'} allocation — ${goal?.name || 'goal'}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setForm({ ...form, cadence: 'snapshot' })}
            className={`py-2 rounded-lg text-sm font-medium ${!isRecurring ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200' : 'bg-slate-800 text-slate-400'}`}
            title="One-time claim on an account or investment balance"
          >
            One-off slice
          </button>
          <button
            type="button"
            onClick={() => setForm({ ...form, cadence: 'monthly_recurring' })}
            className={`py-2 rounded-lg text-sm font-medium ${isRecurring ? 'bg-cyan-500/20 border border-cyan-500/40 text-cyan-200' : 'bg-slate-800 text-slate-400'}`}
            title="Monthly contribution from your projected savings pool"
          >
            Monthly recurring
          </button>
        </div>

        {!isRecurring && (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, sourceKind: 'account' })}
              className={`py-2 rounded-lg text-sm font-medium ${form.sourceKind === 'account' ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200' : 'bg-slate-800 text-slate-400'}`}
            >
              From Account
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, sourceKind: 'investment' })}
              className={`py-2 rounded-lg text-sm font-medium ${form.sourceKind === 'investment' ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200' : 'bg-slate-800 text-slate-400'}`}
            >
              From Investment
            </button>
          </div>
        )}

        {!isRecurring && (form.sourceKind === 'account' ? (
          <Field label="Source account">
            <select value={form.accountId} onChange={(e) => setForm({ ...form, accountId: e.target.value })} className="input">
              <option value="">Select an account…</option>
              {liveAccounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
            </select>
          </Field>
        ) : (
          <Field label="Source investment">
            <select value={form.investmentId} onChange={(e) => setForm({ ...form, investmentId: e.target.value })} className="input">
              <option value="">Select an investment…</option>
              {liveInvestments.map((i) => <option key={i.id} value={i.id}>{i.symbol} ({i.currency})</option>)}
            </select>
          </Field>
        ))}

        {isRecurring && (
          <Field label="Start date">
            <input
              type="date"
              value={form.startDate || ''}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              className="input"
            />
            <div className="text-[10px] text-slate-500 mt-1">Allocation accrues monthly from this date. Past start dates count as already contributed.</div>
          </Field>
        )}

        <Field label="Allocation Type">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, allocationType: 'percentage' })}
              className={`py-2 rounded text-sm ${form.allocationType === 'percentage' ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200' : 'bg-slate-800 text-slate-400'}`}
            >
              Percentage
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, allocationType: 'fixed_amount' })}
              className={`py-2 rounded text-sm ${form.allocationType === 'fixed_amount' ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200' : 'bg-slate-800 text-slate-400'}`}
            >
              Fixed Amount
            </button>
          </div>
        </Field>

        <Field label={
          isRecurring
            ? (form.allocationType === 'percentage' ? 'Percentage of monthly savings (0–100)' : `Fixed amount per month (${goal?.currency || 'AED'})`)
            : (form.allocationType === 'percentage' ? 'Percentage (0–100)' : 'Fixed amount (source currency)')
        }>
          <input
            type="number" step="0.01" min="0"
            max={form.allocationType === 'percentage' ? 100 : undefined}
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            className="input"
            placeholder={form.allocationType === 'percentage' ? (isRecurring ? '15' : '60') : (isRecurring ? '1500' : '5000')}
          />
        </Field>

        {isRecurring && form.allocationType === 'percentage' && (
          <div className={`text-xs rounded-lg p-2 border ${existingRecurringPctOthers + (parseFloat(form.value) || 0) > 100 ? 'bg-rose-500/10 border-rose-500/40 text-rose-300' : 'bg-slate-800/40 border-slate-800 text-slate-400'} space-y-0.5`}>
            <div className="flex justify-between">
              <span>Other goals' recurring %</span>
              <span className="font-mono">{existingRecurringPctOthers.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span>This allocation</span>
              <span className="font-mono">{(parseFloat(form.value) || 0).toFixed(1)}%</span>
            </div>
            <div className="border-t border-slate-700/50 my-1" />
            <div className="flex justify-between font-semibold">
              <span>Total of monthly savings</span>
              <span className="font-mono">{(existingRecurringPctOthers + (parseFloat(form.value) || 0)).toFixed(1)}%</span>
            </div>
          </div>
        )}

        {!isRecurring && sourceMeta && (
          <div className={`text-xs rounded-lg p-2 border ${overAllocated ? 'bg-rose-500/10 border-rose-500/40 text-rose-300' : 'bg-slate-800/40 border-slate-800 text-slate-400'} space-y-0.5`}>
            <div className="flex justify-between">
              <span>{sourceMeta.name} balance</span>
              <span className="font-mono">{fmtMoney(sourceMeta.balance, sourceMeta.currency, false)}</span>
            </div>
            <div className="flex justify-between">
              <span>Already allocated{isEdit ? ' (excl. this)' : ''}</span>
              <span className="font-mono">{fmtMoney(sourceMeta.usedByOthers, sourceMeta.currency, false)}</span>
            </div>
            <div className="flex justify-between">
              <span>This allocation</span>
              <span className="font-mono">{fmtMoney(proposedSlice, sourceMeta.currency, false)}</span>
            </div>
            <div className="border-t border-slate-700/50 my-1" />
            <div className="flex justify-between font-semibold">
              <span>{overAllocated ? 'Over by' : 'Free capacity'}</span>
              <span className="font-mono">
                {overAllocated
                  ? fmtMoney(proposedSlice - availableForThis, sourceMeta.currency, false)
                  : fmtMoney(availableForThis - proposedSlice, sourceMeta.currency, false)}
              </span>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button
            onClick={handleSave}
            disabled={
              saving
              || !form.value
              || (!isRecurring && overAllocated)
              || (isRecurring && form.allocationType === 'percentage' && existingRecurringPctOthers + (parseFloat(form.value) || 0) > 100)
              || (isRecurring && !form.startDate)
            }
            className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500"
          >
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============ CARDS ============
function Cards({ cards, transactions, cardInstallments = [], accounts = [], categories = [], cardStatements = [], onSave, onDelete, onSaveStatement, onDeleteStatement, onImportTransactions, onDeleteTransaction, showModal, setShowModal, baseCurrency }) {
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [stmtFor, setStmtFor] = useState(null); // card whose statement modal is open
  const [uploadFor, setUploadFor] = useState(null); // card whose PDF upload modal is open
  const [cbFor, setCbFor] = useState(null); // card whose cashback-adjust modal is open
  // Per-card collapsible sections: key = `${cardId}:installments` | `${cardId}:statement`.
  const [expandedSec, setExpandedSec] = useState<Record<string, boolean>>({});
  const toggleSec = (k: string) => setExpandedSec(p => ({ ...p, [k]: !p[k] }));
  // Creation/deletion of installments now lives on the Loans page; this tab
  // just shows a read-only summary at the bottom of each card.

  // Compute live card balances from transactions via the centralized helper.
  // See computeCardLiveBalance for the sign conventions.
  const cardsWithLive = useMemo(() => cards
    .map(c => ({
      ...c,
      liveCurrentBalance: computeCardLiveBalance(c, transactions, cardInstallments),
    }))
    .sort((a, b) => toBase(b.liveCurrentBalance, b.currency) - toBase(a.liveCurrentBalance, a.currency)),
    [cards, transactions, cardInstallments]);

  const totals = useMemo(() => {
    let totalLimit = 0, totalDebt = 0;
    cardsWithLive.forEach(c => {
      totalLimit += toBase(c.creditLimit || 0, c.currency);
      totalDebt += toBase(c.liveCurrentBalance || 0, c.currency);
    });
    const totalAvailable = totalLimit - totalDebt;
    return { totalLimit, totalDebt, totalAvailable, utilization: totalLimit > 0 ? (totalDebt / totalLimit) * 100 : 0 };
  }, [cardsWithLive]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Credit Cards</h2>
          <p className="text-sm text-slate-400">Track balances, limits, and payment dates</p>
        </div>
        <button onClick={() => { setEditing(null); setShowModal(true); }} className="px-3 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 flex items-center gap-2 whitespace-nowrap flex-shrink-0">
          <Plus className="w-4 h-4" /> Card
        </button>
      </div>

      {cards.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total Credit Limit" value={fmtMoney(totals.totalLimit, baseCurrency)} />
          <StatCard label="Total Debt" value={fmtMoney(totals.totalDebt, baseCurrency)} valueClass="text-rose-400" />
          <StatCard label="Total Available" value={fmtMoney(totals.totalAvailable, baseCurrency)} valueClass="text-emerald-400" />
          <StatCard label="Overall Utilization" value={`${totals.utilization.toFixed(1)}%`} valueClass={totals.utilization > 70 ? 'text-rose-400' : totals.utilization > 30 ? 'text-amber-400' : 'text-emerald-400'} />
        </div>
      )}

      {cards.length === 0 ? (
        <div className="bg-slate-900 rounded-xl p-12 border border-slate-800 text-center">
          <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-400">No credit cards yet. Add one to track its balance, limit, and payment cycle.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-3">
          {cardsWithLive.map(c => {
            const limit = c.creditLimit || 0;
            const balance = c.liveCurrentBalance || 0;
            const stmtBalance = c.statementBalance || 0;
            const available = limit - balance;
            const utilization = limit > 0 ? (balance / limit) * 100 : 0;
            const utilColor = utilization > 70 ? 'text-rose-400' : utilization > 30 ? 'text-amber-400' : 'text-emerald-400';
            const utilBg = utilization > 70 ? 'bg-rose-500' : utilization > 30 ? 'bg-amber-500' : 'bg-emerald-500';

            // Latest uploaded/entered statement for this card + live payment
            // progress (transfers landing on the card on/after the statement date).
            const latestStmt = (cardStatements || [])
              .filter(s => s.cardId === c.id && !s.deletedAt)
              .sort((a, b) => cmpDateStr(b.statementDate, a.statementDate))[0] || null;
            let stmtDue = 0, stmtPaid = 0, stmtRemaining = 0, stmtPct = 0, stmtState = 'open', stmtOverdue = false, stmtNothingDue = false;
            if (latestStmt) {
              stmtDue = statementAmountDue(latestStmt);
              stmtPaid = (transactions || [])
                .filter(t => !t.deletedAt && t.type === 'transfer' && t.toCardId === c.id && cmpDateStr(t.date, latestStmt.statementDate) >= 0)
                .reduce((s, t) => s + (Number(t.amount) || 0), 0);
              stmtRemaining = Math.max(0, Math.round((stmtDue - stmtPaid) * 100) / 100);
              // Nothing due (credit / CR statement, or fully paid before any payment):
              // show a full green bar as if settled.
              stmtNothingDue = stmtDue <= 0.01;
              stmtPct = stmtDue > 0 ? Math.min(100, (stmtPaid / stmtDue) * 100) : 100;
              stmtOverdue = !!latestStmt.dueDate && stmtRemaining > 0.01 && cmpDateStr(todayStr(), latestStmt.dueDate) > 0;
              stmtState = stmtRemaining <= 0.01 ? 'paid' : stmtOverdue ? 'overdue' : 'open';
            }
            const minMet = latestStmt && latestStmt.minimumPayment != null && stmtPaid >= (Number(latestStmt.minimumPayment) || 0);

            const stmtDate = nextCardStatementDate(c);
            const dueDate = nextCardPaymentDate(c);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const daysToStmt = stmtDate ? Math.ceil((stmtDate - today) / (MS_PER_DAY)) : null;
            const daysToDue = dueDate ? Math.ceil((dueDate - today) / (MS_PER_DAY)) : null;

            const isConfirming = confirmDelete === c.id;

            if (isConfirming) {
              return (
                <div key={c.id} className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 lg:col-span-1">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm font-medium mb-3">Delete "{c.name}"?</div>
                      <div className="flex gap-2">
                        <button onClick={async () => { await onDelete(c.id); setConfirmDelete(null); }} className="px-3 py-1.5 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">Delete</button>
                        <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600">Cancel</button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={c.id} className="bg-gradient-to-br from-slate-900 to-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
                {/* Card header — credit-card-style gradient */}
                <div className="p-4 bg-gradient-to-br from-indigo-600/20 via-purple-600/10 to-transparent border-b border-slate-800">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <CreditCard className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-base font-semibold truncate">{c.name}</span>
                        {(c.network || c.lastFour) && (
                          <span className="text-[11px] text-slate-500 flex-shrink-0 whitespace-nowrap">
                            {c.network ? `· ${c.network}` : ''}{c.lastFour ? ` · ···${c.lastFour}` : ''}
                          </span>
                        )}
                      </div>
                      {c.bank && <div className="text-xs text-slate-400 mt-0.5 truncate">{c.bank}</div>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setUploadFor(c)} className="p-1.5 hover:bg-slate-700 rounded" title="Upload statement PDF">
                        <Upload className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                      <button onClick={() => setStmtFor(c)} className="p-1.5 hover:bg-slate-700 rounded" title="Add / edit statement">
                        <FileText className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                      <button onClick={() => { setEditing(c); setShowModal(true); }} className="p-1.5 hover:bg-slate-700 rounded" title="Edit">
                        <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                      <button onClick={() => setConfirmDelete(c.id)} className="p-1.5 hover:bg-rose-900/50 rounded" title="Delete">
                        <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-end justify-between gap-3 mb-2">
                      <div className="text-xl font-bold text-rose-400 whitespace-nowrap">−{fmtMoney(balance, c.currency)} <span className="text-xs font-medium text-slate-400">owed</span></div>
                      <div className="text-base font-semibold text-emerald-400 whitespace-nowrap">{fmtMoney(available, c.currency)} <span className="text-xs font-medium text-slate-400">left</span></div>
                    </div>
                    <div className="h-2 bg-slate-800/80 rounded-full overflow-hidden">
                      <div className={`h-full transition-all ${utilBg}`} style={{ width: `${Math.min(utilization, 100)}%` }} />
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1.5">
                      <span className={`text-xs ${utilColor}`}>{utilization.toFixed(1)}% of {fmtMoney(limit, c.currency)} limit</span>
                      {utilization > 70
                        ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 flex-shrink-0">● High</span>
                        : utilization > 30
                          ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 flex-shrink-0">● Moderate</span>
                          : <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 flex-shrink-0">● Healthy</span>}
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">

                  {/* Bank sync — Floosi's computed available vs the bank's last SMS-reported limit */}
                  {c.lastSmsAvailableLimit != null && (() => {
                    const smsAvail = Number(c.lastSmsAvailableLimit);
                    const delta = available - smsAvail;
                    const absD = Math.abs(delta);
                    const aligned = absD < 1;
                    const tone = aligned
                      ? { txt: 'text-emerald-400', box: 'bg-emerald-500/[0.07] border-emerald-500/20' }
                      : absD < 50
                        ? { txt: 'text-amber-400', box: 'bg-amber-500/[0.07] border-amber-500/20' }
                        : { txt: 'text-rose-400', box: 'bg-rose-500/[0.07] border-rose-500/20' };
                    return (
                      <div className={`rounded-lg border ${tone.box} p-3`}>
                        <div className="flex items-center justify-between mb-2.5">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                            <Zap className="w-3.5 h-3.5 text-cyan-400" /> Bank sync
                          </div>
                          {c.lastSmsAt && <span className="text-[10px] text-slate-500">as of {new Date(c.lastSmsAt).toLocaleDateString()}</span>}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="rounded-md bg-slate-900/40 px-2.5 py-1.5 min-w-0">
                            <div className="text-[10px] text-slate-500 truncate">Floosi available</div>
                            <div className="text-sm font-semibold text-slate-100 tabular-nums whitespace-nowrap">{fmtMoney(available, c.currency)}</div>
                          </div>
                          <div className="rounded-md bg-slate-900/40 px-2.5 py-1.5 min-w-0">
                            <div className="text-[10px] text-slate-500 truncate">Bank (last SMS)</div>
                            <div className="text-sm font-semibold text-slate-100 tabular-nums whitespace-nowrap">{fmtMoney(smsAvail, c.currency)}</div>
                          </div>
                          <div className="rounded-md bg-slate-900/40 px-2.5 py-1.5 min-w-0">
                            <div className="text-[10px] text-slate-500 truncate">Difference</div>
                            <div className={`text-sm font-bold tabular-nums whitespace-nowrap ${tone.txt}`}>{aligned ? 'in sync' : `${delta >= 0 ? '+' : '−'}${fmtMoney(absD, c.currency, false)}`}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Statement / Due dates */}
                  {(c.statementDay || c.paymentDueDay) && (
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      {stmtDate && (
                        <div className="bg-slate-800/50 rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-1 min-w-0">
                          <span className="text-slate-400 flex-shrink-0">Next stmt</span>
                          <span className="font-medium whitespace-nowrap truncate">{stmtDate.toLocaleDateString('en', { month: 'short', day: 'numeric' })} <span className="text-slate-500">· {daysToStmt === 0 ? 'today' : daysToStmt === 1 ? 'tmrw' : `${daysToStmt}d`}</span></span>
                        </div>
                      )}
                      {dueDate && (
                        <div className={`rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-1 min-w-0 ${daysToDue !== null && daysToDue <= 5 ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-slate-800/50'}`}>
                          <span className="text-slate-400 flex-shrink-0">Due</span>
                          <span className={`font-medium whitespace-nowrap truncate ${daysToDue !== null && daysToDue <= 5 ? 'text-amber-300' : ''}`}>{dueDate.toLocaleDateString('en', { month: 'short', day: 'numeric' })} <span className={daysToDue !== null && daysToDue <= 5 ? 'text-amber-400' : 'text-slate-500'}>· {daysToDue === 0 ? 'today!' : daysToDue === 1 ? 'tmrw!' : `${daysToDue}d`}</span></span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Real statement record: amount due + live payment progress (expandable) */}
                  {latestStmt && (() => {
                    const key = `${c.id}:statement`;
                    const open = !!expandedSec[key];
                    return (
                    <div className={`rounded-lg p-3 text-xs space-y-2 border ${stmtState === 'overdue' ? 'bg-rose-500/10 border-rose-500/30' : stmtState === 'paid' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800/40 border-slate-700'}`}>
                      <button onClick={() => toggleSec(key)} className="w-full flex justify-between items-center gap-2">
                        <span className="text-slate-300 font-semibold flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Statement due</span>
                        <span className="flex items-center gap-1.5 flex-shrink-0">
                          <span className="font-semibold">{fmtMoney(stmtDue, c.currency)}</span>
                          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </span>
                      </button>
                      {open && (<>
                      <div className="h-2 bg-slate-700/60 rounded-full overflow-hidden">
                        <div className={`h-full transition-all ${stmtState === 'overdue' ? 'bg-rose-500' : 'bg-emerald-500'}`} style={{ width: `${stmtPct}%` }} />
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-slate-400">{stmtNothingDue ? 'Nothing to pay this cycle' : `${fmtMoney(stmtPaid, c.currency)} paid (${stmtPct.toFixed(0)}%)`}</span>
                        {stmtState === 'paid'
                          ? <span className="text-emerald-400 font-medium">{stmtNothingDue ? 'Settled ✓' : 'Paid in full ✓'}</span>
                          : <span className="text-slate-300">{fmtMoney(stmtRemaining, c.currency)} left</span>}
                      </div>
                      {latestStmt.minimumPayment != null && Number(latestStmt.minimumPayment) > 0 && stmtState !== 'paid' && (
                        <div className="flex justify-between text-[11px] border-t border-slate-700/60 pt-1.5">
                          <span className="text-slate-400">Min payment {fmtMoney(latestStmt.minimumPayment, c.currency)}</span>
                          <span className={minMet ? 'text-emerald-400' : 'text-amber-400'}>{minMet ? 'met ✓' : 'not met'}</span>
                        </div>
                      )}
                      {Number(latestStmt.cashbackApplied) > 0 && (
                        <div className="flex justify-between text-[11px] text-emerald-300/80"><span>Cashback credited</span><span>{fmtMoney(latestStmt.cashbackApplied, c.currency)}</span></div>
                      )}
                      {latestStmt.cashbackBalance != null && (
                        <div className="flex justify-between text-[11px] text-slate-400"><span>Cashback balance</span><span>{fmtMoney(latestStmt.cashbackBalance, c.currency)}</span></div>
                      )}
                      </>)}
                    </div>
                    );
                  })()}

                  {/* Statement balance vs current (legacy, when no statement record yet) */}
                  {!latestStmt && stmtBalance > 0 && (
                    <div className="bg-slate-800/40 rounded-lg p-3 text-xs space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Statement Balance (locked-in due)</span>
                        <span className="font-semibold">{fmtMoney(stmtBalance, c.currency)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Post-Statement Spending</span>
                        <span className="text-slate-300">{fmtMoney(Math.max(0, balance - stmtBalance), c.currency)}</span>
                      </div>
                      {c.minimumPayment > 0 && (
                        <div className="flex justify-between border-t border-slate-700 pt-1.5 mt-1.5">
                          <span className="text-slate-400">Minimum Payment</span>
                          <span className="text-amber-400 font-semibold">{fmtMoney(c.minimumPayment, c.currency)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Cashback balance tracker */}
                  {(c.cashbackBalance != null || true) && (
                    <div className="flex items-center justify-between bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs">
                      <span className="text-slate-400">Cashback balance</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-emerald-300">{c.cashbackBalance != null ? fmtMoney(c.cashbackBalance, c.currency) : '—'}</span>
                        <button onClick={() => setCbFor(c)} className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] text-slate-300">Adjust</button>
                      </div>
                    </div>
                  )}

                  {/* APR */}
                  {c.apr > 0 && (() => {
                    // Audit win: actually show what the APR costs. The live
                    // balance (incl. installments) is what the card revolves
                    // on. Daily simple interest at APR / 365, ×30 ≈ monthly.
                    const liveOwed = c.liveCurrentBalance || 0;
                    const monthlyInterest = Math.max(0, liveOwed) * (c.apr / 100) / 12;
                    return (
                      <div className="text-xs flex items-center justify-between">
                        <span className="text-slate-500">APR {c.apr.toFixed(2)}%</span>
                        <span className="text-amber-400" title="Estimated monthly interest if balance is unchanged">
                          ≈ {fmtMoney(monthlyInterest, c.currency, false)}/mo
                        </span>
                      </div>
                    );
                  })()}

                  {/* Active installments on this card — read-only summary. Creating /
                       deleting installments now lives on the Loans page. */}
                  {(() => {
                    const live = cardInstallments.filter((ci) => ci.cardId === c.id && !ci.deletedAt);
                    if (live.length === 0) return null;
                    const key = `${c.id}:installments`;
                    const open = !!expandedSec[key];
                    const totalMonthly = live.reduce((s, ci) => s + (Number(ci.monthlyPayment) || 0), 0);
                    return (
                      <div className="border-t border-slate-800 pt-3 mt-1">
                        <button onClick={() => toggleSec(key)} className="w-full flex items-center justify-between gap-2 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                          <span className="flex items-center gap-1"><Repeat className="w-3 h-3" /> Active installments ({live.length})</span>
                          <span className="flex items-center gap-1.5 normal-case tracking-normal text-slate-400">{fmtMoney(totalMonthly, c.currency, false)}/mo<ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} /></span>
                        </button>
                        {open && (<>
                        <div className="space-y-1.5 text-xs mt-2">
                          {live.map((ci) => {
                            const original = Number(ci.originalAmount) || 0;
                            const remaining = Number(ci.remainingBalance) || 0;
                            const paid = Math.max(0, original - remaining);
                            const monthly = Number(ci.monthlyPayment) || 0;
                            const term = Number(ci.termMonths) || 0;
                            // Use principal-paid as the source of truth for
                            // "payments made" — derivable directly from
                            // (original - remaining) / monthly. The earlier
                            // formula (term - ceil(remaining/monthly)) only
                            // worked when monthly ≈ original/term; an
                            // installment whose stored monthly_payment is off
                            // (e.g. computed against a stale term value)
                            // produced wildly wrong "X paid" counts.
                            const paymentsMade = monthly > 0
                              ? Math.min(term, Math.max(0, Math.round((original - remaining) / monthly)))
                              : 0;
                            const paymentsLeft = Math.max(0, term - paymentsMade);
                            return (
                              <div key={ci.id} className="bg-slate-800/40 rounded px-2 py-1.5">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium truncate">{ci.name}</span>
                                  <span className="text-slate-400 text-[10px] flex-shrink-0">{ci.kind.replace(/_/g, ' ')}</span>
                                </div>
                                <div className="text-[10px] text-slate-400 mt-0.5">
                                  {fmtMoney(monthly, ci.currency, false)}/mo · {(Number(ci.interestRate) || 0).toFixed(2)}% · {paymentsMade}/{term} payments · {fmtMoney(paid, ci.currency, false)}/{fmtMoney(original, ci.currency, false)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-2">Manage on the Loans page.</div>
                        </>)}
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && <CardModal card={editing} categories={categories} onSave={onSave} onClose={() => { setShowModal(false); setEditing(null); }} />}
      {stmtFor && (
        <StatementModal
          card={stmtFor}
          existing={(cardStatements || []).filter(s => s.cardId === stmtFor.id && !s.deletedAt).sort((a, b) => cmpDateStr(b.statementDate, a.statementDate))[0] || null}
          onSave={onSaveStatement}
          onDelete={onDeleteStatement}
          onClose={() => setStmtFor(null)}
        />
      )}
      {uploadFor && (
        <StatementUploadModal
          card={uploadFor}
          cardTxns={transactions}
          accounts={accounts}
          categories={categories}
          cardStatements={cardStatements}
          onImportTransactions={onImportTransactions}
          onDeleteTransaction={onDeleteTransaction}
          onSaveStatement={onSaveStatement}
          onSaveCard={onSave}
          onClose={() => setUploadFor(null)}
        />
      )}
      {cbFor && (
        <CashbackAdjustModal
          card={cbFor}
          onSave={(newBalance) => onSave({ ...cbFor, cashbackBalance: newBalance })}
          onClose={() => setCbFor(null)}
        />
      )}
    </div>
  );
}

// Quick cashback-balance adjust: set it directly, or earn (+) / use (−) an
// amount against the current running balance for this card.
function CashbackAdjustModal({ card, onSave, onClose }) {
  const [saving, setSaving] = useState(false);
  const cur = Number(card.cashbackBalance) || 0;
  const [mode, setMode] = useState('earn'); // earn | use | set
  const [amount, setAmount] = useState('');
  const amt = parseFloat(String(amount).replace(/[^\d.\-]/g, ''));
  const valid = Number.isFinite(amt);
  const next = !valid ? cur : mode === 'set' ? amt : mode === 'earn' ? Math.round((cur + amt) * 100) / 100 : Math.round((cur - amt) * 100) / 100;
  return (
    <Modal title={`Cashback — ${card.name}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="text-xs text-slate-400">Current balance: <span className="font-semibold text-emerald-300">{fmtMoney(cur, card.currency)}</span></div>
        <div className="grid grid-cols-3 gap-2">
          {[['earn', 'Earned +'], ['use', 'Used −'], ['set', 'Set to']].map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)} className={`py-2 rounded-lg text-sm font-medium ${mode === m ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200' : 'bg-slate-800 text-slate-400'}`}>{label}</button>
          ))}
        </div>
        <Field label={`Amount (${card.currency || 'AED'})`}>
          <input type="text" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} className="input text-right font-mono" placeholder="0.00" autoFocus />
        </Field>
        {valid && <div className="text-xs text-slate-400">New balance: <span className="font-semibold text-emerald-300">{fmtMoney(next, card.currency)}</span></div>}
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={async () => { setSaving(true); try { await onSave(next); onClose(); } finally { setSaving(false); } }} disabled={saving || !valid} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-sm font-medium">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}

// Manual statement entry (Phase 1 of statement reconciliation). Captures the
// cycle's headline numbers so the card can show amount-due + payment progress
// and remind before the due date. Phase 2 will fill these from a parsed PDF.
function StatementModal({ card, existing, onSave, onDelete, onClose }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    statementDate: existing?.statementDate || todayStr(),
    dueDate: existing?.dueDate || '',
    closingBalance: existing?.closingBalance != null ? String(existing.closingBalance) : '',
    totalPaymentDue: existing?.totalPaymentDue != null ? String(existing.totalPaymentDue) : '',
    openingBalance: existing?.openingBalance != null ? String(existing.openingBalance) : '',
    minimumPayment: existing?.minimumPayment != null ? String(existing.minimumPayment) : '',
    cashbackApplied: existing?.cashbackApplied != null ? String(existing.cashbackApplied) : '',
    cashbackBalance: existing?.cashbackBalance != null ? String(existing.cashbackBalance) : '',
    notes: existing?.notes || '',
  });
  const num = (v) => v === '' || v == null ? null : (parseFloat(String(v).replace(/[^\d.\-]/g, '')) || 0);
  // CR-aware: "727.71 CR" (a credit) → -727.71. Used for Total Payment Due.
  const numDue = (v) => {
    if (v === '' || v == null) return null;
    const credit = /cr/i.test(String(v));
    const n = parseFloat(String(v).replace(/[^\d.\-]/g, '')) || 0;
    return credit ? -Math.abs(n) : n;
  };
  const valid = !!form.statementDate && form.closingBalance !== '' && Number.isFinite(num(form.closingBalance));
  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        id: existing?.id || uid('cst'),
        cardId: card.id,
        statementDate: form.statementDate,
        dueDate: form.dueDate || null,
        closingBalance: num(form.closingBalance),
        totalPaymentDue: numDue(form.totalPaymentDue),
        openingBalance: num(form.openingBalance),
        minimumPayment: num(form.minimumPayment),
        cashbackApplied: num(form.cashbackApplied) || 0,
        cashbackBalance: num(form.cashbackBalance),
        creditLimitSnapshot: card.creditLimit || null,
        currencyCode: card.currency || 'AED',
        notes: form.notes?.trim() || null,
      });
      onClose();
    } finally { setSaving(false); }
  };
  return (
    <Modal title={`${existing ? 'Edit' : 'Add'} statement — ${card.name}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="text-[11px] text-slate-500">Enter the headline numbers from this cycle's statement. Payment progress is tracked automatically from transfers you make onto this card on/after the statement date.</div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Statement date">
            <input type="date" value={form.statementDate} onChange={e => setForm(f => ({ ...f, statementDate: e.target.value }))} className="input" />
          </Field>
          <Field label="Payment due date">
            <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} className="input" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label={`Statement balance (${card.currency || 'AED'})`}>
            <input type="text" inputMode="decimal" value={form.closingBalance} onChange={e => setForm(f => ({ ...f, closingBalance: e.target.value }))} className="input text-right font-mono" placeholder="0.00" />
          </Field>
          <Field label="Total payment due">
            <input type="text" inputMode="text" value={form.totalPaymentDue} onChange={e => setForm(f => ({ ...f, totalPaymentDue: e.target.value }))} className="input text-right font-mono" placeholder="same as balance" />
          </Field>
        </div>
        <div className="text-[11px] text-slate-500 -mt-1">Statement balance = total outstanding (incl. installments). <span className="text-slate-400">Total payment due</span> = what's actually due this cycle — leave blank if same, or add <span className="font-mono">CR</span> for a credit (nothing due).</div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Minimum payment">
            <input type="text" inputMode="decimal" value={form.minimumPayment} onChange={e => setForm(f => ({ ...f, minimumPayment: e.target.value }))} className="input text-right font-mono" placeholder="0.00" />
          </Field>
          <div />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Cashback credited (optional)">
            <input type="text" inputMode="decimal" value={form.cashbackApplied} onChange={e => setForm(f => ({ ...f, cashbackApplied: e.target.value }))} className="input text-right font-mono" placeholder="0.00" />
          </Field>
          <Field label="Cashback balance (optional)">
            <input type="text" inputMode="decimal" value={form.cashbackBalance} onChange={e => setForm(f => ({ ...f, cashbackBalance: e.target.value }))} className="input text-right font-mono" placeholder="0.00" />
          </Field>
        </div>
        <Field label="Notes (optional)">
          <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="input" />
        </Field>
        <div className="flex gap-2 pt-2">
          {existing && (
            <button onClick={async () => { await onDelete(existing.id); onClose(); }} disabled={saving} className="px-3 py-2 rounded-lg bg-rose-900/40 hover:bg-rose-900/60 text-rose-300 text-sm">Delete</button>
          )}
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !valid} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-sm font-medium">{saving ? 'Saving…' : 'Save statement'}</button>
        </div>
      </div>
    </Modal>
  );
}

// PDF statement upload + reconcile preview.
// Statuses: new | matched | skip | payment_missing (statement rows) | floosi_only (Floosi rows)
// On confirm: creates new txns, links matched/suggested-confirmed/manually-linked txns,
// optionally deletes floosi-only txns, auto-saves statement summary + cashback balance.
const FAB_AED_PER_POINT = 0.003;

function StatementUploadModal({ card, cardTxns, accounts = [], categories = [], cardStatements = [], onImportTransactions, onDeleteTransaction, onSaveStatement, onSaveCard, onClose }) {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState(card.statementPdfPassword || '');
  const [status, setStatus] = useState('idle'); // idle | parsing | parsed | importing | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const [parsedResult, setParsedResult] = useState(null);
  const [reconcileRows, setReconcileRows] = useState([]);
  // checked: indices of 'new' rows to import
  const [checked, setChecked] = useState(new Set());
  // rejected: indices of auto-'matched' rows the user rejected — demoted to 'new',
  // their Floosi tx freed back into the manual-match candidate pool.
  const [rejected, setRejected] = useState(new Set());
  // deleting: indices of 'floosi_only' rows to soft-delete
  const [deleting, setDeleting] = useState(new Set());
  // payAccts: for ticked 'payment_missing' rows, the source account the
  // transfer-into-this-card came from (keyed by row index).
  const [payAccts, setPayAccts] = useState({});
  // manualMatches: a 'new' statement row the user manually linked to an
  // existing Floosi tx the engine couldn't auto-match (row index → floosi tx id).
  const [manualMatches, setManualMatches] = useState({});
  // catOverrides: chosen category per 'new' row (row index → category name).
  const [catOverrides, setCatOverrides] = useState({});

  const counts = {
    new: reconcileRows.filter(r => r.status === 'new').length,
    matched: reconcileRows.filter(r => r.status === 'matched').length,
    floosi: reconcileRows.filter(r => r.status === 'floosi_only').length,
    paymentMissing: reconcileRows.filter(r => r.status === 'payment_missing').length,
    skip: reconcileRows.filter(r => r.status === 'skip').length,
  };

  const parse = async () => {
    if (!file) return;
    setStatus('parsing');
    setErrorMsg('');
    try {
      const pages = await extractPages(file, password);
      const parser = dispatchParser(card.name);
      if (!parser) throw new Error(`No parser for "${card.name}". Supported: CBD, ENBD, SIB, EI, ADIB, DIB, FAB.`);
      const result = parser(pages);
      const rows = reconcile({ parsedStatement: result, cardTxns, cardId: card.id });
      setChecked(new Set(rows.map((r, i) => r.status === 'new' ? i : -1).filter(i => i >= 0)));
      setRejected(new Set());
      setDeleting(new Set());
      setPayAccts({});
      setManualMatches({});
      setCatOverrides({});
      setParsedResult(result);
      setReconcileRows(rows);
      setStatus('parsed');
    } catch (e) {
      setErrorMsg(e.message || 'Failed to parse PDF.');
      setStatus('error');
    }
  };

  const buildNewTx = (pt, stmtId, categoryOverride) => {
    const isCredit = !!pt.credit;
    const tag = pt.tag;
    const category = categoryOverride || (isCredit
      ? (tag === 'cashback' ? 'Cashback & Rewards' : 'Other Income')
      : (tag === 'fee' ? 'Other' : tag === 'installment' ? 'Loan Payment' : 'Other'));
    return {
      id: uid('tx'),
      date: pt.date || pt.postingDate,
      description: pt.description,
      amount: pt.amount,
      currency: pt.currency || card.currency || 'AED',
      type: isCredit ? 'income' : 'expense',
      account: card.name,
      cardId: card.id,
      category,
      originalCurrency: pt.originalCurrency || null,
      originalAmount: pt.originalAmount ?? null,
      source: 'statement_pdf',
      linkedStatementId: stmtId,
    };
  };

  // A card-bill payment that wasn't found in Floosi imports as a transfer
  // FROM the chosen bank account INTO this card (not a card charge) — that's
  // how Floosi models paying a card, so the cash side is tracked correctly.
  const buildPaymentTransfer = (pt, stmtId, accountId) => {
    const acct = accounts.find(a => a.id === accountId);
    return {
      id: uid('tx'),
      type: 'transfer',
      date: pt.date || pt.postingDate,
      description: pt.description,
      amount: pt.amount,
      currency: pt.currency || card.currency || 'AED',
      category: 'Transfer',
      accountId: null, cardId: null,
      fromAccountId: accountId || null, fromCardId: null, fromInvestmentId: null,
      toAccountId: null, toCardId: card.id, toInvestmentId: null,
      fromAccount: acct?.name || null, toAccount: card.name,
      source: 'statement_pdf',
      linkedStatementId: stmtId,
    };
  };

  const importTxns = async () => {
    setStatus('importing');
    try {
      const stmtId = uid('cst');
      const toSave = [];
      const floosiTxById = {};
      reconcileRows.forEach((r, i) => {
        if (r.kind === 'floosi') floosiTxById[r.floosiTx.id] = r.floosiTx;
        if (r.suggested) floosiTxById[r.suggested.id] = r.suggested;
        if (r.status === 'matched' && rejected.has(i) && r.matchedTx) floosiTxById[r.matchedTx.id] = r.matchedTx;
      });

      // refTo maps a statement Ref. No. → the resulting Floosi tx, so DCC / fee
      // lines (which carry the SAME Ref. No. as the purchase they sit on) can be
      // linked to their parent. feeTxs are linked after the map is built.
      const refTo = {};
      const feeTxs = [];

      reconcileRows.forEach((r, i) => {
        if (r.kind !== 'statement') return;
        const pt = r.parsedTxn;
        // A rejected auto-match behaves like a 'new' row.
        const asNew = r.status === 'new' || (r.status === 'matched' && rejected.has(i));
        let tx = null;
        if (asNew && manualMatches[i]) {
          // Manually linked / confirmed-suggestion: link the existing tx (don't
          // create new) and true-up its amount to the statement's final figure.
          const ftx = floosiTxById[manualMatches[i]];
          if (ftx) tx = { ...ftx, amount: pt.amount, amountBase: pt.amount, linkedStatementId: stmtId };
        } else if (asNew && checked.has(i)) {
          // Fees inherit their parent's category below; others use the row's
          // chosen/learned category.
          tx = buildNewTx(pt, stmtId, pt.tag === 'fee' ? undefined : (rowCategory(r, i) || undefined));
        } else if (r.status === 'payment_missing' && checked.has(i)) {
          const acctId = payAccts[i] || accounts[0]?.id;
          if (acctId) tx = buildPaymentTransfer(pt, stmtId, acctId);
        } else if (r.status === 'matched' && r.matchedTx) {
          // Auto-matched: true-up the AED to the statement's exact billed figure
          // too (foreign txs match on the ORIGINAL amount, so their SMS-estimated
          // AED is stale until corrected here). Card txs are AED-billed, so
          // amountBase tracks amount 1:1. Domestic txs are already ±0.01, no-op.
          tx = { ...r.matchedTx, amount: pt.amount, amountBase: pt.amount, linkedStatementId: stmtId };
        }
        if (!tx) return;
        toSave.push(tx);
        if (pt?.refNo) {
          if (pt.tag === 'fee') feeTxs.push({ tx, refNo: pt.refNo });
          else refTo[pt.refNo] = { id: tx.id, desc: pt.description, category: tx.category };
        }
      });

      // Link each fee line to the purchase it belongs to (shared Ref. No.) and
      // inherit its category — a fee belongs to the same bucket as its purchase.
      for (const { tx, refNo } of feeTxs) {
        const parent = refTo[refNo];
        if (parent && parent.id !== tx.id) {
          tx.parentTxId = parent.id;
          if (parent.category) tx.category = parent.category;
          tx.notes = `Foreign-transaction fee · ${parent.desc || ''}`.trim().replace(/ ·\s*$/, '');
        }
      }

      // Soft-delete floosi-only rows the user chose to delete (but never one
      // that's been manually claimed as a match).
      const claimedFloosiIds = new Set(Object.values(manualMatches));
      for (const [i, r] of reconcileRows.entries()) {
        if (r.kind === 'floosi' && deleting.has(i) && !claimedFloosiIds.has(r.floosiTx.id)) {
          await onDeleteTransaction(r.floosiTx.id);
        }
      }

      // Statement's closing cashback balance (AED). EI/DIB report it directly;
      // FAB reports a points balance we convert. Truth as of the close date.
      const s = parsedResult.summary;
      let closingCashback = s.cashbackBalance;
      if (closingCashback == null && parsedResult.bank === 'FAB' && s.rewardsPoints != null) {
        closingCashback = Math.round(s.rewardsPoints * FAB_AED_PER_POINT * 100) / 100;
      }

      // The statement row must exist BEFORE the transactions that link to it
      // (linked_statement_id FK). card_statements requires both statement_date
      // and closing_balance (NOT NULL); if either is missing we can't save it,
      // so don't link the txs to a statement that won't exist.
      const canSaveStatement = s.statementDate != null && s.closingBalance != null;
      if (canSaveStatement) {
        await onSaveStatement({
          id: stmtId,
          cardId: card.id,
          statementDate: s.statementDate,
          dueDate: s.dueDate || null,
          closingBalance: s.closingBalance,
          totalPaymentDue: s.totalPaymentDue ?? null,
          openingBalance: null,
          minimumPayment: s.minimumPayment || null,
          cashbackApplied: s.cashbackEarned || 0,
          cashbackBalance: closingCashback,
          creditLimitSnapshot: s.creditLimit || card.creditLimit || null,
          currencyCode: card.currency || 'AED',
          notes: `Imported from ${parsedResult.bank} PDF`,
        });
      } else {
        // No statement row to point at — strip the dangling link.
        toSave.forEach(t => { t.linkedStatementId = null; });
      }

      // Insert parents before fee children so the parent_tx_id FK is satisfied
      // even when the parent purchase is itself newly imported in this batch.
      const feeIds = new Set(feeTxs.map(f => f.tx.id));
      const parentBatch = toSave.filter(t => !feeIds.has(t.id));
      const feeBatch = toSave.filter(t => feeIds.has(t.id));
      if (parentBatch.length > 0) await onImportTransactions(parentBatch);
      if (feeBatch.length > 0) await onImportTransactions(feeBatch);

      if (canSaveStatement) {
        // Learn this card's FX markup from every statement-confirmed foreign tx —
        // the gold-standard observation (we know the bank's billed AED). The
        // per-card multiplier self-improves with each import. Best-effort.
        const foreignConfirmed = reconcileRows.filter((r, i) =>
          r.kind === 'statement' && r.parsedTxn
          && r.parsedTxn.originalCurrency && r.parsedTxn.originalCurrency !== 'AED'
          && Number(r.parsedTxn.originalAmount) > 0
          && ((r.status === 'matched' && !rejected.has(i)) || (r.status === 'new' && manualMatches[i])));
        for (const r of foreignConfirmed) {
          const pt = r.parsedTxn;
          try {
            await supabase.rpc('learn_card_fx_markup', {
              p_card_id: card.id,
              p_currency: pt.originalCurrency,
              p_original: Number(pt.originalAmount),
              p_billed_aed: Number(pt.amount),
              p_date: pt.date || pt.postingDate,
            });
          } catch { /* learning is best-effort, never block the import */ }
        }

        // A reconciled statement is authoritative — it supersedes any pre-statement
        // SMS available-limit drift alert for this card (those only guess the
        // markup; the statement now carries the exact figures). Clear them so the
        // markup estimator never divides a stale drift across now-corrected txs.
        try {
          await supabase.from('sms_reconciliation_alerts')
            .update({ resolved_at: new Date().toISOString(), resolution_note: `Superseded by ${parsedResult.bank} statement import` })
            .eq('card_id', card.id).is('resolved_at', null);
        } catch { /* non-fatal */ }
      }

      // Re-anchor the card's running cashback balance to the statement's closing
      // balance, then subtract only redemptions dated AFTER the statement closed
      // (the statement balance already reflects everything up to its close).
      if (closingCashback != null) {
        const postCloseRedeemed = (cardTxns || [])
          .filter(t => !t.deletedAt && t.cardId === card.id && t.type === 'income'
            && /CASHBACK.*REDE(MPTION|EMED)|REWARDS.*REDE(MPTION|EMED)/i.test(t.description || '')
            && s.statementDate && t.date > s.statementDate)
          .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
        const newBalance = Math.max(0, Math.round((closingCashback - postCloseRedeemed) * 100) / 100);
        await onSaveCard({ ...card, cashbackBalance: newBalance });
      }

      setStatus('done');
    } catch (e) {
      setErrorMsg(e.message || 'Import failed.');
      setStatus('error');
    }
  };

  const toggleAllNew = () => {
    const idxs = reconcileRows.map((r, i) => r.status === 'new' ? i : -1).filter(i => i >= 0);
    setChecked(checked.size === idxs.length ? new Set() : new Set(idxs));
  };

  const toggle = (setter, i) => setter(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });

  // ── Manual matching: link a 'new' statement row to an unmatched Floosi tx ──
  // A Floosi tx is a "credit" (matches a statement CR) if it's income or a refund.
  const isCreditTx = (t) => t.type === 'income' || (t.type === 'expense' && !!t.isRefund);
  // A fee line (INTL DCC FEE) belongs to the purchase sharing its Ref. No. —
  // surface that association so it's clear what the fee is for.
  const parentByRef = {};
  reconcileRows.forEach(r => {
    if (r.kind === 'statement' && r.parsedTxn?.refNo && r.parsedTxn.tag !== 'fee') parentByRef[r.parsedTxn.refNo] = r.parsedTxn;
  });

  // ── Category picker for 'new' rows (pre-filled from the user's own history) ──
  const expenseCats = categories.filter(c => !c.deletedAt && c.kind === 'expense');
  const incomeCats = categories.filter(c => !c.deletedAt && c.kind === 'income');
  const catNames = new Set(categories.filter(c => !c.deletedAt).map(c => c.name));
  const firstWord = (s) => (String(s || '').toUpperCase().match(/[A-Z0-9]{4,}/) || [])[0] || null;
  // Most common category among existing card txns sharing this row's merchant word + direction.
  const learnedCategory = (pt) => {
    const want = firstWord(pt.description); if (!want) return null;
    const isInc = !!pt.credit;
    const counts = {};
    for (const t of cardTxns) {
      if (t.deletedAt || t.type === 'transfer' || (t.type === 'income') !== isInc) continue;
      if (!t.category || !catNames.has(t.category) || firstWord(t.description) !== want) continue;
      counts[t.category] = (counts[t.category] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
  };
  // Category a 'new' row imports with: explicit pick → learned → none.
  const rowCategory = (r, i) => (catOverrides[i] != null ? catOverrides[i] : (learnedCategory(r.parsedTxn) || ''));
  const claimedFloosi = new Set(Object.values(manualMatches));
  const floosiTxIndex = {};
  // Pool of Floosi txs available to link: the floosi-only rows, the engine's
  // suggestions, and any tx freed by rejecting its auto-match.
  const linkPool = [];
  reconcileRows.forEach((r, i) => {
    if (r.kind === 'floosi' && r.status === 'floosi_only') { floosiTxIndex[r.floosiTx.id] = r.floosiTx; linkPool.push(r.floosiTx); }
    if (r.suggested) floosiTxIndex[r.suggested.id] = r.suggested;
    if (r.status === 'matched' && rejected.has(i) && r.matchedTx) { floosiTxIndex[r.matchedTx.id] = r.matchedTx; linkPool.push(r.matchedTx); }
  });
  // Linkable candidates (same direction), nearest amount first, not already claimed.
  const candidatesFor = (pt) => linkPool
    .filter(t => !claimedFloosi.has(t.id) && isCreditTx(t) === !!pt.credit)
    .sort((a, b) => Math.abs((Number(a.amount) || 0) - pt.amount) - Math.abs((Number(b.amount) || 0) - pt.amount));
  // Linking removes the row from the "import as new" set; unlinking restores it.
  const linkManual = (i, fid) => {
    setManualMatches(p => { const n = { ...p }; if (fid) n[i] = fid; else delete n[i]; return n; });
    setChecked(prev => { const n = new Set(prev); if (fid) n.delete(i); else n.add(i); return n; });
  };
  // Reject an auto-match → behaves like 'new' (default unchecked; tx is freed).
  const rejectMatch = (i) => { setRejected(prev => { const n = new Set(prev); n.add(i); return n; }); };
  const restoreMatch = (i) => {
    setRejected(prev => { const n = new Set(prev); n.delete(i); return n; });
    setChecked(prev => { const n = new Set(prev); n.delete(i); return n; });
    setManualMatches(p => { const n = { ...p }; delete n[i]; return n; });
  };

  // Total actions that will happen on confirm
  const actionCount = (counts.matched - rejected.size) + checked.size + Object.keys(manualMatches).length;

  if (status === 'done') {
    return (
      <Modal title={`Statement uploaded — ${card.name}`} onClose={onClose}>
        <div className="space-y-4">
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-4 text-sm text-emerald-300">
            Done. Statement saved, transactions reconciled.
          </div>
          <button onClick={onClose} className="w-full py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Close</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Upload statement — ${card.name}`} onClose={onClose} wide xWide={!!parsedResult} staticBackdrop={!!parsedResult}>
      <div className="space-y-4">
        {status === 'idle' || status === 'error' ? (
          <>
            <div className="text-[11px] text-slate-500">PDF is parsed locally — no data leaves your device.</div>
            <Field label="Statement PDF">
              <input type="file" accept="application/pdf,.pdf"
                onChange={e => { setFile(e.target.files?.[0] || null); setStatus('idle'); setErrorMsg(''); }}
                className="input text-sm" />
            </Field>
            <Field label="PDF password (if any)">
              <input type="text" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Leave blank if not password-protected" className="input font-mono text-sm" />
            </Field>
            {errorMsg && <div className="rounded-lg bg-rose-500/10 border border-rose-500/20 p-3 text-xs text-rose-300">{errorMsg}</div>}
            <div className="flex gap-2 pt-1">
              <button onClick={onClose} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
              <button onClick={parse} disabled={!file}
                className="flex-1 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-sm font-medium flex items-center justify-center gap-2">
                <Upload className="w-3.5 h-3.5" /> Parse PDF
              </button>
            </div>
          </>
        ) : status === 'parsing' ? (
          <div className="text-center py-8 text-sm text-slate-400">Extracting text from PDF…</div>
        ) : (
          <>
            {/* Statement summary */}
            {parsedResult?.summary && (() => {
              const s = parsedResult.summary;
              const cols = [
                { label: 'Closing balance', val: s.closingBalance != null ? fmtMoney(s.closingBalance, card.currency, false) : '—' },
                { label: 'Min payment', val: s.minimumPayment != null ? fmtMoney(s.minimumPayment, card.currency, false) : '—' },
                { label: 'Due date', val: s.dueDate || '—' },
              ];
              if (s.rewardsPoints != null) cols.push({ label: 'FAB Rewards', val: `${s.rewardsPoints.toLocaleString()} pts → ${fmtMoney(Math.round(s.rewardsPoints * FAB_AED_PER_POINT * 100) / 100, 'AED', false)}` });
              if (s.cashbackBalance != null) cols.push({ label: 'Cashback balance', val: `${fmtMoney(s.cashbackBalance, card.currency, false)}${s.cashbackEarned ? ` (+${fmtMoney(s.cashbackEarned, card.currency, false)} earned)` : ''}` });
              return (
                <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-3 grid grid-cols-3 gap-2 text-xs">
                  {cols.map(c => <div key={c.label}><div className="text-slate-500">{c.label}</div><div className="font-mono font-semibold">{c.val}</div></div>)}
                </div>
              );
            })()}

            {/* Already-imported guard: a statement for this card+date is on file,
                so its txns are already linked and excluded from matching (they'd
                show as "new" here) — re-confirming would duplicate them. */}
            {(() => {
              // Only warn while REVIEWING (pre-confirm). Once the user hits
              // confirm, the import itself saves this statement — which flips
              // `existing` true and would flash the banner right before the
              // success view. Suppress it during 'importing'/'done'.
              if (status !== 'parsed') return null;
              const sd = parsedResult?.summary?.statementDate;
              const existing = sd && cardStatements.find(s => s.cardId === card.id && !s.deletedAt && s.statementDate === sd);
              if (!existing) return null;
              return (
                <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-3 text-xs text-rose-300">
                  ⚠️ This statement (dated {sd}) was already imported — its transactions are already linked, so they show as “new” below. <span className="font-semibold">Don't re-confirm them or you'll create duplicates.</span> Close unless you're intentionally re-importing.
                </div>
              );
            })()}

            {/* Parse sanity-check: previous balance + Σ(signed parsed txns) should
                equal the stated closing balance. A mismatch means lines were
                dropped/misparsed (the silent failure mode) — surface it loudly. */}
            {(() => {
              const s = parsedResult?.summary;
              // Reconcile against the cycle's billed total — Total Payment Due where
              // it differs from the carried balance (FAB: installments), else closing.
              const target = s?.totalPaymentDue != null ? s.totalPaymentDue : s?.closingBalance;
              if (!s || s.previousBalance == null || target == null) return null;
              const net = (parsedResult.transactions || []).reduce((sum, t) => sum + (t.credit ? -(Number(t.amount) || 0) : (Number(t.amount) || 0)), 0);
              const diff = Math.round(((s.previousBalance + net) - target) * 100) / 100;
              if (Math.abs(diff) <= 1) return null;
              return (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-300">
                  ⚠️ Parsed transactions don't reconcile to the statement — off by <span className="font-mono font-semibold">{fmtMoney(Math.abs(diff), card.currency, false)}</span>. Some lines may not have parsed (previous balance {fmtMoney(s.previousBalance, card.currency, false)} + parsed activity ≠ {fmtMoney(target, card.currency, false)}). Review carefully before importing.
                </div>
              );
            })()}

            {/* Status counts */}
            <div className="flex flex-wrap gap-2 text-xs">
              {counts.new > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">{counts.new} new</span>}
              {Object.keys(manualMatches).length > 0 && <span className="px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300">{Object.keys(manualMatches).length} linked</span>}
              {counts.matched - rejected.size > 0 && <span className="px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">{counts.matched - rejected.size} matched</span>}
              {counts.floosi > 0 && <span className="px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400">{counts.floosi} in Floosi only</span>}
              {counts.paymentMissing > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">{counts.paymentMissing} card payment{counts.paymentMissing > 1 ? 's' : ''}</span>}
              {counts.skip > 0 && <span className="px-2 py-0.5 rounded-full bg-slate-800 text-slate-600">{counts.skip} skipped</span>}
            </div>

            {/* Reconcile table */}
            <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-slate-700 text-xs">
              {reconcileRows.length === 0
                ? <div className="p-4 text-slate-500 text-center">No transactions found.</div>
                : (
                  <table className="w-full">
                    <thead className="sticky top-0 bg-slate-900 z-10">
                      <tr className="text-slate-500">
                        <th className="px-3 py-2.5 text-left w-6">
                          <input type="checkbox" checked={counts.new > 0 && checked.size === counts.new} onChange={toggleAllNew} className="accent-indigo-500" />
                        </th>
                        <th className="px-3 py-2.5 text-left">Description</th>
                        <th className="px-3 py-2.5 text-right">Date</th>
                        <th className="px-3 py-2.5 text-right">Amount</th>
                        <th className="px-3 py-2.5 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconcileRows.map((r, i) => {
                        // ── Statement rows ──
                        if (r.kind === 'statement') {
                          const pt = r.parsedTxn;
                          const isSkip = r.status === 'skip';
                          const eff = (r.status === 'matched' && rejected.has(i)) ? 'new' : r.status;
                          const sug = r.suggested;
                          const sm = r.suggestMeta;
                          // Show every dimension: ✓ when it matches, the delta when it doesn't.
                          const sugReason = () => {
                            if (!sm) return '';
                            const name = sm.merchant ? '✓ name' : '✗ name';
                            // Both figures are the BILLED amount (card currency); pt.currency
                            // may be the foreign original, so always render in card.currency.
                            const amount = sm.amount === 2 ? '✓ amount' : `amount ${fmtMoney(sug.amount, card.currency, false)}→${fmtMoney(pt.amount, card.currency, false)}`;
                            const date = sm.date === 2 ? '✓ date' : `date ${sug.date} vs ${pt.date || pt.postingDate}`;
                            return `${name} · ${amount} · ${date}`;
                          };
                          return (
                            <tr key={i} className={`border-t border-slate-800 ${isSkip ? 'opacity-35' : ''}`}>
                              <td className="px-3 py-2.5 align-top">
                                {eff === 'new' && !manualMatches[i] && (
                                  <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(setChecked, i)} className="accent-indigo-500" />
                                )}
                                {r.status === 'payment_missing' && accounts.length > 0 && (
                                  <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(setChecked, i)} className="accent-indigo-500" title="Add this card payment as a transfer" />
                                )}
                              </td>
                              <td className="px-3 py-2.5 align-top">
                                <div className="text-slate-300 break-words max-w-[360px]" title={pt.description}>{pt.description}</div>
                                {pt.tag === 'fee' && pt.refNo && parentByRef[pt.refNo] && (
                                  <div className="text-[11px] text-slate-500 mt-0.5 break-words max-w-[360px]">↳ fee for: <span className="text-slate-400">{parentByRef[pt.refNo].description}</span></div>
                                )}
                                {r.status === 'payment_missing' && (
                                  accounts.length > 0 ? (
                                    <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                                      <span>Add as transfer from</span>
                                      <select value={payAccts[i] ?? accounts[0]?.id}
                                        onChange={e => setPayAccts(p => ({ ...p, [i]: e.target.value }))}
                                        className="bg-slate-800 border border-slate-700 rounded px-1.5 py-1 text-slate-300 max-w-[160px]">
                                        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                      </select>
                                    </div>
                                  ) : (
                                    <div className="text-amber-400 text-[11px] mt-1">Add a bank account first to import this card payment.</div>
                                  )
                                )}
                                {eff === 'new' && (manualMatches[i] ? (
                                  <div className="mt-1.5 flex items-center gap-2 text-[11px] text-indigo-300">
                                    <span className="break-words max-w-[320px]">↔ linked to {floosiTxIndex[manualMatches[i]]?.description || 'tx'}{Math.abs((Number(floosiTxIndex[manualMatches[i]]?.amount) || 0) - pt.amount) > 0.01 ? ` — amount set to ${fmtMoney(pt.amount, pt.currency || card.currency, false)}` : ''}</span>
                                    <button onClick={() => linkManual(i, '')} className="text-slate-500 hover:text-slate-300 underline shrink-0">unlink</button>
                                  </div>
                                ) : (
                                  <div className="mt-1.5 space-y-1.5">
                                    {sug && !claimedFloosi.has(sug.id) && (
                                      <div className="flex items-start gap-2 text-[11px] text-amber-300/90">
                                        <span className="break-words max-w-[300px]">Same as <span className="text-slate-300">{(sug.description || '').slice(0, 30)}</span>? <span className="text-slate-500">({sugReason()})</span></span>
                                        <button onClick={() => linkManual(i, sug.id)} className="px-2 py-0.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white shrink-0">link</button>
                                      </div>
                                    )}
                                    {candidatesFor(pt).length > 0 && (
                                      <select value="" onChange={e => e.target.value && linkManual(i, e.target.value)}
                                        className="bg-slate-800 border border-slate-700 rounded px-1.5 py-1 text-[11px] text-slate-400 max-w-[280px]">
                                        <option value="">↔ link {sug ? 'a different' : 'an existing'} tx…</option>
                                        {candidatesFor(pt).map(f => (
                                          <option key={f.id} value={f.id}>{fmtMoney(f.amount, f.currency || card.currency, false)} · {f.date} · {(f.description || '').slice(0, 28)}</option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                ))}
                                {eff === 'new' && !manualMatches[i] && (
                                  pt.tag === 'fee'
                                    ? <div className="mt-1.5 text-[11px] text-slate-500">↳ category inherited from parent purchase</div>
                                    : (
                                      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                                        <span>Category</span>
                                        <select value={rowCategory(r, i)} onChange={e => setCatOverrides(p => ({ ...p, [i]: e.target.value }))}
                                          className="bg-slate-800 border border-slate-700 rounded px-1.5 py-1 text-slate-300 max-w-[170px]">
                                          <option value="">— none —</option>
                                          {(pt.credit ? incomeCats : expenseCats).map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                                        </select>
                                      </div>
                                    )
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-right text-slate-400 font-mono whitespace-nowrap align-top">{pt.date || pt.postingDate || '—'}</td>
                              <td className={`px-3 py-2.5 text-right font-mono whitespace-nowrap align-top ${pt.credit ? 'text-emerald-400' : 'text-slate-200'}`}>
                                {pt.credit ? '+' : ''}{fmtMoney(pt.amount, pt.currency || card.currency, false)}
                                {pt.originalCurrency && pt.originalCurrency !== 'AED' && pt.originalAmount != null && (
                                  <div className="text-[10px] text-slate-500 font-normal">{fmtMoney(pt.originalAmount, pt.originalCurrency, false)}</div>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-right whitespace-nowrap align-top">
                                {eff === 'new' && (manualMatches[i]
                                  ? <span className="px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300">Linked</span>
                                  : <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">New</span>)}
                                {r.status === 'matched' && !rejected.has(i) && (
                                  <div className="flex items-center gap-1.5 justify-end">
                                    <span className="px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">Matched</span>
                                    <button onClick={() => rejectMatch(i)} className="text-slate-500 hover:text-rose-300" title="Not a match">✕</button>
                                  </div>
                                )}
                                {r.status === 'matched' && rejected.has(i) && (
                                  <button onClick={() => restoreMatch(i)} className="text-[10px] text-slate-500 hover:text-slate-300 underline">undo reject</button>
                                )}
                                {r.status === 'payment_missing' && <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">{checked.has(i) ? 'Add' : 'Payment'}</span>}
                                {isSkip && <span className="px-2 py-0.5 rounded-full bg-slate-800 text-slate-600">Skip</span>}
                              </td>
                            </tr>
                          );
                        }

                        // ── Floosi-only rows ──
                        const t = r.floosiTx;
                        // Hidden once manually claimed as a match for a statement row.
                        if (claimedFloosi.has(t.id)) return null;
                        const isDeleting = deleting.has(i);
                        return (
                          <tr key={i} className="border-t border-slate-800 bg-rose-950/10">
                            <td className="px-3 py-2.5 align-top" />
                            <td className="px-3 py-2.5 align-top">
                              <div className="text-rose-300 break-words max-w-[360px]" title={t.description}>{t.description}</div>
                              <div className="text-[11px] text-rose-400/70 mt-0.5">In Floosi, not on statement</div>
                            </td>
                            <td className="px-3 py-2.5 text-right text-slate-400 font-mono whitespace-nowrap align-top">{t.date || '—'}</td>
                            <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap text-slate-300 align-top">
                              {fmtMoney(t.amount, t.currency || card.currency, false)}
                              {t.originalCurrency && t.originalCurrency !== 'AED' && t.originalAmount != null && (
                                <div className="text-[10px] text-slate-500 font-normal">{fmtMoney(t.originalAmount, t.originalCurrency, false)}</div>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right whitespace-nowrap align-top">
                              <div className="flex gap-1.5 justify-end">
                                <button onClick={() => { const n = new Set(deleting); n.delete(i); setDeleting(n); }}
                                  className={`px-2 py-0.5 rounded text-[11px] ${!isDeleting ? 'bg-slate-700 text-slate-300' : 'bg-slate-800 text-slate-500'}`}>Keep</button>
                                <button onClick={() => { const n = new Set(deleting); n.add(i); setDeleting(n); }}
                                  className={`px-2 py-0.5 rounded text-[11px] ${isDeleting ? 'bg-rose-900/60 text-rose-300' : 'bg-slate-800 text-slate-500'}`}>Delete</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
            </div>

            {errorMsg && <div className="rounded-lg bg-rose-500/10 border border-rose-500/20 p-3 text-xs text-rose-300">{errorMsg}</div>}

            <div className="flex gap-2 pt-1">
              <button onClick={onClose} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
              <button onClick={importTxns} disabled={status === 'importing'}
                className="flex-1 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-sm font-medium">
                {status === 'importing' ? 'Saving…' : `Confirm (${actionCount} tx${deleting.size ? ` · ${deleting.size} delete` : ''})`}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ============ CARD INSTALLMENT MODAL (M2.5 Session C) ============
// Lets the user "convert" something on a card into a mini-loan attached to it.
// The four kinds:
//   - balance_transfer:    pay off another card with this one
//   - easy_payment_plan:   spread an existing past tx over months
//   - balance_installment: convert this card's outstanding balance to installments
//   - easy_cash:           cash advance landing in a bank account
// All four are amortizing loans with the same shape. The orchestration that
// creates the matching transfer/fee transactions lives in saveCardInstallment.
const INSTALLMENT_KINDS = [
  { value: 'balance_installment', label: 'Balance Installment', desc: 'Convert this card’s current balance to monthly payments' },
  { value: 'easy_payment_plan',   label: 'Easy Payment Plan',   desc: 'Spread a past transaction on this card over months' },
  { value: 'balance_transfer',    label: 'Balance Transfer',    desc: 'Use this card to pay off another card’s balance' },
  { value: 'easy_cash',           label: 'Easy Cash',           desc: 'Cash advance from this card to a bank account' },
];

function CardInstallmentModal({ installment, cardId, prefill, cards, accounts, categories = [], onSave, onClose }) {
  // Edit mode: the installment row is passed in. The card and kind are locked
  // because changing them would corrupt the side-effects that already fired on
  // creation. Other fields (name, monthlyPayment, remainingBalance, etc.) are
  // editable so the user can adjust as life happens.
  // `prefill` (create mode only) seeds name/amount/card/kind — used when
  // converting an existing transaction into an EPP. The form stays fully
  // editable; nothing is locked.
  const isEdit = !!installment;
  const liveCards = cards.filter((c) => !c.deletedAt);
  const initialCard = cards.find((c) => c.id === (installment?.cardId || cardId || prefill?.cardId)) || liveCards[0];
  const [autoRecurring, setAutoRecurring] = useState(false);
  const [backfillFromStart, setBackfillFromStart] = useState(false);
  const liveExpenseCats = categories.filter((c) => !c.deletedAt && c.kind === 'expense');
  const [recurringCategory, setRecurringCategory] = useState('');
  // Monthly payment is auto-calculated by default for fresh installments
  // (Amount / interest / term feed `calculateMonthlyPayment`). The user can
  // override it — e.g. when the bank's published value differs by rounding.
  // Matches the LoanModal pattern: type into the field -> autoCalc flips off;
  // click "Auto" -> autoCalc back on, value recomputes.
  // Auto-recalc ON by default in both create and edit — so changing term /
  // principal / rate immediately propagates to the monthly payment field.
  // The user can click "Auto" off to manually override.
  const [autoCalc, setAutoCalc] = useState(true);
  const [form, setForm] = useState(installment ? {
    ...installment,
    // Stringify numerics so the inputs are happy.
    originalAmount: String(installment.originalAmount ?? ''),
    remainingBalance: String(installment.remainingBalance ?? ''),
    interestRate: String(installment.interestRate ?? ''),
    termMonths: installment.termMonths ?? 12,
    monthlyPayment: String(installment.monthlyPayment ?? ''),
    feeAmount: String(installment.feeAmount ?? ''),
    paymentDay: installment.paymentDay ?? 1,
  } : {
    id: uid('ci'),
    cardId: prefill?.cardId || cardId || initialCard?.id || '',
    kind: prefill?.kind || 'balance_installment',
    name: prefill?.name || '',
    currency: prefill?.currency || initialCard?.currency || 'AED',
    originalAmount: prefill?.originalAmount != null ? String(prefill.originalAmount) : '',
    interestRate: '',
    termMonths: 12,
    monthlyPayment: '',
    feeAmount: '',
    startDate: todayStr(),
    paymentDay: 1,
    linkedSourceCardId: '',
    linkedSourceAccountId: '',
    notes: prefill?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  // Following the LoanModal pattern: the selected card determines which other
  // cards can be a balance-transfer source, and the default currency.
  const card = liveCards.find((c) => c.id === form.cardId);
  const otherCards = liveCards.filter((c) => c.id !== form.cardId);

  const computedMonthlyPayment = useMemo(() => {
    const p = Number(form.originalAmount);
    const r = Number(form.interestRate);
    const t = Number(form.termMonths);
    if (!(p > 0) || !(t > 0)) return 0;
    return calculateMonthlyPayment(p, r || 0, t);
  }, [form.originalAmount, form.interestRate, form.termMonths]);

  // Keep form.monthlyPayment in lock-step with the calculated value while
  // autoCalc is on. The moment the user types into the field, the change
  // handler flips autoCalc off and this effect stops overwriting.
  useEffect(() => {
    if (!autoCalc) return;
    if (computedMonthlyPayment > 0) {
      setForm(f => ({ ...f, monthlyPayment: computedMonthlyPayment.toFixed(2) }));
    }
  }, [computedMonthlyPayment, autoCalc]);

  const handleSave = async () => {
    if (!form.name?.trim() || !form.originalAmount || !form.cardId) return;
    if (!isEdit && autoRecurring && !recurringCategory) {
      toast('Pick a category for the auto-created payment rule.');
      return;
    }
    setSaving(true);
    try {
      const original = parseFloat(form.originalAmount) || 0;
      // On edit, respect whatever remainingBalance the user typed (so they can
      // catch up after a manual extra payment). On create, default to original.
      const remaining = isEdit
        ? (form.remainingBalance !== '' && form.remainingBalance != null ? parseFloat(form.remainingBalance) : original)
        : original;
      await onSave({
        ...form,
        name: form.name.trim(),
        originalAmount: original,
        remainingBalance: remaining,
        interestRate: parseFloat(form.interestRate) || 0,
        termMonths: parseInt(form.termMonths) || 1,
        monthlyPayment: parseFloat(form.monthlyPayment) || computedMonthlyPayment || 0,
        feeAmount: parseFloat(form.feeAmount) || 0,
        paymentDay: parseInt(form.paymentDay) || 1,
        linkedSourceCardId: form.kind === 'balance_transfer' ? (form.linkedSourceCardId || null) : null,
        linkedSourceAccountId: form.kind === 'easy_cash' ? (form.linkedSourceAccountId || null) : null,
      }, !isEdit && autoRecurring, recurringCategory, backfillFromStart);
      onClose();
    } catch (e) {
      toast('Save failed: ' + (e?.message || String(e)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? `Edit installment — ${installment.name}` : 'New credit card installment'} onClose={onClose} wide>
      <div className="space-y-3">
        <Field label="Credit card">
          <select
            value={form.cardId}
            disabled={isEdit}
            onChange={(e) => {
              const newCard = liveCards.find((c) => c.id === e.target.value);
              setForm({ ...form, cardId: e.target.value, currency: newCard?.currency || form.currency });
            }}
            className="input"
          >
            <option value="">Select a card…</option>
            {liveCards.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.currency})</option>
            ))}
          </select>
          {liveCards.length === 0 && !isEdit && (
            <div className="text-[10px] text-amber-400 mt-1">No cards yet — add one in the Cards tab first.</div>
          )}
          {isEdit && (
            <div className="text-[10px] text-slate-500 mt-1">Card cannot be changed after creation.</div>
          )}
        </Field>

        <Field label="Kind">
          <div className="grid grid-cols-2 gap-2">
            {INSTALLMENT_KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                disabled={isEdit}
                onClick={() => setForm({ ...form, kind: k.value })}
                className={`text-left px-3 py-2 rounded-lg border text-xs disabled:opacity-50 disabled:cursor-not-allowed ${
                  form.kind === k.value
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-200'
                    : 'border-slate-700 bg-slate-800/40 hover:bg-slate-800 text-slate-300'
                }`}
              >
                <div className="font-medium">{k.label}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{k.desc}</div>
              </button>
            ))}
          </div>
          {isEdit && (
            <div className="text-[10px] text-slate-500 mt-1">Kind cannot be changed after creation.</div>
          )}
        </Field>

        <Field label="Name / description">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input"
            placeholder="e.g. Aug 2025 BT, Sofa EPP, 12-mo balance"
            autoFocus
          />
        </Field>

        <div className="grid grid-cols-3 gap-2">
          <Field label={isEdit ? 'Original amount' : 'Amount'} className="col-span-2">
            <input
              type="number" step="0.01" min="0"
              value={form.originalAmount}
              onChange={(e) => setForm({ ...form, originalAmount: e.target.value })}
              className="input"
              placeholder="0.00"
            />
          </Field>
          <Field label="Currency">
            <select
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="input"
            >
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        {isEdit && (
          <Field label="Remaining balance">
            <input
              type="number" step="0.01" min="0"
              value={form.remainingBalance}
              onChange={(e) => setForm({ ...form, remainingBalance: e.target.value })}
              className="input"
              placeholder="0.00"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              Drops on its own each payment day; edit here to catch up after an extra/manual payment.
            </div>
          </Field>
        )}

        <div className="grid grid-cols-3 gap-2">
          <Field label="Interest (%)">
            <input
              type="number" step="0.01" min="0"
              value={form.interestRate}
              onChange={(e) => setForm({ ...form, interestRate: e.target.value })}
              className="input"
              placeholder="0.00"
            />
          </Field>
          <Field label="Term (months)">
            <input
              type="number" min="1"
              value={form.termMonths}
              onChange={(e) => setForm({ ...form, termMonths: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Fee (one-time)">
            <input
              type="number" step="0.01" min="0"
              value={form.feeAmount}
              onChange={(e) => setForm({ ...form, feeAmount: e.target.value })}
              className="input"
              placeholder="0.00"
            />
          </Field>
        </div>

        <Field label="Monthly payment">
          <div className="flex gap-2">
            <input
              type="number" step="0.01" min="0"
              value={form.monthlyPayment}
              onChange={(e) => { setForm({ ...form, monthlyPayment: e.target.value }); setAutoCalc(false); }}
              className="input flex-1"
              placeholder="0.00"
            />
            <button
              type="button"
              onClick={() => setAutoCalc(true)}
              className={`px-3 text-xs rounded-lg ${autoCalc ? 'bg-emerald-600 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'}`}
              title="Auto-calculate from amount, interest, and term"
            >
              Auto
            </button>
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            {autoCalc
              ? 'Auto-calculated from standard amortisation. Type in the field to override (e.g. if the bank rounded differently).'
              : 'Manual entry. Click Auto to recalculate.'}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Start date">
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Payment day">
            <input
              type="number" min="1" max="31"
              value={form.paymentDay}
              onChange={(e) => setForm({ ...form, paymentDay: e.target.value })}
              className="input"
            />
          </Field>
        </div>

        {form.kind === 'balance_transfer' && (
          <Field label="Source card (the card being paid off)">
            <select
              value={form.linkedSourceCardId}
              onChange={(e) => setForm({ ...form, linkedSourceCardId: e.target.value })}
              className="input"
            >
              <option value="">Select source card…</option>
              {otherCards.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.currency})</option>
              ))}
            </select>
          </Field>
        )}

        {form.kind === 'easy_cash' && (
          <Field label="Destination account (where the cash lands)">
            <select
              value={form.linkedSourceAccountId}
              onChange={(e) => setForm({ ...form, linkedSourceAccountId: e.target.value })}
              className="input"
            >
              <option value="">Select destination account…</option>
              {accounts.filter((a) => !a.deletedAt).map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Notes (optional)">
          <input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="input"
          />
        </Field>

        <div className="text-[10px] text-slate-400 bg-slate-900 rounded p-2 border border-slate-800">
          Saving will create the installment row plus the appropriate side-effect transactions: a one-time
          fee expense on this card{form.kind === 'balance_transfer' ? ', and a transfer from this card to the source card.' : form.kind === 'easy_cash' ? ', and a transfer from this card into the destination account.' : '.'}
          {' '}For <strong>balance installment</strong> and <strong>easy payment plan</strong>, the card's total owed stays the same at conversion — only the fee and future interest add to it. Payments reduce it as the installment is paid down.
        </div>

        {!isEdit && parseFloat(form.monthlyPayment || computedMonthlyPayment) > 0 && card && (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRecurring}
                onChange={(e) => setAutoRecurring(e.target.checked)}
                className="w-4 h-4 accent-emerald-500"
              />
              <div>
                <div className="text-sm text-emerald-300 font-medium">Auto-create recurring rule</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Charges <strong>{card.name}</strong> {form.currency} {parseFloat(form.monthlyPayment || computedMonthlyPayment).toLocaleString()} on day {form.paymentDay || 1} for {form.termMonths || 1} months — mirrors how the bank actually adds the installment to your statement each month. Combined with the auto-decrement of remaining balance, the net effect each month is just the interest portion.
                </div>
              </div>
            </label>
            {autoRecurring && (
              <>
                <label className="flex items-center gap-3 cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={backfillFromStart}
                    onChange={(e) => setBackfillFromStart(e.target.checked)}
                    className="w-4 h-4 accent-emerald-500"
                  />
                  <div>
                    <div className="text-sm text-emerald-300 font-medium">Backfill missed payments since installment start</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      Off (default): the rule fires from today onward — no past months recorded.<br />
                      On: the materialiser walks from <strong>{form.startDate || 'the installment start date'}</strong> to today and creates one (informational) transaction per past payment. These rollup into spending charts and budgets but don't double-affect the card.
                    </div>
                  </div>
                </label>
                <Field label="Budget category">
                  <select
                    value={recurringCategory}
                    onChange={(e) => setRecurringCategory(e.target.value)}
                    className="input"
                  >
                    <option value="">— select a category —</option>
                    {liveExpenseCats.map((c) => (
                      <option key={c.id} value={c.name}>{c.icon ? `${c.icon} ` : ''}{c.name}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-slate-500 mt-1">
                    Pick what this installment "is for" so the monthly payment shows up under the right budget — e.g. an iPhone EPP could go under Personal, a furniture EPP under Household, etc.
                  </div>
                </Field>
              </>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm disabled:opacity-50">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !form.cardId || !form.name?.trim() || !form.originalAmount || (form.kind === 'balance_transfer' && !form.linkedSourceCardId) || (form.kind === 'easy_cash' && !form.linkedSourceAccountId)}
            className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CardModal({ card, categories = [], onSave, onClose }) {
  const [form, setForm] = useState(card || {
    id: uid('card'),
    name: '', bank: '', network: '', lastFour: '', currency: 'AED',
    creditLimit: '', currentBalance: '', statementBalance: '', minimumPayment: '',
    statementDay: 25, paymentDueDay: 15, apr: '', annualFee: '',
  });
  const [saving, setSaving] = useState(false);
  const [showStmtPwd, setShowStmtPwd] = useState(false);
  // Annual-fee auto-recurring opt-in — same pattern as loan / installment.
  // Only meaningful when creating a new card with a non-zero fee.
  const [autoAnnualFee, setAutoAnnualFee] = useState(false);
  const [annualFeeStartDate, setAnnualFeeStartDate] = useState(todayStr());
  const liveExpenseCats = categories.filter((c) => !c.deletedAt && c.kind === 'expense');
  const [annualFeeCategory, setAnnualFeeCategory] = useState(() => {
    const preferred = ['Card Fees', 'Loan Payment'];
    for (const want of preferred) {
      if (liveExpenseCats.find((c) => c.name === want)) return want;
    }
    return '';
  });

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      const fee = parseFloat(form.annualFee);
      await onSave({
        ...form,
        name: form.name.trim(),
        bank: (form.bank || '').trim(),
        lastFour: (form.lastFour || '').replace(/\D/g, '').slice(0, 4),
        creditLimit: parseFloat(form.creditLimit) || 0,
        currentBalance: parseFloat(form.currentBalance) || 0,
        statementBalance: parseFloat(form.statementBalance) || 0,
        minimumPayment: parseFloat(form.minimumPayment) || 0,
        statementDay: parseInt(form.statementDay) || 25,
        paymentDueDay: parseInt(form.paymentDueDay) || 15,
        apr: parseFloat(form.apr) || 0,
        annualFee: isFinite(fee) && fee > 0 ? fee : null,
        fxMarkupPct: form.fxMarkupPct === '' || form.fxMarkupPct == null ? null : parseFloat(form.fxMarkupPct),
        fxVatPct: form.fxVatPct == null || form.fxVatPct === '' ? 5 : parseFloat(form.fxVatPct),
        cashbackBalance: form.cashbackBalance === '' || form.cashbackBalance == null ? null : parseFloat(form.cashbackBalance),
      }, autoAnnualFee && isFinite(fee) && fee > 0, annualFeeCategory, annualFeeStartDate);
      onClose();
    } catch (e) { toast('Save failed: ' + (e?.message || String(e))); } finally { setSaving(false); }
  };

  return (
    <Modal title={card ? 'Edit Card' : 'New Card'} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Card Name">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input" placeholder="e.g. Platinum Rewards" autoFocus />
          </Field>
          <Field label="Bank">
            <input value={form.bank} onChange={e => setForm({ ...form, bank: e.target.value })} className="input" placeholder="e.g. Emirates NBD, ADCB" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Network">
            <select value={form.network} onChange={e => setForm({ ...form, network: e.target.value })} className="input">
              <option value="">Select…</option>
              <option>Visa</option>
              <option>Mastercard</option>
              <option>American Express</option>
              <option>Diners Club</option>
              <option>Discover</option>
              <option>Other</option>
            </select>
          </Field>
          <Field label="Last 4 Digits">
            <input value={form.lastFour} onChange={e => setForm({ ...form, lastFour: e.target.value })} maxLength="4" className="input font-mono" placeholder="1234" />
          </Field>
        </div>

        <div className="bg-slate-800/40 rounded-lg p-3 space-y-3 border border-slate-800">
          <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Balance & Limit</div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Credit Limit" className="col-span-2">
              <input type="number" step="0.01" min="0" value={form.creditLimit} onChange={e => setForm({ ...form, creditLimit: e.target.value })} className="input" placeholder="0.00" />
            </Field>
            <Field label="Currency">
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="input">
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <div className="text-[10px] text-slate-500 -mt-1">Default reporting currency for this card. Individual transactions can still be in any currency.</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Current Balance (what you owe now)">
              <input type="number" step="0.01" min="0" value={form.currentBalance} onChange={e => setForm({ ...form, currentBalance: e.target.value })} className="input" placeholder="0.00" />
            </Field>
            <Field label="Statement Balance (optional)">
              <input type="number" step="0.01" min="0" value={form.statementBalance} onChange={e => setForm({ ...form, statementBalance: e.target.value })} className="input" placeholder="0.00" />
            </Field>
          </div>
          <Field label="Minimum Payment (optional)">
            <input type="number" step="0.01" min="0" value={form.minimumPayment} onChange={e => setForm({ ...form, minimumPayment: e.target.value })} className="input" placeholder="0.00" />
          </Field>
        </div>

        <div className="bg-slate-800/40 rounded-lg p-3 space-y-3 border border-slate-800">
          <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Billing Cycle</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Statement Closing Day">
              <select value={form.statementDay} onChange={e => setForm({ ...form, statementDay: e.target.value })} className="input">
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">Day of the month your statement closes (e.g., 25).</div>
            </Field>
            <Field label="Payment Due Day">
              <select value={form.paymentDueDay} onChange={e => setForm({ ...form, paymentDueDay: e.target.value })} className="input">
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">Day of the month your payment is due (e.g., 15).</div>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="APR (% annual interest, optional)">
              <input type="number" step="0.01" min="0" value={form.apr} onChange={e => setForm({ ...form, apr: e.target.value })} className="input" placeholder="0.00" />
            </Field>
            <Field label="FX markup % (foreign txs, optional)">
              <input type="number" step="0.01" min="0" value={form.fxMarkupPct ?? ''} onChange={e => setForm({ ...form, fxMarkupPct: e.target.value })} className="input" placeholder="e.g. 3.76" />
              <div className="text-[10px] text-slate-500 mt-1">If this card adds its FX fee at settlement (e.g. FAB), set it here — foreign txs auto-use the settled amount + auto-add a VAT line. Leave blank if the markup is already in the SMS amount (e.g. DIB).</div>
            </Field>
            <Field label="VAT on FX fee % (optional)">
              <input type="number" step="0.01" min="0" value={form.fxVatPct ?? ''} onChange={e => setForm({ ...form, fxVatPct: e.target.value })} className="input" placeholder="5" />
            </Field>
            <Field label="Statement PDF password (optional)">
              <div className="flex gap-2">
                <input type={showStmtPwd ? 'text' : 'password'} value={form.statementPdfPassword ?? ''} onChange={e => setForm({ ...form, statementPdfPassword: e.target.value })} className="input flex-1" placeholder="opens the locked statement PDF" autoComplete="off" />
                <button type="button" onClick={() => setShowStmtPwd(v => !v)} className="px-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs text-slate-400">{showStmtPwd ? 'Hide' : 'Show'}</button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Used to open this card's password-protected statement during reconciliation.</div>
            </Field>
            <Field label="Cashback balance (AED, optional)">
              <input type="number" step="0.01" value={form.cashbackBalance ?? ''} onChange={e => setForm({ ...form, cashbackBalance: e.target.value })} className="input text-right font-mono" placeholder="0.00" />
              <div className="text-[10px] text-slate-500 mt-1">Your running cashback/rewards balance for this card. Adjust it from the card later as you earn/use it.</div>
            </Field>
            <Field label="Annual fee (optional)">
              <input type="number" step="0.01" min="0" value={form.annualFee} onChange={e => setForm({ ...form, annualFee: e.target.value })} className="input" placeholder="0.00" />
              <div className="text-[10px] text-slate-500 mt-1">Fee the bank charges yearly. Leave blank if none.</div>
            </Field>
          </div>
        </div>

        {parseFloat(form.annualFee) > 0 && (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoAnnualFee}
                onChange={e => setAutoAnnualFee(e.target.checked)}
                className="w-4 h-4 accent-emerald-500"
              />
              <div>
                <div className="text-sm text-emerald-300 font-medium">Auto-create yearly fee rule</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Charges <strong>{form.name || 'this card'}</strong> {form.currency} {parseFloat(form.annualFee || 0).toLocaleString()} every year on today's date — mirrors how the bank posts the annual fee to your statement.
                </div>
              </div>
            </label>
            {autoAnnualFee && (
              <div className="grid grid-cols-2 gap-2">
                <Field label="First charge date">
                  <input
                    type="date"
                    value={annualFeeStartDate}
                    onChange={(e) => setAnnualFeeStartDate(e.target.value)}
                    className="input"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">When the bank next charges this fee. The rule fires yearly on this date.</div>
                </Field>
                <Field label="Budget category">
                  <select
                    value={annualFeeCategory}
                    onChange={(e) => setAnnualFeeCategory(e.target.value)}
                    className="input"
                  >
                    <option value="">— select a category —</option>
                    {liveExpenseCats.map((c) => (
                      <option key={c.id} value={c.name}>{c.icon ? `${c.icon} ` : ''}{c.name}</option>
                    ))}
                  </select>
                </Field>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name?.trim()} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============ LOANS ============
function Loans({ loans, transactions, accounts, cards = [], cardInstallments = [], recurring = [], categories = [], onSave, onDelete, onSaveAsset, onSaveInstallment, onDeleteInstallment, showModal, setShowModal, baseCurrency }) {
  // Type chooser state for the "+ New" button. null = nothing open;
  // 'choose' = picking type; 'loan' / 'installment' = the chosen modal is open.
  const [creating, setCreating] = useState(null);
  // When set, the CardInstallmentModal opens in edit mode against this row.
  const [editingInstallment, setEditingInstallment] = useState(null);
  const liveInstallments = cardInstallments.filter((ci) => !ci.deletedAt);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const totals = useMemo(() => {
    let totalPrincipal = 0, totalRemaining = 0, monthlyPayments = 0;
    loans.forEach(l => {
      totalPrincipal += toBase(l.originalPrincipal || 0, l.currency);
      totalRemaining += toBase((l.liveBalance ?? l.currentBalance) || 0, l.currency);
      monthlyPayments += toBase(l.monthlyPayment || 0, l.currency);
    });
    return { totalPrincipal, totalRemaining, totalPaid: totalPrincipal - totalRemaining, monthlyPayments };
  }, [loans]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-bold">Debt</h2>
          <p className="text-sm text-slate-400">Track mortgages, auto / personal / student / business loans, and credit-card installments</p>
        </div>
        <button onClick={() => setCreating('choose')} className="px-3 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 flex items-center gap-2 whitespace-nowrap flex-shrink-0">
          <Plus className="w-4 h-4" /> New debt
        </button>
      </div>

      {loans.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total Borrowed" value={fmtMoney(totals.totalPrincipal, baseCurrency)} />
          <StatCard label="Total Remaining" value={fmtMoney(totals.totalRemaining, baseCurrency)} valueClass="text-rose-400" />
          <StatCard label="Total Paid Off" value={fmtMoney(totals.totalPaid, baseCurrency)} valueClass="text-emerald-400" />
          <StatCard label="Monthly Payments" value={fmtMoney(totals.monthlyPayments, baseCurrency)} valueClass="text-amber-400" />
        </div>
      )}

      {loans.length === 0 && liveInstallments.length === 0 ? (
        <div className="bg-slate-900 rounded-xl p-12 border border-slate-800 text-center">
          <Landmark className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-400">No debt yet. Track mortgages, auto / personal / student / business loans, or credit-card installments (EPP, balance transfers, easy cash).</p>
        </div>
      ) : (
        <div className="space-y-3">
          {loans.length > 0 && liveInstallments.length > 0 && (
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Loans</div>
          )}
          {loans.length > 0 && (
          <div className="grid lg:grid-cols-2 gap-3 items-start">
          {loans.map(l => {
            const principal = l.originalPrincipal || 0;
            const balance = (l.liveBalance ?? l.currentBalance) || 0;
            const paid = principal - balance;
            const progress = principal > 0 ? (paid / principal) * 100 : 0;
            const monthsRemaining = calculateMonthsRemaining(balance, l.interestRate, l.monthlyPayment);
            const split = splitPayment(balance, l.interestRate, l.monthlyPayment);
            const dueDate = nextLoanPaymentDate(l);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const daysToDue = dueDate ? Math.ceil((dueDate - today) / (MS_PER_DAY)) : null;
            const payoffDate = monthsRemaining && isFinite(monthsRemaining)
              ? new Date(today.getFullYear(), today.getMonth() + monthsRemaining, today.getDate())
              : null;

            const linkedRules = recurring.filter(r => !r.deletedAt && r.linkedLoanId === l.id);
            const isConfirming = confirmDelete === l.id;
            if (isConfirming) {
              return (
                <div key={l.id} className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm font-medium mb-1">Delete "{l.name}"?</div>
                      {linkedRules.length > 0 ? (
                        <>
                          <div className="text-xs text-slate-400 mb-3">
                            Has {linkedRules.length} linked recurring payment rule{linkedRules.length > 1 ? 's' : ''}.
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            <button onClick={async () => { await onDelete(l.id, false); setConfirmDelete(null); }} className="px-2 py-1 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">Delete loan only</button>
                            <button onClick={async () => { await onDelete(l.id, true); setConfirmDelete(null); }} className="px-2 py-1 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">Delete loan & recurring transactions</button>
                            <button onClick={() => setConfirmDelete(null)} className="px-2 py-1 text-xs rounded bg-slate-800 hover:bg-slate-700">Cancel</button>
                          </div>
                        </>
                      ) : (
                        <div className="flex gap-2 mt-2">
                          <button onClick={async () => { await onDelete(l.id, false); setConfirmDelete(null); }} className="px-3 py-1.5 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">Delete</button>
                          <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600">Cancel</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            const loanTypeIcon = {
              mortgage: '🏠', auto: '🚗', personal: '💵', student: '🎓', business: '💼', other: '📋'
            }[l.loanType] || '📋';

            return (
              <div key={l.id} className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                {/* header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-lg flex-shrink-0">{loanTypeIcon}</div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{l.name}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400 capitalize flex-shrink-0">{l.loanType}</span>
                      </div>
                      {(l.lender || dueDate) && (
                        <div className="text-xs text-slate-400 flex items-center gap-1.5 flex-wrap mt-0.5">
                          {l.lender && <span className="truncate">{l.lender}</span>}
                          {l.lender && dueDate && <span className="text-slate-600">·</span>}
                          {dueDate && (
                            <span className={daysToDue !== null && daysToDue <= 5 ? 'text-amber-400 font-medium' : ''}>
                              Next {dueDate.toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                              {daysToDue !== null && ` · ${daysToDue === 0 ? 'today' : daysToDue === 1 ? 'tomorrow' : `${daysToDue}d`}`}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => { setEditing(l); setShowModal(true); }} className="p-1.5 hover:bg-slate-700 rounded" title="Edit">
                      <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    <button onClick={() => setConfirmDelete(l.id)} className="p-1.5 hover:bg-rose-900/50 rounded" title="Delete">
                      <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                    </button>
                  </div>
                </div>

                {/* progress hero — Remaining + % above, paid-of-total + months-left below */}
                <div className="mb-3">
                  <div className="flex items-end justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Remaining</div>
                      <div className="text-xl font-bold text-rose-400 whitespace-nowrap">{fmtMoney(balance, l.currency)}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-emerald-400">{progress.toFixed(1)}%</div>
                      <div className="text-[10px] text-slate-500">paid off</div>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
                  </div>
                  <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500 mt-1.5">
                    <span className="truncate">{fmtMoney(paid, l.currency)} of {fmtMoney(principal, l.currency)}</span>
                    <span className="whitespace-nowrap flex-shrink-0">
                      {isFinite(monthsRemaining) && monthsRemaining > 0 ? `${monthsRemaining} mo left` : `${l.termMonths || 0} mo term`}
                      {payoffDate && isFinite(monthsRemaining) && ` · ${payoffDate.toLocaleDateString('en', { month: 'short', year: 'numeric' })}`}
                    </span>
                  </div>
                </div>

                {/* payment terms */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <div className="text-[10px] text-slate-400">Monthly Payment</div>
                    <div className="text-sm font-bold">{fmtMoney(l.monthlyPayment || 0, l.currency)}</div>
                    {l.monthlyPayment > 0 && l.interestRate > 0 && (
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        P: {fmtMoney(split.principal, l.currency)} · I: {fmtMoney(split.interest, l.currency)}
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <div className="text-[10px] text-slate-400">Interest Rate</div>
                    <div className="text-sm font-bold">{(l.interestRate || 0).toFixed(2)}%</div>
                    {l.monthlyPayment > 0 && l.interestRate > 0 && (
                      <div className="text-[10px] text-slate-500 mt-0.5">≈ {fmtMoney(split.interest, l.currency)}/mo</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
          )}

          {liveInstallments.length > 0 && (
            <>
              {loans.length > 0 && (
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide pt-2">Credit card installments</div>
              )}
              <div className="grid lg:grid-cols-2 gap-3 items-start">
              {liveInstallments.map((ci) => {
                const card = cards.find((c) => c.id === ci.cardId);
                const original = Number(ci.originalAmount) || 0;
                const remaining = Number(ci.remainingBalance) || 0;
                const paid = Math.max(0, original - remaining);
                const progress = original > 0 ? (paid / original) * 100 : 0;
                const monthly = Number(ci.monthlyPayment) || 0;
                const term = Number(ci.termMonths) || 0;
                // See the Loans-page Debt row for the rationale: derive
                // payments-made from (original - remaining) / monthly.
                const paymentsMade = monthly > 0
                  ? Math.min(term, Math.max(0, Math.round((original - remaining) / monthly)))
                  : 0;
                const paymentsLeft = Math.max(0, term - paymentsMade);
                const kindLabel = (INSTALLMENT_KINDS.find(k => k.value === ci.kind)?.label) || ci.kind;
                return (
                  <div key={ci.id} className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                    {/* header */}
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-lg flex-shrink-0">💳</div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold truncate">{ci.name}</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400">{kindLabel}</span>
                          </div>
                          {card && <div className="text-xs text-slate-400 mt-0.5 truncate">on {card.name}</div>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => setEditingInstallment(ci)} className="p-1.5 hover:bg-slate-700 rounded" title="Edit">
                          <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                        <button
                          onClick={async () => { if (await confirmDialog(`Delete installment "${ci.name}"?`)) onDeleteInstallment?.(ci.id); }}
                          className="p-1.5 hover:bg-rose-900/50 rounded" title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                        </button>
                      </div>
                    </div>

                    {/* progress hero */}
                    <div className="mb-3">
                      <div className="flex items-end justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wide text-slate-500">Remaining</div>
                          <div className="text-xl font-bold text-rose-400 whitespace-nowrap">{fmtMoney(remaining, ci.currency)}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-semibold text-emerald-400">{progress.toFixed(1)}%</div>
                          <div className="text-[10px] text-slate-500">paid off</div>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
                      </div>
                      <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500 mt-1.5">
                        <span className="truncate">{fmtMoney(paid, ci.currency)} of {fmtMoney(original, ci.currency)}</span>
                        <span className="whitespace-nowrap flex-shrink-0">{paymentsLeft} mo left · {paymentsMade}/{term} paid</span>
                      </div>
                    </div>

                    {/* payment terms */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-slate-800/50 rounded-lg p-2.5">
                        <div className="text-[10px] text-slate-400">Monthly Payment</div>
                        <div className="text-sm font-bold">{fmtMoney(monthly, ci.currency)}</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-2.5">
                        <div className="text-[10px] text-slate-400">Interest Rate</div>
                        <div className="text-sm font-bold">{(Number(ci.interestRate) || 0).toFixed(2)}%</div>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Type chooser: open before either modal */}
      {creating === 'choose' && (
        <Modal title="What kind of debt?" onClose={() => setCreating(null)}>
          <div className="space-y-2">
            <button
              onClick={() => setCreating('loan')}
              className="w-full text-left p-3 rounded-lg border border-slate-700 bg-slate-800/40 hover:bg-slate-800 transition"
            >
              <div className="text-sm font-medium text-slate-100">Traditional loan</div>
              <div className="text-xs text-slate-400 mt-0.5">Mortgage, auto, personal, student, business — paid from a bank account.</div>
            </button>
            <button
              onClick={() => setCreating('installment')}
              className="w-full text-left p-3 rounded-lg border border-slate-700 bg-slate-800/40 hover:bg-slate-800 transition"
            >
              <div className="text-sm font-medium text-slate-100">Credit card installment</div>
              <div className="text-xs text-slate-400 mt-0.5">Balance transfer, easy payment plan, balance installment, easy cash — attached to a card, paid through its statement.</div>
            </button>
          </div>
        </Modal>
      )}

      {(showModal || creating === 'loan') && (
        <LoanModal
          loan={editing}
          accounts={accounts}
          categories={categories}
          onSave={onSave}
          onSaveAsset={onSaveAsset}
          onClose={() => { setShowModal(false); setEditing(null); setCreating(null); }}
        />
      )}

      {(creating === 'installment' || editingInstallment) && (
        <CardInstallmentModal
          installment={editingInstallment}
          cardId={editingInstallment?.cardId || null}
          cards={cards}
          accounts={accounts}
          categories={categories}
          onSave={async (ci, autoCreateRecurring, recurringCategoryName, backfillFromStart) => { await onSaveInstallment?.(ci, autoCreateRecurring, recurringCategoryName, backfillFromStart); }}
          onClose={() => { setCreating(null); setEditingInstallment(null); }}
        />
      )}
    </div>
  );
}

function LoanModal({ loan, accounts = [], categories = [], onSave, onSaveAsset, onClose }) {
  const liveAccounts = accounts.filter((a) => !a.deletedAt);
  const liveExpenseCats = categories.filter((c) => !c.deletedAt && c.kind === 'expense');
  const [form, setForm] = useState(loan || {
    id: uid('loan'),
    name: '', lender: '', loanType: 'personal', currency: 'AED',
    originalPrincipal: '', currentBalance: '', interestRate: '',
    termMonths: '', monthlyPayment: '', startDate: todayStr(), paymentDay: 1,
  });
  const [autoCalc, setAutoCalc] = useState(!loan); // auto-calc payment for new loans
  // Both side-effects opt-in by default. The user explicitly ticks them.
  const [autoRecurring, setAutoRecurring] = useState(false);
  const [backfillFromStart, setBackfillFromStart] = useState(false);
  const [paymentAccountId, setPaymentAccountId] = useState(loan?.paymentAccountId || liveAccounts[0]?.id || '');
  // Smart-default the recurring rule's category by loan_type. Falls back to
  // an existing user category (no system-created categories) so the
  // dropdown is never empty.
  const smartCategoryDefault = (() => {
    const map = { mortgage: 'Housing', auto: 'Transportation', student: 'Education', business: 'Business' };
    const want = map[form.loanType];
    if (want && liveExpenseCats.find((c) => c.name === want)) return want;
    return '';
  })();
  const [recurringCategory, setRecurringCategory] = useState(smartCategoryDefault);
  // Keep the default in sync when the user changes loanType (but only while
  // they haven't manually picked a different category).
  const [categoryTouched, setCategoryTouched] = useState(false);
  useEffect(() => {
    if (!categoryTouched) setRecurringCategory(smartCategoryDefault);
  }, [form.loanType]);
  const [autoAsset, setAutoAsset] = useState(false);
  const [assetName, setAssetName] = useState('');
  const [assetPurchasePrice, setAssetPurchasePrice] = useState('');
  const [assetAppreciationRate, setAssetAppreciationRate] = useState('');
  const [saving, setSaving] = useState(false);

  // Mortgage -> real_estate (3%/yr), Auto -> vehicle (-12%/yr). Other types
  // don't get an asset prompt — they rarely correspond to a tangible asset.
  const assetTypeForLoan = form.loanType === 'mortgage' ? 'real_estate'
    : form.loanType === 'auto' ? 'vehicle' : null;
  const defaultAppreciation = ASSET_TYPES.find(t => t.value === assetTypeForLoan)?.defaultRate ?? 0;

  // Auto-calculate monthly payment when principal/rate/term change
  useEffect(() => {
    if (!autoCalc) return;
    const p = parseFloat(form.originalPrincipal);
    const r = parseFloat(form.interestRate);
    const t = parseInt(form.termMonths);
    if (p > 0 && t > 0) {
      const calc = calculateMonthlyPayment(p, r || 0, t);
      setForm(f => ({ ...f, monthlyPayment: calc.toFixed(2) }));
    }
  }, [form.originalPrincipal, form.interestRate, form.termMonths, autoCalc]);

  const handleSave = async () => {
    if (!form.name?.trim() || !form.originalPrincipal) return;
    if (autoRecurring && !loan && !recurringCategory) {
      toast('Pick a category for the auto-created loan payment rule.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        name: form.name.trim(),
        lender: form.lender?.trim() || '',
        originalPrincipal: parseFloat(form.originalPrincipal) || 0,
        currentBalance: parseFloat(form.currentBalance) || parseFloat(form.originalPrincipal) || 0,
        interestRate: parseFloat(form.interestRate) || 0,
        budgetCategoryId: form.budgetCategoryId || (liveExpenseCats.find(c => c.name === smartCategoryDefault)?.id ?? null),
        // #4: persist the account the loan is withdrawn from + optional
        // direct-debit (feeder) account, so reminders can check both have funds.
        paymentAccountId: paymentAccountId || null,
        directDebitAccountId: form.directDebitAccountId || null,
        termMonths: parseInt(form.termMonths) || 0,
        monthlyPayment: parseFloat(form.monthlyPayment) || 0,
        paymentDay: parseInt(form.paymentDay) || 1,
      }, autoRecurring && !loan && !!paymentAccountId, paymentAccountId, recurringCategory, backfillFromStart);
      // Optional: also create a linked asset (mortgage -> real_estate, auto -> vehicle).
      if (!loan && autoAsset && assetTypeForLoan && onSaveAsset) {
        await onSaveAsset({
          id: uid('asset'),
          name: (assetName || form.name).trim(),
          assetType: assetTypeForLoan,
          currency: form.currency,
          purchasePrice: parseFloat(assetPurchasePrice) || parseFloat(form.originalPrincipal) || 0,
          purchaseDate: form.startDate || todayStr(),
          appreciationRate: assetAppreciationRate === '' ? defaultAppreciation : (parseFloat(assetAppreciationRate) || 0),
          linkedLoanId: form.id,
        });
      }
      onClose();
    } catch (e) { toast('Save failed: ' + (e?.message || String(e))); } finally { setSaving(false); }
  };

  return (
    <Modal title={loan ? 'Edit Loan' : 'New Loan'} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Loan Name">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input" placeholder="e.g. Mortgage, Car Loan" autoFocus />
          </Field>
          <Field label="Loan Type">
            <select value={form.loanType} onChange={e => setForm({ ...form, loanType: e.target.value })} className="input">
              <option value="mortgage">🏠 Mortgage</option>
              <option value="auto">🚗 Auto</option>
              <option value="personal">💵 Personal</option>
              <option value="student">🎓 Student</option>
              <option value="business">💼 Business</option>
              <option value="other">📋 Other</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Lender (optional)">
            <input value={form.lender} onChange={e => setForm({ ...form, lender: e.target.value })} className="input" placeholder="e.g. Emirates NBD" />
          </Field>
          <Field label="Currency">
            <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="input">
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <div className="bg-slate-800/40 rounded-lg p-3 space-y-3 border border-slate-800">
          <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Loan Terms</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Original Principal">
              <input type="number" step="0.01" min="0" value={form.originalPrincipal} onChange={e => setForm({ ...form, originalPrincipal: e.target.value })} className="input" placeholder="0.00" />
            </Field>
            <Field label="Current Balance">
              <input type="number" step="0.01" min="0" value={form.currentBalance} onChange={e => setForm({ ...form, currentBalance: e.target.value })} className="input" placeholder="Same as principal if new" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Interest Rate (% APR)">
              <input type="number" step="0.01" min="0" value={form.interestRate} onChange={e => setForm({ ...form, interestRate: e.target.value })} className="input" placeholder="0.00" />
            </Field>
            <Field label="Term (months)">
              <input type="number" min="1" value={form.termMonths} onChange={e => setForm({ ...form, termMonths: e.target.value })} className="input" placeholder="60, 240, etc." />
            </Field>
          </div>
          <Field label="Monthly Payment">
            <div className="flex gap-2">
              <input
                type="number" step="0.01" min="0"
                value={form.monthlyPayment}
                onChange={e => { setForm({ ...form, monthlyPayment: e.target.value }); setAutoCalc(false); }}
                className="input flex-1"
                placeholder="0.00"
              />
              <button
                type="button"
                onClick={() => setAutoCalc(true)}
                className={`px-3 text-xs rounded-lg ${autoCalc ? 'bg-emerald-600 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'}`}
                title="Auto-calculate from principal, rate, and term"
              >
                Auto
              </button>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              {autoCalc ? 'Auto-calculated using standard amortization. Click into the field to override.' : 'Manual entry. Click Auto to recalculate.'}
            </div>
          </Field>
        </div>

        <div className="bg-slate-800/40 rounded-lg p-3 space-y-3 border border-slate-800">
          <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Payment Schedule</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Loan Start Date">
              <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} className="input" />
            </Field>
            <Field label="Payment Day (of month)">
              <input type="number" min="1" max="31" value={form.paymentDay} onChange={e => setForm({ ...form, paymentDay: e.target.value })} className="input" />
            </Field>
          </div>
          <Field label="Budget category (for spending view)">
            <select
              value={form.budgetCategoryId || (liveExpenseCats.find(c => c.name === smartCategoryDefault)?.id || '')}
              onChange={e => setForm({ ...form, budgetCategoryId: e.target.value })}
              className="input"
            >
              <option value="">None — don't show payments in a budget</option>
              {liveExpenseCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="text-[10px] text-slate-500 mt-1">Each payment amortises the loan (net-worth-correct); if you set a category here, the full payment also shows as spending under it (cash-flow view).</div>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Withdrawn from account">
              <select value={paymentAccountId} onChange={e => setPaymentAccountId(e.target.value)} className="input">
                <option value="">— none —</option>
                {liveAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">The bank account this loan is debited from each month.</div>
            </Field>
            <Field label="Direct debit account (optional)">
              <select value={form.directDebitAccountId || ''} onChange={e => setForm({ ...form, directDebitAccountId: e.target.value })} className="input">
                <option value="">— none —</option>
                {liveAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">If a different account feeds the one above (e.g. salary auto-transfers in). Reminders check both have enough.</div>
            </Field>
          </div>
        </div>

        <div className="text-xs text-slate-400 bg-slate-800/40 rounded-lg p-2.5">
          💡 <strong>Tip:</strong> To track payments automatically, create a Recurring transaction with the same monthly payment amount. Then manually update the current balance here each month, or just track interest/principal separately.
        </div>

        {!loan && parseFloat(form.monthlyPayment) > 0 && (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRecurring}
                onChange={e => setAutoRecurring(e.target.checked)}
                className="w-4 h-4 accent-emerald-500"
              />
              <div>
                <div className="text-sm text-emerald-300 font-medium">Auto-create recurring payment rule</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Creates a monthly "{form.name || 'loan'} Payment" expense of {form.currency} {parseFloat(form.monthlyPayment || 0).toLocaleString()} on day {form.paymentDay || 1}, starting today.
                </div>
              </div>
            </label>
            {autoRecurring && (
              <>
                <label className="flex items-center gap-3 cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={backfillFromStart}
                    onChange={(e) => setBackfillFromStart(e.target.checked)}
                    className="w-4 h-4 accent-emerald-500"
                  />
                  <div>
                    <div className="text-sm text-emerald-300 font-medium">Backfill missed payments since loan start</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      Off (default): the rule fires from today onward — no past months recorded.<br />
                      On: the materialiser walks from <strong>{form.startDate || 'the loan start date'}</strong> to today and creates one transaction per past payment.
                    </div>
                  </div>
                </label>
                <Field label="Budget category">
                  <select
                    value={recurringCategory}
                    onChange={(e) => { setRecurringCategory(e.target.value); setCategoryTouched(true); }}
                    className="input"
                  >
                    <option value="">— select a category —</option>
                    {liveExpenseCats.map((c) => (
                      <option key={c.id} value={c.name}>{c.icon ? `${c.icon} ` : ''}{c.name}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-slate-500 mt-1">
                    The recurring payment will land in this category for budgeting / spending charts.
                  </div>
                </Field>
              </>
            )}
          </div>
        )}

        {!loan && assetTypeForLoan && (
          <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoAsset}
                onChange={e => setAutoAsset(e.target.checked)}
                className="w-4 h-4 accent-cyan-500"
              />
              <div>
                <div className="text-sm text-cyan-300 font-medium">Also create a linked asset</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {form.loanType === 'mortgage'
                    ? 'A real-estate asset financed by this loan. Appreciates by default at ~3% / year.'
                    : 'A vehicle asset financed by this loan. Depreciates by default at ~12% / year.'} Counts toward net worth alongside the loan as a liability.
                </div>
              </div>
            </label>
            {autoAsset && (
              <div className="space-y-2 mt-2">
                <Field label="Asset name">
                  <input
                    value={assetName}
                    onChange={e => setAssetName(e.target.value)}
                    className="input"
                    placeholder={form.name || (form.loanType === 'mortgage' ? 'e.g. Downtown apartment' : 'e.g. BMW i4')}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Purchase price">
                    <input
                      type="number" step="0.01" min="0"
                      value={assetPurchasePrice}
                      onChange={e => setAssetPurchasePrice(e.target.value)}
                      className="input"
                      placeholder={form.originalPrincipal ? String(form.originalPrincipal) : '0.00'}
                    />
                    <div className="text-[10px] text-slate-500 mt-1">Defaults to the loan principal; override if you put a down payment.</div>
                  </Field>
                  <Field label="Appreciation (% / yr)">
                    <input
                      type="number" step="0.01"
                      value={assetAppreciationRate}
                      onChange={e => setAssetAppreciationRate(e.target.value)}
                      className="input"
                      placeholder={String(defaultAppreciation)}
                    />
                    <div className="text-[10px] text-slate-500 mt-1">Negative for depreciation (e.g. -12 for a car).</div>
                  </Field>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name?.trim() || !form.originalPrincipal} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============ ASSETS ============
const ASSET_TYPES = [
  { value: 'real_estate', label: 'Real Estate', icon: '🏠', defaultRate: 3 },
  { value: 'vehicle', label: 'Vehicle', icon: '🚗', defaultRate: -12 },
  { value: 'jewelry', label: 'Jewelry / Gold', icon: '💍', defaultRate: 2 },
  { value: 'art', label: 'Art / Collectibles', icon: '🖼️', defaultRate: 1 },
  { value: 'other', label: 'Other', icon: '📦', defaultRate: 0 },
];

function getAssetCurrentValue(asset) {
  const purchaseDate = asset.purchaseDate ? strToDate(asset.purchaseDate) : new Date();
  const yearsHeld = (Date.now() - purchaseDate.getTime()) / (MS_PER_YEAR);
  const rate = (asset.appreciationRate || 0) / 100;
  return (asset.purchasePrice || 0) * Math.pow(1 + rate, Math.max(0, yearsHeld));
}

function Assets({ assets, loans, onSave, onDelete, showModal, setShowModal, baseCurrency }) {
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const totalValue = useMemo(() =>
    assets.reduce((s, a) => s + toBase(getAssetCurrentValue(a), a.currency || 'AED'), 0),
    [assets]
  );
  const totalCost = useMemo(() =>
    assets.reduce((s, a) => s + toBase(a.purchasePrice || 0, a.currency || 'AED'), 0),
    [assets]
  );

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    await onDelete(confirmDelete);
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Assets</h2>
          <p className="text-sm text-slate-400">Total current value: {fmtMoney(totalValue, baseCurrency)}</p>
        </div>
        <button onClick={() => { setEditing(null); setShowModal(true); }} className="px-3 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 flex items-center gap-2 whitespace-nowrap flex-shrink-0">
          <Plus className="w-4 h-4" /> Asset
        </button>
      </div>

      {assets.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Purchase Cost" value={fmtMoney(totalCost, baseCurrency)} />
          <StatCard label="Current Value" value={fmtMoney(totalValue, baseCurrency)} valueClass="text-emerald-400" />
          <StatCard
            label="Gain / Loss"
            value={`${totalValue - totalCost >= 0 ? '+' : ''}${fmtMoney(totalValue - totalCost, baseCurrency)}`}
            valueClass={totalValue - totalCost >= 0 ? 'text-emerald-400' : 'text-rose-400'}
          />
        </div>
      )}

      {assets.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Home className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No assets yet. Add real estate, vehicles, or other valuables.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {assets.map(a => {
            const typeInfo = ASSET_TYPES.find(t => t.value === a.assetType) || ASSET_TYPES[4];
            const currentVal = getAssetCurrentValue(a);
            const gain = currentVal - (a.purchasePrice || 0);
            const gainPct = a.purchasePrice > 0 ? (gain / a.purchasePrice) * 100 : 0;
            const linkedLoan = loans.find(l => l.id === a.linkedLoanId);
            const loanBalance = linkedLoan ? toBase((linkedLoan.liveBalance ?? linkedLoan.currentBalance) || 0, linkedLoan.currency) : 0;
            const equity = toBase(currentVal, a.currency || 'AED') - loanBalance;
            const purchaseDate = a.purchaseDate ? strToDate(a.purchaseDate) : null;
            const yearsHeld = purchaseDate ? (Date.now() - purchaseDate.getTime()) / (MS_PER_YEAR) : 0;

            return (
              <div key={a.id} className="bg-slate-900 rounded-xl border border-slate-800 p-4 lg:p-3">
                <div className="lg:flex lg:items-center lg:gap-4">
                <div className="flex items-start justify-between gap-4 lg:items-center lg:flex-1 lg:min-w-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center text-lg flex-shrink-0">{typeInfo.icon}</div>
                      <div className="min-w-0">
                        <div className="font-semibold line-clamp-2">{a.name}</div>
                        <div className="text-xs text-slate-400 truncate">{typeInfo.label}{a.notes ? ` · ${a.notes}` : ''}</div>
                      </div>
                    </div>
                    {linkedLoan && (
                      <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-2 min-w-0">
                        <Landmark className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">Linked: {linkedLoan.name} · {fmtMoney((linkedLoan.liveBalance ?? linkedLoan.currentBalance) || 0, linkedLoan.currency)}</span>
                      </div>
                    )}
                  </div>
                  {/* edit/delete — mobile/tablet (top-right) */}
                  <div className="flex gap-2 flex-shrink-0 lg:hidden">
                    <button onClick={() => { setEditing(a); setShowModal(true); }} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-slate-200">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => setConfirmDelete(a.id)} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-rose-400">
                      <Trash2 className="w-4 h-4 text-rose-400" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 lg:mt-0 lg:flex-none lg:w-[700px] grid grid-cols-2 sm:grid-cols-4 gap-2 lg:[&>div]:p-2">
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <div className="text-[10px] text-slate-400">Purchase Price</div>
                    <div className="text-sm font-bold">{fmtMoney(a.purchasePrice || 0, a.currency || 'AED')}</div>
                    {purchaseDate && <div className="text-[10px] text-slate-500 mt-0.5">{purchaseDate.toLocaleDateString('en', { year: 'numeric', month: 'short' })}</div>}
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <div className="text-[10px] text-slate-400">Current Value</div>
                    <div className="text-sm font-bold text-emerald-400">{fmtMoney(currentVal, a.currency || 'AED')}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{yearsHeld.toFixed(1)}y held</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <div className="text-[10px] text-slate-400">Gain / Loss</div>
                    <div className={`text-sm font-bold ${gain >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {gain >= 0 ? '+' : ''}{fmtMoney(gain, a.currency || 'AED')}
                    </div>
                    <div className={`text-[10px] mt-0.5 ${gainPct >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {gainPct >= 0 ? '+' : ''}{gainPct.toFixed(1)}%
                    </div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <div className="text-[10px] text-slate-400">{linkedLoan ? 'Equity' : 'Rate/yr'}</div>
                    {linkedLoan ? (
                      <>
                        <div className={`text-sm font-bold ${equity >= 0 ? 'text-cyan-400' : 'text-rose-400'}`}>{fmtMoney(equity, baseCurrency)}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">Loan: {fmtMoney(loanBalance, baseCurrency)}</div>
                      </>
                    ) : (
                      <div className={`text-sm font-bold ${(a.appreciationRate || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {(a.appreciationRate || 0) >= 0 ? '+' : ''}{a.appreciationRate || 0}%
                      </div>
                    )}
                  </div>
                </div>
                {/* edit/delete — desktop one-line (far right) */}
                <div className="hidden lg:flex gap-1 flex-shrink-0">
                  <button onClick={() => { setEditing(a); setShowModal(true); }} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-slate-200">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setConfirmDelete(a.id)} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-rose-400">
                    <Trash2 className="w-4 h-4 text-rose-400" />
                  </button>
                </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmDelete && (
        <Modal title="Delete Asset" onClose={() => setConfirmDelete(null)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-300">Are you sure you want to delete this asset? This cannot be undone.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
              <button onClick={handleConfirmDelete} className="flex-1 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-sm font-medium">Delete</button>
            </div>
          </div>
        </Modal>
      )}

      {showModal && <AssetModal asset={editing} loans={loans} onSave={onSave} onClose={() => { setShowModal(false); setEditing(null); }} />}
    </div>
  );
}

function AssetModal({ asset, loans, onSave, onClose }) {
  const [form, setForm] = useState(asset || {
    id: uid('asset'),
    name: '', assetType: 'real_estate', currency: 'AED',
    purchasePrice: '', purchaseDate: todayStr(),
    appreciationRate: 3, linkedLoanId: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  // Auto-set default rate when assetType changes (only for new assets)
  const handleTypeChange = (type) => {
    const info = ASSET_TYPES.find(t => t.value === type);
    setForm(f => ({ ...f, assetType: type, appreciationRate: asset ? f.appreciationRate : (info?.defaultRate ?? 0) }));
  };

  const currentValue = form.purchasePrice ? getAssetCurrentValue({ ...form, purchasePrice: parseFloat(form.purchasePrice) || 0 }) : 0;

  const handleSave = async () => {
    if (!form.name?.trim() || !form.purchasePrice) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        name: form.name.trim(),
        notes: form.notes?.trim() || '',
        purchasePrice: parseFloat(form.purchasePrice) || 0,
        appreciationRate: parseFloat(form.appreciationRate) || 0,
        linkedLoanId: form.linkedLoanId || '',
      });
      onClose();
    } catch (e) { toast('Save failed.'); } finally { setSaving(false); }
  };

  return (
    <Modal title={asset ? 'Edit Asset' : 'New Asset'} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Asset Name">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input" placeholder="e.g. Dubai Apartment" autoFocus />
          </Field>
          <Field label="Asset Type">
            <select value={form.assetType} onChange={e => handleTypeChange(e.target.value)} className="input">
              {ASSET_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Purchase Price">
            <input type="number" step="0.01" min="0" value={form.purchasePrice} onChange={e => setForm({ ...form, purchasePrice: e.target.value })} className="input" placeholder="0.00" />
          </Field>
          <Field label="Currency">
            <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="input">
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Purchase Date">
            <input type="date" value={form.purchaseDate} onChange={e => setForm({ ...form, purchaseDate: e.target.value })} className="input" />
          </Field>
          <Field label="Yearly Rate (% appreciation/depreciation)">
            <input type="number" step="0.1" value={form.appreciationRate} onChange={e => setForm({ ...form, appreciationRate: e.target.value })} className="input" placeholder="e.g. 3 or -12" />
          </Field>
        </div>

        {form.purchasePrice > 0 && (
          <div className="bg-slate-800/40 rounded-lg p-3 text-sm flex items-center justify-between">
            <span className="text-slate-400">Estimated current value</span>
            <span className="font-bold text-emerald-400">{fmtMoney(currentValue, form.currency)}</span>
          </div>
        )}

        <Field label="Linked Loan (optional)">
          <select value={form.linkedLoanId} onChange={e => setForm({ ...form, linkedLoanId: e.target.value })} className="input">
            <option value="">None</option>
            {loans.map(l => <option key={l.id} value={l.id}>{l.name} ({fmtMoney((l.liveBalance ?? l.currentBalance) || 0, l.currency)})</option>)}
          </select>
        </Field>

        <Field label="Notes (optional)">
          <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="input" placeholder="e.g. JVC - 2BR" />
        </Field>

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name?.trim() || !form.purchasePrice} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============ INVESTMENTS ============
function Investments({ investments, accounts = [], cards = [], onSave, onDelete, onBuy, onSell, showModal, setShowModal, baseCurrency }) {
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [trade, setTrade] = useState(null); // { mode: 'buy' } | { mode: 'sell', investment }

  const totals = useMemo(() => {
    let value = 0, cost = 0;
    investments.forEach(i => {
      value += toBase((i.shares || 0) * (i.currentPrice || 0), i.currency);
      cost += toBase((i.shares || 0) * (i.avgCost || 0), i.currency);
    });
    return { value, cost, gain: value - cost, gainPct: cost > 0 ? ((value - cost) / cost) * 100 : 0 };
  }, [investments]);

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    await onDelete(confirmDelete);
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Investments</h2>
          <p className="text-sm text-slate-400">Total value: {fmtMoney(totals.value, baseCurrency)}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTrade({ mode: 'buy' })} className="px-3 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 flex items-center gap-2 whitespace-nowrap flex-shrink-0">
            <Plus className="w-4 h-4" /> Buy
          </button>
          <button onClick={() => { setEditing(null); setShowModal(true); }} className="px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center gap-2" title="Record a holding you already own, with no money movement">
            <Plus className="w-4 h-4" /> Holding
          </button>
        </div>
      </div>

      {investments.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Cost Basis" value={fmtMoney(totals.cost, baseCurrency)} />
          <StatCard label="Market Value" value={fmtMoney(totals.value, baseCurrency)} />
          <StatCard
            label="Gain/Loss"
            value={`${totals.gain >= 0 ? '+' : ''}${fmtMoney(totals.gain, baseCurrency)} (${totals.gainPct.toFixed(1)}%)`}
            valueClass={totals.gain >= 0 ? 'text-emerald-400' : 'text-rose-400'}
          />
        </div>
      )}

      {confirmDelete && (() => {
        const inv = investments.find(i => i.id === confirmDelete);
        if (!inv) return null;
        return (
          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium mb-3">Delete "{inv.symbol}{inv.name ? ` — ${inv.name}` : ''}"?</div>
                <div className="flex gap-2">
                  <button onClick={handleConfirmDelete} className="px-3 py-1.5 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">Delete</button>
                  <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {investments.length === 0 ? (
        <div className="bg-slate-900 rounded-xl p-12 border border-slate-800 text-center">
          <TrendingUp className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-400">Track stocks, ETFs, crypto, and other holdings.</p>
        </div>
      ) : (
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-slate-800/50 text-slate-400 text-xs">
              <tr>
                <th className="text-left p-3">Symbol</th>
                <th className="text-right p-3">Shares</th>
                <th className="text-right p-3">Avg Cost</th>
                <th className="text-right p-3">Current</th>
                <th className="text-right p-3">Value</th>
                <th className="text-right p-3">Gain/Loss</th>
                <th className="w-28"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {investments.map(i => {
                const value = (i.shares || 0) * (i.currentPrice || 0);
                const cost = (i.shares || 0) * (i.avgCost || 0);
                const gain = value - cost;
                const gainPct = cost > 0 ? (gain / cost) * 100 : 0;
                return (
                  <tr key={i.id} className="hover:bg-slate-800/30">
                    <td className="p-3">
                      <div className="font-medium">{i.symbol}</div>
                      <div className="text-xs text-slate-400">{i.name}</div>
                    </td>
                    <td className="text-right p-3">{i.shares}</td>
                    <td className="text-right p-3">{i.currency} {(i.avgCost || 0).toFixed(2)}</td>
                    <td className="text-right p-3">{i.currency} {(i.currentPrice || 0).toFixed(2)}</td>
                    <td className="text-right p-3 font-semibold">{i.currency} {value.toLocaleString('en-US', { maximumFractionDigits: 2 })}</td>
                    <td className={`text-right p-3 ${gain >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {gain >= 0 ? '+' : ''}{gain.toFixed(2)} ({gainPct.toFixed(1)}%)
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 items-center">
                        <button onClick={() => setTrade({ mode: 'sell', investment: i })} className="px-2 py-1 text-xs rounded bg-slate-800 hover:bg-slate-700 border border-slate-700" title="Sell shares of this holding">
                          Sell
                        </button>
                        <button onClick={() => { setEditing(i); setShowModal(true); }} className="p-1.5 hover:bg-slate-700 rounded" title="Edit">
                          <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                        <button onClick={() => setConfirmDelete(i.id)} className="p-1.5 hover:bg-rose-900/50 rounded" title="Delete">
                          <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <InvestmentModal investment={editing} onSave={onSave} onClose={() => { setShowModal(false); setEditing(null); }} />}
      {trade && (
        <TradeModal
          mode={trade.mode}
          investment={trade.investment}
          accounts={accounts}
          cards={cards}
          onBuy={onBuy}
          onSell={onSell}
          onClose={() => setTrade(null)}
        />
      )}
    </div>
  );
}

function InvestmentModal({ investment, onSave, onClose }) {
  const [form, setForm] = useState(investment || {
    id: uid('inv'), symbol: '', name: '', shares: '', avgCost: '', currentPrice: '', currency: 'USD', type: 'stock',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.symbol?.trim() || !form.shares) return;
    const shares = parseFloat(form.shares);
    if (isNaN(shares) || shares <= 0) { toast('Shares must be a positive number.'); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        symbol: form.symbol.trim().toUpperCase(),
        name: form.name?.trim() || '',
        shares,
        avgCost: parseFloat(form.avgCost) || 0,
        currentPrice: parseFloat(form.currentPrice) || 0,
      });
      onClose();
    } catch (e) { toast('Save failed.'); } finally { setSaving(false); }
  };

  return (
    <Modal title={investment ? 'Edit Holding' : 'New Holding'} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Symbol">
            <input value={form.symbol} onChange={e => setForm({ ...form, symbol: e.target.value.toUpperCase() })} className="input" placeholder="AAPL" autoFocus />
          </Field>
          <Field label="Type">
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="input">
              <option value="stock">Stock</option>
              <option value="etf">ETF</option>
              <option value="crypto">Crypto</option>
              <option value="bond">Bond</option>
              <option value="other">Other</option>
            </select>
          </Field>
        </div>
        <Field label="Name">
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input" placeholder="Apple Inc." />
        </Field>
        <Field label="Shares / Units">
          <input type="number" step="0.0001" min="0" value={form.shares} onChange={e => setForm({ ...form, shares: e.target.value })} className="input" />
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Avg Cost">
            <input type="number" step="0.01" min="0" value={form.avgCost} onChange={e => setForm({ ...form, avgCost: e.target.value })} className="input" />
          </Field>
          <Field label="Current Price">
            <input type="number" step="0.01" min="0" value={form.currentPrice} onChange={e => setForm({ ...form, currentPrice: e.target.value })} className="input" />
          </Field>
          <Field label="Currency">
            <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="input">
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <div className="text-xs text-slate-400">Update current price periodically for accuracy.</div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.symbol?.trim() || !form.shares} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Buy / sell a holding. Buying debits a funding account/card and creates (or
// averages into) the holding; selling reduces shares and credits the proceeds.
// Both go through the parent's onBuy/onSell, which write the transfer + the
// investment change together. See buyHolding / sellHolding.
function TradeModal({ mode, investment, accounts = [], cards = [], onBuy, onSell, onClose }) {
  const isBuy = mode === 'buy';

  const sources = [
    ...accounts.filter((a) => !a.deletedAt).map((a) => ({ kind: 'account', id: a.id, name: a.name })),
    ...cards.filter((c) => !c.deletedAt).map((c) => ({ kind: 'card', id: c.id, name: c.name })),
  ];

  const [form, setForm] = useState(
    isBuy
      ? { symbol: '', name: '', type: 'stock', currency: 'USD', shares: '', price: '', fee: '', source: '', date: todayStr() }
      : { shares: '', price: String(investment?.currentPrice ?? ''), fee: '', source: '', date: todayStr() },
  );
  const [saving, setSaving] = useState(false);

  const sh = parseFloat(form.shares) || 0;
  const px = parseFloat(form.price) || 0;
  const fee = parseFloat(form.fee) || 0;
  const ccy = isBuy ? form.currency : investment?.currency;
  const held = Number(investment?.shares) || 0;
  const total = isBuy ? sh * px + fee : sh * px - fee; // buy: cash out · sell: net proceeds
  const realised = !isBuy && investment ? total - sh * (Number(investment.avgCost) || 0) : 0;
  const picked = sources.find((s) => `${s.kind}:${s.id}` === form.source);

  const valid = isBuy
    ? !!(form.symbol.trim() && sh > 0 && px > 0 && picked)
    : !!(sh > 0 && sh <= held + 1e-9 && px > 0 && total > 0 && picked);

  const handleSave = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      if (isBuy) {
        await onBuy({
          symbol: form.symbol, name: form.name, type: form.type, currency: form.currency,
          shares: sh, pricePerShare: px, fee,
          fundingId: picked.id, fundingKind: picked.kind, fundingName: picked.name, date: form.date,
        });
      } else {
        await onSell({
          investmentId: investment.id, shares: sh, pricePerShare: px, fee,
          destId: picked.id, destKind: picked.kind, destName: picked.name, date: form.date,
        });
      }
      onClose();
    } catch (e) { toast(e?.message || 'Trade failed.'); } finally { setSaving(false); }
  };

  return (
    <Modal title={isBuy ? 'Buy Holding' : `Sell ${investment?.symbol}`} onClose={onClose}>
      <div className="space-y-3">
        {isBuy ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Symbol">
                <input value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })} className="input" placeholder="AAPL" autoFocus />
              </Field>
              <Field label="Type">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">
                  <option value="stock">Stock</option>
                  <option value="etf">ETF</option>
                  <option value="crypto">Crypto</option>
                  <option value="bond">Bond</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>
            <Field label="Name">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="Apple Inc." />
            </Field>
            <div className="text-xs text-slate-500">Buying a symbol you already hold averages into that holding's cost basis.</div>
          </>
        ) : (
          <div className="bg-slate-800/40 rounded-lg p-3 text-xs">
            <div className="font-medium">{investment?.symbol}{investment?.name ? ` — ${investment.name}` : ''}</div>
            <div className="text-slate-400 mt-0.5">Holding {held} · avg cost {ccy} {(Number(investment?.avgCost) || 0).toFixed(2)}</div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <Field label={isBuy ? 'Shares' : `Shares (max ${held})`}>
            <input type="number" step="0.0001" min="0" value={form.shares} onChange={(e) => setForm({ ...form, shares: e.target.value })} className="input" />
          </Field>
          <Field label="Price / share">
            <input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input" />
          </Field>
          {isBuy ? (
            <Field label="Currency">
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="input">
                {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          ) : (
            <Field label="Fee">
              <input type="number" step="0.01" min="0" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} className="input" />
            </Field>
          )}
        </div>

        {isBuy && (
          <Field label="Fee (optional)">
            <input type="number" step="0.01" min="0" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} className="input" />
          </Field>
        )}

        <Field label={isBuy ? 'Pay from' : 'Deposit proceeds to'}>
          <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="input">
            <option value="">Select account or card…</option>
            <optgroup label="Accounts">
              {sources.filter((s) => s.kind === 'account').map((s) => <option key={`account:${s.id}`} value={`account:${s.id}`}>{s.name}</option>)}
            </optgroup>
            <optgroup label="Cards">
              {sources.filter((s) => s.kind === 'card').map((s) => <option key={`card:${s.id}`} value={`card:${s.id}`}>{s.name}</option>)}
            </optgroup>
          </select>
        </Field>

        <Field label="Date">
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input" />
        </Field>

        <div className="bg-slate-800/40 rounded-lg p-3 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-slate-400">{isBuy ? 'Total cost' : 'Net proceeds'}</span>
            <span className="font-semibold">{ccy} {total.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
          </div>
          {isBuy ? (
            <div className="text-xs text-slate-500">Recorded as a transfer from {picked?.name || 'the selected source'} — not counted as spending.</div>
          ) : (
            <div className="flex justify-between">
              <span className="text-slate-400">Realised {realised >= 0 ? 'gain' : 'loss'}</span>
              <span className={realised >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{realised >= 0 ? '+' : ''}{ccy} {realised.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !valid} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">
            {saving ? 'Saving…' : (isBuy ? 'Buy' : 'Sell')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============ RECURRING ============
function Recurring({ recurring, transactions, accounts, cards = [], categories = [], onSave, onDelete, onFireNow, showModal, setShowModal, baseCurrency, highlightIds = [], onClearHighlight }) {
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const highlightRef = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!highlightIds.length) return;
    const firstId = highlightIds[0];
    const el = highlightRef.current[firstId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const t = setTimeout(() => onClearHighlight?.(), 3000);
    return () => clearTimeout(t);
  }, [highlightIds]);

  const rulesWithMeta = useMemo(() => recurring.map(r => {
    const today = todayStr();
    const horizon = new Date(strToDate(today));
    horizon.setMonth(horizon.getMonth() + 24); // 24mo so yearly subs resolve a next date
    const occurrences = generateOccurrences(r, today, dateToStr(horizon));
    const next = occurrences[0] || null;
    const matchedTx = transactions.filter(t => t.recurringId === r.id);
    const totalGenerated = matchedTx.length;
    const lastGenerated = matchedTx.length > 0
      ? matchedTx.map(t => strToDate(t.date)).sort((a, b) => b - a)[0]
      : null;
    return { ...r, next, totalGenerated, lastGenerated };
  }).sort((a, b) => {
    if (!a.next && !b.next) return 0;
    if (!a.next) return 1;
    if (!b.next) return -1;
    return a.next - b.next;
  }), [recurring, transactions]);

  const totals = useMemo(() => {
    let income = 0, expense = 0;
    rulesWithMeta.filter(r => r.active).forEach(r => {
      // Interest rules carry a placeholder amount of 1 — their real value
      // is MAB × rate/12 at materialise time and varies month-to-month, so
      // they don't belong in a fixed monthly/annual rollup.
      if (r.isInterestRule) return;
      const interval = Math.max(1, Number(r.interval) || 1);
      const unit = r.unit || 'month';
      let perMonth = 0;
      if (unit === 'month') perMonth = 1 / interval;
      else if (unit === 'year') perMonth = 1 / (12 * interval);
      else if (unit === 'week') perMonth = (52 / 12) / interval;
      const m = toBase(r.amount, r.currency) * perMonth;
      if (r.type === 'income') income += m; else expense += m;
    });
    return {
      income, expense, net: income - expense,
      annualIncome: income * 12, annualExpense: expense * 12, annualNet: (income - expense) * 12,
    };
  }, [rulesWithMeta]);

  const [showAnnual, setShowAnnual] = useState(false);
  const display = showAnnual
    ? { income: totals.annualIncome, expense: totals.annualExpense, net: totals.annualNet, label: 'Annual' }
    : { income: totals.income, expense: totals.expense, net: totals.net, label: 'Monthly' };

  const handleConfirmDelete = async (alsoDeleteOccurrences) => {
    if (!confirmDelete) return;
    await onDelete(confirmDelete.id, alsoDeleteOccurrences);
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-bold">Recurring Transactions</h2>
          <p className="text-sm text-slate-400">Your recurring money. The play/pause toggles <span className="text-emerald-300">auto-logging</span>: leave it <span className="text-emerald-300">on</span> for things with no SMS (EPP, statement-only); turn it <span className="text-amber-300">off</span> for things you get an SMS for, so they stay visible here without double-logging.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowModal(true); }} className="px-3 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 flex items-center gap-2 whitespace-nowrap flex-shrink-0">
          <Plus className="w-4 h-4" /> Rule
        </button>
      </div>

      {recurring.length > 0 && (
        <>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">View:</span>
            <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800">
              <button onClick={() => setShowAnnual(false)} className={`px-3 py-1 text-xs rounded transition ${!showAnnual ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                Monthly
              </button>
              <button onClick={() => setShowAnnual(true)} className={`px-3 py-1 text-xs rounded transition ${showAnnual ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                Annual
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <StatCard label={`${display.label} Income`} value={`+${fmtMoney(display.income, baseCurrency)}`} valueClass="text-emerald-400" />
            <StatCard label={`${display.label} Expense`} value={`−${fmtMoney(display.expense, baseCurrency)}`} valueClass="text-rose-400" />
            <StatCard
              label={`Net ${display.label}`}
              value={`${display.net >= 0 ? '+' : '−'}${fmtMoney(Math.abs(display.net), baseCurrency)}`}
              valueClass={display.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}
            />
          </div>
        </>
      )}

      {rulesWithMeta.length === 0 ? (
        <div className="bg-slate-900 rounded-xl p-12 border border-slate-800 text-center">
          <Repeat className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-400 mb-2">No recurring transactions yet.</p>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            Click the <strong>+ Rule</strong> button above to create one. You'll pick a description, amount, schedule, and target account or card.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {(() => {
            // Group rules so the page reads cleanly: Income up top, then
            // anything tied to a loan or card installment, then ordinary
            // expenses, then transfers. Section headers only render when
            // there's more than one group — same pattern as the Debt page.
            const groups = [
              { label: 'Income',        items: rulesWithMeta.filter(r => r.type === 'income') },
              { label: 'Debt payments', items: rulesWithMeta.filter(r => (r.linkedLoanId || r.linkedCardInstallmentId) && r.type !== 'income') },
              { label: 'Expenses',      items: rulesWithMeta.filter(r => r.type === 'expense' && !r.linkedLoanId && !r.linkedCardInstallmentId) },
              { label: 'Transfers',     items: rulesWithMeta.filter(r => r.type === 'transfer' && !r.linkedLoanId && !r.linkedCardInstallmentId) },
            ].filter(g => g.items.length > 0);
            const showHeaders = groups.length > 1;
            const renderRow = (r) => {
              const cat = categories.find(c => c.name === r.category && !c.deletedAt) || ALL_CATEGORIES.find(c => c.name === r.category);
            const isIncome = r.type === 'income';
            const today = strToDate(todayStr());
            const daysUntilNext = r.next ? Math.ceil((r.next - today) / (MS_PER_DAY)) : null;
            const ended = r.endDate && strToDate(r.endDate) < today;
            const isConfirming = confirmDelete?.id === r.id;

            if (isConfirming) {
              return (
                <div key={r.id} className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm font-medium mb-1">Delete "{r.description}"?</div>
                      {r.totalGenerated > 0 ? (
                        <>
                          <div className="text-xs text-slate-400 mb-3">
                            This rule has generated {r.totalGenerated} transaction{r.totalGenerated > 1 ? 's' : ''}. What should happen to them?
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => handleConfirmDelete(false)} className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600">
                              Keep transactions
                            </button>
                            <button onClick={() => handleConfirmDelete(true)} className="px-3 py-1.5 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">
                              Delete transactions too
                            </button>
                            <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs rounded bg-slate-800 hover:bg-slate-700 text-slate-300">
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex gap-2">
                          <button onClick={() => handleConfirmDelete(false)} className="px-3 py-1.5 text-xs rounded bg-rose-600 hover:bg-rose-500 text-white">
                            Delete
                          </button>
                          <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600">
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            const isHighlighted = highlightIds.includes(r.id);
            return (
              <div key={r.id} id={r.id} ref={el => { highlightRef.current[r.id] = el; }} className={`bg-slate-900 rounded-xl p-4 border transition-all duration-700 ${isHighlighted ? 'border-amber-400/60 ring-2 ring-amber-400/40' : 'border-slate-800'}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-base flex-shrink-0" style={{ backgroundColor: '#64748b20' }}>
                    {cat?.icon || '🔁'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium break-words">{r.description}</span>
                      {r.autoLog === false
                        ? <span className="text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-300" title="Visible for awareness only — Floosi won't auto-create this. You log it yourself (e.g. it arrives by SMS).">Manual Logging</span>
                        : <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300" title="Floosi auto-creates this transaction on schedule.">Auto Logging</span>}
                      {ended && <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">Ended</span>}
                    </div>
                    <div className="text-xs text-slate-400 break-words">{describeRule(r)} · {r.category} · {r.account}</div>
                    {r.next && !ended && (
                      <div className="text-xs text-cyan-400 mt-0.5 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Next: {r.next.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {daysUntilNext !== null && (
                          <span className="text-slate-500">({daysUntilNext <= 0 ? 'today' : daysUntilNext === 1 ? 'tomorrow' : `in ${daysUntilNext} days`})</span>
                        )}
                      </div>
                    )}
                    {r.totalGenerated > 0 && (
                      <div className="text-xs text-slate-500 mt-0.5">
                        {r.totalGenerated} generated · last on {r.lastGenerated?.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <div className="text-right">
                    {r.isInterestRule ? (
                      <>
                        <div className="text-sm font-semibold text-sky-300">
                          {Number(r.interestAnnualRatePct ?? 0).toFixed(2)}% p.a.
                        </div>
                        <div className="text-xs text-slate-500">computed monthly</div>
                      </>
                    ) : (
                      <>
                        <div className={`text-sm font-semibold ${isIncome ? 'text-emerald-400' : ''}`}>
                          {isIncome ? '+' : '−'}{fmtMoney(r.amount, r.currency)}
                        </div>
                        {r.currency !== baseCurrency && (
                          <div className="text-xs text-slate-500">≈ {fmtMoney(toBase(r.amount, r.currency), baseCurrency)}</div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {r.active && !r.isInterestRule && onFireNow && (
                      <button
                        onClick={() => { if (r.autoLog !== false) onFireNow(r.id); }}
                        disabled={r.autoLog === false}
                        className={`p-1.5 rounded ${r.autoLog === false ? 'opacity-30 cursor-not-allowed' : 'hover:bg-amber-900/30'}`}
                        title={r.autoLog === false
                          ? "Auto-logging is off — this rule isn't auto-created (you log it, e.g. via SMS)"
                          : "Fire now — generate this rule's next occurrence dated today; the scheduled date won't fire again"}
                      >
                        <Zap className="w-3.5 h-3.5 text-amber-400" />
                      </button>
                    )}
                    <button
                      onClick={() => onSave({ ...r, autoLog: r.autoLog === false, ...(r.autoLog === false ? { materializedThrough: todayStr() } : {}) })}
                      className="p-1.5 hover:bg-slate-700 rounded"
                      title={r.autoLog === false ? 'Turn on auto-logging (logs from today forward — no backfill)' : 'Turn off auto-logging (visible only — you log it, e.g. via SMS)'}
                    >
                      {r.autoLog === false ? <Play className="w-3.5 h-3.5 text-emerald-400" /> : <Pause className="w-3.5 h-3.5 text-slate-400" />}
                    </button>
                    {!r.isInterestRule && (
                      <button
                        onClick={() => onSave({ ...r, remind: !r.remind })}
                        className="p-1.5 hover:bg-slate-700 rounded"
                        title={r.remind ? 'Reminders ON — notify before this is due. Tap to turn off.' : 'Remind me before this is due'}
                      >
                        <Bell className={`w-3.5 h-3.5 ${r.remind ? 'text-cyan-400' : 'text-slate-500'}`} />
                      </button>
                    )}
                    <button onClick={() => { setEditing(r); setShowModal(true); }} className="p-1.5 hover:bg-slate-700 rounded" title="Edit">
                      <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    <button onClick={() => setConfirmDelete({ id: r.id })} className="p-1.5 hover:bg-rose-900/50 rounded" title="Delete">
                      <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                    </button>
                  </div>
                  </div>
                </div>
              </div>
            );
            };
            return groups.map((g) => (
              <Fragment key={g.label}>
                {showHeaders && (
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide pt-2">{g.label}</div>
                )}
                <div className="grid lg:grid-cols-2 gap-3 items-start">{g.items.map(renderRow)}</div>
              </Fragment>
            ));
          })()}
        </div>
      )}

      {showModal && <RecurringModal rule={editing} accounts={accounts} cards={cards} categories={categories} onSave={onSave} onClose={() => { setShowModal(false); setEditing(null); }} />}
    </div>
  );
}

function RecurringModal({ rule, accounts, cards = [], categories = [], onSave, onClose }) {
  const liveAccounts = accounts.filter((a) => !a.deletedAt);
  const liveCards = cards.filter((c) => !c.deletedAt);
  const liveCats = (categories || []).filter((c) => !c.deletedAt);
  const [form, setForm] = useState(rule ? { ...rule, autoLog: rule.autoLog !== false } : {
    id: uid('rec'),
    description: '', amount: '', currency: 'AED', type: 'expense', category: '',
    account: liveAccounts[0]?.name || liveCards[0]?.name || 'Default',
    pattern: 'day_of_month', dayOfMonth: 1, weekday: new Date().getDay(), occurrence: -1, nthLastWorkingDay: 1, monthOfYear: new Date().getMonth(),
    // start_date is left blank by default — see handleSave for the today
    // default. If the user wants to backfill historical occurrences they
    // pick a past date here; for the everyday "salary / subscription" case
    // they don't need to think about it.
    interval: 1, unit: 'month', startDate: '', endDate: '', active: true, autoLog: true, notes: '',
    isInterestRule: false, interestAnnualRatePct: '', maxWithdrawalsPerMonth: '',
  });
  const [saving, setSaving] = useState(false);
  const isInterest = !!form.isInterestRule;

  const fromState = liveCats.filter((c) => c.kind === form.type);
  const cats = fromState.length > 0 ? fromState : DEFAULT_CATEGORIES[form.type];

  const preview = useMemo(() => {
    // Preview uses the effective start date (user's pick OR today) so leaving
    // start blank still shows what the next few occurrences will look like.
    const effectiveStart = form.startDate || todayStr();
    const horizon = new Date();
    horizon.setFullYear(horizon.getFullYear() + 2);
    try { return generateOccurrences({ ...form, startDate: effectiveStart }, effectiveStart, dateToStr(horizon)).slice(0, 3); } catch { return []; }
  }, [form]);

  const handleSave = async () => {
    if (!form.description?.trim()) return;
    // Interest rules: amount is computed each materialisation from MAB × rate/12,
    // so we just need a non-zero placeholder to satisfy the recurring_rules
    // amount > 0 check constraint. The user picks rate + max withdrawals.
    let amount;
    if (isInterest) {
      const rate = parseFloat(form.interestAnnualRatePct);
      if (isNaN(rate) || rate <= 0) { toast('Annual interest rate must be greater than zero.'); return; }
      if (rate > 100) { toast('Annual interest rate must be ≤ 100%.'); return; }
      amount = 1;
    } else {
      if (!form.amount) return;
      amount = parseFloat(form.amount);
      if (isNaN(amount) || amount <= 0) { toast('Amount must be greater than zero.'); return; }
      // Expense/income rules must carry a category. Transfer rules don't
      // (form.type === 'transfer' uses from/to instead of a category).
      if (form.type !== 'transfer' && !form.category) {
        toast('Pick a category for this rule. The forecast and budget rollups rely on it.');
        return;
      }
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        description: form.description.trim(),
        amount,
        active: true,
        // autoLog: false = paused (visible/forecasted, never materialised).
        // Checkbox "Auto-log transactions" drives this; false creates the rule paused.
        autoLog: form.autoLog ? undefined : false,
        type: isInterest ? 'income' : form.type,
        category: isInterest ? 'Interest' : form.category,
        dayOfMonth: parseInt(form.dayOfMonth) || 1,
        weekday: parseInt(form.weekday) || 0,
        occurrence: parseInt(form.occurrence) || 1,
        nthLastWorkingDay: parseInt(form.nthLastWorkingDay) || 1,
        // Only persist month_of_year for yearly rules; null otherwise so
        // monthly/weekly rules don't carry irrelevant state.
        monthOfYear: form.unit === 'year' ? (parseInt(form.monthOfYear) ?? new Date().getMonth()) : null,
        // Interest rules are always monthly day-of-month on the picked day.
        interval: isInterest ? 1 : (parseInt(form.interval) || 1),
        unit: isInterest ? 'month' : form.unit,
        pattern: isInterest ? 'day_of_month' : form.pattern,
        // Blank start = "start from today, no backfill"; the materialiser
        // still needs a concrete date in the row.
        startDate: form.startDate || todayStr(),
        endDate: form.endDate || null,
        // Interest-specific fields. Stored as numbers (or null) regardless
        // of whether isInterestRule is on, so they round-trip cleanly.
        isInterestRule: isInterest,
        interestAnnualRatePct: isInterest ? parseFloat(form.interestAnnualRatePct) : null,
        maxWithdrawalsPerMonth: isInterest && form.maxWithdrawalsPerMonth !== '' && form.maxWithdrawalsPerMonth != null
          ? parseInt(form.maxWithdrawalsPerMonth) : null,
      });
      onClose();
    } catch (e) { toast('Save failed: ' + (e?.message || String(e))); } finally { setSaving(false); }
  };

  return (
    <Modal title={rule ? 'Edit Recurring Transaction' : 'New Recurring Transaction'} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <button onClick={() => setForm({ ...form, type: 'expense', category: 'Other', isInterestRule: false })} className={`py-2 rounded-lg text-sm font-medium transition ${form.type === 'expense' && !isInterest ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40' : 'bg-slate-800 text-slate-400'}`}>Expense</button>
          <button onClick={() => setForm({ ...form, type: 'income', category: 'Salary', isInterestRule: false })} className={`py-2 rounded-lg text-sm font-medium transition ${form.type === 'income' && !isInterest ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400'}`}>Income</button>
          <button onClick={() => setForm({ ...form, type: 'transfer', category: 'Transfer', isInterestRule: false })} className={`py-2 rounded-lg text-sm font-medium transition ${form.type === 'transfer' && !isInterest ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40' : 'bg-slate-800 text-slate-400'}`}>Transfer</button>
        </div>

        <button
          type="button"
          onClick={() => setForm({ ...form, isInterestRule: !isInterest, type: 'income', category: 'Interest' })}
          className={`w-full py-2 rounded-lg text-sm font-medium transition ${isInterest ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}
        >
          {isInterest ? '✓ Interest rule (MAB-based payout)' : 'Interest rule (savings interest)'}
        </button>

        <Field label="Description">
          <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="input" placeholder={isInterest ? 'e.g. Liv. savings interest' : 'e.g. Salary, Rent, Spotify'} autoFocus />
        </Field>

        {isInterest ? (
          <div className="grid grid-cols-3 gap-2">
            <Field label="Annual rate %" className="col-span-1">
              <input type="number" step="0.01" min="0" max="100" value={form.interestAnnualRatePct} onChange={e => setForm({ ...form, interestAnnualRatePct: e.target.value })} className="input" placeholder="3.50" />
            </Field>
            <Field label="Max withdrawals / month" className="col-span-1">
              <input type="number" step="1" min="0" value={form.maxWithdrawalsPerMonth} onChange={e => setForm({ ...form, maxWithdrawalsPerMonth: e.target.value })} className="input" placeholder="blank = unlimited" />
            </Field>
            <Field label="Currency">
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="input">
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <Field label="Amount" className="col-span-2">
              <input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="input" placeholder="0.00" />
            </Field>
            <Field label="Currency">
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="input">
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
        )}

        {form.type === 'transfer' ? (
          <div className="grid grid-cols-2 gap-2">
            <Field label="From">
              <select value={form.fromAccount || ''} onChange={(e) => setForm({ ...form, fromAccount: e.target.value })} className="input">
                <option value="">Select source…</option>
                {liveAccounts.length > 0 && (
                  <optgroup label="Accounts">
                    {liveAccounts.map((a) => <option key={`from-acc-${a.id}`} value={a.name}>{a.name} ({a.currency})</option>)}
                  </optgroup>
                )}
                {liveCards.length > 0 && (
                  <optgroup label="Credit Cards">
                    {liveCards.map((c) => <option key={`from-card-${c.id}`} value={c.name}>{c.name} ({c.currency})</option>)}
                  </optgroup>
                )}
              </select>
            </Field>
            <Field label="To">
              <select value={form.toAccount || ''} onChange={(e) => setForm({ ...form, toAccount: e.target.value })} className="input">
                <option value="">Select destination…</option>
                {liveAccounts.length > 0 && (
                  <optgroup label="Accounts">
                    {liveAccounts.map((a) => <option key={`to-acc-${a.id}`} value={a.name}>{a.name} ({a.currency})</option>)}
                  </optgroup>
                )}
                {liveCards.length > 0 && (
                  <optgroup label="Credit Cards">
                    {liveCards.map((c) => <option key={`to-card-${c.id}`} value={c.name}>{c.name} ({c.currency})</option>)}
                  </optgroup>
                )}
              </select>
            </Field>
          </div>
        ) : isInterest ? (
          <>
            <Field label="Account">
              <select value={form.account} onChange={e => setForm({ ...form, account: e.target.value })} className="input">
                {liveAccounts.map((a) => <option key={`int-acc-${a.id}`} value={a.name}>{a.name} ({a.currency})</option>)}
              </select>
            </Field>
            <div className="text-xs text-slate-400 bg-slate-800/40 border border-slate-800 rounded-lg p-2 leading-relaxed">
              Each month on the chosen day, the materialiser computes
              MAB × ({form.interestAnnualRatePct || 'rate'}% / 12) for the prior month
              and posts it as an Income on this account.
              {form.maxWithdrawalsPerMonth !== '' && form.maxWithdrawalsPerMonth != null
                ? ` If transfers OUT of this account exceeded ${form.maxWithdrawalsPerMonth} in that month, the payout is skipped (forfeit).`
                : ' No withdrawal limit.'}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Category">
              <select value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })} className="input">
                <option value="">Select a category…</option>
                {cats.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Account / Card">
              <select value={form.account} onChange={e => setForm({ ...form, account: e.target.value })} className="input">
                {liveAccounts.length > 0 && (
                  <optgroup label="Accounts">
                    {liveAccounts.map((a) => <option key={`acc-${a.id}`} value={a.name}>{a.name} ({a.currency})</option>)}
                  </optgroup>
                )}
                {liveCards.length > 0 && (
                  <optgroup label="Credit Cards">
                    {liveCards.map((c) => <option key={`card-${c.id}`} value={c.name}>{c.name} ({c.currency})</option>)}
                  </optgroup>
                )}
              </select>
            </Field>
          </div>
        )}

        <div className="bg-slate-800/40 rounded-lg p-3 space-y-3 border border-slate-800">
          <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Schedule</div>
          {isInterest ? (
            <Field label="Payout day of month">
              <input type="number" min="1" max="31" value={form.dayOfMonth} onChange={e => setForm({ ...form, dayOfMonth: e.target.value })} className="input" />
              <div className="text-xs text-slate-500 mt-1">Runs monthly on this day. Interest paid out covers the previous calendar month.</div>
            </Field>
          ) : (
          <>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Every">
                <input type="number" min="1" max="99" value={form.interval} onChange={e => setForm({ ...form, interval: e.target.value })} className="input" />
              </Field>
              <Field label="Unit">
                <select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} className="input">
                  <option value="week">Week(s)</option>
                  <option value="month">Month(s)</option>
                  <option value="year">Year(s)</option>
                </select>
              </Field>
            </div>
            <Field label="Pattern">
              <select value={form.pattern} onChange={e => setForm({ ...form, pattern: e.target.value })} className="input" disabled={form.unit === 'week'}>
                <option value="day_of_month">On a specific day</option>
                <option value="last_day">On the last day</option>
                <option value="last_working_day">Last working day (Mon–Fri)</option>
                <option value="nth_last_working_day">Nth-to-last working day</option>
                <option value="weekday_of_month">On a specific weekday</option>
              </select>
            </Field>
          </div>

          {form.unit !== 'week' && form.pattern === 'day_of_month' && (
            <Field label="Day of month">
              <input type="number" min="1" max="31" value={form.dayOfMonth} onChange={e => setForm({ ...form, dayOfMonth: e.target.value })} className="input" />
              <div className="text-xs text-slate-500 mt-1">Falls back to last day if month is shorter (e.g., Feb).</div>
            </Field>
          )}

          {form.unit !== 'week' && form.pattern === 'nth_last_working_day' && (
            <Field label="Which one">
              <select value={form.nthLastWorkingDay} onChange={e => setForm({ ...form, nthLastWorkingDay: e.target.value })} className="input">
                <option value={1}>Last working day</option>
                <option value={2}>2nd-to-last working day</option>
                <option value={3}>3rd-to-last working day</option>
                <option value={4}>4th-to-last working day</option>
                <option value={5}>5th-to-last working day</option>
              </select>
            </Field>
          )}

          {form.unit !== 'week' && form.pattern === 'weekday_of_month' && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Which one">
                <select value={form.occurrence} onChange={e => setForm({ ...form, occurrence: e.target.value })} className="input">
                  <option value={1}>First</option>
                  <option value={2}>Second</option>
                  <option value={3}>Third</option>
                  <option value={4}>Fourth</option>
                  <option value={-1}>Last</option>
                </select>
              </Field>
              <Field label="Weekday">
                <select value={form.weekday} onChange={e => setForm({ ...form, weekday: e.target.value })} className="input">
                  {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </Field>
            </div>
          )}

          {form.unit === 'week' && (
            <Field label="On which day">
              <select value={form.weekday} onChange={e => setForm({ ...form, weekday: e.target.value })} className="input">
                {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">Fires every {form.interval || 1} week(s) on this day.</div>
            </Field>
          )}

          {form.unit === 'year' && (
            <Field label="Month">
              <select value={form.monthOfYear} onChange={e => setForm({ ...form, monthOfYear: e.target.value })} className="input">
                {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">Combine with the pattern above to say e.g. "the 15th of November" or "the last working day of November".</div>
            </Field>
          )}
          </>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Field label="Start date (optional)">
              <input type="date" value={form.startDate || ''} onChange={e => setForm({ ...form, startDate: e.target.value })} className="input" placeholder="today" />
              <div className="text-[10px] text-slate-500 mt-1">Blank = start from today. Pick a past date to backfill missed occurrences.</div>
            </Field>
            <Field label="End date (optional)">
              <input type="date" value={form.endDate || ''} onChange={e => setForm({ ...form, endDate: e.target.value })} className="input" />
              <div className="text-[10px] text-slate-500 mt-1">Blank = no end. Pick a date to stop on/before it.</div>
            </Field>
          </div>

          {preview.length > 0 && (
            <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
              <div className="text-xs text-slate-400 mb-2">Next occurrences:</div>
              <div className="flex flex-wrap gap-2">
                {preview.map((d, i) => (
                  <div key={i} className="text-xs px-2 py-1 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                    {d.toLocaleDateString('en', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <Field label="Notes (optional)">
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="input min-h-[50px]" />
        </Field>

        <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 cursor-pointer">
          <input type="checkbox" checked={!!form.autoLog} onChange={e => setForm({ ...form, autoLog: e.target.checked })} className="w-4 h-4" />
          <span className="text-sm">Auto-log transactions <span className="text-slate-500 text-xs">(uncheck to create paused)</span></span>
        </label>

        {rule && (
          <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2">
            Note: editing this rule changes future occurrences only. Past generated transactions stay as they are.
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.description?.trim() || (!isInterest && !form.amount) || (isInterest && !form.interestAnnualRatePct)} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============ IMPORT MODAL ============
function ImportModal({ onImport, smsTokens = [], cards = [], accounts = [], categories = [], loans = [], transactions = [], onReloadTxs, onClose }) {
  const [mode, setMode] = useState('sms');
  const [text, setText] = useState('');
  const [preview, setPreview] = useState([]);
  const [step, setStep] = useState('input');
  const [importing, setImporting] = useState(false);
  // Each SMS preview row carries the parsed fields + the source sms_raw_id +
  // a "skip" flag the user can flip to drop a row from the commit.
  const [smsRows, setSmsRows] = useState([]);
  const [parseProgress, setParseProgress] = useState({ done: 0, total: 0 });
  const SMS_ENDPOINT = `${(import.meta as any).env?.VITE_SUPABASE_URL || ''}/functions/v1/sms-ingest`;

  const liveCards = cards.filter(c => !c.deletedAt);
  const liveExpCats = categories.filter(c => !c.deletedAt && c.kind === 'expense');
  const liveIncCats = categories.filter(c => !c.deletedAt && c.kind === 'income');
  const catName = (id) => categories.find(c => c.id === id)?.name || '';
  const cardName = (id) => cards.find(c => c.id === id)?.name || '';

  const handleParse = () => {
    if (!text.trim()) return;
    if (mode === 'sms') return runSmsParse();
    const parsed = parseCSV(text);
    setPreview(parsed);
    setStep('preview');
  };

  const runSmsParse = async () => {
    const active = (smsTokens || []).find((t) => !t.revokedAt);
    if (!active?.token) {
      toast('Generate an SMS token in Settings → SMS Auto-import first.');
      return;
    }
    const chunks = splitSmsBatch(text);
    if (chunks.length === 0) { toast('No SMSes detected. Each SMS should start with a recognised opener (POS Purchase, Online Purchase, A transaction, etc.) — one per line or separated by blank lines.'); return; }
    setImporting(true);
    setStep('sms-parsing');
    setParseProgress({ done: 0, total: chunks.length });
    const rows: any[] = [];
    // Track simulated last_sms_available_limit per card across this batch.
    // dry_run doesn't persist it to the DB; without this each call would
    // delta against the same stale DB value and produce cumulative growth.
    const simulatedLimits: Record<string, number> = {};
    for (let i = 0; i < chunks.length; i++) {
      try {
        // Peek the body for last4 → look up the card so we can pass the
        // most recent simulated limit (if any) as prev_limit_override.
        const last4Match = chunks[i].match(/\b(?:card|ending|ends|\*+|x+)[^\d]{0,8}(\d{4})\b/i);
        const last4 = last4Match?.[1] ?? null;
        const cardForLast4 = last4 ? liveCards.find(c => (c as any).lastFour === last4) : null;
        const overrideKey = cardForLast4?.id;
        const reqBody: any = { sms_body: chunks[i], sender: 'bulk-import', dry_run: true };
        if (overrideKey && simulatedLimits[overrideKey] != null) {
          reqBody.prev_limit_override = simulatedLimits[overrideKey];
        }
        const res = await fetch(SMS_ENDPOINT, {
          method: 'POST',
          headers: { Authorization: `Bearer ${active.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(reqBody),
        });
        const body = await res.json().catch(() => ({}));
        if (res.status === 200 && body?.ok) {
          const txType = body.parsed?.tx_type || 'expense';
          // A refund is a negative card expense — it carries the same card /
          // category / debit-account fields, so pre-fill them like an expense.
          const isExpenseLike = txType === 'expense' || txType === 'refund';
          const kind = body.parsed?.kind || null;
          rows.push({
            idx: i + 1,
            rawId: body.raw_id,
            txType, // 'expense' | 'transfer' | 'income'
            kind,
            skipped: !body.parsed || body.reason ? true : false,
            skipReason: body.reason || null,
            description: body.parsed?.merchant
              || (kind === 'cash_withdrawal' ? 'Cash withdrawal' : null)
              || (kind === 'mashreq_cc_payment' ? `Credit card payment${body.parsed?.to_card_last4 ? ` *${body.parsed.to_card_last4}` : ''}` : null)
              || (kind === 'mashreq_transfer' ? 'Bank transfer' : null)
              || (kind === 'account_credit' ? `Credit: ${body.parsed?.counterparty || ''}`.trim() : null)
              || (kind === 'account_debit' ? `Debit: ${body.parsed?.counterparty || ''}`.trim() : null)
              || (body.parsed?.is_refund ? 'Refund' : 'Card purchase'),
            date: body.parsed?.date || '',
            // Expense fields. Use the card the parser resolved by last-4; if it
            // matched NONE, leave it empty so the row flags "needs a card"
            // (never silently default to the first card — that hides a wrong /
            // missing last-4). A debit-card purchase resolves to a bank account
            // (debit_account_id) instead, so leave the card empty there too.
            cardId: isExpenseLike ? (body.parsed?.card_id || '') : '',
            debitAccountId: isExpenseLike ? (body.parsed?.debit_account_id || '') : '',
            // Use the category the parser resolved (learned map or keyword
            // guess); if neither matched, leave it BLANK so the user picks one
            // (don't silently default to the first category).
            categoryId: isExpenseLike ? (body.parsed?.category_id || '') : '',
            // Transfer / income fields. For account_credit: the receiving
            // account is known (to), source unknown. For account_debit:
            // the source account is known (from), destination unknown.
            fromAccountId: body.parsed?.from_account_id_guess
              || (kind === 'account_debit' ? (body.parsed?.account_id || '') : ''),
            fromCardId: '',
            toAccountId: kind === 'account_credit' ? (body.parsed?.account_id || '') : '',
            toCardId: body.parsed?.to_card_id || '',
            // Shared
            amountAed: body.parsed?.amount_aed != null ? String(body.parsed.amount_aed) : '',
            originalAmount: body.parsed?.original_amount,
            originalCcy: body.parsed?.original_currency,
            isRefund: !!body.parsed?.is_refund,
            cardLast4: body.parsed?.card_last4 || body.parsed?.to_card_last4,
            bankLimit: body.parsed?.bank_available_limit,
            refNo: body.parsed?.ref_no || null,
            counterparty: body.parsed?.counterparty || null,
            notes: '',
            // Foreign-tx verification — used to flag the row + learn the rate.
            fxFlagged: !!body.parsed?.fx_flagged,
            fxFlagReason: body.parsed?.fx_flag_reason || null,
            fxLimitAed: body.parsed?.fx_limit_delta_aed ?? null,
            fxRateAed: body.parsed?.fx_rate_aed ?? null,
            fxDivergence: body.parsed?.fx_divergence_pct ?? null,
            fxMidRate: body.parsed?.fx_mid_rate ?? null,
            fxLearnable: body.parsed?.fx_learnable_multiplier ?? null,
            fxVatAmount: body.parsed?.fx_vat_amount ?? null,
            fxVatPct: body.parsed?.fx_vat_pct ?? null,
            fxMarkupApplied: !!body.parsed?.fx_markup_applied,
            flags: {
              cardMissing: isExpenseLike && !body.parsed?.card_id && !body.parsed?.debit_account_id,
              catMissing: isExpenseLike && !body.parsed?.category_id,
              amountMissing: body.parsed?.amount_aed == null,
              dateMissing: !body.parsed?.date,
              derivedFromDelta: !!body.parsed?.derived_from_delta,
              derivedFromFxTable: !!body.parsed?.derived_from_fx_table,
              baselineSource: body.parsed?.baseline_source,
              fxFlagged: !!body.parsed?.fx_flagged,
            },
            originalBody: chunks[i],
          });
          // Persist the simulated limit so the next SMS on the same card
          // chains off it correctly. Two cases:
          //   1. Tx SMS with an "Available Credit Limit" line — use that
          //      directly as the new baseline.
          //   2. Refund SMS that DOESN'T report a new limit — the bank
          //      auto-credits the refund amount to the available limit
          //      even though the SMS doesn't restate it. We virtually
          //      add the refund amount to the existing baseline so the
          //      next tx's delta computes against the right number.
          if (body.parsed?.card_id) {
            const cid = body.parsed.card_id;
            if (body.parsed?.bank_available_limit != null) {
              simulatedLimits[cid] = body.parsed.bank_available_limit;
            } else if (
              body.parsed?.is_refund
              && body.parsed?.amount_aed != null
              && simulatedLimits[cid] != null
            ) {
              simulatedLimits[cid] = simulatedLimits[cid] + Number(body.parsed.amount_aed);
            }
          }
        } else {
          rows.push({ idx: i + 1, skipped: true, skipReason: body?.error || `HTTP ${res.status}`, originalBody: chunks[i] });
        }
      } catch (e: any) {
        rows.push({ idx: i + 1, skipped: true, skipReason: e?.message || String(e), originalBody: chunks[i] });
      }
      setParseProgress({ done: i + 1, total: chunks.length });
    }
    setSmsRows(rows);
    setStep('sms-preview');
    setImporting(false);
  };

  // Validation: a row is committable if it has card, category, amount > 0,
  // date in valid YYYY-MM-DD shape, and description.
  const rowIsValid = (r: any) => {
    if (r.skipped) return false;
    if (!r.description?.trim()) return false;
    const amt = parseFloat(r.amountAed);
    if (!Number.isFinite(amt) || amt <= 0) return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(r.date)) return false;
    if (r.txType === 'transfer') {
      const from = r.fromAccountId || r.fromCardId;
      const to = r.toAccountId || r.toCardId || r.toLoanId;
      if (!from || !to) return false;
      if (from === to) return false;
      return true;
    }
    if (r.txType === 'income') {
      // Income lands on a single account (the "to" side). Category required.
      if (!r.toAccountId || !r.categoryId) return false;
      return true;
    }
    // expense — target is either a credit card (cardId) or, for a debit-card
    // purchase, the bank account it draws on (debitAccountId).
    if ((!r.cardId && !r.debitAccountId) || !r.categoryId) return false;
    return true;
  };
  const liveRows = smsRows.filter(r => !r.skipped);
  const invalidCount = liveRows.filter(r => !rowIsValid(r)).length;

  // Suspected-duplicate detection for the preview: a row matches an already-
  // logged tx, or another row in the same batch (same keys as the live dedup —
  // ref_no, or amount+card+merchant+same-day). Rows still import; they're just
  // badged so the user can untick accidents before committing.
  const rowAsTx = (r) => ({
    refNo: r.refNo, type: r.txType, amount: parseFloat(r.amountAed) || 0, currency: 'AED',
    originalAmount: r.originalAmount, originalCurrency: r.originalCcy,
    date: r.date, description: r.description, cardId: r.cardId,
    accountId: r.debitAccountId || r.toAccountId, fromAccountId: r.fromAccountId,
    toAccountId: r.toAccountId, toCardId: r.toCardId, toLoanId: r.toLoanId,
  });
  const existingDupeKeys = useMemo(() => {
    const s = new Set<string>();
    for (const t of (transactions || [])) { if (t.deletedAt) continue; for (const k of txDupeKeys(t)) s.add(k); }
    return s;
  }, [transactions]);
  const dupeRowReason = useMemo(() => {
    const reason: Record<number, string> = {};
    const batchCount = new Map<string, number>();
    for (const r of liveRows) for (const k of txDupeKeys(rowAsTx(r))) batchCount.set(k, (batchCount.get(k) || 0) + 1);
    for (const r of liveRows) {
      const keys = txDupeKeys(rowAsTx(r));
      if (keys.some(k => existingDupeKeys.has(k))) reason[r.idx] = 'already imported';
      else if (keys.some(k => (batchCount.get(k) || 0) >= 2)) reason[r.idx] = 'twice in this batch';
    }
    return reason;
  }, [smsRows, existingDupeKeys]);

  const commitSmsRows = async () => {
    if (invalidCount > 0) {
      toast(`${invalidCount} row${invalidCount === 1 ? '' : 's'} still need values for card, category, date, or amount.`);
      return;
    }
    setImporting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      for (const r of liveRows) {
        const aedAmt = parseFloat(r.amountAed);
        if (r.txType === 'transfer') {
          await supabase.from('transactions').insert({
            user_id: user.id,
            type: 'transfer',
            date: r.date,
            description: r.description.trim(),
            amount: aedAmt,
            currency_code: 'AED',
            amount_base: aedAmt,
            fx_rate: 1,
            from_account_id: r.fromAccountId || null,
            from_card_id: r.fromCardId || null,
            to_account_id: r.toAccountId || null,
            to_card_id: r.toCardId || null,
            to_loan_id: r.toLoanId || null,
            sms_raw_id: r.rawId || null,
            sms_needs_review: false,
            ref_no: r.refNo || null,
            notes: r.notes?.trim() || `Auto-imported from SMS${r.refNo ? ` · ref ${r.refNo}` : ' (reviewed in bulk import)'}`,
          });
        } else if (r.txType === 'income') {
          await supabase.from('transactions').insert({
            user_id: user.id,
            type: 'income',
            date: r.date,
            description: r.description.trim(),
            amount: aedAmt,
            currency_code: 'AED',
            amount_base: aedAmt,
            fx_rate: 1,
            account_id: r.toAccountId,
            category_id: r.categoryId,
            sms_raw_id: r.rawId || null,
            sms_needs_review: false,
            ref_no: r.refNo || null,
            notes: r.notes?.trim() || `Auto-imported from SMS${r.refNo ? ` · ref ${r.refNo}` : ' (reviewed in bulk import)'}`,
          });
        } else {
          await supabase.from('transactions').insert({
            user_id: user.id,
            type: 'expense',
            date: r.date,
            description: r.description.trim(),
            amount: aedAmt,
            currency_code: 'AED',
            amount_base: aedAmt,
            fx_rate: 1,
            // Debit-card purchase → expense from the bank account; otherwise
            // → the credit card. The DB CHECK requires exactly one.
            card_id: r.debitAccountId ? null : r.cardId,
            account_id: r.debitAccountId || null,
            category_id: r.categoryId,
            is_refund: !!r.isRefund,
            original_amount: r.originalAmount || null,
            original_currency: r.originalCcy || null,
            effective_fx_rate: r.originalAmount && aedAmt ? aedAmt / r.originalAmount : null,
            sms_raw_id: r.rawId || null,
            sms_needs_review: false,
            // Persist the FX-estimate marker so it's findable after commit.
            fx_estimated: !!(r.fxFlagged || r.fxMarkupApplied),
            ref_no: r.refNo || null,
            notes: r.notes?.trim() || `Auto-imported from SMS (reviewed in bulk import)`,
          }).select('id').single().then(({ data: mainTx }) => {
            // Settlement-markup cards: emit the linked FX VAT line (Fees & Charges).
            if (mainTx?.id && r.fxVatAmount && Number(r.fxVatAmount) > 0) {
              const feeCat = liveExpCats.find(c => /fee|charge|bank/i.test(c.name));
              return supabase.from('transactions').insert({
                user_id: user.id, type: 'expense', date: r.date,
                description: `VAT on FX fee · ${r.description.trim()}`,
                amount: Number(r.fxVatAmount), currency_code: 'AED', amount_base: Number(r.fxVatAmount), fx_rate: 1,
                card_id: r.cardId || null, account_id: r.debitAccountId || null,
                category_id: feeCat?.id || r.categoryId,
                parent_tx_id: mainTx.id, sms_raw_id: r.rawId || null, sms_needs_review: false, notes: 'Auto FX VAT',
              });
            }
          });
          // Save merchant→category for next time the parser sees the same merchant.
          const mkey = String(r.description).toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 40);
          if (mkey) {
            supabase.from('merchant_category_map').upsert({
              user_id: user.id,
              merchant_key: mkey,
              category_id: r.categoryId,
              updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id,merchant_key' }).then(() => {});
          }
          // FX-multiplier learning (foreign expense rows). Learn from a clean
          // delta-derived tx (fxLearnable, computed by the edge function) or
          // from a row the user corrected away from the rate estimate — never
          // from an un-edited flagged row (which just echoes the rate back).
          if (r.cardId && r.originalCcy && r.originalCcy !== 'AED' && r.fxMidRate && r.originalAmount) {
            let observed = null;
            if (r.fxLearnable != null) {
              observed = Number(r.fxLearnable);
            } else {
              const m = aedAmt / (Number(r.originalAmount) * Number(r.fxMidRate));
              const echoesRate = r.fxRateAed != null && Math.abs(aedAmt - Number(r.fxRateAed)) < 0.02;
              if (!echoesRate && m >= 0.97 && m <= 1.15) observed = m;
            }
            if (observed != null) {
              await supabase.rpc('update_card_fx_multiplier', { p_card_id: r.cardId, p_observed: observed });
            }
          }
        }
        // Update card's last_sms_available_limit so the chain stays correct
        // even though we committed via client. Refund SMSes without a
        // reported limit virtually bump the existing baseline by the
        // refund amount (bank auto-credit, see runSmsParse for the dry-run
        // analogue).
        if (r.txType !== 'expense' || !r.cardId) {
          // No bank-limit chain to maintain on transfers / CC payments, or
          // on debit-card purchases (those hit a bank account, not a card).
        } else if (r.bankLimit != null) {
          await supabase.from('cards').update({
            last_sms_available_limit: r.bankLimit,
            last_sms_at: new Date().toISOString(),
          }).eq('id', r.cardId);
        } else if (r.isRefund && r.amountAed && r.cardId) {
          const { data: card } = await supabase
            .from('cards')
            .select('last_sms_available_limit')
            .eq('id', r.cardId)
            .single();
          if (card?.last_sms_available_limit != null) {
            await supabase.from('cards').update({
              last_sms_available_limit: Number(card.last_sms_available_limit) + parseFloat(r.amountAed),
              last_sms_at: new Date().toISOString(),
            }).eq('id', r.cardId);
          }
        }
      }

      // Post-commit reconciliation pass per touched card. The edge function
      // does this on its non-dry-run path, but the preview-edit-confirm
      // flow commits client-side and bypasses that. Compute Floosi's view
      // of available per card and compare to the last bank-reported limit
      // (which is now in last_sms_available_limit because of the loop
      // above). Drift > 1 AED → insert a reconciliation alert so the bell
      // surfaces it.
      const today = todayStr();
      const cardsTouched = [...new Set(liveRows.map(r => r.cardId).filter(Boolean))];
      for (const cardId of cardsTouched) {
        const { data: cardFull } = await supabase
          .from('cards')
          .select('credit_limit, opening_balance, last_sms_available_limit')
          .eq('id', cardId)
          .single();
        if (!cardFull || cardFull.last_sms_available_limit == null) continue;
        const { data: txs } = await supabase
          .from('transactions')
          .select('amount, is_refund, excludes_from_balances, type, card_id, from_card_id, to_card_id')
          .eq('user_id', user.id)
          .is('deleted_at', null)
          .lte('date', today);
        let txContrib = 0;
        for (const t of (txs ?? []) as any[]) {
          if (t.excludes_from_balances) continue;
          if (t.type === 'expense' && t.card_id === cardId) {
            txContrib += t.is_refund ? -Number(t.amount) : Number(t.amount);
          } else if (t.type === 'transfer' && t.to_card_id === cardId) {
            txContrib -= Number(t.amount);
          } else if (t.type === 'transfer' && t.from_card_id === cardId) {
            txContrib += Number(t.amount);
          }
        }
        const { data: insts } = await supabase
          .from('card_installments')
          .select('term_months, payment_day, start_date, monthly_payment')
          .eq('user_id', user.id)
          .eq('card_id', cardId)
          .is('deleted_at', null)
          .in('kind', ['easy_payment_plan', 'balance_installment']);
        let instLoad = 0;
        for (const ci of (insts ?? []) as any[]) {
          if (!ci.monthly_payment || !ci.term_months) continue;
          const { data: cycles } = await supabase.rpc('installment_cycles_through_today', {
            p_start_date: ci.start_date,
            p_payment_day: ci.payment_day,
          });
          const future = Math.max(0, Number(ci.term_months) - Number(cycles ?? 0));
          instLoad += future * Number(ci.monthly_payment);
        }
        const floosiOwed = Number(cardFull.opening_balance) + txContrib + instLoad;
        const floosiAvail = Number(cardFull.credit_limit) - floosiOwed;
        const bankLimit = Number(cardFull.last_sms_available_limit);
        const drift = floosiAvail - bankLimit;
        if (Math.abs(drift) > 1.0) {
          // Use the LAST row's rawId for this card as the alert's anchor.
          const lastRowForCard = [...liveRows].reverse().find(r => r.cardId === cardId);
          await supabase.from('sms_reconciliation_alerts').insert({
            user_id: user.id,
            card_id: cardId,
            sms_raw_id: lastRowForCard?.rawId || null,
            bank_limit: bankLimit,
            floosi_limit: floosiAvail,
            delta: drift,
          });
        }
      }

      if (onReloadTxs) await onReloadTxs();
      onClose();
    } catch (e: any) {
      toast('Commit failed: ' + (e?.message || String(e)));
    } finally { setImporting(false); }
  };

  const handleConfirm = async () => {
    setImporting(true);
    try {
      await onImport(preview);
      onClose();
    } catch (e) { toast('Import failed.'); } finally { setImporting(false); }
  };

  return (
    <Modal title="Import Transactions" onClose={onClose} wide={step === 'input'} xWide={step === 'sms-preview'}>
      {step === 'input' ? (
        <div className="space-y-3">
          <div className="flex gap-2">
            <button onClick={() => setMode('sms')} className={`flex-1 py-2 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 ${mode === 'sms' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400'}`}>
              <MessageSquare className="w-4 h-4" /> SMS Bulk Paste
            </button>
            <button onClick={() => setMode('csv')} className={`flex-1 py-2 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 ${mode === 'csv' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400'}`}>
              <Upload className="w-4 h-4" /> CSV Statement
            </button>
          </div>

          <div className="text-xs text-slate-400 bg-slate-800/50 rounded-lg p-3">
            {mode === 'sms' ? (
              <>
                <strong>SMS Mode:</strong> Paste bank SMS messages separated by blank lines, <em>in chronological order</em>. Each is sent to the sms-ingest edge function — same smart parser the live iOS Shortcut uses. Foreign-currency txs (MAD/EUR/USD/…) without an explicit AED amount have their AED derived from the bank's available-limit delta between consecutive SMSes on the same card.
              </>
            ) : (
              <>
                <strong>CSV Mode:</strong> Paste CSV from a bank statement. Looks for: Date, Description/Narration, Amount or Debit/Credit, Currency.
              </>
            )}
          </div>

          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={mode === 'sms'
              ? `Paste SMS messages here, e.g.:\n\nDear Customer, AED 245.50 has been debited from your account at Carrefour on 12/03/24...\n\nDear Customer, AED 12,500 credited to your account as Salary on 25/03/24...`
              : `Paste CSV content here, e.g.:\nDate,Description,Debit,Credit,Currency\n2024-03-12,Carrefour,245.50,,AED\n2024-03-25,Salary,,12500,AED`
            }
            className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm font-mono min-h-[200px] resize-none"
          />

          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
            <button onClick={handleParse} disabled={!text.trim() || importing} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-sm font-medium">
              {mode === 'sms' ? 'Run import' : 'Parse'}
            </button>
          </div>
        </div>
      ) : step === 'sms-parsing' ? (
        <div className="space-y-3 py-8 text-center">
          <Clock className="w-8 h-8 mx-auto text-cyan-400 animate-pulse" />
          <div className="text-sm text-slate-300">Parsing {parseProgress.done} / {parseProgress.total}…</div>
          <div className="h-1.5 max-w-sm mx-auto bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-cyan-500 transition-all" style={{ width: `${parseProgress.total ? (parseProgress.done / parseProgress.total) * 100 : 0}%` }} />
          </div>
        </div>
      ) : step === 'sms-preview' ? (
        <div className="space-y-3">
          <div className={`flex items-center gap-2 rounded-lg p-3 ${invalidCount > 0 ? 'bg-amber-500/10 border border-amber-500/40' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
            {invalidCount > 0 ? <AlertCircle className="w-5 h-5 text-amber-400" /> : <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
            <span className="text-sm">
              {invalidCount > 0
                ? `${invalidCount} row${invalidCount === 1 ? '' : 's'} still need values. Fill highlighted cells before importing.`
                : `${liveRows.length} transaction${liveRows.length === 1 ? '' : 's'} ready to import. Review and adjust as needed.`}
            </span>
          </div>
          <div className="overflow-x-auto bg-slate-950/40 border border-slate-800 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-900/80 text-slate-400 text-[11px] uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 w-10">#</th>
                  <th className="text-left px-3 py-2 w-[100px]">Type</th>
                  <th className="text-left px-3 py-2 min-w-[180px]">Description</th>
                  <th className="text-left px-3 py-2 w-[140px]">Date</th>
                  <th className="text-left px-3 py-2 min-w-[300px]" colSpan={2}>Card / From → To · Category</th>
                  <th className="text-right px-3 py-2 w-[120px] min-w-[120px]">AED</th>
                  <th className="text-left px-3 py-2 w-[120px]">Foreign</th>
                  <th className="text-center px-3 py-2 w-[70px]">Refund</th>
                  <th className="text-left px-3 py-2 min-w-[180px]">Notes</th>
                  <th className="text-center px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/80">
                {smsRows.map((r, idx) => {
                  if (r.skipped) {
                    return (
                      <tr key={idx} className="opacity-60">
                        <td className="px-3 py-2 text-slate-500 font-mono">#{r.idx}</td>
                        <td colSpan={10} className="px-3 py-2 text-slate-400 italic">
                          skipped — {r.skipReason || 'non-transactional'}
                          <span className="block text-[10px] text-slate-600 truncate mt-0.5" title={r.originalBody}>
                            {r.originalBody?.slice(0, 100)}{r.originalBody && r.originalBody.length > 100 ? '…' : ''}
                          </span>
                        </td>
                      </tr>
                    );
                  }
                  const update = (patch: any) => {
                    setSmsRows(prev => prev.map((x, i) => i === idx ? { ...x, ...patch } : x));
                  };
                  const valid = rowIsValid(r);
                  const isTransfer = r.txType === 'transfer';
                  const isIncome = r.txType === 'income';
                  const cardOk = !!r.cardId;
                  const catOk = !!r.categoryId;
                  const toAcctOk = !!r.toAccountId;
                  const fromOk = !!(r.fromAccountId || r.fromCardId);
                  const toOk = !!(r.toAccountId || r.toCardId || r.toLoanId);
                  const sameFromTo = (r.fromAccountId && r.fromAccountId === r.toAccountId) || (r.fromCardId && r.fromCardId === r.toCardId);
                  const amtOk = Number.isFinite(parseFloat(r.amountAed)) && parseFloat(r.amountAed) > 0;
                  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(r.date);
                  const cats = isIncome
                    ? (categories || []).filter((c: any) => !c.deletedAt && c.kind === 'income')
                    : liveExpCats;
                  const liveAccts = accounts.filter(a => !a.deletedAt);
                  const cellInput = (ok: boolean) => `w-full bg-slate-950 border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500 ${ok ? 'border-slate-700' : 'border-amber-500/60'}`;
                  // Sources/destinations include accounts and cards.
                  const liveLoans = (loans || []).filter(l => !l.deletedAt);
                  const renderAcctCardOptions = () => [
                    ...(accounts.filter(a => !a.deletedAt).map(a => (
                      <option key={`a-${a.id}`} value={`a:${a.id}`}>{a.name} ({a.currency})</option>
                    ))),
                    ...(liveCards.map(c => (
                      <option key={`c-${c.id}`} value={`c:${c.id}`}>{c.name} (card)</option>
                    ))),
                  ];
                  // Destinations also include loans (pay one down) and External
                  // (money to a party you don't track → it's an expense).
                  const renderToOptions = () => [
                    ...renderAcctCardOptions(),
                    ...(liveLoans.length ? [
                      <optgroup key="loans" label="Loans — pay down">
                        {liveLoans.map(l => (
                          <option key={`l-${l.id}`} value={`l:${l.id}`}>{l.name}{l.currency ? ` (${l.currency})` : ''}</option>
                        ))}
                      </optgroup>,
                    ] : []),
                    <option key="ext" value="ext">External payee → expense…</option>,
                  ];
                  const currentFromValue = r.fromAccountId ? `a:${r.fromAccountId}` : r.fromCardId ? `c:${r.fromCardId}` : '';
                  const currentToValue = r.toAccountId ? `a:${r.toAccountId}` : r.toCardId ? `c:${r.toCardId}` : r.toLoanId ? `l:${r.toLoanId}` : '';
                  const pickFrom = (v: string) => {
                    if (!v) return update({ fromAccountId: '', fromCardId: '' });
                    const [k, id] = v.split(':');
                    if (k === 'a') update({ fromAccountId: id, fromCardId: '' });
                    else update({ fromAccountId: '', fromCardId: id });
                  };
                  const pickTo = (v: string) => {
                    if (!v) return update({ toAccountId: '', toCardId: '', toLoanId: '' });
                    if (v === 'ext') {
                      // External → convert this row to an expense funded by the
                      // source account/card; user picks the category.
                      return update({
                        txType: 'expense',
                        debitAccountId: r.fromAccountId || '',
                        cardId: r.fromCardId || '',
                        categoryId: r.categoryId || (liveExpCats[0]?.id || ''),
                        fromAccountId: '', fromCardId: '', toAccountId: '', toCardId: '', toLoanId: '',
                      });
                    }
                    const [k, id] = v.split(':');
                    if (k === 'a') update({ toAccountId: id, toCardId: '', toLoanId: '' });
                    else if (k === 'l') update({ toAccountId: '', toCardId: '', toLoanId: id });
                    else update({ toAccountId: '', toCardId: id, toLoanId: '' });
                  };
                  return (
                    <tr key={idx} className={`${valid ? '' : 'bg-amber-500/5'} hover:bg-slate-800/30`}>
                      <td className="px-3 py-2 text-slate-500 font-mono align-top">#{r.idx}</td>
                      <td className="px-2 py-2 align-top">
                        <select
                          value={r.txType}
                          onChange={(e) => {
                            const newType = e.target.value;
                            const patch: any = { txType: newType };
                            if (newType === 'income') {
                              patch.cardId = ''; patch.categoryId = '';
                              patch.fromAccountId = ''; patch.fromCardId = '';
                            } else if (newType === 'transfer') {
                              patch.cardId = ''; patch.categoryId = '';
                            } else {
                              patch.fromAccountId = ''; patch.fromCardId = '';
                              patch.toAccountId = ''; patch.toCardId = '';
                            }
                            update(patch);
                          }}
                          className={`text-[10px] px-1 py-1 rounded font-medium bg-slate-950 border border-slate-700 focus:outline-none ${isTransfer ? 'text-cyan-300' : isIncome ? 'text-emerald-300' : 'text-rose-300'}`}
                        >
                          <option value="expense">Expense</option>
                          <option value="transfer">Transfer</option>
                          <option value="income">Income</option>
                        </select>
                        {r.refNo && (
                          <div className="text-[9px] text-slate-500 mt-1 font-mono truncate" title={r.refNo}>{r.refNo}</div>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <input
                          value={r.description || ''}
                          onChange={(e) => update({ description: e.target.value })}
                          className={cellInput(!!r.description?.trim())}
                        />
                        {(r.flags?.derivedFromDelta || r.flags?.derivedFromFxTable || r.flags?.dateMissing || r.fxFlagged || (r.fxVatAmount > 0) || r.flags?.cardMissing || r.flags?.catMissing) && (
                          <div className="text-[10px] text-slate-500 mt-1 flex gap-1.5 flex-wrap">
                            {dupeRowReason[r.idx] && (
                              <span className="px-1 py-0.5 rounded bg-rose-500/15 text-rose-300 font-medium"
                                title={dupeRowReason[r.idx] === 'already imported'
                                  ? 'Same amount, merchant, card and day as a transaction you already logged (or same bank reference). Untick it if this is an accidental re-import.'
                                  : 'This exact transaction appears more than once in this paste. Untick the extra copy if it was pasted twice.'}>
                                ⚠ possible duplicate · {dupeRowReason[r.idx]}
                              </span>
                            )}
                            {r.flags?.cardMissing && (
                              <span className="px-1 py-0.5 rounded bg-rose-500/15 text-rose-300 font-medium"
                                title={`No card matched the last-4 in this SMS${r.cardLast4 ? ` (${r.cardLast4})` : ''}. Check the card's "Last 4 Digits" in the Cards tab, or pick the card manually.`}>
                                ⚠ no card matched{r.cardLast4 ? ` ·${r.cardLast4}` : ''}
                              </span>
                            )}
                            {r.flags?.catMissing && !r.categoryId && (
                              <span className="px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 font-medium"
                                title="Couldn't confidently guess a category for this merchant — pick one (Floosi will remember it for next time).">
                                pick a category
                              </span>
                            )}
                            {r.fxVatAmount > 0 && (
                              <span className="px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-300"
                                title="This card charges VAT on the FX fee as a separate line (never on the SMS). Floosi auto-creates it in Fees & Charges, linked to this tx, when you import.">
                                + VAT {Number(r.fxVatAmount).toFixed(2)} on import
                              </span>
                            )}
                            {r.flags?.derivedFromDelta && !r.fxFlagged && <span className="px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-400">FX·delta</span>}
                            {r.flags?.derivedFromFxTable && !r.fxFlagged && <span className="px-1 py-0.5 rounded bg-violet-500/10 text-violet-400">FX·rate</span>}
                            {r.fxFlagged && (() => {
                              const usingRate = r.fxRateAed != null && Math.abs((parseFloat(r.amountAed) || 0) - Number(r.fxRateAed)) < 0.02;
                              return (
                                <span className="px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 font-medium"
                                  title={usingRate
                                    ? "The bank's available-limit drop disagreed with the FX rate — using the rate estimate. Verify the amount."
                                    : "First foreign tx on this card — using the bank's figure; confirm to calibrate this card's FX rate."}>
                                  {usingRate ? `⚠ FX check${r.fxDivergence != null ? ` · ${r.fxDivergence}% off` : ''}` : '⚠ FX calibrating'}
                                </span>
                              );
                            })()}
                            {r.flags?.dateMissing && <span className="px-1 py-0.5 rounded bg-amber-500/10 text-amber-400">no date in SMS</span>}
                          </div>
                        )}
                        {r.fxFlagged && r.fxLimitAed != null && r.fxRateAed != null && (() => {
                          const usingRate = Math.abs((parseFloat(r.amountAed) || 0) - Number(r.fxRateAed)) < 0.02;
                          return (
                            <div className="text-[10px] text-slate-500 mt-1">
                              {usingRate
                                ? <>limit-delta said <span className="text-slate-400 font-mono">{Number(r.fxLimitAed).toFixed(2)}</span>, rate says <span className="text-emerald-400 font-mono">{Number(r.fxRateAed).toFixed(2)}</span> — using rate.</>
                                : <>using the bank's amount <span className="text-emerald-400 font-mono">{Number(r.fxLimitAed).toFixed(2)}</span> (rate est. {Number(r.fxRateAed).toFixed(2)}) — calibrating this card's FX rate.</>}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <input
                          type="date"
                          value={r.date || ''}
                          onChange={(e) => update({ date: e.target.value })}
                          className={cellInput(dateOk)}
                        />
                      </td>
                      {isTransfer ? (
                        <>
                          <td className="px-2 py-2 align-top">
                            <select
                              value={currentFromValue}
                              onChange={(e) => pickFrom(e.target.value)}
                              className={cellInput(fromOk && !sameFromTo)}
                            >
                              <option value="">From…</option>
                              {renderAcctCardOptions()}
                            </select>
                          </td>
                          <td className="px-2 py-2 align-top">
                            <select
                              value={currentToValue}
                              onChange={(e) => pickTo(e.target.value)}
                              className={cellInput(toOk && !sameFromTo)}
                            >
                              <option value="">To…</option>
                              {renderToOptions()}
                            </select>
                          </td>
                        </>
                      ) : isIncome ? (
                        <>
                          <td className="px-2 py-2 align-top">
                            <select
                              value={r.toAccountId || ''}
                              onChange={(e) => update({ toAccountId: e.target.value })}
                              className={cellInput(toAcctOk)}
                            >
                              <option value="">To account…</option>
                              {liveAccts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-2 align-top">
                            <select
                              value={r.categoryId || ''}
                              onChange={(e) => update({ categoryId: e.target.value })}
                              className={cellInput(catOk)}
                            >
                              <option value="">Category…</option>
                              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 align-top">
                            <select
                              value={r.cardId ? `c:${r.cardId}` : r.debitAccountId ? `a:${r.debitAccountId}` : ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (!v) return update({ cardId: '', debitAccountId: '' });
                                const [k, id] = v.split(':');
                                if (k === 'c') update({ cardId: id, debitAccountId: '' });
                                else update({ cardId: '', debitAccountId: id });
                              }}
                              className={cellInput(cardOk || !!r.debitAccountId)}
                            >
                              <option value="">Card / account…</option>
                              <optgroup label="Credit cards">
                                {liveCards.map(c => <option key={c.id} value={`c:${c.id}`}>{c.name}{(c as any).lastFour ? ` ·${(c as any).lastFour}` : ''}</option>)}
                              </optgroup>
                              <optgroup label="Bank accounts (debit)">
                                {liveAccts.map((a: any) => <option key={a.id} value={`a:${a.id}`}>{a.name}</option>)}
                              </optgroup>
                            </select>
                          </td>
                          <td className="px-2 py-2 align-top">
                            <select
                              value={r.categoryId || ''}
                              onChange={(e) => update({ categoryId: e.target.value })}
                              className={cellInput(catOk)}
                            >
                              <option value="">Category…</option>
                              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </td>
                        </>
                      )}
                      <td className="px-2 py-2 align-top">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={r.amountAed ?? ''}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^\d.\-]/g, '');
                            update({ amountAed: v });
                          }}
                          className={`${cellInput(amtOk)} text-right font-mono text-sm`}
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-3 py-2 align-top text-slate-400 text-[11px] whitespace-nowrap">
                        {r.originalAmount && r.originalCcy ? `${r.originalCcy} ${r.originalAmount}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-center align-top">
                        <input
                          type="checkbox"
                          checked={!!r.isRefund}
                          onChange={(e) => update({ isRefund: e.target.checked })}
                          className="w-4 h-4 accent-emerald-500"
                          title="Refund: counts as a negative expense (reverses against the category's budget)"
                        />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <input
                          value={r.notes || ''}
                          onChange={(e) => update({ notes: e.target.value })}
                          placeholder="optional…"
                          className={cellInput(true)}
                        />
                      </td>
                      <td className="px-3 py-2 text-center align-top">
                        <button
                          onClick={() => setSmsRows(prev => prev.map((x, i) => i === idx ? { ...x, skipped: true, skipReason: 'skipped by user' } : x))}
                          className="p-1 hover:bg-rose-900/40 rounded"
                          title="Drop this row"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setStep('input'); setSmsRows([]); }}
              disabled={importing}
              className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm"
            >
              Back
            </button>
            <button
              onClick={commitSmsRows}
              disabled={importing || invalidCount > 0 || liveRows.length === 0}
              className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-sm font-medium"
            >
              {importing ? 'Importing…' : `Import ${liveRows.length} transaction${liveRows.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {preview.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
              <p className="text-sm text-slate-400">Couldn't parse any transactions. Try again with different formatting.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-sm">Found <strong>{preview.length}</strong> transactions. Review and confirm.</span>
              </div>
              <div className="max-h-[300px] overflow-y-auto bg-slate-800/50 rounded-lg divide-y divide-slate-700">
                {preview.map((tx, i) => (
                  <div key={i} className="p-3 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{tx.description}</div>
                      <div className="text-xs text-slate-400 truncate">
                        {strToDate(tx.date).toLocaleDateString()} · {tx.category}
                      </div>
                    </div>
                    <div className={`text-sm font-semibold ${tx.type === 'income' ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {tx.type === 'income' ? '+' : '−'}{fmtMoney(tx.amount, tx.currency)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex gap-2">
            <button onClick={() => setStep('input')} disabled={importing} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Back</button>
            {preview.length > 0 && (
              <button onClick={handleConfirm} disabled={importing} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700">
                {importing ? 'Importing…' : `Import ${preview.length}`}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ============ SHARED UI ============
// Notification panel — bell-icon dropdown listing pending alerts.
// Each SMS reconciliation alert is expandable: shows the drift, lists
// foreign-currency txs since the last markup pass that haven't been
// adjusted yet, computes the implied markup %, and offers a one-click
// apply that updates each tx's AED + amount_base and marks the alert
// resolved. Alternative paths: dismiss without changes, or "investigate
// manually" which just resolves the alert (user fixes via the txs).
// Shared notification item builder — one definition so the header badge and
// the NotificationPanel agree on what is active vs already in Recent.
function buildTidyItems(tidy, baseCurrency) {
  const uncatTotal = (tidy.uncatRules || 0) + (tidy.uncatTxs || 0);
  return [
    uncatTotal > 0 && {
      key: 'uncat', tab: tidy.uncatRules > 0 ? 'recurring' : 'transactions', color: 'amber', highlightIds: tidy.uncatRuleIds || [],
      filter: tidy.uncatRules > 0 ? null : { cat: '__uncat__' },
      title: 'Uncategorised items',
      detail: [
        tidy.uncatRules > 0 && `${tidy.uncatRules} recurring rule${tidy.uncatRules === 1 ? '' : 's'}`,
        tidy.uncatTxs > 0 && `${tidy.uncatTxs} transaction${tidy.uncatTxs === 1 ? '' : 's'}`,
      ].filter(Boolean).join(' + ') + ' have no category — the forecast can\'t place them.',
    },
    (tidy.review || 0) > 0 && {
      key: 'review', tab: 'transactions', color: 'amber', filter: { source: 'sms_review' },
      title: `${tidy.review} SMS transaction${tidy.review === 1 ? '' : 's'} need review`,
      detail: 'The parser couldn\'t confidently match the merchant or amount — open and confirm.',
    },
    (tidy.dupes || 0) > 0 && {
      key: 'dupes', tab: 'transactions', color: 'rose', expandable: true,
      title: `${tidy.dupes} possible duplicate${tidy.dupes === 1 ? '' : 's'}`,
      detail: 'Two or more txs share the same amount, merchant, card and day. Tap to keep both or review them.',
    },
    ...(tidy.budgetAlerts || []).map((b: any) => ({
      key: 'budget:' + b.category, tab: 'budgets', color: b.band === 'warn' ? 'amber' : 'rose',
      title: b.band === 'over' ? `${b.category} overspent (${b.pct.toFixed(0)}%)`
        : b.band === 'full' ? `${b.category} budget fully spent (${b.pct.toFixed(0)}%)`
        : `${b.category} approaching limit (${b.pct.toFixed(0)}%)`,
      detail: b.band === 'over' ? `You're over your ${b.category} budget this month.`
        : b.band === 'full' ? `${b.category} is fully consumed for this month.`
        : `${b.category} is at ${b.pct.toFixed(0)}% — close to fully spent.`,
    })),
    tidy.liquidity && (() => {
      const L = tidy.liquidity;
      const cashNeg = L.cash && L.cash.amount < 0;
      const both = L.cash && L.netWorth;
      const which = both ? 'cash & net worth' : L.cash ? (cashNeg ? 'cash' : 'cash (low)') : 'net worth';
      const key = `liquidity:${L.cash ? L.cash.label + (cashNeg ? ':neg' : ':low') : '-'}:${L.netWorth ? L.netWorth.label : '-'}`;
      const parts: string[] = [];
      if (L.cash) parts.push(`cash ~${fmtMoney(L.cash.amount, baseCurrency)}${cashNeg ? '' : ` (below your ${fmtMoney(L.buffer, baseCurrency)} buffer)`} by end of ${L.cash.label}`);
      if (L.netWorth) parts.push(`net worth ~${fmtMoney(L.netWorth.amount, baseCurrency)} by end of ${L.netWorth.label}`);
      return {
        key, tab: 'dashboard', color: 'rose',
        title: `⚠️ Forecast: your ${which} ${both || cashNeg || L.netWorth ? 'goes negative' : 'dips low'}`,
        detail: `Forecast shows ${parts.join('; and ')}. Move money in or cut spend before then.`,
      };
    })(),
    ...(tidy.savingsNeg || []).map((m: any) => ({
      key: `savings:${m.key}:${m.period}`, tab: 'budgets', color: 'rose',
      title: `⚠️ ${m.label}: you'd spend more than you make`,
      detail: `Forecast: income ~${fmtMoney(m.income, baseCurrency)} vs spending ~${fmtMoney(m.expense, baseCurrency)} — about ${fmtMoney(Math.abs(m.net), baseCurrency)} would come out of savings${m.isEstimated ? ' (spending estimated from your last budget)' : ''}. Trim ${m.label}'s budget or plan for the dip.`,
    })),
    ...(tidy.recReminders || []).map((rr: any) => ({
      key: `recremind:${rr.ruleId}:${rr.occKey}:${rr.bucket}`, tab: 'recurring', color: 'cyan', expandable: true, rr,
      title: `${rr.desc} — ${rr.days === 0 ? 'due today' : rr.days === 1 ? 'due tomorrow' : `due in ${rr.days} days`}`,
      detail: `${rr.type === 'income' ? 'Incoming' : 'Payment'} ~${fmtMoney(rr.amount, rr.currency, false)} on ${rr.occKey.slice(5)}. Tap for options.`,
    })),
    ...(tidy.loanDue || []).map((l: any) => {
      const when = l.days === 0 ? 'today' : l.days === 1 ? 'tomorrow' : `in ${l.days}d`;
      const m = l.days <= 0 ? 'd0' : l.days <= 1 ? 'd1' : l.days <= 3 ? 'd3' : 'd7';
      return {
        key: `loan:${l.name}:${m}:${l.shortfalls.length ? 'short' : 'ok'}`, tab: 'loans',
        color: l.shortfalls.length ? 'rose' : 'amber',
        title: `${l.name} payment ${when}`,
        detail: l.shortfalls.length ? l.shortfalls.join('; ') : 'Funds look OK across the linked account(s).',
      };
    }),
    (tidy.stmtUpload || []).length > 0 && {
      key: 'stmtupload', tab: 'cards', color: 'amber',
      title: `${tidy.stmtUpload.length} statement${tidy.stmtUpload.length === 1 ? '' : 's'} ${Math.max(...tidy.stmtUpload.map((s: any) => s.days || 0)) >= 7 ? 'still to upload' : 'to upload'}`,
      detail: `${tidy.stmtUpload.slice(0, 4).map((s: any) => `${s.name} (${s.days}d ago)`).join(', ')} — upload or enter the amount due to reconcile.`,
    },
    tidy.reimbAging && {
      key: 'reimbaging:' + (tidy.reimbAging.month || ''), tab: 'transactions', color: 'amber', filter: { reimb: 'outstanding' },
      title: `${tidy.reimbAging.count} reimbursable${tidy.reimbAging.count === 1 ? '' : 's'} not yet reimbursed`,
      detail: `${fmtMoney(tidy.reimbAging.total, baseCurrency)} owed to you${tidy.reimbAging.names?.length ? ` — ${tidy.reimbAging.names.join(', ')}${tidy.reimbAging.count > tidy.reimbAging.names.length ? '…' : ''}` : ''}. Tag the incoming reimbursement and tick these off when it lands.`,
    },
    ...(tidy.goalAlerts || []).map((g: any) => ({
      key: 'goal:' + g.name, tab: 'savings', color: 'emerald',
      title: g.band === 'done' ? `${g.name} reached 🎉` : `${g.name} — ${g.pct.toFixed(0)}% funded`,
      detail: g.band === 'done' ? `Fully funded. Nice.` : `You're ${g.pct.toFixed(0)}% of the way to this goal.`,
    })),
    // FX-estimated foreign txs are an expected steady state, not an anomaly:
    // they're already flagged inline with the ≈ est pill and the reconcile
    // engine clears them automatically when the statement posts. No notification.
  ].filter(Boolean);
}
const isMovableTidy = (it: any) => it.key?.startsWith('budget:') || it.key?.startsWith('goal:') || it.key?.startsWith('loan:') || it.key?.startsWith('reimbaging:') || it.key?.startsWith('liquidity:') || it.key?.startsWith('savings:') || it.key?.startsWith('recremind:');
const stmtKey = (s: any) => 'stmt:' + s.cardId + ':' + (s.days < 0 ? 'overdue' : s.days <= 0 ? 'd0' : s.days <= 1 ? 'd1' : s.days <= 2 ? 'd2' : s.days <= 3 ? 'd3' : 'd7');

function NotificationPanel({ alerts = [], pending = [], cards = [], transactions = [], tidy = { uncatRules: 0, uncatTxs: 0, fxEst: 0, review: 0 }, statements = [], onNavigate, onResolve, onApplyMarkup, onCompletePending, onAddTransaction, onMarkDuplicatesIntentional, onMarkRecReminderHandled, onSeenUpdate, onClose, baseCurrency }) {
  const [expandedId, setExpandedId] = useState(null);
  const [expandedTidy, setExpandedTidy] = useState(null);
  // Per-item "seen" snapshot for informational items (budgets / goals): keys
  // present at the LAST open. They show in Review & tidy the first open they
  // appear, then move to Recent on subsequent opens — popping back only when a
  // new band is crossed (the key changes). Action items never move on view.
  const [tidySeen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('floosi_tidy_seen') || '[]')); } catch { return new Set(); }
  });
  const cardName = (id) => cards.find(c => c.id === id)?.name || 'Card';
  const cardCcy = (id) => cards.find(c => c.id === id)?.currency || baseCurrency;
  const active = alerts.filter(a => !a.resolvedAt);
  const resolved = alerts
    .filter(a => a.resolvedAt)
    .sort((a, b) => (b.resolvedAt || '').localeCompare(a.resolvedAt || ''))
    .slice(0, 20);
  const uncatTotal = (tidy.uncatRules || 0) + (tidy.uncatTxs || 0);
  const tidyItems = buildTidyItems(tidy, baseCurrency);
  // Split off already-seen read-only items (budgets / goals / statement-due) →
  // Recent. Statement reminders are keyed by card + due-milestone so each
  // escalation (7/3/2/1/today/overdue) re-surfaces to active.
  const activeTidy = tidyItems.filter((it: any) => !isMovableTidy(it) || !tidySeen.has(it.key));
  const readTidy = tidyItems.filter((it: any) => isMovableTidy(it) && tidySeen.has(it.key));
  const activeStatements = statements.filter((s: any) => !tidySeen.has(stmtKey(s)));
  const readStatements = statements.filter((s: any) => tidySeen.has(stmtKey(s)));
  // On open, snapshot what's showing so it counts as "seen" next time.
  useEffect(() => {
    try {
      const keys = [...tidyItems.filter(isMovableTidy).map((it: any) => it.key), ...statements.map(stmtKey)];
      localStorage.setItem('floosi_tidy_seen', JSON.stringify(keys));
      onSeenUpdate?.(keys);
    } catch { /* ignore */ }
  }, []);
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" onClick={onClose}>
      <div
        className="absolute top-16 right-4 w-[420px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-5rem)] overflow-y-auto bg-slate-900 border border-slate-800 rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-semibold">Notifications</h3>
            {active.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300">{active.length}</span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-800 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        {active.length === 0 && resolved.length === 0 && pending.length === 0 && tidyItems.length === 0 && statements.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400/60 mb-2" />
            <p className="text-sm text-slate-400">You're all caught up.</p>
          </div>
        ) : (
          <>
            {pending.length > 0 && (
              <div className="divide-y divide-slate-800">
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Needs your input</div>
                {pending.map((p) => {
                  const pr = (p.parseResult || {}) as any;
                  return (
                    <button
                      key={p.id}
                      onClick={() => onCompletePending?.(p)}
                      className="w-full text-left p-3 flex items-start gap-2 hover:bg-slate-800/40"
                    >
                      <Clock className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-amber-200">
                          {pr.kind === 'mashreq_cc_payment' ? 'Credit card payment' : 'Bank transfer'}
                          {' — '}
                          {fmtMoney(Number(pr.amount) || 0, pr.ccy || baseCurrency, false)}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5 truncate" title={p.body}>
                          {pr.refNo ? `ref ${pr.refNo} · ` : ''}
                          received {p.receivedAt ? new Date(p.receivedAt).toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </button>
                  );
                })}
              </div>
            )}
            {activeStatements.length > 0 && (
              <div className="divide-y divide-slate-800">
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Statements due</div>
                {activeStatements.map((s: any) => (
                  <button key={s.cardId} onClick={() => onNavigate?.('cards')} className="w-full text-left p-3 flex items-start gap-2 hover:bg-slate-800/40">
                    <FileText className={`w-4 h-4 flex-shrink-0 mt-0.5 ${s.overdue ? 'text-rose-400' : 'text-amber-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${s.overdue ? 'text-rose-200' : 'text-amber-200'}`}>
                        {s.cardName} — {s.paid > 0.01
                          ? `${Math.round((s.remaining / s.due) * 100)}% (${fmtMoney(s.remaining, s.currency, false)}) remaining`
                          : `${fmtMoney(s.remaining, s.currency, false)} due`}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {s.overdue ? `Overdue by ${Math.abs(s.days)} day${Math.abs(s.days) === 1 ? '' : 's'}` : s.days === 0 ? 'Due today' : s.days === 1 ? 'Due tomorrow' : `Due in ${s.days} days`}
                        {s.paid > 0.01 ? ` · ${fmtMoney(s.paid, s.currency, false)} paid` : ''}
                        {s.minUnmet ? ' · minimum not yet met' : ''}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
            {active.length > 0 && (
              <div className="divide-y divide-slate-800">
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Bank sync</div>
                {active.map((a) => {
                  const isOpen = expandedId === a.id;
                  const drift = Number(a.delta) || 0;
                  const ccy = cardCcy(a.cardId);
                  const card = cards.find(c => c.id === a.cardId);
                  const owed = card ? (Number(card.creditLimit) || 0) - Number(a.floosiLimit) : 0;
                  const pct = owed > 0 ? (Math.abs(drift) / owed) * 100 : 0;
                  const floosiShowsLess = drift > 0; // Floosi available higher → less spend logged
                  const candidates = transactions.filter(t =>
                    !t.deletedAt && t.cardId === a.cardId && t.smsRawId
                    && t.originalCurrency && t.originalCurrency !== 'AED'
                    && (t.fxMarkupPctApplied == null) && !t.linkedStatementId
                  );
                  const foreignTotal = candidates.reduce((s, t) => s + (Number(t.amount) || 0), 0);
                  const impliedMarkupPct = foreignTotal > 0 ? (drift / foreignTotal) * 100 : 0;
                  const markupSensible = candidates.length > 0
                    && Math.abs(impliedMarkupPct) > 0.1 && Math.abs(impliedMarkupPct) < 20 && drift > 0;
                  const ignore7d = () => {
                    try { localStorage.setItem('floosi_drift_snooze_' + a.cardId, String(Date.now() + 7 * 86400000)); } catch { /* ignore */ }
                    onResolve(a.id);
                  };
                  return (
                    <div key={a.id} className="p-3">
                      <button onClick={() => setExpandedId(isOpen ? null : a.id)} className="w-full text-left flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-rose-200">
                            {cardName(a.cardId)} — {pct > 0 ? `off by ${pct.toFixed(1)}% (${fmtMoney(Math.abs(drift), ccy, false)})` : `${fmtMoney(Math.abs(drift), ccy, false)} off the bank`}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            {floosiShowsLess
                              ? `Floosi shows ${fmtMoney(Math.abs(drift), ccy, false)} less spent than the bank${markupSensible ? ` — likely ~${impliedMarkupPct.toFixed(1)}% FX markup` : ''}. Tap for options.`
                              : `Floosi shows ${fmtMoney(Math.abs(drift), ccy, false)} more spent than the bank — likely a duplicate or extra. Tap for options.`}
                          </div>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="mt-3 pl-6 space-y-2">
                          {/* 1) Adjust FX rate — only when there are foreign txs to amend */}
                          {markupSensible && (
                            <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-2 space-y-2">
                              <div className="text-[11px] text-slate-300">
                                Adjust FX rate — apply <span className="font-semibold text-cyan-300">{impliedMarkupPct.toFixed(2)}%</span> to {candidates.length} foreign tx{candidates.length === 1 ? '' : 's'}:
                              </div>
                              <div className="text-[10px] text-slate-400 space-y-0.5 max-h-[120px] overflow-y-auto">
                                {candidates.map((t) => {
                                  const before = Number(t.amount) || 0;
                                  const after = Math.round(before * (1 + impliedMarkupPct / 100) * 100) / 100;
                                  return (
                                    <div key={t.id} className="flex justify-between gap-2">
                                      <span className="truncate flex-1">{t.description}{t.originalCurrency ? ` (${t.originalCurrency} ${t.originalAmount})` : ''}</span>
                                      <span className="font-mono whitespace-nowrap">{before.toFixed(2)} → {after.toFixed(2)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                              <button onClick={() => onApplyMarkup(a.id, impliedMarkupPct, candidates.map(t => t.id))} className="w-full py-1.5 rounded bg-cyan-600 hover:bg-cyan-500 text-xs font-medium text-white">
                                Apply {impliedMarkupPct.toFixed(2)}% FX markup
                              </button>
                            </div>
                          )}
                          {/* 2) Edit existing (non-statement-verified) transactions */}
                          <button onClick={() => { onResolve(a.id); onNavigate?.('transactions', { filter: { card: cardName(a.cardId), source: 'unverified', showFilters: true } }); }} className="w-full py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] text-slate-200">
                            Edit transactions (not yet on a statement)
                          </button>
                          {/* 3) Add a missing tx — only when Floosi shows LESS than the bank */}
                          {floosiShowsLess && (
                            <button onClick={() => { onResolve(a.id); onAddTransaction?.(); }} className="w-full py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] text-slate-200">
                              Add a missing transaction
                            </button>
                          )}
                          {/* 4) Ignore — snooze this card's drift for ~1 week */}
                          <button onClick={ignore7d} className="w-full py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] text-slate-400">
                            Ignore for now (re-checks in ~1 week)
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {activeTidy.length > 0 && (
              <div className="divide-y divide-slate-800">
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Review &amp; tidy</div>
                {activeTidy.map((it: any) => {
                  const open = expandedTidy === it.key;
                  return (
                  <div key={it.key}>
                    <button
                      onClick={() => it.expandable ? setExpandedTidy(open ? null : it.key) : onNavigate?.(it.tab, { highlightIds: it.highlightIds, filter: it.filter })}
                      className="w-full text-left p-3 flex items-start gap-2 hover:bg-slate-800/40"
                    >
                      <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${it.color === 'cyan' ? 'text-cyan-400' : it.color === 'rose' ? 'text-rose-400' : it.color === 'emerald' ? 'text-emerald-400' : 'text-amber-400'}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${it.color === 'cyan' ? 'text-cyan-200' : it.color === 'rose' ? 'text-rose-200' : it.color === 'emerald' ? 'text-emerald-200' : 'text-amber-200'}`}>{it.title}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{it.detail}</div>
                      </div>
                      {it.expandable
                        ? <ChevronDown className={`w-4 h-4 text-slate-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                        : <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />}
                    </button>
                    {it.expandable && open && it.key === 'dupes' && (
                      <div className="px-3 pb-3 pt-0 space-y-2">
                        <button
                          onClick={() => onMarkDuplicatesIntentional?.(tidy.dupeIds || [])}
                          className="w-full py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] text-slate-200"
                        >
                          Not a duplicate — keep both
                        </button>
                        <button
                          onClick={() => onNavigate?.('transactions', { filter: { source: 'duplicate', showFilters: true } })}
                          className="w-full py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] text-slate-200"
                        >
                          Review duplicates →
                        </button>
                      </div>
                    )}
                    {it.expandable && open && it.key?.startsWith('recremind:') && (
                      <div className="px-3 pb-3 pt-0 space-y-2">
                        <button
                          onClick={() => onMarkRecReminderHandled?.(it.rr.ruleId, it.rr.occKey)}
                          className="w-full py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] text-slate-200"
                        >
                          Handled — stop reminders for this one
                        </button>
                        <button
                          onClick={() => onNavigate?.('recurring')}
                          className="w-full py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] text-slate-200"
                        >
                          Open in Recurring →
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}

            {(resolved.length > 0 || readTidy.length > 0 || readStatements.length > 0 || (tidy.recRemindersPassed || []).length > 0) && (
              <div className="divide-y divide-slate-800/60">
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Recent</div>
                {(tidy.recRemindersPassed || []).map((rr: any) => (
                  <div key={`recpassed-${rr.ruleId}-${rr.occKey}`} className="px-3 py-2.5 flex items-start gap-2 opacity-75">
                    <CheckCircle2 className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium text-slate-300">{rr.desc} — was due {rr.occKey.slice(5)}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{rr.type === 'income' ? 'Incoming' : 'Payment'} ~{fmtMoney(rr.amount, rr.currency, false)} · {rr.daysAgo === 1 ? 'yesterday' : `${rr.daysAgo}d ago`}</div>
                    </div>
                  </div>
                ))}
                {readStatements.map((s: any) => (
                  <button
                    key={s.cardId}
                    onClick={() => onNavigate?.('cards')}
                    className="w-full text-left px-3 py-2.5 flex items-start gap-2 opacity-75 hover:opacity-100 hover:bg-slate-800/40"
                  >
                    <FileText className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium text-slate-300">
                        {s.cardName} — {s.paid > 0.01 ? `${Math.round((s.remaining / s.due) * 100)}% (${fmtMoney(s.remaining, s.currency, false)}) remaining` : `${fmtMoney(s.remaining, s.currency, false)} due`}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {s.overdue ? `Overdue by ${Math.abs(s.days)}d` : s.days === 0 ? 'Due today' : s.days === 1 ? 'Due tomorrow' : `Due in ${s.days} days`}
                      </div>
                    </div>
                  </button>
                ))}
                {readTidy.map((it: any) => (
                  <button
                    key={it.key}
                    onClick={() => onNavigate?.(it.tab, { filter: it.filter })}
                    className="w-full text-left px-3 py-2.5 flex items-start gap-2 opacity-75 hover:opacity-100 hover:bg-slate-800/40"
                  >
                    <CheckCircle2 className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium text-slate-300">{it.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{it.detail}</div>
                    </div>
                  </button>
                ))}
                {resolved.map((a) => {
                  const drift = Number(a.delta) || 0;
                  const ccy = cardCcy(a.cardId);
                  return (
                    <div key={a.id} className="px-3 py-2.5 flex items-start gap-2 opacity-75">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400/70 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium text-slate-300">{cardName(a.cardId)} — drift {fmtMoney(Math.abs(drift), ccy, false)}</div>
                        <div className="text-[11px] text-emerald-300/80 mt-0.5">{a.resolutionNote || `Resolved on ${a.resolvedAt?.slice(0, 10)}`}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Modal launched from the bell-icon notification panel's "Needs your input"
// section. The raw SMS body is shown verbatim so the user can sanity-check
// the parse. The user fills in from / to / date / description, hits Save,
// and the tx gets created + pending_completion is cleared on the raw row.
function CompleteSmsModal({ raw, cards = [], accounts = [], categories = [], loans = [], onSave, onDismiss, onClose }) {
  const pr = (raw?.parseResult || {}) as any;
  const isCcPayment = pr.kind === 'mashreq_cc_payment';
  const isAccountCredit = pr.kind === 'account_credit';
  const isAccountDebit = pr.kind === 'account_debit';
  // Default tx type: account_credit → income; everything else → transfer.
  // User can toggle income vs transfer for credits (e.g. "this was actually
  // a transfer from my own Mashreq account, not external income").
  const [txType, setTxType] = useState<'transfer' | 'income' | 'expense'>(isAccountCredit ? 'income' : 'transfer');
  const expenseCats = (categories || []).filter((c: any) => !c.deletedAt && c.kind === 'expense');
  const incomeCats = (categories || []).filter((c: any) => !c.deletedAt && c.kind === 'income');
  const liveLoans = (loans || []).filter((l: any) => !l.deletedAt);
  const [form, setForm] = useState({
    description: isCcPayment
      ? `Credit card payment${pr.toCardLast4 ? ` *${pr.toCardLast4}` : ''}`
      : isAccountCredit
        ? (pr.counterparty || 'Incoming credit')
        : isAccountDebit
          ? (pr.counterparty || 'Outgoing debit')
          : 'Bank transfer',
    date: pr.date || (raw?.receivedAt ? new Date(raw.receivedAt).toISOString().slice(0, 10) : ''),
    amountAed: String(pr.amount ?? ''),
    fromAccountId: isAccountDebit ? (pr.acctId || '') : (pr.fromAccountIdGuess || ''),
    fromCardId: '',
    toAccountId: isAccountCredit ? (pr.acctId || '') : '',
    toCardId: pr.toCardId || '',
    toLoanId: '',
    categoryId: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const liveAccounts = (accounts || []).filter((a: any) => !a.deletedAt);
  const liveCards = (cards || []).filter((c: any) => !c.deletedAt);

  const fromValue = form.fromAccountId ? `a:${form.fromAccountId}` : form.fromCardId ? `c:${form.fromCardId}` : '';
  const toValue = form.toAccountId ? `a:${form.toAccountId}` : form.toCardId ? `c:${form.toCardId}` : form.toLoanId ? `l:${form.toLoanId}` : '';
  const pickFrom = (v: string) => {
    if (!v) return setForm(f => ({ ...f, fromAccountId: '', fromCardId: '' }));
    const [k, id] = v.split(':');
    setForm(f => ({ ...f, fromAccountId: k === 'a' ? id : '', fromCardId: k === 'c' ? id : '' }));
  };
  const pickTo = (v: string) => {
    if (!v) return setForm(f => ({ ...f, toAccountId: '', toCardId: '', toLoanId: '' }));
    const [k, id] = v.split(':');
    setForm(f => ({ ...f, toAccountId: k === 'a' ? id : '', toCardId: k === 'c' ? id : '', toLoanId: k === 'l' ? id : '' }));
  };

  const amt = parseFloat(form.amountAed);
  const fromOk = !!(form.fromAccountId || form.fromCardId);
  const toOk = !!(form.toAccountId || form.toCardId || form.toLoanId);
  const sameEnd = (form.fromAccountId && form.fromAccountId === form.toAccountId)
    || (form.fromCardId && form.fromCardId === form.toCardId);
  const baseValid = !!form.description.trim() && !!form.date && Number.isFinite(amt) && amt > 0;
  const valid = baseValid && (
    txType === 'income'  ? (!!form.toAccountId && !!form.categoryId)
    : txType === 'expense' ? (!!form.fromAccountId && !!form.categoryId)
    : (fromOk && toOk && !sameEnd)
  );

  return (
    <Modal title="Complete SMS — fill in transfer details" onClose={onClose} wide>
      <div className="space-y-3">
        <div className="bg-slate-950/60 border border-slate-800 rounded-lg p-2 text-[11px] text-slate-400 font-mono whitespace-pre-wrap break-words">
          {raw?.body}
        </div>
        {pr.refNo && (
          <div className="text-[11px] text-slate-500">Bank ref: <span className="font-mono text-slate-300">{pr.refNo}</span></div>
        )}
        {/* Always offer all three: a forwarded "fund transfer" SMS can be a move
            between your own accounts, money sent OUT to an external party (expense),
            or money received from outside (income). */}
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => setTxType('transfer')}
            className={`py-2 rounded-lg text-sm font-medium ${txType === 'transfer' ? 'bg-cyan-500/20 border border-cyan-500/40 text-cyan-200' : 'bg-slate-800 text-slate-400'}`}
          >Transfer (own)</button>
          <button
            type="button"
            onClick={() => setTxType('expense')}
            className={`py-2 rounded-lg text-sm font-medium ${txType === 'expense' ? 'bg-rose-500/20 border border-rose-500/40 text-rose-200' : 'bg-slate-800 text-slate-400'}`}
          >Expense (external)</button>
          <button
            type="button"
            onClick={() => setTxType('income')}
            className={`py-2 rounded-lg text-sm font-medium ${txType === 'income' ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200' : 'bg-slate-800 text-slate-400'}`}
          >Income (external)</button>
        </div>
        <Field label="Description">
          <input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="input" />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Date">
            <input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} className="input" />
          </Field>
          <Field label="Amount (AED)">
            <input
              type="text" inputMode="decimal"
              value={form.amountAed}
              onChange={(e) => setForm(f => ({ ...f, amountAed: e.target.value.replace(/[^\d.\-]/g, '') }))}
              className="input text-right font-mono"
            />
          </Field>
        </div>
        {txType === 'income' ? (
          <div className="grid grid-cols-2 gap-2">
            <Field label="To account">
              <select value={form.toAccountId} onChange={(e) => setForm(f => ({ ...f, toAccountId: e.target.value }))} className="input">
                <option value="">Pick destination…</option>
                {liveAccounts.map((a: any) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
              </select>
            </Field>
            <Field label="Category">
              <select value={form.categoryId} onChange={(e) => setForm(f => ({ ...f, categoryId: e.target.value }))} className="input">
                <option value="">Pick category…</option>
                {incomeCats.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>
        ) : txType === 'expense' ? (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Paid from">
              <select value={form.fromAccountId} onChange={(e) => setForm(f => ({ ...f, fromAccountId: e.target.value, fromCardId: '' }))} className="input">
                <option value="">Pick account…</option>
                {liveAccounts.map((a: any) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
              </select>
            </Field>
            <Field label="Category">
              <select value={form.categoryId} onChange={(e) => setForm(f => ({ ...f, categoryId: e.target.value }))} className="input">
                <option value="">Pick category…</option>
                {expenseCats.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Field label="From">
              <select value={fromValue} onChange={(e) => pickFrom(e.target.value)} className="input">
                <option value="">Pick source…</option>
                {liveAccounts.map((a: any) => <option key={`a-${a.id}`} value={`a:${a.id}`}>{a.name} ({a.currency})</option>)}
                {liveCards.map((c: any) => <option key={`c-${c.id}`} value={`c:${c.id}`}>{c.name} (card)</option>)}
              </select>
            </Field>
            <Field label="To">
              <select value={toValue} onChange={(e) => pickTo(e.target.value)} className="input">
                <option value="">Pick destination…</option>
                {liveAccounts.map((a: any) => <option key={`a-${a.id}`} value={`a:${a.id}`}>{a.name} ({a.currency})</option>)}
                {liveCards.map((c: any) => <option key={`c-${c.id}`} value={`c:${c.id}`}>{c.name} (card)</option>)}
                {liveLoans.length > 0 && (
                  <optgroup label="Loans — pay down">
                    {liveLoans.map((l: any) => <option key={`l-${l.id}`} value={`l:${l.id}`}>{l.name}</option>)}
                  </optgroup>
                )}
              </select>
            </Field>
          </div>
        )}
        <Field label="Notes (optional)">
          <input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="input" />
        </Field>
        {sameEnd && <div className="text-xs text-rose-400">Source and destination can't be the same.</div>}
        <div className="flex gap-2 pt-2">
          <button onClick={onDismiss} disabled={saving} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">
            Dismiss (no tx)
          </button>
          <button
            onClick={async () => { setSaving(true); try { await onSave({ ...form, txType }); } finally { setSaving(false); } }}
            disabled={saving || !valid}
            className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-sm font-medium"
          >
            {saving ? 'Saving…' : (txType === 'income' ? 'Save income' : txType === 'expense' ? 'Save expense' : 'Save transfer')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, wide, xWide, staticBackdrop, children }) {
  // Close on Escape — unless the modal is "static" (must dismiss via X/confirm).
  useEffect(() => {
    if (staticBackdrop) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, staticBackdrop]);

  const widthClass = xWide ? 'max-w-[96vw]' : wide ? 'max-w-2xl' : 'max-w-md';
  const panelRef = useRef<HTMLDivElement>(null);
  // Move focus into the dialog on open so keyboard/screen-reader users land
  // inside it (basic a11y; full focus-trap is a later polish).
  useEffect(() => {
    const el = panelRef.current?.querySelector<HTMLElement>('input,select,textarea,button,[tabindex]');
    (el || panelRef.current)?.focus();
  }, []);
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={staticBackdrop ? undefined : onClose}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} tabIndex={-1} className={`bg-slate-900 border border-slate-800 rounded-xl ${widthClass} w-full max-h-[90vh] overflow-y-auto outline-none`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-800 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// Mounted once at the app root. Backs confirmDialog()/toast() with themed,
// RN-portable UI and registers them with src/lib/dialogs.ts.
export function DialogHost() {
  const [confirmReq, setConfirmReq] = useState<any>(null); // {message,title,confirmLabel,danger,resolve}
  const [toasts, setToasts] = useState<any[]>([]);
  useEffect(() => {
    registerDialogs(
      (o) => new Promise<boolean>((resolve) => setConfirmReq({ ...o, resolve })),
      (message, kind = 'info') => {
        const id = uid('toast');
        setToasts((t) => [...t, { id, message, kind }]);
        setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500);
      },
    );
  }, []);
  const close = (val) => { confirmReq?.resolve(val); setConfirmReq(null); };
  return (
    <>
      {confirmReq && (
        <Modal title={confirmReq.title || 'Please confirm'} onClose={() => close(false)}>
          <div className="space-y-4">
            <div className="text-sm text-slate-300 whitespace-pre-wrap">{confirmReq.message}</div>
            <div className="flex gap-2">
              <button onClick={() => close(false)} className="flex-1 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm min-h-[44px]">Cancel</button>
              <button onClick={() => close(true)} className={`flex-1 py-2.5 rounded-lg text-sm font-medium min-h-[44px] ${confirmReq.danger ? 'bg-rose-600 hover:bg-rose-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}>{confirmReq.confirmLabel || 'Confirm'}</button>
            </div>
          </div>
        </Modal>
      )}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[60] space-y-2 max-w-[calc(100vw-2rem)]">
          {toasts.map((t) => (
            <div
              key={t.id}
              onClick={() => setToasts((cur) => cur.filter((x) => x.id !== t.id))}
              className={`px-4 py-2.5 rounded-lg shadow-lg text-sm border cursor-pointer ${t.kind === 'error' ? 'bg-rose-950 border-rose-800 text-rose-100' : t.kind === 'success' ? 'bg-emerald-950 border-emerald-800 text-emerald-100' : 'bg-slate-800 border-slate-700 text-slate-100'}`}
            >
              {t.message}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Field({ label, children, className = '' }) {
  return (
    // flex-col + justify-end: grid cells stretch to the tallest in their row,
    // and bottom-aligning the content makes a 1-line label and a 2-line label
    // keep their inputs lined up — with the extra slack sitting ABOVE the label
    // (barely noticeable) instead of as a gap between the label and its input.
    // Standalone (non-grid) fields aren't stretched, so they're unaffected.
    <div className={`flex flex-col justify-end ${className}`}>
      <label className="text-xs text-slate-400 mb-1 leading-tight">{label}</label>
      {children}
    </div>
  );
}

// Inject input styles once at module level (not inside Modal which re-renders)
if (typeof document !== 'undefined' && !document.getElementById('finance-app-styles')) {
  const style = document.createElement('style');
  style.id = 'finance-app-styles';
  style.textContent = `
    .input {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;
      min-height: 2.375rem;        /* keep every field (incl. date) the same height */
      line-height: 1.25rem;
      background: rgb(30 41 59);
      border: 1px solid rgb(51 65 85);
      border-radius: 0.5rem;
      padding: 0.5rem 0.75rem;
      font-size: 0.875rem;
      outline: none;
      color: rgb(241 245 249);
      transition: border-color 0.15s;
    }
    /* Date/time inputs on iOS Safari keep an intrinsic width that overflows
       grid cells and render at an odd height — normalize them so they shrink
       to the cell AND match the height of the text inputs. */
    .input[type="date"], .input[type="time"], .input[type="datetime-local"] {
      -webkit-appearance: none;
      appearance: none;
      text-align: left;
    }
    /* iOS Safari renders the date's value in this pseudo-element and centers it
       by default — force it left so date fields read like every other input. */
    .input::-webkit-date-and-time-value { text-align: left; }
    .input::-webkit-datetime-edit { text-align: left; }
    /* One exact height for every single-line field (text / number / date /
       select) so two fields in a grid row never differ. Textareas grow. */
    input.input, select.input { height: 2.375rem; }
    textarea.input { min-height: 3.5rem; height: auto; }
    .input:focus { border-color: rgb(16 185 129); }
    .input:disabled { opacity: 0.5; cursor: not-allowed; }
  `;
  document.head.appendChild(style);
}
